/**
 * ChatPanel.jsx
 * ------------------------------------------------------------
 * 🆕 UPDATED 2024-12-20: Ultra-Compact Welcome Screen + Start Over Button
 * - Welcome screen now ultra-compact to ensure input is always visible
 * - "Start Over" button added to reset conversation
 * - Welcome screen logic: shows when only greeting message exists
 * - Input panel always visible below welcome screen
 * 
 * PREVIOUS FEATURES:
 * ✅ Welcome screen with prompt cards (now ultra-compact)
 * ✅ Spark Store modal with activation
 * ✅ Dark theme support
 * ✅ Transcript loading and debugging
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import * as microsoftTeams from "@microsoft/teams-js";
import { Button, Input, Text } from "@fluentui/react-components";
import {
    PublicClientApplication,
    InteractionRequiredAuthError,
} from "@azure/msal-browser";
import AgentValidation from "./AgentValidation";
import CreateSparkPanel from "./CreateSparkPanel";
import GuidedTour from "./GuidedTour";
import SttTabViewer from "./SttTabViewer";
import O365WriteActionDialog from "./O365WriteActionDialog";
import { useFeatureFlags } from "./FeatureFlags";
import AdminFlagsPanel from "./AdminFlagsPanel";
import UsageLogService from "../services/UsageLogService";
import SparkStoreModelsService from "../services/SparkStoreModelsService";
import sparkDBService from "../services/SparkIndexedDBService";
import ChatHistoryService from "../services/ChatHistoryService";
import "../index.css";

import ApiClient from "../services/ApiClient";
import AuthTokenProvider from "../services/AuthTokenProvider";
import useIsAdmin from "../hooks/useIsAdmin";

// MSAL Configuration for Graph API token acquisition
const GRAPH_MSAL_CONFIG = {
    auth: {
        clientId: "52cb6025-bca2-447e-acdf-b7603c63bae1",
        authority: "https://login.microsoftonline.com/8f3e36ea-8039-4b40-81a7-7dc0599e8645",
        redirectUri: `${window.location.origin}/auth.html`,
        navigateToLoginRequestUrl: false,
        supportsNestedAppAuth: true,
    },
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: false,
    },
    system: {
        allowRedirectInIframe: true,
        windowHashTimeout: 60000,
        iframeHashTimeout: 10000,
        loadFrameTimeout: 60000,
        asyncPopups: false,
    },
};

// Request scopes for all O365 resources: Outlook, Teams, OneDrive, Calendar
// These scopes are ALL already enabled in Azure AD as Delegated permissions
const GRAPH_TOKEN_REQUEST = {
    scopes: [
        // Identity & Profile
        "openid",
        "profile",
        "User.Read",

        // Outlook Email
        "Mail.Read",
        "Mail.Send",

        // Teams Chat & Channels
        "Chat.Read",
        "Chat.ReadWrite",
        "Channel.ReadBasic.All",
        "ChannelMessage.Send",
        "Team.ReadBasic.All",

        // Calendar
        "Calendars.Read",
        "Calendars.ReadBasic",
        "OnlineMeetings.Read",

        // OneDrive & SharePoint Files
        "Files.Read",
        "Files.ReadWrite",
    ],
};

// Initialize MSAL for Graph API
let graphMsalInstance = null;
try {
    graphMsalInstance = new PublicClientApplication(GRAPH_MSAL_CONFIG);
    console.log("✅ MSAL initialized for Graph API token acquisition");
} catch (err) {
    console.warn("⚠️ MSAL initialization failed:", err.message);
}

// Spark Fetch constants
const SPARK_FETCH_CONFIG = {
    // Batch size: Number of pages to fetch in parallel
    batchSize: 20,

    // Max retries per batch if it fails
    maxRetries: 2,

    // Delay between batches (ms) - helps prevent rate limiting
    batchDelay: 0, // Set to 100-200 if hitting rate limits

    // Enable adaptive batch sizing (experimental)
    adaptive: true
};

// Live recording timer — updates every second
const RecordingTimer = ({ startedAt, color }) => {
    const [label, setLabel] = React.useState("0:00");
    React.useEffect(() => {
        const tick = () => {
            const s = Math.floor((Date.now() - startedAt) / 1000);
            setLabel(`${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [startedAt]);
    return (
        <span style={{ fontSize: 11, fontWeight: 700, color, letterSpacing: "0.5px", flexShrink: 0 }}>
            {label}
        </span>
    );
};

// Reusable ghost icon button for the meeting toolbar
const MeetIconBtn = ({
    onClick, disabled, title, children, color,
    danger, dangerColor,
    isDark, cardBorder, textSecondary, textColor,
}) => {
    const [hov, setHov] = React.useState(false);
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            disabled={disabled}
            onMouseEnter={() => setHov(true)}
            onMouseLeave={() => setHov(false)}
            style={{
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                border: `1px solid ${danger && hov && !disabled ? dangerColor + "55" : cardBorder}`,
                background: hov && !disabled
                    ? danger ? `${dangerColor}15` : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.07)")
                    : "transparent",
                color: disabled ? textSecondary
                    : danger && hov ? dangerColor
                        : color || textColor,
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.35 : 1,
                fontSize: 15,
                transition: "background 0.13s, border-color 0.13s, color 0.13s",
            }}
        >
            {children}
        </button>
    );
};

// Mic SVG icon
const SvgMic = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="2" width="6" height="11" rx="3" />
        <path d="M5 10a7 7 0 0 0 14 0" />
        <line x1="12" y1="19" x2="12" y2="22" />
        <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
);

// Stop SVG icon
const SvgStop = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
        <rect x="4" y="4" width="16" height="16" rx="3" />
    </svg>
);

// Spark brand icon — 8-point seal badge with asterisk (store button + message avatars)
// Spark brand icon — 10-bump wavy badge with stylised A (matches actual Spark Assist icon)
const SparkLogoIcon = ({ size = 20, color = "#0891b2" }) => (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none" aria-hidden="true">
        {/* 10-bump wavy badge outline */}
        <path
            d="M 93 50
               C 93 55, 85.7 56.3, 84.2 61.1
               C 82.7 65.9, 87.7 71.3, 84.8 75.3
               C 81.9 79.3, 75.2 76.2, 71.2 79.1
               C 67.2 82,   68.1 89.4, 63.3 90.9
               C 58.5 92.4, 55 86,   50 86
               C 45 86,   41.5 92.4, 36.7 90.9
               C 31.9 89.4, 32.8 82,   28.8 79.1
               C 24.8 76.2, 18.1 79.3, 15.2 75.3
               C 12.3 71.3, 17.3 65.9, 15.8 61.1
               C 14.3 56.3, 7 55,   7 50
               C 7 45,   14.3 43.7, 15.8 38.9
               C 17.3 34.1, 12.3 28.7, 15.2 24.7
               C 18.1 20.7, 24.8 23.8, 28.8 20.9
               C 32.8 18,   31.9 10.6, 36.7 9.1
               C 41.5 7.6,  45 14,   50 14
               C 55 14,   58.5 7.6,  63.3 9.1
               C 68.1 10.6, 67.2 18,   71.2 20.9
               C 75.2 23.8, 81.9 20.7, 84.8 24.7
               C 87.7 28.7, 82.7 34.1, 84.2 38.9
               C 85.7 43.7, 93 45,   93 50 Z"
            stroke={color} strokeWidth="3.5" strokeLinejoin="round" fill="none"
        />
        {/* Stylised A — main legs + crossbar */}
        <path
            d="M 34 70 L 50 27 L 66 70 M 41 52 L 59 52"
            stroke={color} strokeWidth="5" fill="none"
            strokeLinecap="round" strokeLinejoin="round"
        />
        {/* Diagonal crossing stroke (matches actual brand icon) */}
        <path
            d="M 33 67 L 64 30"
            stroke={color} strokeWidth="4" fill="none" strokeLinecap="round"
        />
    </svg>
);

// ── Markdown → HTML converter (fallback when model returns markdown instead of HTML) ──
const mdToHtml = (text) => {
    if (!text) return "";
    // If already contains HTML tags, return as-is
    if (/<(h[1-6]|p|ul|ol|li|table|div|strong|em|br)\b/i.test(text)) return text;

    let h = text
        // Escape HTML special chars first (except we need to keep & for now)
        .replace(/&(?![a-zA-Z]+;)/g, "&amp;")
        // Headers
        .replace(/^#### (.+)$/gm, "<h4>$1</h4>")
        .replace(/^### (.+)$/gm, "<h3>$1</h3>")
        .replace(/^## (.+)$/gm, "<h2>$1</h2>")
        .replace(/^# (.+)$/gm, "<h1>$1</h1>")
        // Bold & italic
        .replace(/\*\*\*(.+?)\*\*\*/g, "<strong><em>$1</em></strong>")
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
        .replace(/\*(.+?)\*/g, "<em>$1</em>")
        // Horizontal rule
        .replace(/^---+$/gm, "<hr/>")
        // Unordered lists — line-start dashes
        .replace(/^[-*•] (.+)$/gm, "<li>$1</li>")
        // Inline sentence dashes: " - Label: text" → break + bullet
        .replace(/ - ([A-Z][^:]{1,40}:)/g, "<br/>• <strong>$1</strong>")
        // Numbered sections: "1. Title" at start of line → h3
        .replace(/^(\d+)\.\s+([A-Z][^\n]{3,60})$/gm, "<h3>$1. $2</h3>")
        // Numbered lists
        .replace(/^\d+\. (.+)$/gm, "<li>$1</li>")
        // Wrap consecutive <li> into <ul>
        .replace(/(<li>[\s\S]*?<\/li>)(\s*(<li>[\s\S]*?<\/li>))*/g, (m) => `<ul>${m}</ul>`);

    // Paragraphs: double newlines → paragraph breaks
    h = h.split(/\n{2,}/).map(block => {
        const t = block.trim();
        if (!t) return "";
        if (/^<(h[1-6]|ul|ol|hr|table|div)/i.test(t)) return t;
        return `<p>${t.replace(/\n/g, "<br/>")}</p>`;
    }).join("\n");

    return h;
};


// ── Detect email-send intent from a user prompt ────────────────────────────
// Returns null or { type: "send_email", recipientHint: string|null }
const detectEmailIntent = (userText) => {
    if (!userText) return null;
    const lower = userText.toLowerCase();
    // Must contain send/email/compose/draft AND some email-sending noun
    const hasEmailVerb = /\b(send|email|compose|draft|mail)\b/.test(lower);
    const hasEmailNoun = /\b(email|mail|message)\b/.test(lower);
    if (!hasEmailVerb || !hasEmailNoun) return null;

    // 1. Try full email address first
    let recipientHint = null;
    const addrMatch = lower.match(/(?:send(?:\s+(?:this|an?|the)\s+(?:email|mail))?|email|mail|draft(?:\s+an?)?\s+email)\s+(?:it\s+)?to\s+([a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,})(?:\s+(?:and|with|about|,)|$)/);
    if (addrMatch) {
        recipientHint = addrMatch[1].trim();
    } else {
        // 2. Fall back to display name
        const nameMatch = lower.match(/(?:send(?:\s+(?:this|an?|the)\s+(?:email|mail))?|email|mail|draft(?:\s+an?)?\s+email)\s+(?:it\s+)?to\s+([a-z][a-z0-9\s._'-]{0,40}?)(?:\s+(?:and|with|about|,)|$)/);
        if (nameMatch) recipientHint = nameMatch[1].trim();
    }
    return { type: "send_email", recipientHint };
};

const detectTeamsPostIntent = (userText) => {
    if (!userText) return null;
    const lower = userText.toLowerCase();
    const hasVerb = /\b(post|share|send|forward|broadcast)\b/.test(lower);
    const hasNoun = /\b(teams|channel|chat|group)\b/.test(lower);
    if (!hasVerb || !hasNoun) return null;
    return { type: "post_to_teams" };
};

const detectCalendarIntent = (userText) => {
    if (!userText) return null;
    const lower = userText.toLowerCase();
    const hasVerb = /\b(schedule|create|set up|book|organise|organize|arrange|add)\b/.test(lower);
    const hasNoun = /\b(meeting|invite|calendar|event|call|sync|appointment)\b/.test(lower);
    if (!hasVerb || !hasNoun) return null;
    // Try to pull a title hint from the prompt
    let titleHint = null;
    const withMatch = lower.match(/(?:schedule|create|set up|book|arrange)\s+(?:a\s+)?(?:meeting|call|sync|event|invite)?\s*(?:for|about|on|regarding)?\s*([a-z][a-z0-9 _\-']{3,60}?)(?:\s+(?:on|at|for|with|tomorrow|next|monday|tuesday|wednesday|thursday|friday|,|$))/);
    if (withMatch) titleHint = withMatch[1].trim();
    return { type: "create_calendar", titleHint };
};

// ── Strip Spark "thinking" progress blocks + stray HTML tags ─────────────────
const sanitizeSparkResponse = (raw) => {
    if (!raw) return "";
    let text = raw;

    // 1. Remove <blockquote>...<details>...</details>...</blockquote> thinking blocks
    text = text.replace(/<blockquote[\s\S]*?<\/blockquote>/gi, "");

    // 2. Also catch bare <details>...</details> blocks (no wrapping blockquote)
    text = text.replace(/<details[\s\S]*?<\/details>/gi, "");

    // 3. Convert common inline HTML → markdown equivalents
    text = text.replace(/<strong>([\s\S]*?)<\/strong>/gi, "**$1**");
    text = text.replace(/<b>([\s\S]*?)<\/b>/gi, "**$1**");
    text = text.replace(/<em>([\s\S]*?)<\/em>/gi, "_$1_");
    text = text.replace(/<i>([\s\S]*?)<\/i>/gi, "_$1_");
    text = text.replace(/<br\s*\/?>/gi, "\n");
    text = text.replace(/<p>([\s\S]*?)<\/p>/gi, "$1\n\n");
    text = text.replace(/<li>([\s\S]*?)<\/li>/gi, "- $1\n");
    text = text.replace(/<\/?ul>/gi, "\n");
    text = text.replace(/<\/?ol>/gi, "\n");
    text = text.replace(/<a\s+href="([^"]*)"[^>]*>([\s\S]*?)<\/a>/gi, "[$2]($1)");

    // 4. Strip any remaining unrecognised HTML tags
    text = text.replace(/<[^>]+>/g, "");

    // 5. Decode common HTML entities
    text = text
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, " ");

    // 6. Collapse 3+ consecutive blank lines → max 2
    text = text.replace(/\n{3,}/g, "\n\n");

    return text.trim();
};


// ── Parse PPT HTML into individual slide objects ──────────────────────────────
const parseDocSlides = (html, title) => {
    if (!html) return [];

    // ── Strategy 1: proper <div class="slide"> HTML ───────────────────────────
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
        const slideDivs = doc.querySelectorAll("div.slide");
        if (slideDivs.length > 1) {
            return Array.from(slideDivs).map((el, i) => {
                const titleEl = el.querySelector(".slide-title, h2");
                let rawTitle = titleEl?.textContent?.trim() || `Slide ${i + 1}`;

                // Fix: model sometimes puts "Short Title Content: full body text" in the title
                // Split on " Content:" or " - " to recover body text from bloated title
                let recoveredBody = "";
                const contentSplit = rawTitle.match(/^(.{3,60}?)\s+(?:Content\s*:\s*|[-–]\s+)(.{10,})$/i);
                if (contentSplit) {
                    rawTitle = contentSplit[1].trim();
                    recoveredBody = `<p>${contentSplit[2].trim()}</p>`;
                }

                titleEl?.remove();
                const contentEl = el.querySelector(".slide-content");
                const existingBody = contentEl ? contentEl.innerHTML : el.innerHTML;

                // Merge recovered body with existing slide-content
                const finalBody = recoveredBody && existingBody.trim().length < 20
                    ? recoveredBody
                    : existingBody || recoveredBody;

                return { index: i, title: rawTitle, body: finalBody };
            });
        }
    } catch (e) { /* ignore */ }

    // ── Strategy 2: split on "Slide N:", "Title Slide:", "Slide N -" patterns ──
    // Strip all HTML tags to get plain text for splitting
    const plain = html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

    // Match "Title Slide:", "Slide 1:", "Slide 1.", "Slide 1 -", "Slide 1 –"
    const splitPattern = /(?:^|\.\s+|\n)(?=(?:Title\s+Slide|Slide\s+\d+)\s*[:.)\-–])/gi;
    const textChunks = plain.split(splitPattern).map(s => s.trim()).filter(s =>
        s.length > 10 && /(?:Title\s+Slide|Slide\s+\d+)\s*[:.)\-–]/i.test(s)
    );

    if (textChunks.length > 1) {
        return textChunks.map((chunk, i) => {
            // Extract heading — everything before first period/newline after the "Slide N:" prefix
            const headingMatch = chunk.match(/^(?:Title\s+Slide|Slide\s+\d+)\s*[:.)\-–]\s*([^.\n]{3,80})/i);
            const slideTitle = headingMatch
                ? headingMatch[1].replace(/\*+/g, "").trim()
                : `Slide ${i + 1}`;

            // Body = everything after the heading line
            const afterHeading = chunk.replace(/^(?:Title\s+Slide|Slide\s+\d+)\s*[:.)\-–]\s*[^\n]*/i, "").trim();

            // Convert to proper HTML list items
            const bodyHtml = afterHeading
                // "- Label: text" → list item
                .replace(/(?:^| {2,}|\. )- ([A-Z][^:]{1,50}:)\s*/g, "\n• $1 ")
                // Remaining "- text" lines → bullets
                .split(/\n/)
                .map(line => {
                    const l = line.trim();
                    if (!l) return "";
                    if (/^[•\-*]/.test(l)) return `<li>${l.replace(/^[•\-*]\s*/, "")}</li>`;
                    return `<p>${l}</p>`;
                })
                .join("")
                .replace(/(<li>.*?<\/li>)+/gs, m => `<ul>${m}</ul>`);

            return { index: i, title: slideTitle, body: bodyHtml };
        });
    }

    // ── Strategy 3: split on ## headings ─────────────────────────────────────
    const mdSections = html.split(/\n(?=##\s)/).filter(s => s.trim());
    if (mdSections.length > 1) {
        return mdSections.map((sec, i) => {
            const m = sec.match(/^##\s+(.+)/m);
            const slideTitle = m ? m[1].replace(/\*+/g, "").trim() : `Slide ${i + 1}`;
            return { index: i, title: slideTitle, body: mdToHtml(sec.replace(/^##\s+.+/m, "").trim()) };
        });
    }

    // ── Strategy 4: split on <h2> tags ───────────────────────────────────────
    const normalized = mdToHtml(html);
    const h2sections = normalized.split(/(?=<h2\b)/i).filter(s => s.trim());
    if (h2sections.length > 1) {
        return h2sections.map((sec, i) => {
            const m = sec.match(/<h2[^>]*>(.*?)<\/h2>/i);
            const slideTitle = m ? m[1].replace(/<[^>]+>/g, "") : `Slide ${i + 1}`;
            return { index: i, title: slideTitle, body: sec.replace(/<h2[^>]*>.*?<\/h2>/i, "").trim() };
        });
    }

    // ── Fallback: one slide ───────────────────────────────────────────────────
    return [{ index: 0, title: title || "Content", body: mdToHtml(html) }];
};

const ChatPanel = () => {
    // ── Feature Flags ──────────────────────────────────────────────────────
    const flags = useFeatureFlags();

    // ── Session lifecycle tracking ─────────────────────────────────────────
    // Tracks whether the current conversation has been committed to history
    // when the user navigates away. Prevents double-saves on rapid focus changes.
    const sessionActiveRef = useRef(true);

    // State management
    const [teamsParticipants, setTeamsParticipants] = useState([]);
    const [appTheme, setAppTheme] = useState("theme-light");
    const [chatId, setChatId] = useState("");
    const [meetingId, setMeetingId] = useState("");
    const [userName, setUserName] = useState("");
    const [messages, setMessages] = useState(() => {
        // Fresh welcome screen on app boot.
        // Active chat is preserved in React state across screen switches.
        return [{ id: "m0", role: "Spark Assist", text: "Hi! Ask your work question..." }];
    });

    useEffect(() => {
        UsageLogService.trackPageView('ChatPanel', {
            activeSparkId: selectedSpark?.sparkId,           // Which Spark is selected
            activeSparkName: selectedSpark?.sparkName,       // Spark name
            searchMode: searchMode,                          // work/web/sphere/people
            selectedIntegrationsCount: selectedIntegrations.length, // How many integrations
            selectedAgentsCount: selectedAgents.length,      // How many agents
            activeIntegrations: selectedIntegrations.join(','), // integrations
            activeAgents: selectedAgents.join(',')          // agents
        });
    }, []);

    // ── Document Agent state ──────────────────────────────────────────────
    const [docAgentType, setDocAgentType] = useState(null); // "word" | "ppt" | null
    const [docMessages, setDocMessages] = useState([]);     // conversation with doc agent
    const [docDraft, setDocDraft] = useState("");           // doc agent input draft
    const [docContent, setDocContent] = useState("");       // generated document content (HTML)
    const [docTitle, setDocTitle] = useState("");           // document title
    const [isSavingDoc, setIsSavingDoc] = useState(false);  // saving to OneDrive
    const [docSaveSuccess, setDocSaveSuccess] = useState("");
    const [docSaveError, setDocSaveError] = useState("");
    const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
    const [pptActiveSlide, setPptActiveSlide] = useState(0);

    const DOC_AGENTS = [
        {
            key: "word",
            label: "Word",
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="2" width="14" height="20" rx="2" fill="#2B579A" />
                    <path d="M16 6h4a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-4" fill="#2B579A" opacity="0.5" />
                    <path d="M6 8l2 8 2-5 2 5 2-8" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            ),
            color: "#2B579A",
            sparkId: 61602,
            sparkName: "Content Generator",
            systemPrompt: `You are a professional Word document creator. Output ONLY valid HTML — never use Markdown syntax (no # headings, no **bold**, no - bullets). Use real HTML tags: <h1>, <h2>, <h3>, <p>, <ul><li>, <ol><li>, <table><thead><tr><th><td>, <strong>, <em>. Generate a complete, well-structured document.

STRICT OUTPUT FORMAT — two parts, nothing else:
Line 1: TITLE: <document title here>
Lines 2+: Full HTML body content (starting directly with <h1> or <p>, no <!DOCTYPE>, no <html>, no <body> wrapper).

Make documents professional, thorough, and richly formatted with multiple sections.`,
        },
        {
            key: "ppt",
            label: "PowerPoint",
            icon: (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="2" width="14" height="20" rx="2" fill="#D24726" />
                    <path d="M16 6h4a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2h-4" fill="#D24726" opacity="0.5" />
                    <rect x="5" y="7" width="8" height="5" rx="1" fill="white" opacity="0.9" />
                    <line x1="5" y1="15" x2="13" y2="15" stroke="white" strokeWidth="1.3" strokeLinecap="round" />
                    <line x1="5" y1="17" x2="10" y2="17" stroke="white" strokeWidth="1.3" strokeLinecap="round" />
                </svg>
            ),
            color: "#D24726",
            sparkId: 61602,
            sparkName: "Content Generator",
            systemPrompt: `You are a PowerPoint slide generator. Follow these rules WITHOUT EXCEPTION:
- NEVER put content descriptions inside the slide title
- The slide title (<h2 class="slide-title">) must be SHORT (3-6 words max, no colons, no "Content:")
- ALL explanatory text, bullets, and details go inside <div class="slide-content"> only
- Output ONLY: one TITLE line, then the slide divs. Zero other text.

CORRECT EXAMPLE (copy this structure exactly):
TITLE: Quantum Computing Overview
<div class="slide"><h2 class="slide-title">What Is Quantum Computing</h2><div class="slide-content"><p>Quantum computing harnesses quantum mechanics to process information exponentially faster than classical computers.</p><ul><li><strong>Qubits:</strong> Can exist as 0, 1, or both simultaneously via superposition</li><li><strong>Entanglement:</strong> Qubits can be correlated regardless of distance</li><li><strong>Speed:</strong> Solves certain problems millions of times faster</li></ul></div></div>
<div class="slide"><h2 class="slide-title">Key Applications</h2><div class="slide-content"><ul><li><strong>Cryptography:</strong> Breaking and building unbreakable encryption systems</li><li><strong>Drug Discovery:</strong> Simulating molecular interactions at quantum scale</li><li><strong>Optimization:</strong> Solving complex logistics and financial problems</li><li><strong>AI:</strong> Accelerating machine learning model training</li></ul></div></div>
<div class="slide"><h2 class="slide-title">Challenges Ahead</h2><div class="slide-content"><p>Significant barriers remain before quantum computers reach mainstream use.</p><ul><li><strong>Decoherence:</strong> Qubits lose their quantum state easily</li><li><strong>Error rates:</strong> Current systems require extensive error correction</li><li><strong>Temperature:</strong> Must operate near absolute zero (-273°C)</li></ul></div></div>

WRONG EXAMPLE (never do this):
<div class="slide"><h2 class="slide-title">Applications Content: Discuss how quantum computing is used in various fields including cryptography</h2>...

Generate 8-10 slides following ONLY the CORRECT format above.`,
        },
    ];

    const sendDocMessage = async () => {
        const text = docDraft.trim();
        if (!text || isGeneratingDoc) return;

        const agent = DOC_AGENTS.find(a => a.key === docAgentType);
        if (!agent) return;

        setDocDraft("");
        setIsGeneratingDoc(true);
        setDocSaveSuccess("");
        setDocSaveError("");

        const userMsg = { id: makeId(), role: "user", text };
        const updatedMsgs = [...docMessages, userMsg];
        setDocMessages(updatedMsgs);

        const startTime = Date.now();
        const isEdit = !!docContent;

        try {
            const history = updatedMsgs.slice(-6).map(m => ({
                role: m.role === "user" ? "user" : "assistant",
                content: m.text,
            }));

            // Build edit-aware system prompt: if doc already exists, inject it for refinement
            const editContext = docContent
                ? `\n\nEXISTING DOCUMENT TO EDIT (refine this based on the user's request, preserve all slides/content not mentioned):\nTITLE: ${docTitle || "Untitled"}\n${docContent}`
                : "";

            const payload = [
                { role: "system", content: agent.systemPrompt + editContext },
                ...history,
                { role: "user", content: text },
            ];

            const json = await ApiClient.post("/api/sparkmeet/chat", {
                messages: payload,
                temperature: 0.4,
                max_tokens: 2000,
                spark_id: agent.sparkId,
                spark_name: agent.sparkName,
                integrations: [],
                agents: [],
            });

            const duration = Date.now() - startTime;

            const raw = json?.content ?? "";

            // Extract title and content from response
            const lines = raw.split("\n");
            let title = docTitle;
            let content = raw;

            const titleLine = lines.find(l => l.startsWith("TITLE:"));
            if (titleLine) {
                title = titleLine.replace("TITLE:", "").trim();
                content = lines.filter(l => !l.startsWith("TITLE:")).join("\n");
            }

            setDocTitle(title || "Untitled Document");
            // Normalise: convert markdown to HTML if the model returned markdown
            setDocContent(mdToHtml(content));
            setPptActiveSlide(0); // reset to first slide whenever new content arrives

            // Track document generation
            const slideCount = docAgentType === "ppt" ? parseDocSlides(content, title).length : 0;

            UsageLogService.trackEvent('DocumentGenerated', {
                agentType: docAgentType,
                agentLabel: agent.label,
                documentTitle: title || "Untitled",
                requestLength: text.length,
                responseLength: content.length,
                durationMs: duration,
                isEdit: isEdit,
                slideCount: slideCount,
                conversationTurns: updatedMsgs.filter(m => m.role === "user").length
            }, 'Feature');

            const assistantMsg = { id: makeId(), role: "assistant", text: `✅ Document generated: **${title || "Untitled"}**\n\nYou can see the preview on the right. Ask me to refine any section, or click **Save to OneDrive** when ready.` };
            setDocMessages(prev => [...prev, assistantMsg]);

        } catch (err) {
            const errMsg = { id: makeId(), role: "assistant", text: `❌ Generation failed: ${err.message}` };
            setDocMessages(prev => [...prev, errMsg]);

            // Track document generation error
            UsageLogService.trackError(err, {
                feature: 'DocumentGeneration',
                agentType: docAgentType,
                requestLength: text.length,
                isEdit: isEdit
            });
        } finally {
            setIsGeneratingDoc(false);
        }
    };

    const saveDocToOneDrive = async () => {
        if (!docContent || isSavingDoc) return;
        setIsSavingDoc(true);
        setDocSaveSuccess("");
        setDocSaveError("");

        const startTime = Date.now();

        try {
            const ts = new Date().toISOString().slice(0, 10);
            const ext = docAgentType === "ppt" ? "html" : "html";
            const fileName = `${(docTitle || "document").replace(/[^a-zA-Z0-9 ]/g, "").trim()}-${ts}.${ext}`;

            // Wrap in proper HTML document
            const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${docTitle}</title><style>
body { font-family: 'Segoe UI', Arial, sans-serif; max-width: 900px; margin: 40px auto; padding: 0 40px; line-height: 1.6; color: #242424; }
h1 { font-size: 28px; color: #2B579A; border-bottom: 2px solid #2B579A; padding-bottom: 8px; }
h2 { font-size: 22px; color: #2B579A; margin-top: 28px; }
h3 { font-size: 18px; color: #333; }
table { width: 100%; border-collapse: collapse; margin: 16px 0; }
th { background: #2B579A; color: white; padding: 8px 12px; text-align: left; }
td { padding: 8px 12px; border: 1px solid #ddd; }
tr:nth-child(even) { background: #f5f8ff; }
.slide { border: 1px solid #ddd; border-radius: 8px; padding: 24px; margin: 16px 0; background: white; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.slide-title { color: #D24726; font-size: 22px; margin: 0 0 16px 0; }
</style></head><body>${docContent}</body></html>`;

            const result = await ApiClient.post("/api/sparkmeet/onedrive/upload", {
                folderId: "root",
                fileName,
                content: fullHtml,
            });

            const duration = Date.now() - startTime;

            // Track document save
            const slideCount = docAgentType === "ppt" ? parseDocSlides(docContent, docTitle).length : 0;

            UsageLogService.trackEvent('DocumentSavedToOneDrive', {
                agentType: docAgentType,
                fileName: fileName,
                documentTitle: docTitle || "Untitled",
                fileSizeBytes: fullHtml.length,
                fileSizeKB: Math.round(fullHtml.length / 1024),
                slideCount: slideCount,
                durationMs: duration,
                hasWebUrl: !!result.webUrl
            }, 'Integration');

            setDocSaveSuccess(`✅ Saved to OneDrive: ${result.name}`);
            setDocMessages(prev => [...prev, {
                id: makeId(), role: "assistant",
                text: `✅ Saved to OneDrive!\n🔗 [Open file](${result.webUrl})`,
            }]);
        } catch (err) {
            setDocSaveError(`❌ Save failed: ${err.message}`);

            // Track save error
            UsageLogService.trackError(err, {
                feature: 'DocumentSave',
                agentType: docAgentType,
                documentTitle: docTitle
            });
        } finally {
            setIsSavingDoc(false);
        }
    };

    // ── Chat history sidebar ───────────────────────────────────────────────
    const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
        // Always start collapsed on mobile regardless of saved preference
        if (window.innerWidth <= 520) return false;
        try { return localStorage.getItem("spark_sidebar_open") !== "false"; } catch { return true; }
    });
    const [isProfileSettingsOpen, setIsProfileSettingsOpen] = useState(false);
    const [historySearch, setHistorySearch] = useState("");

    // ══════════════════════════════════════════════════════════════
    // OLD localStorage chatHistory state - REMOVED
    // Now using chatHistories from Cosmos DB (line 705)
    // ══════════════════════════════════════════════════════════════
    // const [chatHistory, setChatHistory] = useState(() => {
    //     try {
    //         const saved = localStorage.getItem("spark_chat_history");
    //         if (saved) return JSON.parse(saved);
    //     } catch { /* ignore */ }
    //     return [];
    // });

    // ── CHAT HISTORY STATE ────────────────────────────────────────
    const [chatHistories, setChatHistories] = useState([]);              // List of chat conversations
    const [currentChatId, setCurrentChatId] = useState(null);           // Active chat ID
    const [isLoadingChatHistory, setIsLoadingChatHistory] = useState(false);
    const [hasMoreChats, setHasMoreChats] = useState(false);
    const [chatHistoryContinuationToken, setChatHistoryContinuationToken] = useState(null);
    const [isSavingChat, setIsSavingChat] = useState(false);            // Saving indicator
    const [editingChatId, setEditingChatId] = useState(null);
    const [editingChatTitle, setEditingChatTitle] = useState("");
    const [isSavingChatTitle, setIsSavingChatTitle] = useState(false);

    // const saveChatToHistory = (msgs) => {
    //     // Don't save if it's just the greeting
    //     if (!msgs || msgs.length <= 1) return;
    //     const userMessages = msgs.filter(m => m.role === "user");
    //     if (userMessages.length === 0) return;

    //     // Generate title from first user message
    //     const firstUser = userMessages[0].text || "";
    //     const title = firstUser.length > 55
    //         ? firstUser.substring(0, 55) + "…"
    //         : firstUser;

    //     const entry = {
    //         id: `hist_${Date.now()}`,
    //         title,
    //         savedAt: new Date().toISOString(),
    //         messages: msgs,
    //     };

    //     setChatHistory(prev => {
    //         const updated = [entry, ...prev].slice(0, 50); // keep last 50
    //         try { localStorage.setItem("spark_chat_history", JSON.stringify(updated)); } catch { /* ignore */ }
    //         return updated;
    //     });
    // };

    // const loadChatFromHistory = (entry) => {
    //     try { localStorage.setItem("spark_chat_messages", JSON.stringify(entry.messages)); } catch { /* ignore */ }
    //     setMessages(entry.messages);
    //     setDraft("");
    //     setInsights("");
    //     setPickerMode(null);
    //     setPickerQuery("");
    // };

    // const deleteChatFromHistory = (id, e) => {
    //     e.stopPropagation();
    //     setChatHistory(prev => {
    //         const updated = prev.filter(h => h.id !== id);
    //         try { localStorage.setItem("spark_chat_history", JSON.stringify(updated)); } catch { /* ignore */ }
    //         return updated;
    //     });
    // };

    // const clearAllHistory = () => {
    //     if (!window.confirm("Clear all chat history?")) return;
    //     setChatHistory([]);
    //     try { localStorage.removeItem("spark_chat_history"); } catch { /* ignore */ }
    // };
    const [draft, setDraft] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [feedbackByMessageId, setFeedbackByMessageId] = useState({});
    // Tracks message IDs whose suggested-action banner has been dismissed by the user
    const [dismissedActionIds, setDismissedActionIds] = useState({});
    // Per-message editable email draft state { [msgId]: { to, subject, body, sending, sent } }
    const [draftEdits, setDraftEdits] = useState({});
    const [transcript, setTranscript] = useState("");
    const [isLoadingTranscript, setIsLoadingTranscript] = useState(false);
    const [insights, setInsights] = useState("");
    const [isGeneratingInsights, setIsGeneratingInsights] = useState(false);
    const [processingMessage, setProcessingMessage] = useState("");
    const [showTranscriptViewer, setShowTranscriptViewer] = useState(false);
    const [showAnalyticsDashboard, setShowAnalyticsDashboard] = useState(false);
    const isAdmin = useIsAdmin();
    // Live agent-step progress state (used when workplace integrations are selected)
    const [workplaceSteps, setWorkplaceSteps] = useState([]); // [{emoji,title,detail,type}]
    const [workplaceKql, setWorkplaceKql]     = useState(null); // {kql, sources[]}
    const [workplaceHits, setWorkplaceHits]   = useState(null); // {counts:{Source:N}}
    const [workplaceAgentSteps, setWorkplaceAgentSteps] = useState([]); // [{emoji,title,detail,type,status}]
    const [workplaceAgentError, setWorkplaceAgentError] = useState("");
    const [workplaceAgentLiveLogs, setWorkplaceAgentLiveLogs] = useState([]);
    // Keeps the original user query so the workflow summarize call has context.
    // Also stored in a ref so executeValidatedWorkplaceStep can read the fresh
    // value even though React state may not have flushed inside the async loop.
    const [currentUserQuery, setCurrentUserQuery] = useState("");
    const currentUserQueryRef = React.useRef("");
    // Debug logs toggle — persisted in localStorage, off by default
    const [showWaDebugLogs, setShowWaDebugLogs] = useState(() => {
        try { return localStorage.getItem("spark_wa_debug_logs") === "1"; } catch { return false; }
    });
    // Accumulates decision checkpoints from all steps of the current auto-execute run
    // so the final "How I answered this" trace can show KQL, intent, search params etc.
    const waCheckpointsRef = React.useRef([]);
    // Holds a pending write action (e.g. send email) from a needsPermission step so we can
    // attach it to the final summary message as a button rather than auto-opening the dialog.
    const pendingWriteActionRef = React.useRef(null);
    // Tracks which assistant message IDs have their trace panel expanded
    const [expandedTraceIds, setExpandedTraceIds] = useState({});

    // ── FLOW A: Single utterance mic button state ──────────────────
    const [isListening, setIsListening] = useState(false);  // mic is recording
    const [isTranscribing, setIsTranscribing] = useState(false);  // waiting for STT response
    const [speechError, setSpeechError] = useState("");      // error shown below composer

    // ── MEETING STT: Full meeting recording state ──────────────────
    const [isMeetingRecording, setIsMeetingRecording] = useState(false);  // recording in progress
    const [isMeetingTranscribing, setIsMeetingTranscribing] = useState(false); // waiting for STT
    const [meetingSttResult, setMeetingSttResult] = useState(null);   // { fullTranscript, utterances, wordDetails }
    const [showSttViewer, setShowSttViewer] = useState(false);  // modal open/close
    const [meetingSttError, setMeetingSttError] = useState("");      // error in meeting header
    const [meetingRecordingStartedAt, setMeetingRecordingStartedAt] = useState(null); // ISO timestamp

    const [sttProvider, setSttProvider] = React.useState("google"); // "google" | "kensho"


    //ADO
    const [isAdoModalOpen, setIsAdoModalOpen] = useState(false);
    const [adoMessageText, setAdoMessageText] = useState("");
    const [adoProjects, setAdoProjects] = useState([]);
    const [adoSelectedProject, setAdoSelectedProject] = useState("Spark Assist");
    const [adoWorkItemType, setAdoWorkItemType] = useState("User Story");
    const [adoIterations, setAdoIterations] = useState([]);
    const [adoSelectedIteration, setAdoSelectedIteration] = useState("");
    const [adoTitle, setAdoTitle] = useState("");
    const [adoDescription, setAdoDescription] = useState("");
    const [isLoadingAdoProjects, setIsLoadingAdoProjects] = useState(false);
    const [isLoadingAdoIterations, setIsLoadingAdoIterations] = useState(false);
    const [isCreatingAdoItem, setIsCreatingAdoItem] = useState(false);
    const [adoError, setAdoError] = useState("");
    const [adoSuccess, setAdoSuccess] = useState("");


    //OneDrive
    const [isOneDriveModalOpen, setIsOneDriveModalOpen] = useState(false);
    const [oneDriveMessageText, setOneDriveMessageText] = useState("");
    const [oneDriveFolders, setOneDriveFolders] = useState([]);
    const [oneDriveSelectedFolder, setOneDriveSelectedFolder] = useState("root");
    const [oneDriveSelectedFolderName, setOneDriveSelectedFolderName] = useState("My Files (Root)");
    const [oneDriveFileName, setOneDriveFileName] = useState("");
    const [oneDriveFileContent, setOneDriveFileContent] = useState("");
    const [isLoadingOneDriveFolders, setIsLoadingOneDriveFolders] = useState(false);
    const [isUploadingToOneDrive, setIsUploadingToOneDrive] = useState(false);
    const [oneDriveError, setOneDriveError] = useState("");
    const [oneDriveSuccess, setOneDriveSuccess] = useState("");
    const [oneDriveUploadedUrl, setOneDriveUploadedUrl] = useState("");
    const [oneDriveBreadcrumb, setOneDriveBreadcrumb] = useState([
        { id: "root", name: "My Files" }
    ]);


    // Teams
    // Teams Post Modal
    const [isTeamsModalOpen, setIsTeamsModalOpen] = useState(false);
    const [teamsMessageText, setTeamsMessageText] = useState("");
    const [teamsDestType, setTeamsDestType] = useState("chat"); // "chat" | "channel"
    const [teamsChats, setTeamsChats] = useState([]);
    const [teamsSelectedChat, setTeamsSelectedChat] = useState(null);
    const [teamsJoinedTeams, setTeamsJoinedTeams] = useState([]);
    const [teamsSelectedTeam, setTeamsSelectedTeam] = useState(null);
    const [teamsChannels, setTeamsChannels] = useState([]);
    const [teamsSelectedChannel, setTeamsSelectedChannel] = useState(null);
    const [teamsMessageContent, setTeamsMessageContent] = useState("");
    const [teamsSubject, setTeamsSubject] = useState("");
    const [teamsChatSearch, setTeamsChatSearch] = useState("");
    const [isLoadingTeamsChats, setIsLoadingTeamsChats] = useState(false);
    const [isLoadingTeamsTeams, setIsLoadingTeamsTeams] = useState(false);
    const [isLoadingTeamsChannels, setIsLoadingTeamsChannels] = useState(false);
    const [isPostingToTeams, setIsPostingToTeams] = useState(false);
    const [teamsPostError, setTeamsPostError] = useState("");
    const [teamsPostSuccess, setTeamsPostSuccess] = useState("");
    const [teamsPostedUrl, setTeamsPostedUrl] = useState("");

    // Calendar invite modal state
    const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
    const [calendarTitle, setCalendarTitle] = useState("");
    const [calendarDate, setCalendarDate] = useState("");
    const [calendarStartTime, setCalendarStartTime] = useState("09:00");
    const [calendarEndTime, setCalendarEndTime] = useState("10:00");
    const [calendarAttendees, setCalendarAttendees] = useState("");
    const [calendarLocation, setCalendarLocation] = useState("");
    const [calendarBody, setCalendarBody] = useState("");
    const [isCreatingCalendar, setIsCreatingCalendar] = useState(false);
    const [calendarError, setCalendarError] = useState("");
    const [calendarSuccess, setCalendarSuccess] = useState("");

    //ADO Board Search
    // ADO Board Search
    const [isAdoBoardModalOpen, setIsAdoBoardModalOpen] = useState(false);
    const [adoBoardProjects, setAdoBoardProjects] = useState([]);
    const [adoBoardSelectedProject, setAdoBoardSelectedProject] = useState(null);
    const [adoBoardTeams, setAdoBoardTeams] = useState([]);
    const [adoBoardSelectedTeam, setAdoBoardSelectedTeam] = useState(null);
    const [adoBoards, setAdoBoards] = useState([]);
    const [adoSelectedBoard, setAdoSelectedBoard] = useState(null);
    const [adoAreaPaths, setAdoAreaPaths] = useState([]);
    const [adoSelectedAreaPath, setAdoSelectedAreaPath] = useState(null);
    const [adoSwimlanes, setAdoSwimlanes] = useState([]);
    const [adoSelectedSwimlane, setAdoSelectedSwimlane] = useState(null);
    const [adoBoardSearchText, setAdoBoardSearchText] = useState("");
    const [adoBoardWorkItemType, setAdoBoardWorkItemType] = useState("Any");
    const [adoBoardState, setAdoBoardState] = useState("Any");
    const [adoBoardSearchResults, setAdoBoardSearchResults] = useState([]);
    const [adoBoardTotalResults, setAdoBoardTotalResults] = useState(0);
    const [isLoadingAdoBoardProjects, setIsLoadingAdoBoardProjects] = useState(false);
    const [isLoadingAdoBoardTeams, setIsLoadingAdoBoardTeams] = useState(false);
    const [isLoadingAdoBoards, setIsLoadingAdoBoards] = useState(false);
    const [isLoadingAdoAreaPaths, setIsLoadingAdoAreaPaths] = useState(false);
    const [isLoadingAdoSwimlanes, setIsLoadingAdoSwimlanes] = useState(false);
    const [isSearchingAdoBoard, setIsSearchingAdoBoard] = useState(false);
    const [adoBoardError, setAdoBoardError] = useState("");
    const [adoBoardStep, setAdoBoardStep] = useState("configure"); // "configure" | "results"
    const [adoBoardProjectSearch, setAdoBoardProjectSearch] = useState("");
    const [adoSelectedWorkItems, setAdoSelectedWorkItems] = useState([]); // persists across modal close
    const [adoWorkItemContext, setAdoWorkItemContext] = useState([]); // active context for next send


    // Create Sparks Panel
    const [isSparkCreateOpen, setIsSparkCreateOpen] = useState(false);

    // Initial Load
    const [isAppLoading, setIsAppLoading] = useState(true);

    // ── Guided Tour ───────────────────────────────────────────────────────────
    const [showTour, setShowTour] = useState(false);
    const [showAdminPanel, setShowAdminPanel] = useState(false);
    useEffect(() => {
        try {
            if (!localStorage.getItem("spark_tour_done")) setShowTour(true);
        } catch { setShowTour(true); }
    }, []);

    // ── MOBILE RESPONSIVENESS ────────────────────────────────────────
    // Breakpoint 520 px covers all phones in portrait + small landscape.
    const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= 520);
    React.useEffect(() => {
        const onResize = () => setIsMobile(window.innerWidth <= 520);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);


    const endRef = useRef(null);
    const sparkStoreScrollRef = useRef(null);

    // ── FLOW A refs ────────────────────────────────────────────────
    const mediaRecorderRef = useRef(null);  // MediaRecorder instance for utterance
    const audioChunksRef = useRef([]);    // accumulates audio chunks
    const streamRef = useRef(null);  // mic MediaStream (needed to release mic)

    // ── MEETING STT refs ───────────────────────────────────────────
    const meetingRecorderRef = useRef(null);  // MediaRecorder for full meeting
    const meetingAudioChunksRef = useRef([]);    // full meeting audio chunks
    const meetingStreamRef = useRef(null);  // meeting mic stream

    const isMeetingRecordingRef = useRef(false);  // mirrors isMeetingRecording state for event handlers
    const meetingRestartCountRef = useRef(0);       // counts auto-restart attempts (max 3)
    const meetingUserStoppedRef = useRef(false);   // true = organizer stopped intentionally
    // Captures the live workplace trace so send() can attach it to the stored message
    const workplaceTraceRef = useRef({ steps: [], kql: null, hits: null });


    // ── Dynamic sparks cache config ────────────────────────────────────────
    const DYNAMIC_SPARKS_CACHE_KEY = "spark_dynamic_cache_v1";
    const DYNAMIC_SPARKS_TTL_MS = 24 * 60 * 60 * 1000;
    const DYNAMIC_SPARKS_PAGE_SIZE = 200;
    const DYNAMIC_SPARKS_MAX_PAGES = 40;
    // Max sparks shown in @ picker when no search query is typed
    const SPARK_PICKER_DEFAULT_LIMIT = 25;
    const DYNA_ACCENTS = [
        "#7C3AED", "#2563EB", "#F59E0B", "#06B6D4", "#22C55E",
        "#EC4899", "#EF4444", "#8B5CF6", "#0EA5E9", "#14B8A6",
    ];
    const DYNA_ICONS = ["✦", "🔍", "📚", "💡", "⚙️", "🎯", "📊", "🔗", "🌐", "🤖"];


    // Sparks data
    const SPARKS = [
        { label: "@ Generic", sparkId: 56534, sparkName: "Generic" },
        { label: "@ Ask People - Solutions", sparkId: 14651, sparkName: "Ask People - Solutions" },
        { label: "@ Kensho Document Library", sparkId: 37142, sparkName: "Kensho Document Library" },
        { label: "@ Ask Sphere", sparkId: 20685, sparkName: "Ask Sphere" },
        {
            label: "@ Spark Assist - Confluence & SharePoint Collection",
            sparkId: 31084,
            sparkName: "Spark Assist - Confluence & SharePoint Collection",
        },
        { label: "@ Corporate Policy Assist", sparkId: 20248, sparkName: "Corporate Policy Assist" },
        { label: "@ Spark ADO Board", sparkId: 57130, sparkName: "Spark ADO Board" },
    ];

    // ── Quick-select preset sparks (shown via the + button before the Spark Chip) ──
    const CLASSIC_CHAT_SPARK  = { sparkId: 1,     sparkName: "Classic Chat",  label: "@ Classic Chat"  };
    const DEFAULT_SPARK_SPARK = { sparkId: 25600,  sparkName: "Default Spark", label: "@ Default Spark" };
    const PRESET_SPARKS = [
        { ...CLASSIC_CHAT_SPARK,  icon: "💬", desc: "Standard chat experience" },
        { ...DEFAULT_SPARK_SPARK, icon: "✦",  desc: "Automates model selection, integrates search, and offers advanced data analysis, content processing & creation" },
    ];

    // ── AI model catalogue (hardcoded; will be replaced by API in next iteration) ──
    const FALLBACK_SPARK_MODELS = [
        {
            provider: "OpenAI", color: "#10a37f",
            models: [
                { id: "GPT-4.1",       label: "GPT-4.1",       badge: "Default"     },
                { id: "GPT-4o",        label: "GPT-4o",        badge: "Fast"        },
                { id: "GPT-4o-mini",   label: "GPT-4o mini",   badge: "Efficient"   },
                { id: "GPT-4-turbo",   label: "GPT-4 Turbo",   badge: null          },
                { id: "o1",            label: "o1",            badge: "Reasoning"   },
                { id: "o3-mini",       label: "o3-mini",       badge: "Reasoning"   },
            ],
        },
        {
            provider: "Anthropic", color: "#d97706",
            models: [
                { id: "claude-3-5-sonnet", label: "Claude 3.5 Sonnet", badge: "Recommended" },
                { id: "claude-3-opus",     label: "Claude 3 Opus",     badge: "Powerful"     },
                { id: "claude-3-haiku",    label: "Claude 3 Haiku",    badge: "Fast"         },
            ],
        },
        {
            provider: "Google", color: "#4285f4",
            models: [
                { id: "gemini-1.5-pro",   label: "Gemini 1.5 Pro",   badge: null    },
                { id: "gemini-1.5-flash", label: "Gemini 1.5 Flash", badge: "Fast"  },
                { id: "gemini-2.0-flash", label: "Gemini 2.0 Flash", badge: "New"   },
            ],
        },
    ];

    const FALLBACK_SPARK_STYLES = [
        { key: "Creative", icon: "🎨", temp: 0.8, desc: "Imaginative", color: "#0891b2" },
        { key: "Balanced", icon: "⚖️", temp: 0.6, desc: "Thoughtful", color: "#0891b2" },
        { key: "Precise", icon: "🎯", temp: 0.2, desc: "Accurate", color: "#0891b2" },
    ];

    const FALLBACK_RESPONSE_LENGTHS = [
        { key: "Short", barCount: 1, desc: "Concise" },
        { key: "Medium", barCount: 2, desc: "Balanced" },
        { key: "Long", barCount: 3, desc: "Detailed" },
    ];

    const [selectedSpark, setSelectedSpark] = useState(DEFAULT_SPARK_SPARK);
    const [searchMode, setSearchMode] = useState("work"); // "work" | "web"
    const [pickerMode, setPickerMode] = useState(null);
    const [pickerQuery, setPickerQuery] = useState("");
    const [quickSparkOpen, setQuickSparkOpen] = useState(false);
    const [sparkChipSearch, setSparkChipSearch] = useState("");

    // ── AI model / style / length settings ────────────────────────────────
    const [modelSettings, setModelSettings] = useState(() => {
        try {
            const saved = localStorage.getItem("spark_model_settings");
            if (saved) return JSON.parse(saved);
        } catch { /* ignore */ }
        return { modelId: "GPT-4.1", styleKey: "Balanced", temperature: 0.6, responseLength: "Medium" };
    });
    const [isModelSettingsOpen, setIsModelSettingsOpen] = useState(false);
    const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
    const modelSettingsBtnRef = useRef(null);
    const [modelPopupStyle, setModelPopupStyle] = useState({});

    const [aiModels, setAiModels] = useState(FALLBACK_SPARK_MODELS); // Initialize with fallback
    const [conversationStyles, setConversationStyles] = useState(FALLBACK_SPARK_STYLES);
    const [responseLengths, setResponseLengths] = useState(FALLBACK_RESPONSE_LENGTHS);
    const [isLoadingAiSettings, setIsLoadingAiSettings] = useState(false);
    const [aiSettingsError, setAiSettingsError] = useState("");

    // Initialize UsageLogService context on mount
    useEffect(() => {
        // Set initial spark context (default or will be updated when restored from localStorage)
        if (selectedSpark) {
            UsageLogService.updateSparkContext(
                selectedSpark.sparkId,
                selectedSpark.sparkName
            );
        }
    }, []);    

    const [isSparkStoreOpen, setIsSparkStoreOpen] = useState(false);
    const [sparkStoreDisplayLimit, setSparkStoreDisplayLimit] = useState(50);

    // ── Dynamic sparks state ───────────────────────────────────────────────
    const [dynamicSparks, setDynamicSparks] = useState([]);
    const [isDynamicSparksLoading, setIsDynamicSparksLoading] = useState(false);
    const [dynamicSparksLoaded, setDynamicSparksLoaded] = useState(false);
    const [sparkSyncProgress, setSparkSyncProgress] = useState(0);   // 0-100
    const [sparkSyncTotal, setSparkSyncTotal] = useState(0);
    const [lastSyncedAt, setLastSyncedAt] = useState(null); // Date

    const [sparkStoreNudge, setSparkStoreNudge] = useState(false);
    const [embedSpark, setEmbedSpark] = useState(null);
    const [embedCopied, setEmbedCopied] = useState(false);

    const [sparkStoreSearch, setSparkStoreSearch] = useState("");
    const [sparkStoreCategory, setSparkStoreCategory] = useState("all");
    const [sparkStoreSortBy, setSparkStoreSortBy] = useState("most_liked");
    const [sparkStoreSortMenuOpen, setSparkStoreSortMenuOpen] = useState(false);


    const SPARK_STORE_ITEMS = useMemo(
        () => [
            {
                sparkId: 20685,
                title: "Ask Sphere",
                tags: ["Sphere", "Intranet"],
                likes: 98,
                icon: "✅",
                accent: "#2563EB",
                description: "Ask Sphere helps answer questions based on the information available on Sphere.",
                certified: true,
                liked: true,
                shared: false,
                recentlyAdded: false,
                createdDate: "2024-08-20", // 🆕 Added
                updatedDate: "2024-12-10", // 🆕 Added
                creatorName: "Lakshmana Kumar Kothapalli",  // 🆕 Added
            },
            {
                sparkId: 20248,
                title: "Corporate Policy Assist",
                tags: ["Corporate Policies", "Compliance"],
                likes: 81,
                icon: "💻",
                accent: "#F59E0B",
                description: "Hi, I am Corporate Policies Assist, I can help you answer questions regarding SPGI Corporate Policies.",
                certified: true,
                liked: false,
                shared: true,
                recentlyAdded: false,
                createdDate: "2024-07-10", // 🆕 Added
                updatedDate: "2024-11-20", // 🆕 Added
                creatorName: "Spark Assist", // 🆕 Added
            },
            {
                sparkId: 14651,
                title: "Ask People - Solutions",
                tags: ["People", "Purpose", "HR"],
                likes: 254,
                icon: "🙂",
                accent: "#06B6D4",
                description: "Hi, I am Ask People - Solutions. I can help answer your questions about S&P Global's People content.",
                certified: true,
                liked: true,
                shared: false,
                recentlyAdded: false,
                createdDate: "2024-06-05", // 🆕 Added
                updatedDate: "2024-12-18", // 🆕 Added
                creatorName: "Spark Assist", // 🆕 Added
            },
            {
                sparkId: 37142,
                title: "Kensho Document Library",
                tags: ["Kensho", "Collections", "AI Blueprint"],
                likes: 4,
                icon: "⚖️",
                accent: "#22C55E",
                description: "This Spark focus on various aspects of AI systems and methodologies at S&P Global.",
                certified: false,
                liked: false,
                shared: true,
                recentlyAdded: true,
                createdDate: "2024-10-25", // 🆕 Added
                updatedDate: "2024-12-12", // 🆕 Added
                creatorName: "Gregory Mount", // 🆕 Added
            },
            ],
        []
    );


    // ── Merged @ picker: static pinned first, dynamic appended ───────────
    const allSparks = useMemo(() => {
        if (dynamicSparks.length === 0) return SPARKS;
        const staticIds = new Set(SPARKS.map(s => s.sparkId));
        const extra = dynamicSparks
            .filter(s => s.published_state !== false && !staticIds.has(s.spark_id))
            .map(s => {
                const bgColor = s.spark_color?.background_color;
                const accent = bgColor && bgColor.trim()
                    ? bgColor
                    : DYNA_ACCENTS[s.spark_id % DYNA_ACCENTS.length];
                return {
                    label: `@ ${s.spark_name}`,
                    sparkId: s.spark_id,
                    sparkName: s.spark_name,
                    description: s.spark_description ?? "",
                    creatorName: s.full_name || s.spark_owner || "S&P Global",
                    likes: s.total_likes ?? 0,
                    certified: s.certified === true,
                    tags: Array.isArray(s.tags) ? s.tags : [],
                    accent,
                };
            });
        return [...SPARKS, ...extra];
    }, [dynamicSparks]);

    // ── Merged Spark Store: static cards first, dynamic appended ──────────
    const allSparkStoreItems = useMemo(() => {
        if (dynamicSparks.length === 0) return SPARK_STORE_ITEMS;
        const staticIds = new Set(SPARK_STORE_ITEMS.map(s => s.sparkId));
        const extra = dynamicSparks
            .filter(s => s.published_state !== false && !staticIds.has(s.spark_id))
            .map(s => {
                // Prefer spark_color background if set, else fallback to palette
                const bgColor = s.spark_color?.background_color;
                const accent = bgColor && bgColor.trim()
                    ? bgColor
                    : DYNA_ACCENTS[s.spark_id % DYNA_ACCENTS.length];

                // "Recently Added" = last_modified_date within last 30 days
                let recentlyAdded = false;
                try {
                    if (s.last_modified_date) {
                        const modDate = new Date(s.last_modified_date);
                        const cutoff = new Date();
                        cutoff.setDate(cutoff.getDate() - 30);
                        recentlyAdded = modDate >= cutoff;
                    }
                } catch { /* ignore unparseable dates */ }

                return {
                    sparkId: s.spark_id,
                    title: s.spark_name,
                    tags: Array.isArray(s.tags) ? s.tags : [],
                    likes: s.total_likes ?? 0,
                    icon: DYNA_ICONS[s.spark_id % DYNA_ICONS.length],
                    accent,
                    description: s.spark_description ?? "",
                    certified: s.certified === true,
                    liked: (s.user_like ?? 0) > 0,
                    shared: false,
                    recentlyAdded,
                    createdDate: (s.creation_date ?? "").slice(0, 10),
                    updatedDate: s.last_modified_date ?? "",
                    // Use full_name from API (e.g. "Fatin Idris") instead of email
                    creatorName: s.full_name || s.spark_owner || "S&P Global",
                };
            });
        return [...SPARK_STORE_ITEMS, ...extra];
    }, [dynamicSparks]);



    // 🆕 2024-12-20: UPDATED - Ultra-compact welcome screen prompt cards
    const WELCOME_PROMPTS = useMemo(
        () => [
            {
                id: "policy",
                sparkId: 20248,
                icon: "💼",
                title: "Corporate Policies",
                prompt: "What are the related reference documents mentioned in the Information Classification Policy?",
                accent: "#F59E0B",
                category: "Corporate Policy"
            },
            {
                id: "docs",
                sparkId: 37142,
                icon: "📚",
                title: "Kensho Library - Find AI Blueprints",
                prompt: "How does the Kensho Grounding Agent ensure coherent responses to complex questions?",
                accent: "#22C55E",
                category: "Knowledge"
            },
            {
                id: "people",
                sparkId: 14651,
                icon: "👥",
                title: "Ask People",
                prompt: "What are the S&P Global Wellness Benefits?",
                accent: "#06B6D4",
                category: "People"
            },
            {
                id: "sphere",
                sparkId: 20685,
                icon: "🔍",
                title: "Search Sphere - Find information on our company intranet",
                prompt: "How can I reset my password?",
                accent: "#2563EB",
                category: "Search"
            },
        ],
        []
    );

    const INTEGRATIONS = useMemo(
        () => [
            {
                key: "teams", label: "Teams",
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M14.5 6.5a2 2 0 1 0-4 0 2 2 0 0 0 4 0Z" fill="#5059C9" />
                        <path d="M18 8h-1.5a1.5 1.5 0 1 1 0-3H18a1.5 1.5 0 0 1 0 3Z" fill="#5059C9" />
                        <path d="M12.5 9H6a2 2 0 0 0-2 2v5a4 4 0 0 0 4 4h3a4 4 0 0 0 4-4v-5a2 2 0 0 0-2-2H12.5Z" fill="#7B83EB" />
                        <path d="M18 9h-1.5v5.5A4.5 4.5 0 0 1 12 19h-1a5 5 0 0 0 4.5 2.8A4.5 4.5 0 0 0 20 17.3V10.5A1.5 1.5 0 0 0 18 9Z" fill="#5059C9" />
                    </svg>
                ),
                color: "#5059C9",
            },
            {
                key: "onedrive", label: "OneDrive",
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M9.5 13.5 5 11.2A5.5 5.5 0 0 1 14.8 7a4 4 0 0 1 5.2 3.8v.2l-10.5 2.5Z" fill="#0364B8" />
                        <path d="M9.5 13.5 20 11l.5 1A4 4 0 0 1 16.5 18H7.5a3.5 3.5 0 0 1-2.5-6l4.5 1.5Z" fill="#0078D4" />
                        <path d="M14.8 7a4 4 0 0 1 5.2 3.8L20 11l-5-2-0.2-2Z" fill="#1490DF" />
                        <path d="M5 11.2A5.5 5.5 0 0 1 14.8 7L14.6 9A3.5 3.5 0 0 0 9 11.5l-4-0.3Z" fill="#28A8E8" />
                    </svg>
                ),
                color: "#0078D4",
            },
            {
                key: "sharepoint", label: "SharePoint",
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <circle cx="9.5" cy="8.5" r="4.5" fill="#036C70" />
                        <circle cx="16" cy="12" r="4" fill="#1A9BA1" />
                        <circle cx="11.5" cy="17" r="3.5" fill="#37C6D0" />
                        <path d="M9.5 13v8H15a2 2 0 0 0 2-2v-4.5A4 4 0 0 1 13 16H9.5Z" fill="#036C70" opacity="0.6" />
                    </svg>
                ),
                color: "#036C70",
            },
            {
                key: "outlook", label: "Outlook",
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <rect x="2" y="5" width="13" height="14" rx="2" fill="#0078D4" />
                        <path d="M15 9l5.5-3v12L15 15V9Z" fill="#0078D4" />
                        <path d="M15 9l5.5-3v12L15 15V9Z" fill="#28A8E8" opacity="0.5" />
                        <ellipse cx="8.5" cy="12" rx="3" ry="3.5" fill="white" />
                        <ellipse cx="8.5" cy="12" rx="2" ry="2.5" fill="#0078D4" />
                    </svg>
                ),
                color: "#0078D4",
            },
            {
                key: "ado", label: "ADO Boards",
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <path d="M3 6l5-3 8 3.5V21l-8-3.5L3 20V6Z" fill="#0078D4" />
                        <path d="M16 6.5L21 4v14l-5 2.5V6.5Z" fill="#004578" />
                        <path d="M8 3l8 3.5v14L8 17V3Z" fill="#1490DF" />
                    </svg>
                ),
                color: "#0078D4",
            },
            {
                key: "calendar", label: "Calendar",
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                        <rect x="3" y="4" width="18" height="17" rx="2" fill="#0078D4" />
                        <rect x="3" y="4" width="18" height="6" rx="2" fill="#005A9E" />
                        <rect x="7" y="2" width="2" height="4" rx="1" fill="white" />
                        <rect x="15" y="2" width="2" height="4" rx="1" fill="white" />
                        <rect x="7" y="13" width="2" height="2" rx="0.5" fill="white" />
                        <rect x="11" y="13" width="2" height="2" rx="0.5" fill="white" />
                        <rect x="15" y="13" width="2" height="2" rx="0.5" fill="white" />
                        <rect x="7" y="17" width="2" height="2" rx="0.5" fill="white" />
                        <rect x="11" y="17" width="2" height="2" rx="0.5" fill="white" />
                    </svg>
                ),
                color: "#0078D4",
            },
        ],
        []
    );

    const AGENTS = useMemo(
        () => [
            { key: "product_manager", label: "Product Manager" },
            { key: "data_analyst", label: "Data Analyst" },
            { key: "sales_agent", label: "Sales Agent" },
            { key: "developer_agent", label: "Developer Agent" },
        ],
        []
    );

    const [selectedIntegrations, setSelectedIntegrations] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [selectedWorkplaceAgents, setSelectedWorkplaceAgents] = useState([]);
    const [availableWorkplaceAgents, setAvailableWorkplaceAgents] = useState([]);
    const [workflowValidation, setWorkflowValidation] = useState(null);
    const [isAgentValidationOpen, setIsAgentValidationOpen] = useState(false);
    const [isExecutingAgentStep, setIsExecutingAgentStep] = useState(false);
    const [writeActionDialog, setWriteActionDialog] = useState(null);
    const [isWriteActionSubmitting, setIsWriteActionSubmitting] = useState(false);

    const workplaceAgentFlags = flags?.workplaceAgentFramework || {};
    const isWorkplaceAgentFrameworkEnabled = workplaceAgentFlags?.enabled === true;

    const WORKPLACE_AGENT_ICONS = {
        teams: { color: "#5059C9", icon: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M14.5 6.5a2 2 0 1 0-4 0 2 2 0 0 0 4 0Z" fill="#5059C9" /><path d="M18 8h-1.5a1.5 1.5 0 1 1 0-3H18a1.5 1.5 0 0 1 0 3Z" fill="#5059C9" /><path d="M12.5 9H6a2 2 0 0 0-2 2v5a4 4 0 0 0 4 4h3a4 4 0 0 0 4-4v-5a2 2 0 0 0-2-2H12.5Z" fill="#7B83EB" /><path d="M18 9h-1.5v5.5A4.5 4.5 0 0 1 12 19h-1a5 5 0 0 0 4.5 2.8A4.5 4.5 0 0 0 20 17.3V10.5A1.5 1.5 0 0 0 18 9Z" fill="#5059C9" /></svg>) },
        outlook: { color: "#0078D4", icon: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="13" height="14" rx="2" fill="#0078D4" /><path d="M15 9l5.5-3v12L15 15V9Z" fill="#0078D4" /><path d="M15 9l5.5-3v12L15 15V9Z" fill="#28A8E8" opacity="0.5" /><ellipse cx="8.5" cy="12" rx="3" ry="3.5" fill="white" /><ellipse cx="8.5" cy="12" rx="2" ry="2.5" fill="#0078D4" /></svg>) },
        onedrive: { color: "#0078D4", icon: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9.5 13.5 5 11.2A5.5 5.5 0 0 1 14.8 7a4 4 0 0 1 5.2 3.8v.2l-10.5 2.5Z" fill="#0364B8" /><path d="M9.5 13.5 20 11l.5 1A4 4 0 0 1 16.5 18H7.5a3.5 3.5 0 0 1-2.5-6l4.5 1.5Z" fill="#0078D4" /><path d="M14.8 7a4 4 0 0 1 5.2 3.8L20 11l-5-2-0.2-2Z" fill="#1490DF" /><path d="M5 11.2A5.5 5.5 0 0 1 14.8 7L14.6 9A3.5 3.5 0 0 0 9 11.5l-4-0.3Z" fill="#28A8E8" /></svg>) },
        calendar: { color: "#0078D4", icon: (<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="17" rx="2" fill="#0078D4" /><rect x="3" y="4" width="18" height="6" rx="2" fill="#005A9E" /><rect x="7" y="2" width="2" height="4" rx="1" fill="white" /><rect x="15" y="2" width="2" height="4" rx="1" fill="white" /><rect x="7" y="13" width="2" height="2" rx="0.5" fill="white" /><rect x="11" y="13" width="2" height="2" rx="0.5" fill="white" /><rect x="15" y="13" width="2" height="2" rx="0.5" fill="white" /><rect x="7" y="17" width="2" height="2" rx="0.5" fill="white" /><rect x="11" y="17" width="2" height="2" rx="0.5" fill="white" /></svg>) },
    };

    const WORKPLACE_AGENTS = useMemo(() => {
        if (availableWorkplaceAgents.length > 0) {
            return availableWorkplaceAgents
                .filter((x) => x.isEnabled)
                .map((x) => {
                    const meta = WORKPLACE_AGENT_ICONS[x.id] || {};
                    return { key: x.id, label: x.name, enabled: x.isEnabled, icon: meta.icon || null, color: meta.color || "#0078D4" };
                });
        }

        const all = [
            { key: "teams",    label: "Teams",    enabled: workplaceAgentFlags?.teams    !== false, ...WORKPLACE_AGENT_ICONS.teams },
            { key: "outlook",  label: "Outlook",  enabled: workplaceAgentFlags?.outlook  !== false, ...WORKPLACE_AGENT_ICONS.outlook },
            { key: "onedrive", label: "OneDrive", enabled: workplaceAgentFlags?.onedrive !== false, ...WORKPLACE_AGENT_ICONS.onedrive },
            { key: "calendar", label: "Calendar", enabled: workplaceAgentFlags?.calendar !== false, ...WORKPLACE_AGENT_ICONS.calendar },
        ];

        return all.filter((x) => x.enabled);
    }, [availableWorkplaceAgents, workplaceAgentFlags]);

    // Update integration context whenever it changes (including on mount)
    useEffect(() => {
        UsageLogService.updateIntegrationContext(selectedIntegrations);
    }, [selectedIntegrations]);

    // Update agent context whenever it changes (including on mount)
    useEffect(() => {
        UsageLogService.updateAgentContext(selectedAgents);
    }, [selectedAgents]);

    useEffect(() => {
        if (!isWorkplaceAgentFrameworkEnabled) {
            setAvailableWorkplaceAgents([]);
            setSelectedWorkplaceAgents([]);
            return;
        }

        const loadWorkplaceAgents = async () => {
            const json = await ApiClient.get("/api/workplace-agents/available");
            setAvailableWorkplaceAgents(Array.isArray(json) ? json : []);
        };

        loadWorkplaceAgents();
    }, [isWorkplaceAgentFrameworkEnabled]);

    // Persist model settings to localStorage whenever they change
    useEffect(() => {
        try { localStorage.setItem("spark_model_settings", JSON.stringify(modelSettings)); } catch { /* ignore */ }
    }, [modelSettings]);

    // Close model settings popup when clicking outside
    useEffect(() => {
        if (!isModelSettingsOpen) return;
        const onOutside = (e) => {
            if (modelSettingsBtnRef.current && !modelSettingsBtnRef.current.closest('[data-model-popup]')) {
                setIsModelSettingsOpen(false);
                setIsModelDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", onOutside);
        return () => document.removeEventListener("mousedown", onOutside);
    }, [isModelSettingsOpen]);

    // Update spark context when search mode changes to locked modes
    useEffect(() => {
        if (searchMode === "web") {
            UsageLogService.updateSparkContext(56534, "Generic");
        } else if (searchMode === "sphere") {
            UsageLogService.updateSparkContext(20685, "Ask Sphere");
        } else if (searchMode === "people") {
            UsageLogService.updateSparkContext(14651, "Ask People - Solutions");
        }
    }, [searchMode]);

    // ── Transformation helpers for API response ────────────────────────────────

    const transformToAiModelsFormat = (modelGroups) => {
        if (!modelGroups || !Array.isArray(modelGroups)) return [];

        const providerColorMap = {
            "openai": "#10a37f",
            "anthropic": "#d97706",
            "google": "#4285f4",
        };

        return modelGroups.map(group => ({
            provider: group.group_display_name || group.group_key,
            color: providerColorMap[group.group_key?.toLowerCase()] || "#888",
            models: (group.models || []).map(model => ({
                id: model.key || model.display_name,
                label: model.display_name || model.key,
                badge: model.default ? "Default"
                    : model.supports_reasoning ? "Reasoning"
                    : model.supports_thinking ? "Thinking"
                    : model.description?.toLowerCase().includes("fast") ? "Fast"
                    : null,
            }))
        })).filter(provider => provider.models.length > 0);
    };

    const transformConversationStyles = (conversationStyle) => {
        if (!conversationStyle || !Array.isArray(conversationStyle)) return [];

        const styleIconMap = {
            "Creative": "🎨",
            "Balanced": "⚖️",
            "Precise": "🎯",
        };

        return conversationStyle.map(style => ({
            key: style.key || style.display_name,
            icon: styleIconMap[style.key] || "⚙️",
            temp: parseFloat(style.temperature) || 0.6,
            desc: style.display_name || style.key,
            color: "#0891b2",
            default: style.default || false,
        }));
    };

    const transformResponseLengths = (responseLength) => {
        if (!responseLength || !Array.isArray(responseLength)) return [];

        const barCountMap = {
            "Short": 1,
            "Medium": 2,
            "Long": 3,
        };

        return responseLength.map(length => ({
            key: length.key || length.display_name,
            barCount: barCountMap[length.key] || 2,
            desc: length.display_name || length.key,
            default: length.default || false,
        }));
    };

    const loadAiModelConfigurations = async () => {
        setIsLoadingAiSettings(true);
        setAiSettingsError("");

        try {
            const configs = await SparkStoreModelsService.getStoreConfigs();

            // Transform model_groups into AI_MODELS format
            const transformedModels = transformToAiModelsFormat(configs.model_groups);
            if (transformedModels.length > 0) {
                setAiModels(transformedModels);
            }

            // Transform conversation_style
            const styles = transformConversationStyles(configs.conversation_style);
            if (styles.length > 0) {
                setConversationStyles(styles);

                // Update modelSettings if current style doesn't exist in new data
                const styleExists = styles.some(s => s.key === modelSettings.styleKey);
                if (!styleExists) {
                    const defaultStyle = styles.find(s => s.default) || styles[0];
                    if (defaultStyle) {
                        updateModelSettings({
                            styleKey: defaultStyle.key,
                            temperature: defaultStyle.temp
                        });
                    }
                }
            }

            // Transform response_length
            const lengths = transformResponseLengths(configs.response_length);
            if (lengths.length > 0) {
                setResponseLengths(lengths);

                // Update modelSettings if current length doesn't exist
                const lengthExists = lengths.some(l => l.key === modelSettings.responseLength);
                if (!lengthExists) {
                    const defaultLength = lengths.find(l => l.default) || lengths[0];
                    if (defaultLength) {
                        updateModelSettings({ responseLength: defaultLength.key });
                    }
                }
            }

            console.log("[ChatPanel] AI model configurations loaded dynamically", {
                providers: transformedModels.length,
                totalModels: transformedModels.reduce((sum, p) => sum + p.models.length, 0),
                styles: styles.length,
                lengths: lengths.length
            });

        } catch (error) {
            console.error("[ChatPanel] Failed to load AI model configurations:", error);
            setAiSettingsError("Could not load latest model configurations. Using defaults.");
            // Keep using fallback values (already set in initial state)
        } finally {
            setIsLoadingAiSettings(false);
        }
    };

    const [shareMenuForMessageId, setShareMenuForMessageId] = useState(null);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailTo, setEmailTo] = useState("");
    const [emailSubject, setEmailSubject] = useState("Spark Meet Response");
    const [emailBody, setEmailBody] = useState("");
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    // 🆕 2024-12-20: UPDATED - Show welcome screen whenever only greeting message exists
    const showWelcome = useMemo(() => {
        return messages.length === 1 && messages[0].id === "m0";
    }, [messages]);

    const filteredSparks = useMemo(() => {
        const q = (pickerQuery || "").trim().toLowerCase();
        if (!q) {
            // No query — show top SPARK_PICKER_DEFAULT_LIMIT sparks sorted by likes desc
            // Static pinned sparks always appear first regardless of likes
            const staticIds = new Set(SPARKS.map(s => s.sparkId));
            const pinned = allSparks.filter(s => staticIds.has(s.sparkId));
            const dynamic = allSparks
                .filter(s => !staticIds.has(s.sparkId))
                .sort((a, b) => (b.likes ?? 0) - (a.likes ?? 0));
            return [...pinned, ...dynamic].slice(0, SPARK_PICKER_DEFAULT_LIMIT);
        }
        // With query — search across all sparks, no limit needed (filtered set is small)
        return allSparks.filter(
            (s) =>
                s.label.toLowerCase().includes(q) ||
                s.sparkName.toLowerCase().includes(q) ||
                String(s.sparkId).includes(q) ||
                (s.description && s.description.toLowerCase().includes(q)) ||
                (s.creatorName && s.creatorName.toLowerCase().includes(q)) ||
                (s.tags && s.tags.some(t => t.toLowerCase().includes(q)))
        );
    }, [pickerQuery, allSparks]);

    const filteredIntegrations = useMemo(() => {
        // 🆕 First filter by feature flags
        let available = INTEGRATIONS.filter(x => {
            if (x.key === 'ado') return flags.workplaceIntegrations.ado;
            if (x.key === 'teams') return flags.workplaceIntegrations.teams;
            if (x.key === 'outlook') return flags.workplaceIntegrations.outlook;
            if (x.key === 'sharepoint') return flags.workplaceIntegrations.sharepoint;
            if (x.key === 'onedrive') return flags.workplaceIntegrations.onedrive;
            if (x.key === 'calendar') return flags.workplaceIntegrations.calendar;
            return true;
        });

        // Then filter by search query
        const q = (pickerQuery || "").trim().toLowerCase().replace(/^\//, "");
        if (!q) return available;
        return available.filter((x) => x.label.toLowerCase().includes(q));
    }, [pickerQuery, INTEGRATIONS, flags.workplaceIntegrations]);

    const filteredAgents = useMemo(() => {
        const q = (pickerQuery || "").trim().toLowerCase();
        if (!q) return AGENTS;
        return AGENTS.filter((x) => x.label.toLowerCase().includes(q));
    }, [pickerQuery, AGENTS]);

    const filteredWorkplaceAgents = useMemo(() => {
        const q = (pickerQuery || "").trim().toLowerCase();
        if (!q) return WORKPLACE_AGENTS;
        return WORKPLACE_AGENTS.filter((x) => x.label.toLowerCase().includes(q));
    }, [pickerQuery, WORKPLACE_AGENTS]);



    const filteredSparkStoreItems = useMemo(() => {
        // Filter
        let filtered = allSparkStoreItems.filter(spark => {
            // Search filter
            const searchLower = sparkStoreSearch.toLowerCase();
            const matchesSearch = !sparkStoreSearch ||
                spark.title.toLowerCase().includes(searchLower) ||
                spark.description.toLowerCase().includes(searchLower) ||
                spark.tags.some(tag => tag.toLowerCase().includes(searchLower)) ||
                spark.creatorName.toLowerCase().includes(searchLower); // 🆕 Also search creator

            if (!matchesSearch) return false;

            // Category filter
            if (sparkStoreCategory === "all") return true;
            if (sparkStoreCategory === "certified") return spark.certified === true;
            if (sparkStoreCategory === "my_likes") return spark.liked === true;
            if (sparkStoreCategory === "shared") return spark.shared === true;
            if (sparkStoreCategory === "recent") return spark.recentlyAdded === true;

            return true;
        });

        // 🆕 Sort
        filtered.sort((a, b) => {
            switch (sparkStoreSortBy) {
                case "most_liked":
                    return b.likes - a.likes; // Descending

                case "creation_date":
                    return new Date(b.createdDate) - new Date(a.createdDate); // Newest first

                case "spark_name":
                    return a.title.localeCompare(b.title); // A-Z

                case "creator_name":
                    return a.creatorName.localeCompare(b.creatorName); // A-Z

                case "updated_date":
                    return new Date(b.updatedDate) - new Date(a.updatedDate); // Newest first

                default:
                    return 0;
            }
        });

        return filtered;
    }, [sparkStoreSearch, sparkStoreCategory, sparkStoreSortBy, allSparkStoreItems]);




    // Teams initialization
    useEffect(() => {
        microsoftTeams.app
            .initialize()
            .then(() => microsoftTeams.app.getContext())
            .then((context) => {
                microsoftTeams.app.notifySuccess?.();
                applyTheme(context.app?.theme);
                microsoftTeams.app.registerOnThemeChangeHandler(applyTheme);

                const mId = context.meeting?.id || "";
                const cId = context.chat?.id || "";
                const uName = context.user?.userPrincipalName || context.user?.displayName || "";

                setMeetingId(mId);
                setChatId(cId);
                setUserName(uName);

                console.log("✅ Teams Context Loaded:", {
                    meetingId: mId,
                    chatId: cId,
                    userName: uName,
                });

                // ── ADD THIS BLOCK ──────────────────────────────────────────
                if (mId) {
                    microsoftTeams.meeting.getParticipants?.()
                        .then(result => {
                            const pts = (result?.participants ?? []).map(p => ({
                                displayName: p.displayName ?? "",
                                email: p.userPrincipalName ?? "",
                                userPrincipalName: p.userPrincipalName ?? "",
                            }));
                            setTeamsParticipants(pts);
                            console.log("✅ Meeting participants loaded:", pts.length);
                        })
                        .catch(err => console.warn("⚠️ getParticipants failed (non-critical):", err));
                }
                // ─────────────────────
            })
            .catch((err) => console.error("❌ Teams init/context error:", err))
            .finally(() => {
                // ← ADD THIS: dismiss loader whether Teams init succeeds or fails
                setTimeout(() => setIsAppLoading(false), 2200);
            });
    }, []);

    // ── Load dynamic sparks after Teams init settles ───────────────────────
    useEffect(() => {
        const t = setTimeout(loadDynamicSparks, 900);
        return () => clearTimeout(t);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Load AI model configurations from API ───────────────────────────────────
    useEffect(() => {
        const t = setTimeout(loadAiModelConfigurations, 1200); // Load after dynamic sparks
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run once on mount

    // ═══════════════════════════════════════════════════════════════
    // Load initial 30 chats when app starts
    // ═══════════════════════════════════════════════════════════════
    useEffect(() => {
        const initializeChatHistory = async () => {
            try {
                // Load initial 30 chats from Cosmos DB
                await loadChatHistoryList(false);
            } catch (error) {
                console.warn("Failed to initialize chat history:", error);
                // Non-blocking error - user can still use the app
            }
        };

        // Delay initialization until Teams context is ready (after 2.5 seconds)
        const timer = setTimeout(initializeChatHistory, 2500);
        return () => clearTimeout(timer);
    }, []); // Run once on mount


    // SAVE meeting state to sessionStorage whenever any STT state changes
    useEffect(() => {
        if (!meetingId) return;   // only persist when actually in a meeting
        try {
            const snapshot = {
                meetingSttResult,
                meetingSttError,
                meetingRecordingStartedAt,
                isMeetingRecording: false,       // never persist "recording" — mic would be dead
                isMeetingTranscribing: false,    // never persist "transcribing"
                savedAt: Date.now(),
            };
            sessionStorage.setItem(
                `spark_meeting_${meetingId}`,
                JSON.stringify(snapshot)
            );
        } catch { /* ignore quota errors */ }
    }, [
        meetingId,
        meetingSttResult,
        meetingSttError,
        meetingRecordingStartedAt,
    ]);

    // RESTORE meeting state from sessionStorage on mount (runs before Teams re-init)
    useEffect(() => {
        // We don't have meetingId yet at this point — listen for it to be set
        // by watching meetingId in a separate effect below
    }, []);

    useEffect(() => {
        if (!meetingId) return;
        try {
            const raw = sessionStorage.getItem(`spark_meeting_${meetingId}`);
            if (!raw) return;
            const snapshot = JSON.parse(raw);

            // Only restore if saved within the last 8 hours (meeting session window)
            const ageMs = Date.now() - (snapshot.savedAt ?? 0);
            if (ageMs > 8 * 60 * 60 * 1000) {
                sessionStorage.removeItem(`spark_meeting_${meetingId}`);
                return;
            }

            // Restore state — only if not already set (don't overwrite live recording)
            if (snapshot.meetingSttResult && !meetingSttResult) {
                setMeetingSttResult(snapshot.meetingSttResult);
            }
            if (snapshot.meetingSttError && !meetingSttError) {
                setMeetingSttError(snapshot.meetingSttError);
            }
            if (snapshot.meetingRecordingStartedAt && !meetingRecordingStartedAt) {
                setMeetingRecordingStartedAt(snapshot.meetingRecordingStartedAt);
            }

            console.log("✅ Meeting state restored from sessionStorage", {
                meetingId,
                ageMinutes: Math.round(ageMs / 60000),
                hasTranscript: !!snapshot.meetingSttResult?.fullTranscript,
            });
        } catch { /* ignore */ }
    }, [meetingId]);   // runs once when meetingId is set after Teams init

    // END CHANGE 1 ────────────────────────────────────────────────────────────────


    // Restore active spark from localStorage
    useEffect(() => {
        try {
            const savedSparkId = Number(localStorage.getItem("spark_active_id") || "");
            if (savedSparkId) {
                const found = SPARKS.find((s) => s.sparkId === savedSparkId);
                if (found) {
                    setSelectedSpark(found);
                    // Set context immediately after restoring
                    UsageLogService.updateSparkContext(found.sparkId, found.sparkName);
                    console.log('🔧 UsageLog: Restored spark context:', found.sparkName);
                }
            }
        } catch { /* ignore */ }
    }, []);

    // First-load nudge animation for Spark Store icon
    useEffect(() => {
        try {
            const seen = localStorage.getItem("spark_store_seen") === "1";
            if (!seen) {
                setSparkStoreNudge(true);
                const t = setTimeout(() => setSparkStoreNudge(false), 4200);
                return () => clearTimeout(t);
            }
        } catch { /* ignore */ }
    }, []);

    //Below code is commented as Messages are now saved to Cosmos DB in the send() function
    // // Persist messages to localStorage on every change (cleared by New Chat)
    // useEffect(() => {
    //     try {
    //         // Don't persist if it's just the initial greeting — no point saving that
    //         if (messages.length > 1 || messages[0]?.id !== "m0") {
    //             localStorage.setItem("spark_chat_messages", JSON.stringify(messages));
    //             // Re-arm auto-save guard: a real conversation is now in progress
    //             sessionActiveRef.current = true;
    //         }
    //     } catch { /* ignore quota errors */ }
    // }, [messages]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, insights, pickerMode, showEmailModal, shareMenuForMessageId, isSparkStoreOpen]);

    useEffect(() => {
        const onDocClick = () => setShareMenuForMessageId(null);
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    // ── Spark Store infinite scroll ────────────────────────────────────────
    useEffect(() => {
        const el = sparkStoreScrollRef.current;
        if (!el) return;

        const onScroll = () => {
            // Load more when within 300px of the bottom
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 300) {
                setSparkStoreDisplayLimit(prev => {
                    const total = filteredSparkStoreItems.length;
                    if (prev >= total) return prev;
                    return Math.min(prev + 50, total);
                });
            }
        };

        el.addEventListener("scroll", onScroll, { passive: true });
        return () => el.removeEventListener("scroll", onScroll);
    }, [isSparkStoreOpen, filteredSparkStoreItems.length]);


    useEffect(() => {
        if (!isSending) {
            setProcessingMessage("");
            return;
        }
        const steps = [
            "🔍 Searching knowledge sources...",
            "🧠 Thinking through your question...",
            "📚 Consulting the selected Spark...",
            "✍️ Drafting a response...",
            "⚙️ Processing with integrations...",
            "🔗 Connecting to data sources...",
            "💡 Almost ready...",
        ];
        let i = 0;
        setProcessingMessage(steps[0]);
        const interval = setInterval(() => {
            i = (i + 1) % steps.length;
            setProcessingMessage(steps[i]);
        }, 1800);
        return () => clearInterval(interval);
    }, [isSending]);



    // ── 7A: Warn before navigating away during recording ─────────────────────────
    React.useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isMeetingRecordingRef.current) {
                e.preventDefault();
                e.returnValue = "Meeting transcription is in progress. Leaving will stop the recording.";
                return e.returnValue;
            }
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    // ── 7B: Backup audio chunks to sessionStorage every 30 seconds ───────────────
    React.useEffect(() => {
        if (!isMeetingRecording) return; // only runs while recording is active

        const interval = setInterval(() => {
            if (meetingAudioChunksRef.current.length === 0) return;

            try {
                const blob = new Blob(meetingAudioChunksRef.current, { type: "audio/webm" });
                const reader = new FileReader();
                reader.onloadend = () => {
                    try {
                        sessionStorage.setItem("stt_backup_audio", reader.result);
                        sessionStorage.setItem("stt_backup_time", new Date().toISOString());
                        sessionStorage.setItem("stt_backup_startedAt", meetingRecordingStartedAt || "");
                        console.log(`💾 STT backup saved (${(blob.size / 1024).toFixed(0)} KB)`);
                    } catch (e) {
                        console.warn("⚠️ sessionStorage backup failed (quota?):", e.message);
                    }
                };
                reader.readAsDataURL(blob);
            } catch (e) {
                console.warn("⚠️ STT backup error:", e.message);
            }
        }, 30000); // every 30 seconds

        return () => clearInterval(interval);
    }, [isMeetingRecording, meetingRecordingStartedAt]);

    // ── 7C: On mount — check for interrupted recording backup ────────────────────
    React.useEffect(() => {
        const backup = sessionStorage.getItem("stt_backup_audio");
        const backupTime = sessionStorage.getItem("stt_backup_time");
        const startedAt = sessionStorage.getItem("stt_backup_startedAt");

        if (!backup || !backupTime) return;

        // Only offer recovery if backup is less than 30 minutes old
        const ageMinutes = (Date.now() - new Date(backupTime).getTime()) / 1000 / 60;
        if (ageMinutes > 30) {
            sessionStorage.removeItem("stt_backup_audio");
            sessionStorage.removeItem("stt_backup_time");
            sessionStorage.removeItem("stt_backup_startedAt");
            return;
        }

        // Show recovery prompt in chat panel
        setMessages(prev => [...prev, {
            id: makeId(),
            role: "Spark Assist",
            text: `💾 A meeting recording from ${Math.round(ageMinutes)} minute(s) ago was found.\n` +
                `Tap Recover Recording to process it, or Discard to delete it.`,
            type: "stt_recovery",
            backupTime,
            startedAt,
        }]);
    }, []); // empty deps = runs once on mount only





    const applyTheme = (theme) => {
        switch (theme) {
            case "dark":
                setAppTheme("theme-dark");
                break;
            case "contrast":
                setAppTheme("theme-contrast");
                break;
            default:
                setAppTheme("theme-light");
                break;
        }
    };

    const makeId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const canSend = useMemo(() => draft.trim().length > 0 && !isSending, [draft, isSending]);
    // Compact layout: meeting side-panel OR mobile Static Tabs
    const isCompact = isMobile || !!meetingId;

    // ── Personalised greeting ──────────────────────────────────────────────
    const firstName = useMemo(() => {
        if (!userName) return "";
        // UPN like "saisreenath.sg@spglobal.com" → "Saisreenath"
        if (userName.includes("@")) {
            const part = userName.split("@")[0].split(".")[0];
            return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
        }
        // Display name like "Saisreenath Srinivasan" → "Saisreenath"
        return userName.split(" ")[0];
    }, [userName]);

    const timeGreeting = useMemo(() => {
        const h = new Date().getHours();
        if (h < 12) return "Good Morning";
        if (h < 17) return "Good Afternoon";
        return "Good Evening";
    }, []);

    // Get Graph API token with rights scopes for Outlook, Teams, OneDrive
    const getGraphToken = async () => {
        console.log("🔐 Attempting to get Graph API token with scopes:", GRAPH_TOKEN_REQUEST.scopes);

        if (!graphMsalInstance) {
            console.warn("⚠️ MSAL not initialized, cannot acquire token");
            throw new Error("MSAL not initialized for Graph token acquisition");
        }

        try {
            const account = graphMsalInstance.getActiveAccount() || graphMsalInstance.getAllAccounts()[0];

            if (!account) {
                console.warn("⚠️ No active account found");
                throw new Error("Not authenticated");
            }

            console.log("📝 Active account:", account.username);

            try {
                // Try silent token acquisition first
                const result = await graphMsalInstance.acquireTokenSilent({
                    ...GRAPH_TOKEN_REQUEST,
                    account,
                });

                console.log("✅ Graph token acquired silently");

                // Log token details for debugging
                if (result.accessToken) {
                    try {
                        // Decode JWT payload (second part) to show scopes
                        const parts = result.accessToken.split('.');
                        if (parts.length === 3) {
                            const payload = JSON.parse(atob(parts[1]));
                            const grantedScopes = payload.scp ? payload.scp.split(' ') : [];
                            console.log("✅ Token scopes granted:", grantedScopes);
                            console.log("✅ Token expires at:", new Date(payload.exp * 1000).toISOString());
                        }
                    } catch (decodeErr) {
                        console.log("ℹ️ Token acquired (decode skipped)");
                    }
                }

                return result.accessToken;
            } catch (silentErr) {
                if (silentErr instanceof InteractionRequiredAuthError) {
                    console.warn("⚠️ Silent token failed, attempting popup...");
                    const popupResult = await graphMsalInstance.acquireTokenPopup(GRAPH_TOKEN_REQUEST);

                    if (popupResult?.account) {
                        graphMsalInstance.setActiveAccount(popupResult.account);
                        console.log("✅ Graph token acquired via popup");

                        // Log token details for debugging
                        if (popupResult.accessToken) {
                            try {
                                const parts = popupResult.accessToken.split('.');
                                if (parts.length === 3) {
                                    const payload = JSON.parse(atob(parts[1]));
                                    const grantedScopes = payload.scp ? payload.scp.split(' ') : [];
                                    console.log("✅ Token scopes granted:", grantedScopes);
                                    console.log("✅ Token expires at:", new Date(payload.exp * 1000).toISOString());
                                }
                            } catch (decodeErr) {
                                console.log("ℹ️ Token acquired (decode skipped)");
                            }
                        }
                    }

                    return popupResult.accessToken;
                }
                throw silentErr;
            }
        } catch (error) {
            console.error("❌ Failed to get Graph token:", error);
            throw error;
        }
    };

    const handleDraftChange = (value) => {
        setDraft(value);

        const lastAt = value.lastIndexOf("@");
        const lastSlash = value.lastIndexOf("/");
        const lastTriggerPos = Math.max(lastAt, lastSlash);

        if (lastTriggerPos < 0) {
            setPickerMode(null);
            setPickerQuery("");
            return;
        }

        const triggerChar = value[lastTriggerPos];
        const beforeChar = lastTriggerPos > 0 ? value[lastTriggerPos - 1] : " ";

        const validTrigger = lastTriggerPos === 0 || beforeChar === " ";
        if (!validTrigger) {
            setPickerMode(null);
            setPickerQuery("");
            return;
        }

        if (triggerChar === "@") {
            setPickerMode("spark");
            setPickerQuery(value.substring(lastTriggerPos + 1));
            return;
        }

        if (triggerChar === "/") {
            setPickerMode("integration");
            setPickerQuery(value.substring(lastTriggerPos));
            return;
        }

        setPickerMode(null);
        setPickerQuery("");
    };

    const selectSpark = (spark) => {
        setSelectedSpark(spark);

        // Update UsageLogService spark context
        UsageLogService.updateSparkContext(spark.sparkId, spark.sparkName);

        try {
            localStorage.setItem("spark_active_id", String(spark.sparkId));
        } catch { /* ignore */ }

        const lastAt = draft.lastIndexOf("@");
        if (lastAt >= 0) {
            const before = draft.substring(0, lastAt).trimEnd();
            setDraft(before.length ? before + " " : "");
        }

        setPickerMode(null);
        setPickerQuery("");

        setMessages((prev) => [
            ...prev,
            { id: makeId(), role: "Spark Assist", text: `✅ Switched to Spark: ${spark.sparkName} (ID: ${spark.sparkId})` },
        ]);
    };

    /** Silently select a preset spark (Classic Chat / Default Spark) from the + menu */
    const selectPresetSpark = (preset) => {
        setSelectedSpark(preset);
        UsageLogService.updateSparkContext(preset.sparkId, preset.sparkName);
        try { localStorage.setItem("spark_active_id", String(preset.sparkId)); } catch { /* ignore */ }
        setQuickSparkOpen(false);
    };


    /** Live-update model settings and persist to localStorage */
    const updateModelSettings = (patch) => {
        setModelSettings(prev => ({ ...prev, ...patch }));
    };

    /** Toggle the model settings popup, calculating its anchor position from the button ref */
    const toggleModelSettings = (e) => {
        if (!isModelSettingsOpen) {
            const btn = e?.currentTarget ?? modelSettingsBtnRef.current;
            if (btn) {
                const rect = btn.getBoundingClientRect();
                if (isMobile) {
                    setModelPopupStyle({ bottom: 0, left: 0, right: 0 });
                } else {
                    const popupWidth = 340;
                    let right = window.innerWidth - rect.right;
                    if (right + popupWidth > window.innerWidth - 8) right = Math.max(8, window.innerWidth - popupWidth - 8);
                    setModelPopupStyle({
                        bottom: window.innerHeight - rect.top + 10,
                        right: Math.max(8, right),
                        width: popupWidth,
                    });
                }
            }
        }
        setIsModelSettingsOpen(prev => !prev);
        setIsModelDropdownOpen(false);
    };

    // ──────────────────────────────────────────────────────────────────────
    // DYNAMIC SPARKS LOADER
    // ──────────────────────────────────────────────────────────────────────

    const _readSparksCache = () => {
        try {
            const raw = localStorage.getItem(DYNAMIC_SPARKS_CACHE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    };

    const _writeSparksCache = (cache) => {
        try {
            localStorage.setItem(DYNAMIC_SPARKS_CACHE_KEY, JSON.stringify(cache));
        } catch (e) {
            console.warn("⚠️ spark cache write failed (quota?):", e.message);
        }
    };

    const _readSparksCacheFromIndexedDB = async () => {
        try {
            // Check metadata from localStorage
            const metadata = sparkDBService.getCacheMetadata();

            if (!metadata || !metadata.fetchedAt || !metadata.syncedAt) {
                console.log("No cache metadata found");
                return null;
            }

            // Read sparks from IndexedDB
            const sparks = await sparkDBService.getAllSparks();

            if (sparks.length === 0) {
                console.log("No sparks found in IndexedDB");
                return null;
            }

            console.log(`✅ Loaded ${sparks.length} sparks from IndexedDB cache`);

            return {
                sparks,
                fetchedAt: metadata.fetchedAt,
                syncedAt: metadata.syncedAt,
            };
        } catch (e) {
            console.warn("⚠️ Failed to read from IndexedDB:", e.message);
            return null;
        }
    };

    const _writeSparksCacheFromIndexedDB = async (data) => {
        try {
            // Save sparks to IndexedDB
            await sparkDBService.saveSparks(data.sparks);

            // Save metadata to localStorage (lightweight)
            sparkDBService.saveCacheMetadata({
                fetchedAt: data.fetchedAt,
                syncedAt: data.syncedAt,
                count: data.sparks.length,
            });

            console.log(`✅ Cached ${data.sparks.length} sparks to IndexedDB`);
        } catch (e) {
            console.warn("⚠️ IndexedDB cache write failed:", e.message);
        }
    };

    // NOTE: uses new get_store_sparks page-number-based API
    const _fetchSparksPage = async (page = 1) => {
        const body = {
            page,
            sort_by_field: "CREATION_DATE",
            sort_order: "DESC",
            tab_filter: "ALL",
        };
        return ApiClient.post("/api/sparkstore/sparks", body);
    };

    /** Full paginated fetch — iterates pages until all total_count records are loaded */
    const _fetchAllSparks = async () => {

        const startTime = Date.now();

        const all = [];
        let page = 1;
        let totalCount = null;

        setSparkSyncProgress(0);
        setSparkSyncTotal(0);

        while (true) {
            const result = await _fetchSparksPage(page);
            const batch = result?.sparks ?? [];

            if (totalCount === null) totalCount = result?.total_count ?? 0;

            if (batch.length === 0) break;

            all.push(...batch);

            // Progress counters — use total_count from API for accurate %
            setSparkSyncTotal(all.length);
            setSparkSyncProgress(
                totalCount > 0
                    ? Math.min(95, Math.round((all.length / totalCount) * 100))
                    : Math.min(95, page * 5)
            );

            // Progressive UI paint — sparks appear page by page
            setDynamicSparks(prev => {
                const known = new Set(prev.map(s => s.spark_id));
                return [...prev, ...batch.filter(s => !known.has(s.spark_id))];
            });

            if (totalCount > 0 && all.length >= totalCount) break; // all pages loaded
            if (batch.length === 0) break; // safety guard

            page++;
        }

        const totalDuration = Date.now() - startTime;
        console.log(`Sequential Fetch : Dynamic sparks fetch completed in ${totalDuration} ms`);

        if (all.length > 0) {
            const syncTime = new Date();
            setDynamicSparks(all);
            setDynamicSparksLoaded(true);
            setSparkSyncProgress(100);
            setSparkSyncTotal(all.length);
            setLastSyncedAt(syncTime);
            await _writeSparksCacheFromIndexedDB({
                sparks: all,
                fetchedAt: Date.now(),
                syncedAt: syncTime.toISOString(),
            });
            console.log(`✅ Dynamic sparks: ${all.length} of ${totalCount} total loaded`);
            // Reset progress bar after 2s
            setTimeout(() => setSparkSyncProgress(0), 2000);
        }
    };

    // Fetch Sparks in parallel api calls with a concurrency limit
    const _fetchAllSparksParallel = async () => {

        const startTime = Date.now();

        const all = [];
        let totalCount = null;

        setSparkSyncProgress(0);
        setSparkSyncTotal(0);

        try {
            // Step 1: Fetch first page to get total count
            console.log("Fetching page 1 to determine total pages...");
            const firstPage = await _fetchSparksPage(1);
            const firstBatch = firstPage?.sparks ?? [];
            const pageSize = firstBatch.length || 30; // Assume 30 sparks per page
            totalCount = firstPage?.total_count ?? 0;

            if (totalCount === 0) {
                console.log("No sparks found");
                return;
            }

            // Calculate total pages needed
            const totalPages = Math.ceil(totalCount / pageSize);
            all.push(...firstBatch);

            // Paint first page immediately (instant feedback)
            setDynamicSparks(firstBatch);
            setSparkSyncTotal(firstBatch.length);
            setSparkSyncProgress(Math.round((firstBatch.length / totalCount) * 100));

            // Step 2: Fetch remaining pages in parallel batches
            const BATCH_SIZE = SPARK_FETCH_CONFIG.batchSize;
            const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
            const totalBatches = Math.ceil(remainingPages.length / BATCH_SIZE);

            // Split remaining pages into batches
            for (let i = 0; i < remainingPages.length; i += BATCH_SIZE) {
                const batchPages = remainingPages.slice(i, i + BATCH_SIZE);
                const batchNumber = Math.floor(i / BATCH_SIZE) + 1;

                // RATE LIMITING: Optional delay between batches
                if (SPARK_FETCH_CONFIG.batchDelay > 0 && batchNumber > 1) {
                    await new Promise(resolve => setTimeout(resolve, SPARK_FETCH_CONFIG.batchDelay));
                }

                const batchStartTime = Date.now();

                try {
                    // Fetch batch with retry logic
                    const batchResults = await _fetchBatchWithRetry(batchPages);
                    const batchDuration = Date.now() - batchStartTime;

                    // Extract sparks from results
                    const batchSparks = batchResults.flatMap(result => result?.sparks ?? []);

                    // Add to accumulated results
                    all.push(...batchSparks);

                    // Adaptive batch sizing (optional)
                    if (SPARK_FETCH_CONFIG.adaptive) {
                        if (batchDuration > 2000) {
                            // Batch took too long, reduce size
                            SPARK_FETCH_CONFIG.batchSize = Math.max(5, BATCH_SIZE - 2);
                            console.log(`Reducing batch size to ${SPARK_FETCH_CONFIG.batchSize} (slow batch)`);
                        } else if (batchDuration < 300 && BATCH_SIZE < 30) {
                            // Batch was fast, can increase
                            SPARK_FETCH_CONFIG.batchSize = Math.min(30, BATCH_SIZE + 2);
                            console.log(`Increasing batch size to ${SPARK_FETCH_CONFIG.batchSize} (fast batch)`);
                        }
                    }

                    // Progressive UI update
                    setDynamicSparks(prevSparks => {
                        const known = new Set(prevSparks.map(s => s.spark_id));
                        const newSparks = batchSparks.filter(s => !known.has(s.spark_id));
                        return [...prevSparks, ...newSparks];
                    });

                    // Update progress
                    const progress = Math.min(95, Math.round((all.length / totalCount) * 100));
                    setSparkSyncTotal(all.length);
                    setSparkSyncProgress(progress);

                    console.log(`Batch ${batchNumber}/${totalBatches} complete: ${batchSparks.length} sparks fetched (total: ${all.length}/${totalCount})`);

                } catch (batchError) {
                    console.error(`Error fetching batch ${batchNumber}:`, batchError);
                    // Continue with next batch instead of failing completely
                }
            }

            const totalDuration = Date.now() - startTime;
            console.log(`Parallel Fetch : Dynamic sparks fetch completed in ${totalDuration} ms`);

            // Step 3: Final update
            if (all.length > 0) {
                const syncTime = new Date();
                setDynamicSparks(all);
                setDynamicSparksLoaded(true);
                setSparkSyncProgress(100);
                setSparkSyncTotal(all.length);
                setLastSyncedAt(syncTime);

                // Cache the results
                await _writeSparksCacheFromIndexedDB({
                    sparks: all,
                    fetchedAt: Date.now(),
                    syncedAt: syncTime.toISOString(),
                });

                console.log(`All sparks loaded: ${all.length} of ${totalCount} (${totalPages} pages in parallel)`);

                // Reset progress bar after 2 seconds
                setTimeout(() => setSparkSyncProgress(0), 2000);
            }

        } catch (error) {
            console.error("Error in parallel fetch:", error);
            throw error;
        }
    };

    // Helper: Retry failed batches
    const _fetchBatchWithRetry = async (batchPages, attempt = 1) => {
        try {
            const batchPromises = batchPages.map(page => _fetchSparksPage(page));
            return await Promise.all(batchPromises);
        } catch (error) {
            if (attempt >= SPARK_FETCH_CONFIG.maxRetries) {
                console.error(`Batch failed after ${attempt} attempts`);
                throw error;
            }

            const retryDelay = 1000 * attempt; // Exponential backoff
            console.warn(`Batch failed (attempt ${attempt}), retrying in ${retryDelay}ms...`);
            await new Promise(resolve => setTimeout(resolve, retryDelay));

            return _fetchBatchWithRetry(batchPages, attempt + 1);
        }
    };

    /** Incremental fetch — only checks page 1 (newest) for additions since last sync */
    const _fetchIncrementalSparks = async (cache) => {
        const result = await _fetchSparksPage(1); // page 1 = newest first
        const fresh = result?.sparks ?? [];
        if (fresh.length === 0) return;

        const cachedIds = new Set(cache.sparks.map(s => s.spark_id));
        const brandNew = fresh.filter(s => !cachedIds.has(s.spark_id));

        if (brandNew.length === 0) {
            console.log("✅ Dynamic sparks: cache is current, no new sparks");
            return;
        }

        console.log(`✅ Dynamic sparks: +${brandNew.length} new sparks`);
        const updated = [...brandNew, ...cache.sparks];
        const syncTime = new Date();
        setDynamicSparks(updated);
        setLastSyncedAt(syncTime);
        await _writeSparksCacheFromIndexedDB({
            sparks: updated,
            fetchedAt: Date.now(),
            syncedAt: syncTime.toISOString(),
        });
    };

    /** Entry point — decides full vs incremental fetch */
    const loadDynamicSparks = async () => {
        setIsDynamicSparksLoading(true);
        try {
            const cache = await _readSparksCacheFromIndexedDB();
            const ageMs = Date.now() - (cache?.fetchedAt ?? 0);
            const stale = ageMs > DYNAMIC_SPARKS_TTL_MS;

            if (cache?.sparks?.length > 0) {
                // Paint cached data immediately — user sees sparks instantly
                setDynamicSparks(cache.sparks);
                setDynamicSparksLoaded(true);
                if (cache.syncedAt) setLastSyncedAt(new Date(cache.syncedAt));

                if (stale) {
                    console.log("Dynamic sparks: TTL expired — full refresh");
                    await _fetchAllSparksParallel();
                } else {
                    await _fetchIncrementalSparks(cache);
                }
            } else {
                console.log("Dynamic sparks: first ever load");
                await _fetchAllSparksParallel();
            }
        } catch (err) {
            console.warn("loadDynamicSparks:", err.message);
        } finally {
            setIsDynamicSparksLoading(false);
        }
    };

    /** Force full sync — wipes cache and re-fetches everything from scratch */
    const forceFullSync = async () => {
        if (isDynamicSparksLoading) return;

        try {
            // Clear IndexedDB cache
            await sparkDBService.clearAllSparks();

            // Clear metadata from localStorage
            localStorage.removeItem("spark_cache_metadata");
        } catch (e) {
            console.warn("Failed to clear cache:", e);
        }

        setDynamicSparks([]);
        setDynamicSparksLoaded(false);
        setSparkSyncProgress(0);
        setSparkSyncTotal(0);
        setIsDynamicSparksLoading(true);

        console.log("🔄 Force full spark sync initiated");

        try {
            await _fetchAllSparksParallel();
        } catch (err) {
            console.warn("⚠️ forceFullSync error:", err.message);
        } finally {
            setIsDynamicSparksLoading(false);
        }
    };


    const activateSparkFromStore = (sparkId) => {
        const found = allSparks.find((s) => s.sparkId === sparkId) || SPARKS[0];

        // Track spark activation from store
        UsageLogService.trackEvent('SparkActivatedFromStore', {
            sparkId: found.sparkId,
            sparkName: found.sparkName,
            isCertified: found.certified || false,
            likes: found.likes || 0,
            source: 'SparkStore'
        }, 'Spark');

        try { localStorage.setItem("spark_store_seen", "1"); } catch { /* ignore */ }
        selectSpark(found);
        setIsSparkStoreOpen(false);

        // Reset all filters
        setSparkStoreSearch("");
        setSparkStoreCategory("all");
        setSparkStoreSortBy("most_liked");
    };

    // 🆕 2024-12-20: Handle welcome prompt card click
    const handleWelcomePromptClick = (promptCard) => {
        const spark = SPARKS.find((s) => s.sparkId === promptCard.sparkId) || SPARKS[0];
        setSelectedSpark(spark);

        try {
            localStorage.setItem("spark_active_id", String(spark.sparkId));
        } catch { /* ignore */ }

        const activationMsg = {
            id: makeId(),
            role: "Spark Assist",
            text: `✅ Activated: ${spark.sparkName}`
        };

        const userMsg = {
            id: makeId(),
            role: "user",
            text: promptCard.prompt
        };

        setMessages((prev) => [...prev, activationMsg, userMsg]);
        sendPrompt(promptCard.prompt, [...messages, activationMsg, userMsg]);
    };

    // 🆕 2024-12-20: Send function for welcome screen prompts
    const sendPrompt = async (promptText, currentMessages) => {
        setIsSending(true);

        try {
            const payload = buildConversationPayload(currentMessages, promptText);
            const assistantReply = await callChatApi(payload);
            setMessages((prev) => [...prev, { id: makeId(), role: "Spark Assist", text: assistantReply }]);
        } catch (err) {
            console.error(err);
            setMessages((prev) => [...prev, { id: makeId(), role: "Spark Assist", text: "Sorry — chat call failed. Check server logs." }]);
        } finally {
            setIsSending(false);
        }
    };

    // 🆕 2024-12-20: Start Over - Reset conversation to welcome screen
    const handleStartOver = () => {
        const confirmed = window.confirm("Start a new conversation? This will clear your current chat history.");
        if (!confirmed) return;

        try { localStorage.removeItem("spark_chat_messages"); } catch { /* ignore */ }
        setMessages([{ id: "m0", role: "Spark Assist", text: "Hi! Ask your work question..." }]);
        setDraft("");
        setInsights("");
        setPickerMode(null);
        setPickerQuery("");
    };

    /**
  * saveSessionOnHide()
  * Called when the user navigates AWAY (tab hidden / Teams suspends).
  * Silently saves the conversation to Cosmos DB and re-arms the session guard
  * on next visible event. Avoids double-save on rapid focus changes.
  */
    const saveSessionOnHide = React.useCallback(() => {
        if (!sessionActiveRef.current) return;
        const currentMsgs = messages;
        const hasRealContent = currentMsgs.length > 1 ||
            (currentMsgs.length === 1 && currentMsgs[0]?.id !== "m0");
        if (!hasRealContent) return;

        sessionActiveRef.current = false;

        // Auto-save current chat to Cosmos DB before hiding
        if (currentChatId && currentMsgs.length > 1) {
            saveCurrentChat().catch(err =>
                console.warn("Failed to auto-save chat on app hidden:", err)
            );
        }

        UsageLogService.trackEvent("SessionAutoSaved", {
            messageCount: currentMsgs.length,
            trigger: "appHidden",
        }, "Session");
    }, [messages, currentChatId]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * resetSaveSession()
     * Phase 2 of 2: called when the tab becomes visible again and re-arms the guard.
     */
    const resetSaveSession = React.useCallback(() => {
        sessionActiveRef.current = true; // re-arm for next conversation
    }, []);

    /**
     * App lifecycle — visibility change handler (two-phase).
     * - Page hidden  → saveSessionOnHide()   (save only, no setState)
     * - Page visible → resetSaveSession()  (re-arm session guard)
     */
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                saveSessionOnHide();
            } else {
                resetSaveSession();
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        // Teams SDK v2 lifecycle hooks — fire inside the Teams shell when the
        // tab is suspended/resumed even if visibilitychange doesn't fire.
        try {
            if (microsoftTeams.app?.lifecycle) {
                microsoftTeams.app.lifecycle.registerBeforeSuspendOrTerminateHandler(() => {
                    saveSessionOnHide();
                });
                microsoftTeams.app.lifecycle.registerOnResumeHandler(() => {
                    resetSaveSession();
                });
            }
        } catch { /* Teams SDK lifecycle API not available in this context */ }

        return () => {
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [saveSessionOnHide, resetSaveSession]);

    const toggleIntegration = (integrationKey) => {
        setSelectedIntegrations((prev) => {
            const isAdding = !prev.includes(integrationKey);
            const updated = isAdding
                ? [...prev, integrationKey]
                : prev.filter((k) => k !== integrationKey);

            // Update integration context
            UsageLogService.updateIntegrationContext(updated);

            // Track integration toggle
            UsageLogService.trackIntegrationToggle(
                integrationKey,
                isAdding,
                {
                    totalIntegrations: updated.length,
                    activeIntegrations: updated.join(',')
                }
            );

            return updated;
        });
    };

    const toggleAgent = (agentKey) => {
        setSelectedAgents((prev) => {
            const isAdding = !prev.includes(agentKey);
            const updated = isAdding
                ? [...prev, agentKey]
                : prev.filter((k) => k !== agentKey);

            // Update agent context
            UsageLogService.updateAgentContext(updated);

            // Track agent activation/deactivation
            if (isAdding) {
                const agent = AGENTS.find(a => a.key === agentKey);
                UsageLogService.trackAgentActivated(
                    agent?.label || agentKey,
                    agentKey,
                    {
                        totalAgents: updated.length,
                        activeAgents: updated.join(',')
                    }
                );
            }

            return updated;
        });
    };

    const toggleWorkplaceAgent = (agentKey) => {
        setSelectedWorkplaceAgents((prev) => {
            if (prev.includes(agentKey)) return prev.filter((k) => k !== agentKey);
            return [...prev, agentKey];
        });
    };

    const selectAllWorkplaceAgents = () => {
        const allKeys = WORKPLACE_AGENTS.map((x) => x.key);
        setSelectedWorkplaceAgents((prev) => (prev.length === allKeys.length ? [] : allKeys));
    };

    // Modes that lock the spark and hide integrations/agents
    const isLockedMode = searchMode === "web" || searchMode === "sphere" || searchMode === "people";

    const openSparkPickerFromChip = () => {
        if (isLockedMode) return;
        setPickerMode(pickerMode === "spark" ? null : "spark");
        setPickerQuery("");
        setSparkChipSearch("");
    };

    const openIntegrationPickerFromChip = () => {
        if (isLockedMode) return;
        setPickerMode(pickerMode === "integration" ? null : "integration");
        setPickerQuery("");
    };

    const openAgentPickerFromChip = () => {
        if (isLockedMode) return;
        setPickerMode(pickerMode === "agent" ? null : "agent");
        setPickerQuery("");
    };

    const openWorkplaceAgentPickerFromChip = () => {
        if (isLockedMode || !isWorkplaceAgentFrameworkEnabled) return;
        setPickerMode(pickerMode === "workplace-agent" ? null : "workplace-agent");
        setPickerQuery("");
    };

    const removeIntegrationPill = (key) => {
        setSelectedIntegrations((prev) => prev.filter((k) => k !== key));
    };

    const removeAgentPill = (key) => {
        setSelectedAgents((prev) => prev.filter((k) => k !== key));
    };

    const removeWorkplaceAgentPill = (key) => {
        setSelectedWorkplaceAgents((prev) => prev.filter((k) => k !== key));
    };





    //  Add this function somewhere near your other handler functions in ChatPanel.
    //  It will be called by the Refresh button in the meeting header.
    // ─────────────────────────────────────────────────────────────────────────────

    const clearMeetingState = () => {
        // Clear sessionStorage for this meeting
        try {
            if (meetingId) sessionStorage.removeItem(`spark_meeting_${meetingId}`);
        } catch { /* ignore */ }

        // Reset all STT state
        setMeetingSttResult(null);
        setMeetingSttError("");
        setMeetingRecordingStartedAt(null);
        meetingAudioChunksRef.current = [];
        meetingRestartCountRef.current = 0;
        meetingUserStoppedRef.current = false;
        isMeetingRecordingRef.current = false;
    };

    // END CHANGE 2 ────────────────────────────────────────────────────────────────






    const buildConversationPayload = (currentMessages, userText) => {
        // Filter out spark_metadata assistant blobs before slicing history —
        // dynamic sparks return a spark_metadata JSON blob as the assistant reply
        // during routing detection; including it in history corrupts the next call.
        const cleanMessages = currentMessages.filter((m) => {
            if (!m.text) return true;
            try {
                const parsed = JSON.parse(m.text);
                if (parsed?.flag === "spark_metadata") return false;
            } catch { /* not JSON — keep */ }
            return true;
        });

        const lastThree = cleanMessages.slice(-3);

        const system = {
            role: "system",
            content:
                "You are S&P Global Information Assistant. Answer based only on the provided meeting transcript and relevant internal sources. If transcript is missing, say so. Provide concise answers.",
        };

        const transcriptMsg = transcript
            ? {
                role: "user",
                content: "MEETING TRANSCRIPT (raw):\n\n" + transcript + "\n\nUse this transcript as primary context.",
            }
            : null;

        const history = lastThree.map((m) => ({
            role: m.role === "user" ? "user" : "assistant",
            content: m.text,
        }));

        const user = { role: "user", content: userText };

        return [system, ...(transcriptMsg ? [transcriptMsg] : []), ...history, user];
    };

    const callChatApi = async (payloadMessages) => {
        const startTime = Date.now();

        const resolvedSparkId = searchMode === "web" ? 56534
            : searchMode === "sphere" ? 20685
                : searchMode === "people" ? 14651
                    : (selectedSpark?.sparkId ?? 1);

        const resolvedSparkName = searchMode === "web" ? "Generic"
            : searchMode === "sphere" ? "Ask Sphere"
                : searchMode === "people" ? "Ask People - Solutions"
                    : (selectedSpark?.sparkName ?? "Content Generator");

        const requestBody = {
            messages: payloadMessages,
            temperature: modelSettings.temperature,
            max_tokens: 500,
            top_p: 1.0,
            presence_penalty: 0.0,
            frequency_penalty: 0.0,
            user: "1",
            spark_id: resolvedSparkId,
            spark_name: resolvedSparkName,
            model_id: modelSettings.modelId,
            temperature_key: modelSettings.styleKey,
            response_length: modelSettings.responseLength,
            integrations: selectedIntegrations,
            agents: selectedAgents,
        };

        // ── DEBUG payload ────────────────────────────────────────────────────────────
        console.group("🔥 Spark Chat API Payload");
        console.log("URL:", selectedIntegrations.length > 0 ? "/api/sparkmeet/chat/stream" : "/api/sparkmeet/chat");
        console.log("spark_id:", resolvedSparkId);
        console.log("integrations:", selectedIntegrations);
        console.log(JSON.stringify(requestBody, null, 2));
        console.groupEnd();
        // ────────────────────────────────────────────────────────────────────────────

        // ── WORKPLACE SEARCH: use SSE streaming endpoint ─────────────────────────────
        if (selectedIntegrations.length > 0) {
            setWorkplaceSteps([]);
            setWorkplaceKql(null);
            setWorkplaceHits(null);
            workplaceTraceRef.current = { steps: [], kql: null, hits: null };

            const resp = await fetch("/api/sparkmeet/chat/stream", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${await AuthTokenProvider.getAccessToken() }`,
                },
                body: JSON.stringify(requestBody),
            });

            if (!resp.ok) {
                const text = await resp.text();
                throw new Error(`Chat API failed (${resp.status}): ${text}`);
            }

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let finalContent = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                // SSE frames are separated by \n\n
                const frames = buffer.split("\n\n");
                buffer = frames.pop(); // keep the incomplete trailing frame

                for (const frame of frames) {
                    if (!frame.trim()) continue;

                    const lines = frame.split("\n");
                    let eventName = "message";
                    let dataLine = "";

                    for (const line of lines) {
                        if (line.startsWith("event: ")) eventName = line.slice(7).trim();
                        else if (line.startsWith("data: "))  dataLine  = line.slice(6).trim();
                    }

                    if (!dataLine) continue;

                    let payload;
                    try {
                        payload = JSON.parse(dataLine);
                    } catch (e) {
                        console.warn("[SSE] Could not parse frame data:", dataLine, e);
                        continue;
                    }

                    console.debug("[SSE]", eventName, payload);

                    if (eventName === "progress") {
                        // Each setState is followed by a macro-task yield (setTimeout 0)
                        // so the React scheduler can commit the DOM update and the user
                        // sees each step appear individually before the next one is set.
                        if (payload.type === "step") {
                            workplaceTraceRef.current.steps = [...workplaceTraceRef.current.steps, payload];
                            setWorkplaceSteps(prev => [...prev, payload]);
                            await new Promise(r => setTimeout(r, 50));
                        } else if (payload.type === "kql") {
                            workplaceTraceRef.current.kql = payload;
                            setWorkplaceKql(payload);
                            await new Promise(r => setTimeout(r, 50));
                        } else if (payload.type === "hits") {
                            workplaceTraceRef.current.hits = payload;
                            setWorkplaceHits(payload);
                            await new Promise(r => setTimeout(r, 50));
                        }
                    } else if (eventName === "result") {
                        finalContent = payload.content;
                    } else if (eventName === "error") {
                        throw new Error(payload.message || "Workplace search failed");
                    }
                    // "done" — stream closes naturally, loop exits on next read
                }
            }

            const duration = Date.now() - startTime;
            UsageLogService.trackApiCall("/api/sparkmeet/chat/stream", "POST", 200, duration, {
                sparkId: resolvedSparkId, sparkName: resolvedSparkName,
                searchMode, integrationsCount: selectedIntegrations.length,
            });

            // Keep the live progress panel visible briefly, then clear it
            // (the trace is now permanently attached to the message bubble)
            setTimeout(() => { setWorkplaceSteps([]); setWorkplaceKql(null); setWorkplaceHits(null); }, 1800);

            return sanitizeSparkResponse(finalContent ?? "(No content returned)");
        }

        // ── REGULAR CHAT: original fetch ─────────────────────────────────────────────
        const json = await ApiClient.post("/api/sparkmeet/chat", requestBody);

        const duration = Date.now() - startTime;

        UsageLogService.trackApiCall(
            '/api/sparkmeet/chat',
            'POST',
            200,
            duration,
            {
                sparkId: selectedSpark?.sparkId,
                sparkName: selectedSpark?.sparkName,
                searchMode: searchMode,
                integrationsCount: selectedIntegrations.length,
                agentsCount: selectedAgents.length
            }
        );

        console.log("🔥 Spark Chat API Response:", { content_length: json?.content?.length });
        return sanitizeSparkResponse(json?.content ?? "(No content returned)");
    };

    const downloadTextAsFile = (text, fileNamePrefix = "spark-response") => {
        try {
            const ts = new Date().toISOString().replace(/[:.]/g, "-");
            const fileName = `${fileNamePrefix}-${ts}.txt`;
            const blob = new Blob([text ?? ""], { type: "text/plain;charset=utf-8" });
            const url = URL.createObjectURL(blob);

            const a = document.createElement("a");
            a.href = url;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();

            URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Download failed:", e);
            alert("Download failed. Check console for details.");
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text ?? "");
        } catch (e) {
            console.error("Copy failed:", e);
            alert("Copy failed. Your browser may block clipboard access.");
        }
    };

    const isValidEmail = (email) => {
        const e = (email || "").trim();
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    };

    const openEmailModalForText = (assistantText) => {
        // Track email modal opened
        UsageLogService.trackEvent('EmailModalOpened', {
            messageLength: assistantText?.length || 0,
            sparkId: selectedSpark?.sparkId,
            sparkName: selectedSpark?.sparkName
        }, 'Communication');

        setEmailTo("");
        setEmailSubject(`Spark Meet Response - ${selectedSpark?.sparkName ?? "Spark"}`);
        setEmailBody(assistantText ?? "");
        setShowEmailModal(true);
    };

    const sendEmail = async () => {
        const to = (emailTo || "").trim();
        if (!isValidEmail(to)) {
            alert("Please enter a valid email address.");
            return;
        }

        setIsSendingEmail(true);

        const startTime = Date.now();

        try {
            // Convert plain text to HTML (optional)
            const htmlBody = emailBody.replace(/\n/g, '<br>');

            await ApiClient.post("/api/sparkmeet/email", {
                to,
                subject: emailSubject,
                body: htmlBody,
            });

            const duration = Date.now() - startTime;

            // Track successful email
            UsageLogService.trackEvent('EmailSent', {
                recipient: to.split('@')[1], // Just domain for privacy
                subjectLength: emailSubject.length,
                bodyLength: emailBody.length,
                durationMs: duration,
                sparkId: selectedSpark?.sparkId,
                sparkName: selectedSpark?.sparkName
            }, 'Communication');

            setShowEmailModal(false);
            setMessages((prev) => [...prev, {
                id: makeId(),
                role: "Spark Assist",
                text: `✅ Email sent successfully to: ${to}`
            }]);
        } catch (e) {
            console.error("Email error:", e);
            alert(`Failed to send email: ${e.message}`);

            // Track email error
            UsageLogService.trackError(e, {
                feature: 'EmailSend',
                recipientDomain: to.split('@')[1],
                bodyLength: emailBody.length
            });
        } finally {
            setIsSendingEmail(false);
        }
    };



    //Teams

    const postToTeams = async (text) => {

        // Track Teams modal opened
        UsageLogService.trackEvent('TeamsModalOpened', {
            messageLength: text?.length || 0,
            hasText: !!text
        }, 'Integration');

        setTeamsMessageText(text);
        setTeamsMessageContent(text);
        setTeamsSubject("");
        setTeamsDestType("chat");
        setTeamsSelectedChat(null);
        setTeamsSelectedTeam(null);
        setTeamsSelectedChannel(null);
        setTeamsChannels([]);
        setTeamsChatSearch("");
        setTeamsPostError("");
        setTeamsPostSuccess("");
        setTeamsPostedUrl("");
        setIsTeamsModalOpen(true);
        await fetchTeamsChats();
    };

    const openCalendarModal = (text, titleHint) => {
        // Pre-fill the body with the Spark response; title from AI hint or default
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        setCalendarTitle(titleHint ? titleHint.replace(/\b\w/g, c => c.toUpperCase()) : "");
        setCalendarDate(`${yyyy}-${mm}-${dd}`);
        setCalendarStartTime("09:00");
        setCalendarEndTime("10:00");
        setCalendarAttendees("");
        setCalendarLocation("");
        setCalendarBody(text ?? "");
        setCalendarError("");
        setCalendarSuccess("");
        setIsCalendarModalOpen(true);
    };

    const submitCalendarEvent = async () => {
        if (!calendarTitle.trim()) { setCalendarError("Title is required."); return; }
        if (!calendarDate) { setCalendarError("Date is required."); return; }
        setIsCreatingCalendar(true);
        setCalendarError("");
        setCalendarSuccess("");
        try {
            const start = `${calendarDate}T${calendarStartTime}:00`;
            const end   = `${calendarDate}T${calendarEndTime}:00`;
            const attendeeList = calendarAttendees
                .split(/[,;\n]+/)
                .map(a => a.trim())
                .filter(a => a.length > 0);
            const body = {
                title:     calendarTitle.trim(),
                start,
                end,
                location:  calendarLocation.trim() || null,
                body:      calendarBody.trim() || null,
                attendees: attendeeList,
            };

            await ApiClient.post("/api/sparkmeet/calendar/create", body);

            setCalendarSuccess("✅ Calendar invite created and sent to attendees.");
            setTimeout(() => setIsCalendarModalOpen(false), 1800);
        } catch (err) {
            setCalendarError(err.message);
        } finally {
            setIsCreatingCalendar(false);
        }
    };

    const fetchTeamsChats = async () => {
        setIsLoadingTeamsChats(true);
        setTeamsPostError("");
        try {
            const json = await ApiClient.get("/api/sparkmeet/teams/chats");
            setTeamsChats(json.chats || []);
        } catch (err) {
            setTeamsPostError(`Could not load chats: ${err.message}`);
        } finally {
            setIsLoadingTeamsChats(false);
        }
    };

    const fetchTeamsTeams = async () => {
        setIsLoadingTeamsTeams(true);
        setTeamsPostError("");
        try {
            const json = await ApiClient.get("/api/sparkmeet/teams/myteams");
            setTeamsJoinedTeams(json.teams || []);
        } catch (err) {
            setTeamsPostError(`Could not load teams: ${err.message}`);
        } finally {
            setIsLoadingTeamsTeams(false);
        }
    };

    const fetchTeamsChannels = async (teamId) => {
        setIsLoadingTeamsChannels(true);
        setTeamsSelectedChannel(null);
        setTeamsPostError("");
        try {
            const json = await ApiClient.get(
                `/api/sparkmeet/teams/channels?teamId=${encodeURIComponent(teamId)}`
            );
            setTeamsChannels(json.channels || []);
        } catch (err) {
            setTeamsPostError(`Could not load channels: ${err.message}`);
        } finally {
            setIsLoadingTeamsChannels(false);
        }
    };

    const submitTeamsPost = async () => {
        if (teamsDestType === "chat" && !teamsSelectedChat) {
            setTeamsPostError("Please select a chat to post to.");
            return;
        }
        if (teamsDestType === "channel" && (!teamsSelectedTeam || !teamsSelectedChannel)) {
            setTeamsPostError("Please select a team and channel.");
            return;
        }
        if (!teamsMessageContent.trim()) {
            setTeamsPostError("Message content cannot be empty.");
            return;
        }

        setIsPostingToTeams(true);
        setTeamsPostError("");
        setTeamsPostSuccess("");
        setTeamsPostedUrl("");

        const startTime = Date.now();

        try {
            const body = {
                destinationType: teamsDestType,
                chatId: teamsDestType === "chat" ? teamsSelectedChat?.id : null,
                teamId: teamsDestType === "channel" ? teamsSelectedTeam?.id : null,
                channelId: teamsDestType === "channel" ? teamsSelectedChannel?.id : null,
                subject: teamsSubject.trim() || null,
                content: teamsMessageContent,
            };

            const json = await ApiClient.post("/api/sparkmeet/teams/post", body);

            const duration = Date.now() - startTime;

            const destName = teamsDestType === "chat"
                ? (teamsSelectedChat?.displayName || "Chat")
                : `${teamsSelectedTeam?.displayName} › ${teamsSelectedChannel?.displayName}`;

            // Track successful post
            UsageLogService.trackEvent('TeamsMessagePosted', {
                destinationType: teamsDestType,
                destinationName: destName,
                chatId: teamsDestType === "chat" ? teamsSelectedChat?.id : null,
                teamName: teamsDestType === "channel" ? teamsSelectedTeam?.displayName : null,
                channelName: teamsDestType === "channel" ? teamsSelectedChannel?.displayName : null,
                hasSubject: !!teamsSubject.trim(),
                messageLength: teamsMessageContent.length,
                durationMs: duration,
                hasWebUrl: !!json.webUrl
            }, 'Integration');

            setTeamsPostSuccess(`✅ Posted to ${destName}`);
            setTeamsPostedUrl(json.webUrl || "");

            setMessages((prev) => [
                ...prev,
                {
                    id: makeId(),
                    role: "Spark Assist",
                    text: `✅ Posted to Teams: **${destName}**${json.webUrl ? `\n🔗 [View message](${json.webUrl})` : ""}`,
                },
            ]);
        } catch (err) {
            setTeamsPostError(`Post failed: ${err.message}`);

            // Track post error
            UsageLogService.trackError(err, {
                feature: 'TeamsPost',
                destinationType: teamsDestType,
                messageLength: teamsMessageContent.length
            });
        } finally {
            setIsPostingToTeams(false);
        }
    };




    const addToAdo = async (text) => {

        // Track ADO modal opened
        UsageLogService.trackEvent('AdoModalOpened', {
            messageLength: text?.length || 0,
            hasText: !!text
        }, 'Integration');

        setAdoMessageText(text);
        setAdoDescription(text);
        setAdoTitle("");
        setAdoError("");
        setAdoSuccess("");
        setAdoSelectedProject("Spark Assist");
        setAdoWorkItemType("User Story");
        setAdoSelectedIteration("");
        setAdoIterations([]);
        setIsAdoModalOpen(true);
        await fetchAdoProjects();
    };

    const fetchAdoProjects = async () => {
        setIsLoadingAdoProjects(true);
        setAdoError("");
        try {
            const json = await ApiClient.get("/api/sparkmeet/ado/projects");
            setAdoProjects(json.projects || []);
            if (json.projects?.length > 0) {
                fetchAdoIterations("Spark Assist");
            }
        } catch (err) {
            setAdoError(`Could not load projects: ${err.message}`);
        } finally {
            setIsLoadingAdoProjects(false);
        }
    };

    const fetchAdoIterations = async (projectName) => {
        setIsLoadingAdoIterations(true);
        setAdoSelectedIteration("");
        setAdoIterations([]);
        try {
            const json = await ApiClient.get(
                `/api/sparkmeet/ado/iterations?project=${encodeURIComponent(projectName)}`
            );
            setAdoIterations(json.iterations || []);
        } catch (err) {
            setAdoError(`Could not load sprints: ${err.message}`);
        } finally {
            setIsLoadingAdoIterations(false);
        }
    };

    const submitAdoWorkItem = async () => {
        if (!adoTitle.trim()) {
            setAdoError("Please enter a title.");
            return;
        }
        setIsCreatingAdoItem(true);
        setAdoError("");
        setAdoSuccess("");

        const startTime = Date.now();

        try {
            const json = await ApiClient.post("/api/sparkmeet/ado/workitem", {
                project: adoSelectedProject,
                workItemType: adoWorkItemType,
                title: adoTitle,
                description: adoDescription,
                iterationPath: adoSelectedIteration || undefined,
            });

            const duration = Date.now() - startTime;
            const itemUrl = json.url || "";
            const itemId = json.id || "";

            // Track successful work item creation
            UsageLogService.trackEvent('AdoWorkItemCreated', {
                workItemId: itemId,
                workItemType: adoWorkItemType,
                project: adoSelectedProject,
                iteration: adoSelectedIteration || 'Backlog',
                titleLength: adoTitle.length,
                descriptionLength: adoDescription.length,
                durationMs: duration,
                hasUrl: !!itemUrl
            }, 'Integration');

            setAdoSuccess(`✅ ${adoWorkItemType} #${itemId} created successfully!`);
            setMessages((prev) => [
                ...prev,
                {
                    id: makeId(),
                    role: "Spark Assist",
                    text: `✅ ADO ${adoWorkItemType} created: **#${itemId}** — "${adoTitle}"${itemUrl ? `\n${itemUrl}` : ""}`,
                },
            ]);
            setTimeout(() => setIsAdoModalOpen(false), 1800);
        } catch (err) {
            setAdoError(`Failed to create work item: ${err.message}`);

            // Track work item creation error
            UsageLogService.trackError(err, {
                feature: 'AdoWorkItemCreation',
                project: adoSelectedProject,
                workItemType: adoWorkItemType,
                titleLength: adoTitle.length
            });
        } finally {
            setIsCreatingAdoItem(false);
        }
    };


    //OneDrive

    const uploadToOneDrive = async (text) => {
        // Generate a smart default filename from timestamp + spark name
        const ts = new Date().toISOString().slice(0, 10);
        const sparkSlug = (selectedSpark?.sparkName ?? "spark")
            .replace(/[^a-zA-Z0-9]/g, "-")
            .toLowerCase()
            .slice(0, 30);
        const defaultName = `spark-response-${sparkSlug}-${ts}.txt`;

        // Track OneDrive modal opened
        UsageLogService.trackEvent('OneDriveModalOpened', {
            messageLength: text?.length || 0,
            hasText: !!text,
            defaultFileName: defaultName
        }, 'Integration');

        setOneDriveMessageText(text);
        setOneDriveFileContent(text);
        setOneDriveFileName(defaultName);
        setOneDriveError("");
        setOneDriveSuccess("");
        setOneDriveUploadedUrl("");
        setOneDriveSelectedFolder("root");
        setOneDriveSelectedFolderName("My Files (Root)");
        setOneDriveBreadcrumb([{ id: "root", name: "My Files" }]);
        setIsOneDriveModalOpen(true);
        await fetchOneDriveFolders("root");
    };

    const fetchOneDriveFolders = async (folderId) => {
        setIsLoadingOneDriveFolders(true);
        setOneDriveError("");
        try {
            const json = await ApiClient.get(
                `/api/sparkmeet/onedrive/folders?folderId=${encodeURIComponent(folderId)}`
            );
            setOneDriveFolders(json.folders || []);
        } catch (err) {
            setOneDriveError(`Could not load folders: ${err.message}`);
        } finally {
            setIsLoadingOneDriveFolders(false);
        }
    };

    const navigateOneDriveFolder = async (folder) => {
        setOneDriveSelectedFolder(folder.id);
        setOneDriveSelectedFolderName(folder.name);
        setOneDriveBreadcrumb((prev) => {
            // Avoid duplicates
            if (prev.find((b) => b.id === folder.id)) return prev;
            return [...prev, { id: folder.id, name: folder.name }];
        });
        await fetchOneDriveFolders(folder.id);
    };

    const navigateOneDriveBreadcrumb = async (crumb, index) => {
        setOneDriveSelectedFolder(crumb.id);
        setOneDriveSelectedFolderName(crumb.name);
        setOneDriveBreadcrumb((prev) => prev.slice(0, index + 1));
        await fetchOneDriveFolders(crumb.id);
    };

    const submitOneDriveUpload = async () => {
        if (!oneDriveFileName.trim()) {
            setOneDriveError("Please enter a file name.");
            return;
        }
        // Ensure .txt extension
        const finalName = oneDriveFileName.trim().endsWith(".txt")
            ? oneDriveFileName.trim()
            : oneDriveFileName.trim() + ".txt";

        setIsUploadingToOneDrive(true);
        setOneDriveError("");
        setOneDriveSuccess("");
        setOneDriveUploadedUrl("");

        const startTime = Date.now();

        try {
            const json = await ApiClient.post("/api/sparkmeet/onedrive/upload", {
                folderId: oneDriveSelectedFolder,
                fileName: finalName,
                content: oneDriveFileContent,
            });

            const duration = Date.now() - startTime;

            // Track successful upload
            UsageLogService.trackEvent('OneDriveFileUploaded', {
                fileName: finalName,
                folderName: oneDriveSelectedFolderName,
                folderId: oneDriveSelectedFolder,
                fileSizeBytes: new Blob([oneDriveFileContent]).size,
                fileSizeKB: Math.round(new Blob([oneDriveFileContent]).size / 1024),
                contentLength: oneDriveFileContent.length,
                durationMs: duration,
                hasWebUrl: !!json.webUrl
            }, 'Integration');

            setOneDriveSuccess(`✅ Uploaded "${finalName}" to ${oneDriveSelectedFolderName}`);
            setOneDriveUploadedUrl(json.webUrl || "");
            setMessages((prev) => [
                ...prev,
                {
                    id: makeId(),
                    role: "Spark Assist",
                    text: `✅ Uploaded to OneDrive: **${finalName}**\n📁 Folder: ${oneDriveSelectedFolderName}${json.webUrl ? `\n🔗 [Open file](${json.webUrl})` : ""}`,
                },
            ]);
        } catch (err) {
            setOneDriveError(`Upload failed: ${err.message}`);

            // Track upload error
            UsageLogService.trackError(err, {
                feature: 'OneDriveUpload',
                fileName: finalName,
                folder: oneDriveSelectedFolderName,
                contentLength: oneDriveFileContent.length
            });
        } finally {
            setIsUploadingToOneDrive(false);
        }
    };






    // ADO Search

    const openAdoBoardSearch = async () => {

        // Track ADO search modal opened
        UsageLogService.trackFeatureUsage(
            'AdoBoardSearch',
            'opened',
            {}
        );

        setAdoBoardStep("configure");
        setAdoBoardSelectedProject(null);
        setAdoBoardSelectedTeam(null);
        setAdoSelectedBoard(null);
        setAdoSelectedAreaPath(null);
        setAdoSelectedSwimlane(null);
        setAdoBoardSearchText("");
        setAdoBoardWorkItemType("Any");
        setAdoBoardState("Any");
        setAdoBoardSearchResults([]);
        setAdoBoardTotalResults(0);
        setAdoBoardError("");
        setIsAdoBoardModalOpen(true);
        await fetchAdoBoardProjects();
    };

    const fetchAdoBoardProjects = async () => {
        setIsLoadingAdoBoardProjects(true);
        setAdoBoardError("");
        try {
            const json = await ApiClient.get("/api/sparkmeet/ado/projects");
            setAdoBoardProjects(json.projects || []);
            const spark = (json.projects || []).find(p => p.name === "Spark Assist");
            if (spark) {
                setAdoBoardSelectedProject(spark);
                await Promise.all([
                    fetchAdoBoardTeams(spark.name),
                    fetchAdoAreaPaths(spark.name),
                ]);
            }
        } catch (err) {
            setAdoBoardError(`Could not load projects: ${err.message}`);
        } finally {
            setIsLoadingAdoBoardProjects(false);
        }
    };

    const fetchAdoBoardTeams = async (projectName) => {
        setIsLoadingAdoBoardTeams(true);
        setAdoBoardSelectedTeam(null);
        setAdoBoards([]);
        setAdoSelectedBoard(null);
        setAdoSwimlanes([]);
        setAdoSelectedSwimlane(null);
        try {
            const json = await ApiClient.get(
                `/api/sparkmeet/ado/teams?project=${encodeURIComponent(projectName)}`
            );
            setAdoBoardTeams(json.teams || []);
        } catch (err) {
            setAdoBoardError(`Could not load teams: ${err.message}`);
        } finally {
            setIsLoadingAdoBoardTeams(false);
        }
    };

    const fetchAdoBoards = async (projectName, teamName) => {
        setIsLoadingAdoBoards(true);
        setAdoSelectedBoard(null);
        setAdoSwimlanes([]);
        setAdoSelectedSwimlane(null);
        try {
            const json = await ApiClient.get(
                `/api/sparkmeet/ado/boards?project=${encodeURIComponent(projectName)}&team=${encodeURIComponent(teamName)}`
            );
            setAdoBoards(json.boards || []);
        } catch (err) {
            setAdoBoardError(`Could not load boards: ${err.message}`);
        } finally {
            setIsLoadingAdoBoards(false);
        }
    };

    const fetchAdoSwimlanes = async (projectName, teamName, boardId) => {
        setIsLoadingAdoSwimlanes(true);
        setAdoSelectedSwimlane(null);
        try {
            const json = await ApiClient.get(
                `/api/sparkmeet/ado/swimlanes?project=${encodeURIComponent(projectName)}&team=${encodeURIComponent(teamName)}&board=${encodeURIComponent(boardId)}`
            );
            setAdoSwimlanes(json.swimlanes || []);
        } catch (err) {
            setAdoBoardError(`Could not load swimlanes: ${err.message}`);
        } finally {
            setIsLoadingAdoSwimlanes(false);
        }
    };

    const fetchAdoAreaPaths = async (projectName) => {
        setIsLoadingAdoAreaPaths(true);
        try {
            const json = await ApiClient.get(
                `/api/sparkmeet/ado/areapaths?project=${encodeURIComponent(projectName)}`
            );
            setAdoAreaPaths(json.areaPaths || []);
        } catch (err) {
            setAdoBoardError(`Could not load area paths: ${err.message}`);
        } finally {
            setIsLoadingAdoAreaPaths(false);
        }
    };

    const runAdoBoardSearch = async () => {
        if (!adoBoardSelectedProject) {
            setAdoBoardError("Please select a project first.");
            return;
        }
        setIsSearchingAdoBoard(true);
        setAdoBoardError("");
        setAdoBoardSearchResults([]);

        const startTime = Date.now();

        try {
            const body = {
                project: adoBoardSelectedProject.name,
                team: adoBoardSelectedTeam?.name || null,
                boardId: adoSelectedBoard?.id || null,
                areaPath: adoSelectedAreaPath?.path || null,
                workItemType: adoBoardWorkItemType,
                state: adoBoardState,
                searchText: adoBoardSearchText.trim() || null,
            };

            const json = await ApiClient.post("/api/sparkmeet/ado/search", body);

            const duration = Date.now() - startTime;
            setAdoBoardSearchResults(json.workItems || []);
            setAdoBoardTotalResults(json.total || 0);
            setAdoBoardStep("results");

            // Track successful search
            UsageLogService.trackSearch(
                adoBoardSearchText || '(all)',
                json.workItems?.length || 0,
                {
                    source: 'AdoBoard',
                    project: adoBoardSelectedProject.name,
                    team: adoBoardSelectedTeam?.name,
                    board: adoSelectedBoard?.name,
                    workItemType: adoBoardWorkItemType,
                    state: adoBoardState,
                    durationMs: duration,
                    totalResults: json.total || 0
                }
            );
        } catch (err) {
            setAdoBoardError(`Search failed: ${err.message}`);

            // Track search error
            UsageLogService.trackError(err, {
                feature: 'AdoBoardSearch',
                project: adoBoardSelectedProject?.name,
                searchText: adoBoardSearchText
            });
        } finally {
            setIsSearchingAdoBoard(false);
        }
    };

    const toggleAdoWorkItemSelection = (wi) => {
        setAdoSelectedWorkItems((prev) => {
            const exists = prev.find((x) => x.id === wi.id);
            if (exists) return prev.filter((x) => x.id !== wi.id);
            return [...prev, wi];
        });
    };

    const applyAdoWorkItemsToContext = () => {

        // Track work items applied to context
        UsageLogService.trackEvent('AdoWorkItemsAppliedToContext', {
            workItemCount: adoSelectedWorkItems.length,
            workItemIds: adoSelectedWorkItems.map(wi => wi.id).join(','),
            workItemTypes: [...new Set(adoSelectedWorkItems.map(wi => wi.workItemType))].join(','),
            project: adoBoardSelectedProject?.name
        }, 'Integration');

        setAdoWorkItemContext(adoSelectedWorkItems);
        setIsAdoBoardModalOpen(false);
    };

    const removeAdoWorkItemFromContext = (id) => {
        setAdoWorkItemContext((prev) => prev.filter((x) => x.id !== id));
        setAdoSelectedWorkItems((prev) => prev.filter((x) => x.id !== id));
    };

    const clearAdoWorkItemContext = () => {
        setAdoWorkItemContext([]);
        setAdoSelectedWorkItems([]);
    };




    




    //Transcripts

    const loadTranscript = async () => {
        if (!chatId) {
            const errorMsg = "❌ ChatId not available. Make sure this is opened inside a Teams meeting side panel.";
            console.error(errorMsg);
            alert(errorMsg);
            setMessages((prev) => [
                ...prev,
                { id: makeId(), role: "Spark Assist", text: errorMsg },
            ]);
            return;
        }

        setIsLoadingTranscript(true);
        setTranscript("");

        const startTime = Date.now();

        try {
            const apiUrl = `/api/sparkmeet/transcript?chatId=${encodeURIComponent(chatId)}`;
            const json = await ApiClient.get(apiUrl);

            const duration = Date.now() - startTime;
            const t = json?.transcript ?? "";

            if (!t) {
                const msg = "⚠️ No transcript found yet for this meeting (or not authorized).";
                setTranscript("");

                // Track empty transcript
                UsageLogService.trackEvent('TranscriptLoadEmpty', {
                    chatId: chatId,
                    meetingId: meetingId,
                    durationMs: duration
                }, 'Meeting');

                setMessages((prev) => [
                    ...prev,
                    { id: makeId(), role: "Spark Assist", text: msg },
                ]);
                return;
            }

            setTranscript(t);

            // Track successful transcript load
            UsageLogService.trackEvent('TranscriptLoaded', {
                chatId: chatId,
                meetingId: meetingId,
                transcriptLength: t.length,
                transcriptSizeKB: Math.round(new Blob([t]).size / 1024),
                durationMs: duration
            }, 'Meeting');

            setMessages((prev) => [
                ...prev,
                { id: makeId(), role: "Spark Assist", text: `✅ Transcript loaded successfully! (${t.length} characters)` },
            ]);
        } catch (err) {
            console.error("❌ Transcript load failed:", err);

            // Track transcript load error
            UsageLogService.trackError(err, {
                feature: 'TranscriptLoad',
                chatId: chatId,
                meetingId: meetingId
            });

            setMessages((prev) => [
                ...prev,
                {
                    id: makeId(),
                    role: "Spark Assist",
                    text: `❌ Transcript load failed: ${err.message}`
                }
            ]);
        } finally {
            setIsLoadingTranscript(false);
        }
    };

    const generateInsights = async () => {
        if (!transcript) {
            alert("No transcript loaded. Click 'Load transcript' first.");
            return;
        }

        setIsGeneratingInsights(true);
        setInsights("");

        const startTime = Date.now();

        try {
            const json = await ApiClient.post("/api/sparkmeet/insights", {
                transcript,
                user: userName || "1",
            });

            const duration = Date.now() - startTime;
            const insightsContent = json?.content ?? "";
            setInsights(json?.content ?? "");

            // Track successful insights generation
            UsageLogService.trackEvent('InsightsGenerated', {
                transcriptLength: transcript.length,
                insightsLength: insightsContent.length,
                durationMs: duration,
                durationSeconds: Math.round(duration / 1000),
                chatId: chatId,
                meetingId: meetingId
            }, 'Meeting');

        } catch (err) {
            console.error(err);
            setInsights("Insights generation failed. Check server logs.");

            // Track insights error
            UsageLogService.trackError(err, {
                feature: 'InsightsGeneration',
                transcriptLength: transcript.length
            });
        } finally {
            setIsGeneratingInsights(false);
        }
    };

    const clearTranscript = () => {
        const ok = window.confirm("Clear transcript context? Future responses won't use the transcript unless you load it again.");
        if (!ok) return;

        // Track transcript cleared
        UsageLogService.trackEvent('TranscriptCleared', {
            transcriptLength: transcript.length,
            hadInsights: !!insights,
            insightsLength: insights.length
        }, 'Meeting');

        setTranscript("");
        setInsights("");
        setMessages((prev) => [
            ...prev,
            { id: makeId(), role: "Spark Assist", text: "🧹 Transcript cleared." },
        ]);
    };



    // ══════════════════════════════════════════════════════════════
    // FLOW A — SINGLE UTTERANCE SPEECH HANDLERS
    // These power the mic button next to the send button.
    // User taps mic → speaks → taps again → text drops in draft box.
    // ══════════════════════════════════════════════════════════════

    const startSpeechCapture = async () => {
        setSpeechError("");
        audioChunksRef.current = [];

        // Track STT start
        UsageLogService.trackEvent('SttUtteranceStarted', {
            provider: sttProvider,
            sparkId: selectedSpark?.sparkId,
            sparkName: selectedSpark?.sparkName
        }, 'Voice');

        // ── STEP 2: Try getUserMedia with progressively looser constraints ──
        // Teams Desktop WebView2 sometimes rejects { audio: true } with
        // specific device constraints. Try most permissive first.
        const audioConstraintOptions = [
            // Most permissive — just request any audio
            { audio: true },
            // With echo cancellation off (some WebView2 versions need this)
            { audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } },
            // Fallback — still keep echo cancellation on
            {
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true
                }
            },
            // Last resort — browser defaults (echo cancellation is on by default)
            { audio: true },

            // Explicit default device
            { audio: { deviceId: "default" } },
        ];

        let stream = null;
        let lastError = null;

        for (const constraints of audioConstraintOptions) {
            try {
                console.log("🎙️ Trying getUserMedia with:", JSON.stringify(constraints));
                stream = await navigator.mediaDevices.getUserMedia(constraints);
                console.log("✅ getUserMedia succeeded");
                break; // got a stream — stop trying
            } catch (err) {
                console.warn(`⚠️ getUserMedia failed with constraints ${JSON.stringify(constraints)}:`, err.name, err.message);
                lastError = err;
            }
        }

        // ── STEP 3: Handle failure ───────────────────────────────────
        if (!stream) {
            console.error("❌ All getUserMedia attempts failed. Last error:", lastError);

            // Give the user the most actionable error message possible
            const errName = lastError?.name || "";
            if (errName === "NotAllowedError" || errName === "PermissionDeniedError") {
                setSpeechError(
                    "Microphone access denied. " +
                    "Go to Teams Settings → App Permissions and allow microphone access, " +
                    "then restart Teams."
                );
            } else if (errName === "NotFoundError" || errName === "DevicesNotFoundError") {
                setSpeechError("No microphone found. Please connect a microphone and try again.");
            } else if (errName === "NotReadableError" || errName === "TrackStartError") {
                setSpeechError("Microphone is in use by another application. Close it and try again.");
            } else if (errName === "OverconstrainedError") {
                setSpeechError("Microphone constraints not supported. Try a different browser or device.");
            } else {
                setSpeechError(`Microphone error (${errName}): ${lastError?.message}. Check browser console for details.`);
            }
            return;
        }

        // ── STEP 4: Start recording ──────────────────────────────────
        streamRef.current = stream;

        const mimeType =
            MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" :
                MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" :
                    MediaRecorder.isTypeSupported("audio/ogg;codecs=opus") ? "audio/ogg;codecs=opus" :
                        "audio/ogg";

        console.log("🎙️ Using mimeType:", mimeType);

        let recorder;
        try {
            recorder = new MediaRecorder(stream, { mimeType });
        } catch (recErr) {
            // Some WebView2 versions don't support the codec spec in mimeType
            // Fall back to no mimeType (browser picks default)
            console.warn("⚠️ MediaRecorder with mimeType failed, using default:", recErr.message);
            recorder = new MediaRecorder(stream);
        }

        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        recorder.onstop = async () => {
            streamRef.current?.getTracks().forEach((t) => t.stop());
            streamRef.current = null;

            const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType });
            console.log(`🎙️ Recording stopped. Blob: ${blob.size} bytes, type: ${blob.type}`);

            if (blob.size < 1000) {
                setSpeechError("No audio captured — please try speaking louder.");
                setIsListening(false);
                return;
            }
            await transcribeUtterance(blob, recorder.mimeType);
        };

        recorder.onerror = (e) => {
            console.error("❌ MediaRecorder error:", e.error);
            setSpeechError(`Recording error: ${e.error?.message || "unknown"}`);
            setIsListening(false);
        };

        recorder.start(250);
        setIsListening(true);
        console.log("✅ Recording started successfully");
    };


    const stopSpeechCapture = () => {
        if (mediaRecorderRef.current?.state !== "inactive") {
            mediaRecorderRef.current.stop();  // → triggers onstop → transcribeUtterance
        }
        setIsListening(false);
    };

    // Sends short audio clip to /api/sparkmeet/transcribe
    // On success: transcribed text drops into the draft input box
    const transcribeUtterance = async (audioBlob, mimeType) => {
        setIsTranscribing(true);
        setSpeechError("");

        const startTime = Date.now();

        try {
            const formData = new FormData();
            formData.append("audio", audioBlob, "utterance.webm");
            formData.append("mimeType", mimeType);
            formData.append("userId", userName || "");
            formData.append("chatId", chatId || "");
            formData.append("meetingId", meetingId || "");
            formData.append("sparkId", String(selectedSpark?.sparkId ?? 1));
            formData.append("sparkName", selectedSpark?.sparkName ?? "");
            formData.append("timestamp", new Date().toISOString());

            // IMPORTANT: Do NOT set Content-Type manually with FormData —
            // browser sets it automatically with the correct multipart boundary
            const transcribeUrl = sttProvider === "kensho"
                ? "/api/sparkmeet/transcribe/kensho"
                : "/api/sparkmeet/transcribe";

            const resp = await fetch(transcribeUrl, {                
                method: "POST",
                headers: { "Authorization": `Bearer ${await AuthTokenProvider.getAccessToken() }` },
                body: formData,
            });

            const duration = Date.now() - startTime;

            if (!resp.ok) throw new Error(`STT API error (${resp.status}): ${await resp.text()}`);

            const json = await resp.json();
            const text = (json.text || "").trim();

            if (text) {
                setDraft(text);  // drops into input box — user reviews then hits send

                // Track successful transcription
                UsageLogService.trackEvent('SttUtteranceCompleted', {
                    provider: sttProvider,
                    audioSizeBytes: audioBlob.size,
                    audioSizeKB: Math.round(audioBlob.size / 1024),
                    durationMs: duration,
                    transcribedLength: text.length,
                    success: true
                }, 'Voice');

            } else {
                setSpeechError("Nothing transcribed. Please speak clearly and try again.");

                // Track empty transcription
                UsageLogService.trackEvent('SttUtteranceEmpty', {
                    provider: sttProvider,
                    audioSizeBytes: audioBlob.size,
                    durationMs: duration
                }, 'Voice');
            }
        } catch (err) {
            setSpeechError(`Transcription failed: ${err.message}`);

            // Track transcription error
            UsageLogService.trackError(err, {
                feature: 'SttUtterance',
                provider: sttProvider,
                audioSizeBytes: audioBlob.size
            });
        } finally {
            setIsTranscribing(false);
        }
    };



    const attachStreamEndedHandler = (stream) => {
        if (!stream) return;  // ← ADD
        const track = stream.getAudioTracks()[0];
        if (!track) return;   // ← ADD
        track.addEventListener('ended', async () => {
            if (meetingUserStoppedRef.current) return;
            if (!isMeetingRecordingRef.current) return;
            console.warn("⚠️ Meeting audio track ended unexpectedly — attempting auto-restart...");
            isMeetingRecordingRef.current = false;
            await autoRestartMeetingSTT();
        });
    };



    const MAX_RESTART_ATTEMPTS = 3;

    const autoRestartMeetingSTT = async () => {
        // User stopped intentionally — never auto-restart
        if (meetingUserStoppedRef.current) return;

        // All retries exhausted — notify user
        if (meetingRestartCountRef.current >= MAX_RESTART_ATTEMPTS) {
            console.error("❌ Auto-restart failed after 3 attempts");

            setMeetingSttError(
                "⚠️ Audio capture lost and could not auto-recover. Tap 'Start STT' to resume manually."
            );
            setIsMeetingRecording(false);
            isMeetingRecordingRef.current = false;

            setMessages(prev => [...prev, {
                id: makeId(),
                role: "Spark Assist",
                text: "⚠️ Meeting transcription lost audio capture and failed to auto-recover after 3 attempts.\n" +
                    "Please tap **Start STT** to resume manually. Audio captured so far is preserved."
            }]);

            try {
                await fetch("/api/sparkmeet/teams/post-to-meeting-chat", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${await AuthTokenProvider.getAccessToken() }`
                    },
                    body: JSON.stringify({
                        chatId: chatId,
                        message: "⚠️ Spark Anywhere transcription lost audio and could not auto-recover. " +
                            "Organizer please tap Start STT to resume."
                    })
                });
            } catch (e) { /* silent — chat post is best-effort */ }

            return;
        }

        meetingRestartCountRef.current += 1;
        console.log(`🔄 Auto-restarting meeting STT (attempt ${meetingRestartCountRef.current}/${MAX_RESTART_ATTEMPTS})...`);

        // Wait 2 seconds — gives Teams time to settle after a screen share starts
        await new Promise(r => setTimeout(r, 2000));

        try {
            // Request new audio stream — video: false = no visible screen share
            // Teams Desktop WebView2 does not support getDisplayMedia
            // getUserMedia captures the organizer's mic — works reliably in WebView2
            const audioConstraintOptions = [
                { audio: true },
                { audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } },
                { audio: { deviceId: "default" } },
            ];

            let stream = null; 
            let lastError = null;
            for (const constraints of audioConstraintOptions) {
                try {
                    console.log("🎙️ Trying getUserMedia with:", JSON.stringify(constraints));
                    stream = await navigator.mediaDevices.getUserMedia(constraints);
                    console.log("✅ getUserMedia succeeded");
                    break;
                } catch (err) {
                    console.warn(`⚠️ getUserMedia failed:`, err.name, err.message);
                    lastError = err;
                }
            }

            meetingStreamRef.current = stream;
            isMeetingRecordingRef.current = true;
            setIsMeetingRecording(true);

            // Attach ended handler to the new stream too
            attachStreamEndedHandler(stream);

            // Pick best supported mime type
            const mimeType =
                MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" :
                    MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" :
                        "audio/ogg";

            let recorder;
            try {
                recorder = new MediaRecorder(stream, { mimeType });
            } catch {
                recorder = new MediaRecorder(stream); // fallback: no mime type
            }

            meetingRecorderRef.current = recorder;

            // IMPORTANT: push to EXISTING array — not a new one
            // This merges audio before + after restart into one final transcript
            recorder.ondataavailable = (e) => {
                if (e.data?.size > 0) meetingAudioChunksRef.current.push(e.data);
            };

            recorder.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                meetingStreamRef.current = null;

                // Only send to STT if organizer explicitly stopped (not a crash)
                if (meetingUserStoppedRef.current) {
                    const blob = new Blob(meetingAudioChunksRef.current, { type: recorder.mimeType });
                    if (blob.size >= 1000) {
                        await transcribeMeetingAudio(blob, recorder.mimeType);
                    }
                }
            };

            recorder.onerror = async () => {
                if (!meetingUserStoppedRef.current) await autoRestartMeetingSTT();
            };

            recorder.start(250);

            // Reset counter on successful restart
            meetingRestartCountRef.current = 0;
            console.log("✅ Meeting STT auto-restarted successfully");

        } catch (err) {
            console.error(`❌ Auto-restart attempt ${meetingRestartCountRef.current} failed:`, err.message);
            // Recurse — will increment counter and try again or give up
            await autoRestartMeetingSTT();
        }
    };




    // ══════════════════════════════════════════════════════════════
    // MEETING STT — FULL RECORDING WITH SPEAKER DIARIZATION
    // These power the Start STT / Stop STT buttons in the meeting header.
    // Records continuously from Start → Stop then sends full audio
    // to /api/sparkmeet/transcribe-meeting which returns speaker-labelled
    // utterances with timestamps.
    // ══════════════════════════════════════════════════════════════


    // The key differences from your current version:
    //  - getUserMedia → getDisplayMedia (captures all meeting audio, not just mic)
    //  - Sets meetingUserStoppedRef = false and meetingRestartCountRef = 0
    //  - Sets isMeetingRecordingRef.current = true
    //  - Calls attachStreamEndedHandler(stream)
    //  - Posts Teams chat notification before starting
    // ════════════════════════════════════════════════════════════════════════════════

    const startMeetingSTT = async () => {
        let stream = null;
        setMeetingSttError("");

        // ── DIAGNOSTIC: check mediaDevices availability ───────────────
        console.log("🔍 navigator.mediaDevices:", navigator.mediaDevices);
        console.log("🔍 getUserMedia available:", !!navigator.mediaDevices?.getUserMedia);
        console.log("🔍 isSecureContext:", window.isSecureContext);
        console.log("🔍 location:", window.location.href);

        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setMeetingSttError(
                "Your browser does not support microphone access in this context. " +
                "This is a known Teams Web limitation — please use the Teams Desktop app for recording."
            );
            return;
        }

        // Track meeting STT start
        UsageLogService.trackEvent('MeetingSttStarted', {
            provider: sttProvider,
            chatId: chatId,
            meetingId: meetingId
        }, 'Meeting');

        meetingAudioChunksRef.current = [];          // clear chunks for fresh recording
        meetingUserStoppedRef.current = false;       // mark as NOT user-stopped
        meetingRestartCountRef.current = 0;           // reset retry counter

        // ── Notify all participants in Teams meeting chat via Graph API ─────────────
        // Best-effort — if it fails, recording still starts
        try {
            await fetch("/api/sparkmeet/teams/post-to-meeting-chat", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${await AuthTokenProvider.getAccessToken() }`
                },
                body: JSON.stringify({
                    chatId: chatId,
                    message: "🎙️ Meeting transcription has started via Spark Anywhere. " +
                        "The organizer's audio is being captured. " +
                        "Other participants: please ensure your mic is unmuted."
                })
            });
            console.log("✅ Posted start notification to meeting chat");
        } catch (e) {
            console.warn("⚠️ Could not post transcription start notice to chat:", e.message);
        }

        // ── Request system audio via getDisplayMedia ──────────────────
        // video: false means NO screen share visible to other participants
        // The browser will show a source picker — organizer must select "System Audio"
        
        let lastError = null;  
        try {
            // Teams Desktop WebView2 does not support getDisplayMedia
            // getUserMedia captures the organizer's mic — works reliably in WebView2
            const audioConstraintOptions = [
                { audio: true },
                { audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } },
                { audio: { deviceId: "default" } },
            ];
           
            for (const constraints of audioConstraintOptions) {
                try {
                    console.log("🎙️ Trying getUserMedia with:", JSON.stringify(constraints));
                    stream = await navigator.mediaDevices.getUserMedia(constraints);
                    console.log("✅ getUserMedia succeeded");
                    break;
                } catch (err) {
                    console.warn(`⚠️ getUserMedia failed:`, err.name, err.message);
                    lastError = err;
                }
            }
        }
        catch (err) {
            // outer catch is unreachable now but kept for safety
        }


        // ── Surface actual error if all attempts failed ────────────
        if (!stream && lastError) {
            if (lastError.name === "NotAllowedError") {
                setMeetingSttError("Microphone permission denied. Allow mic access in your browser and try again.");
            } else if (lastError.name === "SecurityError") {
                setMeetingSttError(
                    "Microphone blocked by browser security in Teams Web iframe. " +
                    "Please use the Teams Desktop app for recording."
                );
            } else if (lastError.name === "NotFoundError") {
                setMeetingSttError("No microphone detected on this device.");
            } else {
                setMeetingSttError(`Microphone error: [${lastError.name}] ${lastError.message}`);
            }
            return;
        }


        // ── GUARD: stream must be valid before proceeding ─────────────
        if (!stream) {
            setMeetingSttError(
                "Microphone access was denied or unavailable. " +
                "Click the 🔒 lock icon in your browser address bar, " +
                "set Microphone to Allow, then try again."
            );
            return;
        }
      

        // ── Set up recording ──────────────────────────────────────────
        meetingStreamRef.current = stream;
        isMeetingRecordingRef.current = true;
        setIsMeetingRecording(true);
        setMeetingRecordingStartedAt(new Date().toISOString());

        // Attach interruption handler — auto-restarts if stream dies unexpectedly
        attachStreamEndedHandler(stream);

        const mimeType =
            MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" :
                MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" :
                    MediaRecorder.isTypeSupported("audio/ogg;codecs=opus") ? "audio/ogg;codecs=opus" :
                        "audio/ogg";

        console.log("🎙️ Meeting recorder using mimeType:", mimeType);

        let recorder;
        try {
            recorder = new MediaRecorder(stream, { mimeType });
        } catch (recErr) {
            console.warn("⚠️ MediaRecorder with mimeType failed, using default:", recErr.message);
            recorder = new MediaRecorder(stream);
        }

        meetingRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
            if (e.data?.size > 0) meetingAudioChunksRef.current.push(e.data);
        };

        recorder.onstop = async () => {
            stream.getTracks().forEach(t => t.stop());
            meetingStreamRef.current = null;

            // Only process audio if organizer explicitly stopped
            if (meetingUserStoppedRef.current) {
                const blob = new Blob(meetingAudioChunksRef.current, { type: recorder.mimeType });
                console.log(`🎙️ Meeting recording stopped. Blob: ${blob.size} bytes, type: ${blob.type}`);

                if (blob.size < 1000) {
                    setMeetingSttError("No audio captured — please try again.");
                    return;
                }

                // Clear sessionStorage backup since we're now processing
                sessionStorage.removeItem("stt_backup_audio");
                sessionStorage.removeItem("stt_backup_time");
                sessionStorage.removeItem("stt_backup_startedAt");

                await transcribeMeetingAudio(blob, recorder.mimeType);
            }
        };

        recorder.onerror = async (e) => {
            console.error("❌ Meeting MediaRecorder error:", e.error);
            if (!meetingUserStoppedRef.current) await autoRestartMeetingSTT();
        };

        recorder.start(250);
        console.log("✅ Meeting STT recording started");
    };




    const stopMeetingSTT = async () => {
        meetingUserStoppedRef.current = true;
        isMeetingRecordingRef.current = false;
        setIsMeetingRecording(false);

        // Track meeting STT stop
        const recordingDuration = meetingRecordingStartedAt
            ? Date.now() - new Date(meetingRecordingStartedAt).getTime()
            : 0;

        UsageLogService.trackEvent('MeetingSttStopped', {
            provider: sttProvider,
            recordingDurationMs: recordingDuration,
            recordingDurationMinutes: Math.round(recordingDuration / 60000),
            audioChunksCount: meetingAudioChunksRef.current.length
        }, 'Meeting');

        // ── GUARD: recorder must exist and be active ──────────────────
        if (!meetingRecorderRef.current) {                          // ← ADD
            console.warn("stopMeetingSTT: no recorder — ignoring");// ← ADD
            return;                                                 // ← ADD
        }                                                           // ← ADD

        if (meetingRecorderRef.current?.state !== "inactive") {
            meetingRecorderRef.current.stop();
        }
        meetingStreamRef.current?.getTracks().forEach(t => t.stop());
        try {
            await fetch("/api/sparkmeet/teams/post-to-meeting-chat", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${await AuthTokenProvider.getAccessToken() }`
                },
                body: JSON.stringify({
                    chatId: chatId,
                    message: "🛑 Meeting transcription has stopped."
                })
            });
            console.log("✅ Posted stop notification to meeting chat");
        } catch (e) {
            console.warn("⚠️ Could not post transcription stop notice to chat:", e.message);
        }
    };


    // Sends full meeting audio to /api/sparkmeet/transcribe-meeting
    // Returns { fullTranscript, utterances[], wordDetails[] }
    // utterances have speakerLabel ("Speaker 1"), text, start/end timestamps
    const transcribeMeetingAudio = async (audioBlob, mimeType) => {
        setIsMeetingTranscribing(true);
        setMeetingSttError("");

        const startTime = Date.now();

        // Add a message to chat so user knows processing is happening
        setMessages((prev) => [...prev, {
            id: makeId(),
            role: "Spark Assist",
            text: `⏳ Processing meeting audio with ${sttProvider === "kensho" ? "Kensho Scribe" : "Google STT"} (${(audioBlob.size / 1024).toFixed(0)} KB)… This may take a moment for longer recordings.`
        }]);

        try {
            const formData = new FormData();
            formData.append("audio", audioBlob, "meeting.webm");
            formData.append("mimeType", mimeType);
            formData.append("userId", userName || "");
            formData.append("chatId", chatId || "");
            formData.append("meetingId", meetingId || "");
            formData.append("estimatedSpeakers", "2");       // adjust if needed
            formData.append("startedAt", meetingRecordingStartedAt || "");
            formData.append("stoppedAt", new Date().toISOString());

            const meetingTranscribeUrl = sttProvider === "kensho"
                ? "/api/sparkmeet/transcribe-meeting/kensho"
                : "/api/sparkmeet/transcribe-meeting";

            const resp = await fetch(meetingTranscribeUrl, {
                method: "POST",
                headers: { "Authorization": `Bearer ${await AuthTokenProvider.getAccessToken() }` },
                body: formData,
            });

            const duration = Date.now() - startTime;

            if (!resp.ok) throw new Error(`Meeting STT error (${resp.status}): ${await resp.text()}`);

            const result = await resp.json();
            setMeetingSttResult(result);

            // Add success message with summary to chat
            const utteranceCount = result.utterances?.length || 0;
            const speakerCount = new Set((result.utterances || []).map(u => u.speakerTag)).size;

            // Track successful meeting transcription
            UsageLogService.trackEvent('MeetingSttCompleted', {
                provider: sttProvider,
                audioSizeBytes: audioBlob.size,
                audioSizeKB: Math.round(audioBlob.size / 1024),
                processingDurationMs: duration,
                processingDurationSeconds: Math.round(duration / 1000),
                utteranceCount: utteranceCount,
                speakerCount: speakerCount,
                transcriptLength: result.fullTranscript?.length || 0,
                success: true
            }, 'Meeting');

            setMessages((prev) => [...prev, {
                id: makeId(),
                role: "Spark Assist",
                text: `✅ Meeting STT complete!\n📊 ${utteranceCount} utterances detected across ${speakerCount} speaker(s).\nClick the 👁️ STT button to view the full transcript.`
            }]);

        } catch (err) {
            setMeetingSttError(`Meeting transcription failed: ${err.message}`);

            // Track meeting STT error
            UsageLogService.trackError(err, {
                feature: 'MeetingSTT',
                provider: sttProvider,
                audioSizeBytes: audioBlob.size,
                audioSizeKB: Math.round(audioBlob.size / 1024)
            });

            setMessages((prev) => [...prev, {
                id: makeId(),
                role: "Spark Assist",
                text: `❌ Meeting STT failed: ${err.message}`
            }]);
        } finally {
            setIsMeetingTranscribing(false);
        }
    };

    const startWorkplaceAgentFlow = async (text) => {
        const trail = [];
        const runtimeLogs = [];
        const pushLiveLog = (stage, message, data = null) => {
            const ts = new Date().toISOString();
            const suffix = data ? ` | ${typeof data === "string" ? data : JSON.stringify(data)}` : "";
            const line = `[${ts}] [${stage}] ${message}${suffix}`;
            runtimeLogs.push(line);
            setWorkplaceAgentLiveLogs((prev) => [...prev, line]);
        };

        const logDecisionCheckpoint = (checkpoint, payload, parsed) => {
            pushLiveLog("DECISION", checkpoint, {
                payload,
                parsed
            });
        };

        const mark = (emoji, title, detail = "", status = "in-progress") => {
            trail.push({ emoji, title, detail, status, at: new Date().toISOString() });
            setWorkplaceAgentSteps((prev) => [...prev, { emoji, title, detail, status }]);
            pushLiveLog(status === "completed" ? "STEP_OK" : "STEP", title, detail || "-");
        };

        setWorkplaceAgentSteps([]);
        setWorkplaceAgentError("");
        setWorkplaceAgentLiveLogs([]);
        waCheckpointsRef.current = [];       // reset per-workflow checkpoint accumulator
        pendingWriteActionRef.current = null; // reset any pending write action from prior run
        currentUserQueryRef.current = "";     // reset query ref; will be set after orchestrate

        try {
            pushLiveLog("BOOT", "Workplace flow started", {
                selectedAgents: selectedWorkplaceAgents,
                queryLength: text?.length || 0,
                browserOnline: typeof navigator !== "undefined" ? navigator.onLine : "n/a"
            });

            mark("🔎", "Preflight availability check", "Checking /api/workplace-agents/available");
            pushLiveLog("HTTP", "GET /api/workplace-agents/ping");
            try {
                const pingResp = await fetch("/api/workplace-agents/ping", { method: "GET" });
                const pingText = await pingResp.text();
                let pingJson;
                try { pingJson = JSON.parse(pingText); } catch { pingJson = null; }
                pushLiveLog(pingResp.ok ? "HTTP_OK" : "HTTP_ERR", "Ping response", {
                    status: pingResp.status,
                    body: pingJson || pingText || "(empty response body)"
                });
            } catch (pingErr) {
                pushLiveLog("HTTP_ERR", "Ping request failed", { detail: pingErr?.message || String(pingErr) });
            }

            pushLiveLog("HTTP", "GET /api/workplace-agents/available");

            let availableJson = [];
            let canContinueWithFallback = false;

            const availableResp = await fetch("/api/workplace-agents/available", { method: "GET" });
            if (!availableResp.ok)
            {
                const reason = await availableResp.text();
                let parsed;
                try { parsed = JSON.parse(reason); } catch { parsed = null; }
                const traceId = parsed?.traceId || parsed?.traceID || parsed?.traceid || "n/a";
                const stage = parsed?.stage || "available";
                const code = parsed?.errorCode || "WA_AVAILABLE_HTTP_ERROR";
                const title = parsed?.message || parsed?.title || "Availability request failed";
                const detail = parsed?.detail || reason || "(empty response body)";

                pushLiveLog("HTTP_ERR", "Availability request failed", {
                    status: availableResp.status,
                    stage,
                    code,
                    traceId,
                    detail,
                    responseHeaders: {
                        contentType: availableResp.headers.get("content-type") || "n/a",
                        requestId: availableResp.headers.get("request-id") || availableResp.headers.get("x-request-id") || "n/a",
                        apimRequestId: availableResp.headers.get("x-ms-request-id") || "n/a"
                    }
                });

                // In some published environments 500 responses are emitted with stripped/empty bodies.
                // Continue with selected agents so orchestration can still run and provide deeper diagnostics.
                canContinueWithFallback = true;
                mark("⚠️", "Availability check failed", `Continuing with selected agents fallback. ${code} HTTP ${availableResp.status}; traceId=${traceId}`, "completed");
            }
            else
            {
                availableJson = await availableResp.json();
                pushLiveLog("HTTP_OK", "Availability request succeeded", { count: Array.isArray(availableJson) ? availableJson.length : 0 });
                mark("✅", "Agents available", `Count: ${Array.isArray(availableJson) ? availableJson.length : 0}`, "completed");
            }

            if (canContinueWithFallback)
            {
                pushLiveLog("FALLBACK", "Proceeding without availability payload", {
                    selectedAgents: selectedWorkplaceAgents,
                    reason: "Availability endpoint returned non-2xx"
                });
            }

            mark("🧭", "Planning orchestration", `Selected agents: ${(selectedWorkplaceAgents && selectedWorkplaceAgents.length > 0 ? selectedWorkplaceAgents : ["teams", "outlook", "onedrive", "calendar"]).join(", ") || "none"}`);

            const fallbackSelectedAgents = ["teams", "outlook", "onedrive", "calendar"];
            const effectiveSelectedAgents = (selectedWorkplaceAgents && selectedWorkplaceAgents.length > 0)
                ? selectedWorkplaceAgents
                : fallbackSelectedAgents;

            const orchestrationRequest = {
                userQuery: text,
                selectedAgents: effectiveSelectedAgents,
                conversationContext: transcript ? transcript.slice(0, 2000) : null,
            };

            // Persist the user's original query so it can be forwarded to the summarize
            // endpoint after all workflow steps have completed.
            setCurrentUserQuery(text || "");
            currentUserQueryRef.current = text || "";

            pushLiveLog("HTTP", "POST /api/workplace-agents/orchestrate", {
                selectedAgents: orchestrationRequest.selectedAgents,
                hasConversationContext: !!orchestrationRequest.conversationContext
            });
            logDecisionCheckpoint("orchestrate.request", orchestrationRequest, {
                expectedDecisionPoints: ["intent", "steps", "flowDiagnostics"]
            });
            const orchestrationResponse = await ApiClient.post(
                "/api/workplace-agents/orchestrate",
                orchestrationRequest
            );

            if (!orchestrationResponse.ok) {
                const reason = await orchestrationResponse.text();
                let parsed;
                try { parsed = JSON.parse(reason); } catch { parsed = null; }
                const traceId = parsed?.traceId || parsed?.traceID || parsed?.traceid || "n/a";
                const stage = parsed?.stage || "orchestrate";
                const code = parsed?.errorCode || "WA_ORCH_HTTP_ERROR";
                const title = parsed?.message || parsed?.title || "Orchestration request failed";
                const detail = parsed?.detail || reason || "(empty response body)";
                pushLiveLog("HTTP_ERR", "Orchestrate request failed", {
                    status: orchestrationResponse.status,
                    stage,
                    code,
                    traceId,
                    detail,
                    responseHeaders: {
                        contentType: orchestrationResponse.headers.get("content-type") || "n/a",
                        requestId: orchestrationResponse.headers.get("request-id") || orchestrationResponse.headers.get("x-request-id") || "n/a",
                        apimRequestId: orchestrationResponse.headers.get("x-ms-request-id") || "n/a"
                    }
                });
                throw new Error(`[${stage}] ${code} HTTP ${orchestrationResponse.status} | ${title} | ${detail} | traceId=${traceId}`);
            }

            const orchestration = orchestrationResponse;
            const orchestrationData = orchestration?.orchestrationResponse || orchestration?.orchestrateData || orchestration;
            pushLiveLog("HTTP_OK", "Orchestrate request succeeded", {
                workflowId: orchestration?.workflowId || orchestrationData?.workflowId || "n/a",
                steps: (orchestration?.steps || orchestrationData?.steps || []).length
            });
            logDecisionCheckpoint("orchestrate.response.parsed", {
                workflowId: orchestration?.workflowId || orchestrationData?.workflowId || "n/a"
            }, {
                intent: orchestration?.intent || orchestrationData?.intent || "unknown",
                stepCount: (orchestration?.steps || orchestrationData?.steps || []).length,
                hasFormattedResponse: !!(orchestration?.userFacingResponse || orchestration?.formattedResponse),
                flowId: orchestrationData?.flowDiagnostics?.flowId || "n/a"
            });
            mark("✅", "Plan created", `WorkflowId: ${orchestration?.workflowId || "n/a"}; steps: ${(orchestration?.steps || []).length}`, "completed");

            mark("🧪", "Preparing human validation", "Building editable validation fields");

            const validationRequest = {
                workflowId: orchestration.workflowId || orchestrationData?.workflowId,
                userQuery: text,
                steps: orchestrationData?.steps || orchestration.steps || [],
            };

            pushLiveLog("HTTP", "POST /api/workplace-agents/validation", {
                workflowId: validationRequest.workflowId,
                stepCount: (validationRequest.steps || []).length
            });
            logDecisionCheckpoint("validation.request", validationRequest, {
                expectedDecisionPoints: ["editableFields", "requiresValidation"]
            });
            const validation = await ApiClient.post(
                "/api/workplace-agents/validation",
                validationRequest
            );
            pushLiveLog("HTTP_OK", "Validation request succeeded", {
                workflowId: validation?.workflowId || orchestration?.workflowId || orchestrationData?.workflowId || "n/a",
                pendingSteps: (validation?.steps || []).length
            });
            logDecisionCheckpoint("validation.response.parsed", {
                workflowId: validation?.workflowId || "n/a"
            }, {
                pendingSteps: (validation?.steps || []).length,
                requiresValidation: !!validation?.requiresValidation
            });
            mark("🧑‍⚖️", "Validation ready", `Steps ready to execute: ${(validation?.steps || []).length}`, "completed");

            setWorkflowValidation(validation);
            // Auto-execute all steps sequentially — no human validation popup required
            setIsAgentValidationOpen(false);

            // Kick off the auto-execution loop immediately.
            // Pass validation directly — React state update from setWorkflowValidation above
            // may not have flushed yet, so we carry the live object through the loop.
            const stepsToRun = validation?.steps || [];
            for (const step of stepsToRun) {
                // Merge the step's own parameters (set by the orchestrator, e.g. { query: "..." })
                // so the backend receives the user query and can perform a real Graph search.
                // Without this merge, validatedParameters is {}, rawQuery comes out empty, and
                // the Spark planner is skipped → 0 results → no final summary message.
                const stepParams = { ...(step.parameters || {}), ...(step.params || {}) };
                await executeValidatedWorkplaceStep(step, stepParams, true, /* silent= */ true, validation);
            }

            // ── All steps completed ─────────────────────────────────────────────────────
            // This runs immediately after the loop so it's always reached regardless of
            // how many steps the workflow has (the old "remaining.length === 0" check
            // inside executeValidatedWorkplaceStep was unreliable for multi-step workflows).
            setIsAgentValidationOpen(false);
            setWorkflowValidation(null);
            setWorkplaceAgentSteps((prev) => [...prev, { emoji: "🏁", title: "Workflow completed", detail: "All steps processed.", status: "completed" }]);

            const finalWorkflowId = validation?.workflowId;
            const finalUserQuery  = currentUserQueryRef.current || currentUserQuery || "";

            // Build trace from accumulated checkpoints and emit the final assistant message.
            const emitFinalMessage = (text, emailDraft) => {
                const allCheckpoints = waCheckpointsRef.current;
                const compiledCp = allCheckpoints.find(cp => cp.name === 'spark.plan.compiled');
                const teamsSender = (() => {
                    try { return compiledCp ? JSON.parse(compiledCp.payloadSnippet)?.teamsSender : null; } catch { return null; }
                })();
                const kqlSearch = (() => {
                    try {
                        const p = compiledCp ? JSON.parse(compiledCp.payloadSnippet) : null;
                        return p?.teamsSearch || p?.outlookSearch || null;
                    } catch { return null; }
                })();
                // kqlQuery: prefer the retry query (simpler entity keyword) if the planner returned 0 results,
                // otherwise use the compiled query — but always sanitize: strip KQL AND/OR/NOT and quoted phrases
                // so the trace shows clean keywords rather than the raw AI-generated boolean expression.
                const sanitizeKql = (s) => {
                    if (!s) return s;
                    return s
                        .replace(/\b(AND|OR|NOT)\b/gi, ' ')   // strip boolean operators
                        .replace(/"([^"]*)"/g, '$1')            // unquote phrases
                        .replace(/\s{2,}/g, ' ')               // collapse whitespace
                        .trim();
                };
                const retryCp = allCheckpoints.find(cp => cp.name === 'graph.outlook.retry' || cp.name === 'graph.teams.retry');
                const rawKqlQuery = (() => {
                    if (retryCp) {
                        try { return JSON.parse(retryCp.payloadSnippet)?.retryQuery || null; } catch { return null; }
                    }
                    return compiledCp
                        ? (() => { try { return JSON.parse(compiledCp.payloadSnippet)?.compiledQuery; } catch { return null; } })()
                        : null;
                })();
                const kqlQuery = sanitizeKql(rawKqlQuery);
                const hitCounts = {};
                allCheckpoints.filter(cp => cp.name?.startsWith("graph.") && cp.name?.endsWith(".response"))
                    .forEach(cp => {
                        try {
                            const p = JSON.parse(cp.payloadSnippet);
                            const src = cp.name.split(".")[1];
                            if (p?.resultCount > 0) hitCounts[src] = p.resultCount;
                        } catch { /* ignore */ }
                    });
                const agentTrace = {
                    steps: [...workplaceAgentSteps, { emoji: "🏁", title: "Workflow completed", detail: "All steps processed.", status: "completed" }],
                    kql: kqlQuery ? { kql: [teamsSender ? `from:${teamsSender}` : null, kqlSearch, kqlQuery].filter(Boolean).join(" · "), sources: Object.keys(hitCounts) } : workplaceKql,
                    hits: Object.keys(hitCounts).length > 0 ? { counts: hitCounts } : workplaceHits,
                    intent: finalUserQuery,
                    checkpoints: allCheckpoints,
                };
                const actionIntent = detectEmailIntent(finalUserQuery)
                    || detectTeamsPostIntent(finalUserQuery)
                    || detectCalendarIntent(finalUserQuery);
                const pendingWrite = pendingWriteActionRef.current;
                pendingWriteActionRef.current = null;

                // If the query has any email-draft intent, suppress the legacy banners
                // entirely — the AI draft card (message 2) IS the action surface.
                // We suppress even when emailDraft is null (e.g. draft gen failed) so
                // we never show the empty-body "Open Email" fallback.
                const hasEmailDraftIntent = !!detectEmailIntent(finalUserQuery);

                // ── Section splitter ─────────────────────────────────────────────────────
                // Split the AI summary into named sections (## Summary, ## Key Points, etc.)
                // and emit each as its own focused message so nothing is truncated and each
                // section gets full visual weight. The trace is attached to the first message.
                const parseSections = (rawText) => {
                    // Each entry: { header: "## Key Points", body: "..." }
                    const lines = (rawText || "").split("\n");
                    const sections = [];
                    let current = null;
                    for (const line of lines) {
                        if (/^##\s/.test(line.trim())) {
                            if (current) sections.push(current);
                            current = { header: line.trim(), body: "" };
                        } else if (current) {
                            current.body += (current.body ? "\n" : "") + line;
                        } else {
                            // Pre-section text (shouldn't normally happen but handle gracefully)
                            if (!sections.length) sections.push({ header: "", body: line });
                            else sections[sections.length - 1].body += "\n" + line;
                        }
                    }
                    if (current) sections.push(current);
                    return sections;
                };

                const sections = parseSections(text);

                const newMessages = [];
                sections.forEach((sec, idx) => {
                    const sectionText = (sec.header ? sec.header + "\n" : "") + sec.body.trim();
                    if (!sectionText.trim()) return;
                    newMessages.push({
                        id: makeId(),
                        role: "Spark Assist",
                        text: sectionText,
                        // Only attach trace + actions to the first message
                        ...(idx === 0 ? {
                            workplaceAgentTrace: agentTrace,
                            suggestedAction: hasEmailDraftIntent ? undefined : actionIntent,
                            pendingWriteAction: hasEmailDraftIntent ? undefined : (pendingWrite || undefined),
                        } : {}),
                    });
                });

                // If the model returned unstructured text (no ## headers), emit as a single message
                if (newMessages.length === 0) {
                    newMessages.push({
                        id: makeId(),
                        role: "Spark Assist",
                        text,
                        workplaceAgentTrace: agentTrace,
                        suggestedAction: hasEmailDraftIntent ? undefined : actionIntent,
                        pendingWriteAction: hasEmailDraftIntent ? undefined : (pendingWrite || undefined),
                    });
                }

                if (emailDraft) {
                    const recipientName = emailDraft.to || emailDraft.To || "the recipient";
                    newMessages.push({
                        id: makeId(),
                        role: "Spark Assist",
                        text: `Here's a draft email you can review and send to **${recipientName}**:`,
                        isEmailDraftMessage: true,
                        emailDraft,
                    });
                }

                setMessages((prev) => [...prev, ...newMessages]);
                setTimeout(() => setWorkplaceAgentSteps([]), 2000);
            };

            try {
                pushLiveLog("HTTP", "POST /api/workplace-agents/summarize", { workflowId: finalWorkflowId, userQuery: finalUserQuery });

                let summaryText = null;
                let emailDraft = null;
                try {
                    const summaryData = await ApiClient.post("/api/workplace-agents/summarize", {
                        workflowId: finalWorkflowId,
                        userQuery: finalUserQuery
                    });
                    pushLiveLog("HTTP_OK", "Summarize response", { success: summaryData?.success, hasContent: !!summaryData?.summary, hasDraft: !!summaryData?.emailDraft });
                    if (summaryData?.success && summaryData?.summary) {
                        summaryText = summaryData.summary;
                        if (summaryData.emailDraft) {
                            emailDraft = summaryData.emailDraft;
                            setWorkplaceAgentSteps((prev) => [...prev, {
                                emoji: "✉️", title: "Drafting email…", detail: "Composing email from summary", status: "in-progress"
                            }]);
                            await new Promise(r => setTimeout(r, 420));
                            setWorkplaceAgentSteps((prev) => {
                                const updated = [...prev];
                                let idx = -1;
                                for (let i = updated.length - 1; i >= 0; i--) { if (updated[i].title === "Drafting email…") { idx = i; break; } }
                                if (idx >= 0) updated[idx] = { ...updated[idx], status: "completed", title: "Email draft ready", detail: `Subject: ${emailDraft.subject || emailDraft.Subject || ""}` };
                                return updated;
                            });
                            pushLiveLog("STEP_OK", "Email draft generated", { subject: emailDraft.subject || emailDraft.Subject });
                        }
                    } else {
                        const errDetail = summaryData?.error || "No results were found for this workflow.";
                        pushLiveLog("WARN", "Summarize success=false", errDetail);
                        summaryText = `I completed the workflow steps, but was unable to generate a summary.\n\n**Details:** ${errDetail}\n\nYou can enable Debug mode to inspect what was retrieved.`;
                    }
                } catch (summaryHttpErr) {
                    pushLiveLog("HTTP_ERR", "Summarize HTTP error", { detail: summaryHttpErr?.message });
                    summaryText = `The workflow completed, but the summary service returned an error.\n\nThe agents searched across your selected sources. Enable Debug mode to view detailed logs.`;
                }

                emitFinalMessage(summaryText, emailDraft);
            } catch (summaryErr) {
                pushLiveLog("ERR", "Summarize fetch threw exception", { detail: summaryErr?.message || String(summaryErr) });
                emitFinalMessage("The workflow steps completed, but I was unable to fetch the final summary. Please check your connection and try again.", null);
            }
        } catch (err) {
            const details = err?.message || String(err);
            const trailSummary = trail.map(t => t.title).join(" > ") || "none";
            const isConnectionRefused = /failed to fetch|networkerror|connection refused|refused/i.test(details || "");
            const connectionHint = isConnectionRefused
                ? "Likely connectivity issue (host down, reverse-proxy, CORS, or SSL trust). Confirm app is reachable and endpoint path is correct."
                : "Server responded with an error. Share traceId and logs below for targeted debugging.";

            pushLiveLog("FLOW_ERR", "Workplace flow failed", { details, connectionHint });
            setWorkplaceAgentSteps((prev) => [...prev, {
                emoji: "❌",
                title: "Workplace flow failed",
                detail: isConnectionRefused ? "Connection refused or network failure." : "Endpoint returned an error response.",
                status: "failed"
            }]);
            setWorkplaceAgentError(`${details}\n\nHint: ${connectionHint}`);

            console.error("[WorkplaceAgents] Orchestration flow failed", {
                error: details,
                trail,
                runtimeLogs,
                selectedWorkplaceAgents,
                questionLength: text?.length,
            });

            const flowError = new Error(`Workplace Agent flow failed. ${details} | trail=${trailSummary}`);
            flowError.runtimeLogs = runtimeLogs;
            throw flowError;
        }
    };

    const executeValidatedWorkplaceStep = async (step, validatedParameters, isApproved, silent = false, workflowOverride = null) => {
        // workflowOverride is used when called from the auto-execute loop so we don't
        // depend on React state (workflowValidation) that may not have flushed yet.
        const wf = workflowOverride || workflowValidation;
        if (!wf?.workflowId || !step?.stepId) return;

        setIsExecutingAgentStep(true);
        try {
            setWorkplaceAgentSteps((prev) => [...prev, {
                emoji: isApproved ? "🚀" : "⛔",
                title: isApproved ? `Executing ${step.agentName} · ${step.action}` : `Rejected ${step.agentName} · ${step.action}`,
                detail: `StepId: ${step.stepId}`,
                status: isApproved ? "in-progress" : "completed",
            }]);

            const executeRequestPayload = {
                workflowId: wf.workflowId,
                stepId: step.stepId,
                agentName: step.agentName || "",
                action: step.action || "",
                validatedParameters: validatedParameters || {},
                isApproved,
            };

            setWorkplaceAgentLiveLogs((prev) => [...prev, `[${new Date().toISOString()}] [DECISION] execute.request | ${JSON.stringify({
                payload: {
                    workflowId: executeRequestPayload.workflowId,
                    stepId: executeRequestPayload.stepId,
                    agentName: executeRequestPayload.agentName,
                    action: executeRequestPayload.action,
                    isApproved: executeRequestPayload.isApproved,
                    parameterKeys: Object.keys(executeRequestPayload.validatedParameters || {}),
                    hasSsoToken: true
                },
                parsed: { expectedDecisionPoints: ["canonicalResult", "needsPermission", "fetchSummary"] }
            })}`]);

            // Send the SSO token in the Authorization header so the backend can perform
            // an OBO exchange for a Graph token — same pattern used by all other API calls.
            const result = await ApiClient.post("/api/workplace-agents/execute", executeRequestPayload);
            const statusText = isApproved ? "approved and executed" : "rejected";
            const canonicalResult = result?.canonicalResult || result?.result || null;

            setWorkplaceAgentLiveLogs((prev) => [...prev, `[${new Date().toISOString()}] [DECISION] execute.response.parsed | ${JSON.stringify({
                payload: {
                    success: result?.success,
                    message: result?.message,
                    correlationId: result?.correlationId || canonicalResult?.correlationId || "n/a",
                    decisionCheckpoint: result?.decisionCheckpoint || "n/a"
                },
                parsed: {
                    resultType: canonicalResult?.resultType || "legacy",
                    needsPermission: !!canonicalResult?.needsPermission,
                    fetchCount: canonicalResult?.fetchSummary?.totalCount ?? canonicalResult?.ResultCount ?? 0,
                    hasTopItems: (canonicalResult?.fetchSummary?.topItems?.length || 0) > 0
                }
            })}`]);

            // Emit each backend decision checkpoint as a [DECISION] log line so
            // spark.plan.request/response/compiled and graph.*.query/response are visible
            const checkpoints = canonicalResult?.decisionCheckpoints || [];
            if (checkpoints.length > 0) {
                // Accumulate for the "How I answered this" trace
                waCheckpointsRef.current = [...waCheckpointsRef.current, ...checkpoints];
                const checkpointLogs = checkpoints.map((cp) =>
                    `[${new Date().toISOString()}] [DECISION] ${cp.name} | ${JSON.stringify({
                        status: cp.status,
                        detail: cp.detail,
                        payload: cp.payloadSnippet
                    })}`
                );
                setWorkplaceAgentLiveLogs((prev) => [...prev, ...checkpointLogs]);
            }

            console.log("✅ Execute Response:", {
                success: result?.success,
                message: result?.message,
                resultObject: result?.result,
                canonicalResult,
                hasResults: !!canonicalResult?.Results,
                resultCount: canonicalResult?.fetchSummary?.totalCount ?? canonicalResult?.ResultCount,
                firstResult: canonicalResult?.fetchSummary?.topItems?.[0] || canonicalResult?.Results?.[0]
            });

            // Check if this is a write action that needs user confirmation.
            // We do NOT open the dialog automatically — instead store it so the final
            // summary message can render a "Send Email" button the user clicks explicitly.
            if (canonicalResult?.needsPermission) {
                pendingWriteActionRef.current = {
                    ...canonicalResult,
                    stepId: step.stepId,
                    workflowId: wf.workflowId,
                    agentName: step.agentName,
                    action: step.action,
                };
                // Fall through — do NOT return. Allow the remaining-steps check below
                // to proceed so the summarize call still executes.
            }

            if (!canonicalResult?.needsPermission) {
                // Build the response message including the actual result content
                let responseMessage = `Workplace agent step ${statusText}: ${result?.message || step.action}`;

                console.group("🔍 EXECUTION RESULT ANALYSIS");
                console.log("Full response object:", JSON.stringify(result, null, 2));
                console.log("Has canonicalResult?", !!canonicalResult);
                console.log("Result object keys:", Object.keys(result || {}));
                console.log("Canonical keys:", canonicalResult ? Object.keys(canonicalResult) : "N/A");

                // Prefer canonical contract; fallback to legacy shape
                if (canonicalResult) {
                    const fetchSummary = canonicalResult.fetchSummary || null;
                    const canonicalTopItems = Array.isArray(fetchSummary?.topItems) ? fetchSummary.topItems : [];
                    const legacyItems = Array.isArray(canonicalResult.Results) ? canonicalResult.Results : [];
                    const legacyResultCount = canonicalResult.ResultCount ?? legacyItems.length;

                    if (fetchSummary && canonicalTopItems.length > 0) {
                        responseMessage += `\n\n📊 Found ${fetchSummary.totalCount ?? canonicalTopItems.length} result(s):\n`;
                        canonicalTopItems.slice(0, 5).forEach((item, idx) => {
                            const displayName = item.title || item.name || '(No title)';
                            responseMessage += `${idx + 1}. ${displayName}\n`;
                            if (item.preview) {
                                responseMessage += `   ${String(item.preview).substring(0, 150)}...\n`;
                            }
                        });
                        if ((fetchSummary.totalCount ?? canonicalTopItems.length) > 5) {
                            responseMessage += `\n... and ${(fetchSummary.totalCount ?? canonicalTopItems.length) - 5} more result(s)\n`;
                        }
                    } else if (fetchSummary && fetchSummary.summaryText) {
                        responseMessage += `\n\n${fetchSummary.summaryText}`;
                    } else if (legacyItems.length > 0) {
                        responseMessage += `\n\n📊 Found ${legacyResultCount} result(s):\n`;
                        legacyItems.slice(0, 5).forEach((item, idx) => {
                            const displayName = item.subject || item.title || item.name || '(No title)';
                            responseMessage += `${idx + 1}. ${displayName}\n`;
                            if (item.preview || item.snippet) {
                                responseMessage += `   ${(item.preview || item.snippet).substring(0, 150)}...\n`;
                            }
                        });
                        if (legacyResultCount > 5) {
                            responseMessage += `\n... and ${legacyResultCount - 5} more result(s)\n`;
                        }
                    } else if (canonicalResult.Summary) {
                        responseMessage += `\n\n${canonicalResult.Summary}`;
                    } else if (canonicalResult.Content) {
                        responseMessage += `\n\n${String(canonicalResult.Content).substring(0, 500)}`;
                    } else if (canonicalResult.rawResult) {
                        responseMessage += `\n\n${JSON.stringify(canonicalResult.rawResult)}`;
                    }
                } else {
                    console.log("⚠️ canonicalResult is null or undefined");
                    console.log("⚠️ All properties in result:", Object.entries(result || {}).map(([k,v]) => `${k}: ${typeof v}`).join(", "));
                }

                console.log("📤 Final responseMessage:", responseMessage);
                console.groupEnd();

                // Only emit per-step chat messages in non-silent (manual approval) mode
                if (!silent) {
                    setMessages((prev) => [
                        ...prev,
                        {
                            id: makeId(),
                            role: "Spark Assist",
                            text: responseMessage,
                        },
                    ]);
                }
            } // end !needsPermission guard

            if (!silent) {
                const remaining = (wf.steps || []).filter((x) => x.stepId !== step.stepId);
                setWorkflowValidation(remaining.length === 0 ? null : { ...wf, steps: remaining });
                setIsAgentValidationOpen(remaining.length > 0);
            }
        } catch (err) {
            const details = err?.message || String(err);
            const ts = new Date().toISOString();
            setWorkplaceAgentLiveLogs((prev) => [...prev, `[${ts}] [EXECUTE_ERR] Step ${step?.stepId || "n/a"} failed | ${details}`]);
            setWorkplaceAgentError(details);
            console.error("[WorkplaceAgents] Execute step failed", {
                error: details,
                workflowId: workflowValidation?.workflowId,
                step,
            });
            if (!silent) {
                setMessages((prev) => [
                    ...prev,
                    { id: makeId(), role: "Spark Assist", text: `Workplace action failed: ${details}` },
                ]);
            }
        } finally {
            setIsExecutingAgentStep(false);
        }
    };

    const handleWriteActionSubmit = async (formData) => {
        if (!writeActionDialog) return;

        setIsWriteActionSubmitting(true);
        try {
            // Execute the write action by re-submitting with form data as validated parameters
            const executeRequest = {
                workflowId: writeActionDialog.workflowId,
                stepId: writeActionDialog.stepId,
                agentName: writeActionDialog.agent,
                action: writeActionDialog.action,
                validatedParameters: formData,
                isApproved: true
            };

            const result = await ApiClient.post("/api/workplace-agents/execute", executeRequest);

            setMessages((prev) => [
                ...prev,
                {
                    id: makeId(),
                    role: "Spark Assist",
                    text: `✅ ${result?.message || `${writeActionDialog.agent} action completed`}`,
                },
            ]);

            setWriteActionDialog(null);

            // Check for remaining steps
            const remaining = (workflowValidation?.steps || []).filter((x) => x.stepId !== writeActionDialog.stepId);
            if (remaining.length === 0) {
                setIsAgentValidationOpen(false);
                setWorkflowValidation(null);
                setWorkplaceAgentSteps((prev) => [...prev, { emoji: "🏁", title: "Workflow completed", detail: "All steps processed.", status: "completed" }]);
            } else {
                setWorkflowValidation({ ...workflowValidation, steps: remaining });
            }
        } catch (err) {
            const details = err?.message || String(err);
            setMessages((prev) => [
                ...prev,
                { id: makeId(), role: "Spark Assist", text: `❌ Write action failed: ${details}` },
            ]);
        } finally {
            setIsWriteActionSubmitting(false);
        }
    };

    const refreshClientChatHistoryEntry = (chatPatch, messageDelta = 0) => {
        const nowIso = new Date().toISOString();

        setChatHistories((prev) => {
            const idx = prev.findIndex(c => c.id === chatPatch.id);
            const existing = idx >= 0 ? prev[idx] : null;

            const updated = {
                ...(existing || {}),
                ...(chatPatch || {}),
                id: chatPatch.id || existing?.id,
                messageCount: Math.max(
                    0,
                    (existing?.messageCount ?? chatPatch?.messageCount ?? 0) + messageDelta
                ),
                lastUpdatedAt: nowIso,
                lastAccessedAt: nowIso
            };

            const rest = prev.filter(c => c.id !== updated.id);
            return [updated, ...rest];
        });
    };

    const send = async () => {
        const text = draft.trim();
        if (!text || isSending) return;

        setIsSending(true);
        setDraft("");
        setPickerMode(null);
        setPickerQuery("");

        const updatedMessages = [...messages, { id: makeId(), role: "user", text }];
        setMessages(updatedMessages);

        //Track message sent
        const startTime = Date.now();
        UsageLogService.trackMessageSent(
            text.length,
            selectedSpark?.sparkId,
            selectedSpark?.sparkName,
            selectedIntegrations
        );

        // ═══════════════════════════════════════════════════════════
        // Create chat on first message (if not exists)
        // ═══════════════════════════════════════════════════════════
        let chatId = currentChatId;

        if (!chatId) {
            // This is the first message - create chat in Cosmos DB
            try {
                const chatTitle = text.length > 50 ? text.substring(0, 47) + "..." : text;

                const chatResponse = await ChatHistoryService.createChat(
                    selectedSpark?.sparkId,
                    selectedSpark?.sparkName,
                    chatTitle,
                    selectedIntegrations || [],
                    selectedAgents || [],
                    {
                        meetingId: meetingId || null,
                        chatId: chatId || null,
                        userName: userName || null
                    }
                );

                chatId = chatResponse.id;
                setCurrentChatId(chatId);
                refreshClientChatHistoryEntry({
                    id: chatResponse.id,
                    title: chatResponse.title,
                    sparkId: chatResponse.sparkId,
                    sparkName: chatResponse.sparkName,
                    integrations: chatResponse.integrations || [],
                    agents: chatResponse.agents || [],
                    messageCount: chatResponse.messageCount ?? 0,
                    createdAt: chatResponse.createdAt,
                    lastUpdatedAt: chatResponse.lastUpdatedAt,
                    lastAccessedAt: chatResponse.lastAccessedAt,
                    metadata: chatResponse.metadata || {}
                }, 0);

            } catch (dbError) {
                console.warn("Failed to create chat in Cosmos DB:", dbError);
                // Continue without chat ID (offline mode)
            }
        }

        // ═══════════════════════════════════════════════════════════
        // Save user message to Cosmos DB
        // ═══════════════════════════════════════════════════════════
        if (chatId) {
            try {
                await ChatHistoryService.addMessage(
                    chatId,
                    "user",
                    text,
                    selectedSpark?.sparkName || null,
                    {
                        sparkId: String(selectedSpark?.sparkId ?? ""),
                        sparkName: selectedSpark?.sparkName ?? "",
                        integrations: JSON.stringify(selectedIntegrations || []),
                        agents: JSON.stringify(selectedAgents || []),
                        timestamp: new Date().toISOString()
                    }
                );

                refreshClientChatHistoryEntry({ id: chatId }, 1);

            } catch (dbError) {
                console.warn("Failed to save user message to Cosmos DB:", dbError);
                // Continue anyway - message already in UI
            }
        }

        try {
            if (isWorkplaceAgentFrameworkEnabled && selectedWorkplaceAgents.length > 0) {
                await startWorkplaceAgentFlow(text);
                return; // isSending cleared in the finally block below
            }

            const payload = buildConversationPayload(updatedMessages, text);
            const assistantReply = await callChatApi(payload);

            //Track message received
            const responseTime = Date.now() - startTime;
            UsageLogService.trackMessageReceived(
                responseTime,
                assistantReply.length, // token count approximation
                selectedSpark?.sparkId
            );

            // Attach workplace trace to the message if it was a workplace search
            const trace = selectedIntegrations.length > 0 && workplaceTraceRef.current.steps.length > 0
                ? { ...workplaceTraceRef.current }
                : undefined;
            // Detect actionable intent from the original user prompt
            const suggestedAction = detectEmailIntent(text)
                ?? detectTeamsPostIntent(text)
                ?? detectCalendarIntent(text);

            const assistantMessage = {
                id: makeId(),
                role: "Spark Assist",
                text: assistantReply,
                workplaceTrace: trace,
                suggestedAction
            };

            setMessages((prev) => [...prev, assistantMessage]);

            // ═══════════════════════════════════════════════════════════
            // Save assistant message to Cosmos DB
            // ═══════════════════════════════════════════════════════════
            if (chatId) {
                try {
                    const savedAssistantMsg = await ChatHistoryService.addMessage(
                        chatId,
                        "assistant",
                        assistantReply,
                        selectedSpark?.sparkName || null,
                        {
                            workplaceTrace: trace ? JSON.stringify(trace) : "",
                            suggestedAction: suggestedAction ? JSON.stringify(suggestedAction) : "",
                            responseTimeMs: String(responseTime),
                            timestamp: new Date().toISOString()
                        }
                    );

                    // Replace the temp local ID with the persisted Cosmos ID so that
                    // immediate feedback calls (thumbs up/down) target the real message ID
                    // and don't 404 before the user refreshes or switches chats.
                    if (savedAssistantMsg?.id && savedAssistantMsg.id !== assistantMessage.id) {
                        setMessages(prev =>
                            prev.map(m =>
                                m.id === assistantMessage.id
                                    ? { ...m, id: savedAssistantMsg.id }
                                    : m
                            )
                        );
                    }

                    // Always update local list count first
                    refreshClientChatHistoryEntry({ id: chatId }, 1);

                    try {
                        await ChatHistoryService.updateChat(chatId, {
                            sparkId: selectedSpark?.sparkId ?? null,
                            sparkName: selectedSpark?.sparkName ?? null,
                            integrations: selectedIntegrations || [],
                            agents: selectedAgents || [],
                            metadata: {
                                meetingId: meetingId || "",
                                teamsChatId: chatId || "",
                                userName: userName || ""
                            }
                        });
                    } catch (updateErr) {
                        console.warn(`Failed to update chat ${chatId}:`, updateErr);
                    }

                } catch (dbError) {
                    console.warn("Failed to save assistant message to Cosmos DB:", dbError);
                    // Continue anyway - message already in UI
                }
            }
        } catch (err) {
            console.error(err);

            // Track error
            UsageLogService.trackError(err, {
                sparkId: selectedSpark?.sparkId,
                sparkName: selectedSpark?.sparkName,
                messageLength: text.length,
                integrations: selectedIntegrations.join(','),
                agents: selectedAgents.join(',')
            });

            const detailed = err?.message || String(err);
            const traceMatch = /traceId=([^\s|]+)/i.exec(detailed || "");
            const trailMatch = /\|\s*trail=(.*)$/i.exec(detailed || "");
            const traceId = traceMatch?.[1] || "n/a";
            const fallbackTrail = trailMatch?.[1]?.trim() || "none";
            const stageTrail = workplaceAgentSteps.map(s => s.title).join(" > ") || fallbackTrail;
            const runtimeLogs = Array.isArray(err?.runtimeLogs) ? err.runtimeLogs : workplaceAgentLiveLogs;
            const logsBlock = (runtimeLogs || []).slice(-40).join("\n") || "none";
            const errorText = (isWorkplaceAgentFrameworkEnabled && selectedWorkplaceAgents.length > 0)
                ? `Workplace Agent flow failed.\n\nDetails: ${detailed}\n\nDiagnostics to share:\n- traceId: ${traceId}\n- selectedAgents: ${selectedWorkplaceAgents.join(",") || "none"}\n- stageTrail: ${stageTrail}\n\nEndpoint trail:\n- /api/workplace-agents/available\n- /api/workplace-agents/orchestrate\n- /api/workplace-agents/validation\n- /api/workplace-agents/execute\n\nRealtime logs (copy/paste):\n${logsBlock}`
                : "Sorry — chat call failed. Check server logs.";

            setMessages((prev) => [...prev, { id: makeId(), role: "Spark Assist", text: errorText }]);
        } finally {
            setIsSending(false);
        }
    };

    /**
     * Load chat history list (30 chats at a time)
     * @param {boolean} loadMore - Load next page or refresh
     */
    const loadChatHistoryList = async (loadMore = false) => {
        setIsLoadingChatHistory(true);

        try {
            const response = await ChatHistoryService.getChatHistory(
                30, // Page size
                loadMore ? chatHistoryContinuationToken : null
            );

            const chatListRaw = response.chats || response.data || [];
            const chatList = chatListRaw.map(c => ({
                ...c,
                integrations: c.integrations || [],
                agents: c.agents || [],
                metadata: c.metadata || {}
            }));

            if (loadMore) {
                // Append to existing list
                setChatHistories(prev => [...prev, ...chatList]);
            } else {
                // Replace entire list (refresh)
                setChatHistories(chatList);
            }

            setHasMoreChats(response.hasMore || !!response.continuationToken);
            setChatHistoryContinuationToken(response.continuationToken);
        } catch (error) {
            console.error("Failed to load chat history:", error);
        } finally {
            setIsLoadingChatHistory(false);
        }
    };

    /**
     * Save current chat and messages to Cosmos DB
     * Called before switching to new/different chat
     */
    const saveCurrentChat = async () => {
        if (!currentChatId || messages.length <= 1) {
            return;
        }

        try {
            setIsSavingChat(true);

            // Update chat with latest timestamp
            await ChatHistoryService.updateChat(currentChatId, {
                sparkId: selectedSpark?.sparkId ?? null,
                sparkName: selectedSpark?.sparkName ?? null,
                integrations: selectedIntegrations || [],
                agents: selectedAgents || [],
                metadata: {
                    meetingId: meetingId || "",
                    teamsChatId: chatId || "",
                    userName: userName || ""
                }
            });

        } catch (error) {
            console.warn("Failed to save current chat:", error);
            // Don't throw - allow navigation to continue
        } finally {
            setIsSavingChat(false);
        }
    };

    /**
     * Create new chat conversation
     * Requirement: Save existing chat before creating new one
     */
    const handleNewChat = async () => {
        try {
            // STEP 1: Save current chat if exists
            if (currentChatId) {
                await saveCurrentChat();
            }

            // STEP 2: Reset UI to initial state
            setCurrentChatId(null);
            setMessages([{
                id: "m0",
                role: "Spark Assist",
                text: `${timeGreeting}${firstName ? `, ${firstName}` : ""}! 👋\n\nI'm Spark Assist — your AI-powered meeting companion. How can I help you today?`
            }]);
            setDraft("");
            setInsights("");
            setPickerMode(null);
            setPickerQuery("");
            setTranscript("");
            setAdoWorkItemContext([]);
            setAdoSelectedWorkItems([]);
            setSelectedAgents([]);
            setSelectedWorkplaceAgents([]);

            // STEP 3: Refresh chat history list to show saved chat at top
            await loadChatHistoryList(false);

        } catch (error) {
            console.error("Failed to start new chat:", error);
        }
    };

    /**
     * Load existing chat from history
     * Requirement: Save current chat before loading new one
     * @param {string} chatId - Chat ID to load
     */
    const loadChatFromHistory = async (chatId) => {
        if (chatId === currentChatId) {
            return;
        }

        try {
            setIsLoadingChatHistory(true);

            // STEP 1: Save current chat if exists
            if (currentChatId) {
                await saveCurrentChat();
            }

            // STEP 2: Load chat metadata
            const chatResponse = await ChatHistoryService.getChatById(chatId);

            //STEP 3: Refresh UI with loaded chat
            refreshClientChatHistoryEntry({
                id: chatResponse.id,
                title: chatResponse.title,
                sparkId: chatResponse.sparkId,
                sparkName: chatResponse.sparkName,
                integrations: chatResponse.integrations || [],
                agents: chatResponse.agents || [],
                messageCount: chatResponse.messageCount ?? 0,
                createdAt: chatResponse.createdAt,
                lastUpdatedAt: chatResponse.lastUpdatedAt,
                lastAccessedAt: chatResponse.lastAccessedAt,
                metadata: chatResponse.metadata || {}
            }, 0);

            setSelectedIntegrations(chatResponse.integrations || []);
            setSelectedAgents(chatResponse.agents || []);

            // STEP 3: Load chat messages
            const messagesResponse = await ChatHistoryService.getChatMessages(chatId, 100);

            // STEP 4: Update UI state
            setCurrentChatId(chatId);

            // Transform messages to UI format
            const tryParseJson = (value) => {
                if (!value || typeof value !== "string") return undefined;
                try { return JSON.parse(value); } catch { return undefined; }
            };

            const loadedMessages = (messagesResponse.messages || []).map(msg => {
                const parsedTrace = tryParseJson(msg.metadata?.workplaceTrace);
                const parsedAction = tryParseJson(msg.metadata?.suggestedAction);

                return {
                    id: msg.id,
                    role: msg.role === "assistant" ? "Spark Assist" : msg.role,
                    text: msg.content,
                    workplaceTrace: parsedTrace?.steps ? parsedTrace : undefined,
                    suggestedAction: parsedAction,
                    timestamp: msg.createdAt
                };
            });

            // Restore feedback from loaded messages
            const restoredFeedback = {};
            (messagesResponse.messages || []).forEach(msg => {
                if (msg.feedback) {
                    restoredFeedback[msg.id] = msg.feedback;
                }
            });
            setFeedbackByMessageId(restoredFeedback);

            setMessages(loadedMessages);

            // STEP 5: Restore Spark if available
            if (chatResponse.sparkId) {
                const spark = allSparks.find(s => s.sparkId === chatResponse.sparkId);
                if (spark) {
                    setSelectedSpark(spark);
                    UsageLogService.updateSparkContext(spark.sparkId, spark.sparkName);
                }
            }

        } catch (error) {
            console.error("Failed to load chat:", error);
        } finally {
            setIsLoadingChatHistory(false);
        }
    };

    /**
     * Delete a chat from history
     * @param {string} chatId - Chat ID to delete
     */
    const deleteChatHistory = async (chatId) => {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm("Are you sure you want to delete this chat?")) {
            return;
        }

        try {
            await ChatHistoryService.deleteChat(chatId, false); // Soft delete

            // Remove from local state
            setChatHistories(prev => prev.filter(chat => chat.id !== chatId));

            // If deleting current chat, start new one
            if (chatId === currentChatId) {
                await handleNewChat();
            }

        } catch (error) {
            console.error("Failed to delete chat:", error);
        }
    };

    const startEditChatTitle = (entry, e) => {
        e?.stopPropagation?.();
        setEditingChatId(entry.id);
        setEditingChatTitle(entry.title || "");
    };

    const cancelEditChatTitle = () => {
        setEditingChatId(null);
        setEditingChatTitle("");
    };

    const saveChatTitle = async (chatId) => {
        const newTitle = (editingChatTitle || "").trim();
        if (!chatId) return;

        const existing = chatHistories.find(c => c.id === chatId);
        const oldTitle = existing?.title || "";

        if (!newTitle || newTitle === oldTitle) {
            cancelEditChatTitle();
            return;
        }

        setChatHistories(prev => prev.map(c => c.id === chatId ? { ...c, title: newTitle } : c));
        setIsSavingChatTitle(true);

        try {
            await ChatHistoryService.updateChat(chatId, { title: newTitle });
            cancelEditChatTitle();
        } catch (err) {
            console.error("Failed to update chat title:", err);
            setChatHistories(prev => prev.map(c => c.id === chatId ? { ...c, title: oldTitle } : c));
        } finally {
            setIsSavingChatTitle(false);
        }
    };

    // Theme colors
    const isDark = appTheme === "theme-dark";
    const isContrast = appTheme === "theme-contrast";

    const surfaceBg = isDark ? "rgba(42, 42, 42, 0.95)" : isContrast ? "rgba(0, 0, 0, 0.9)" : "rgba(248, 248, 248, 0.95)";
    const surfaceBorder = isDark ? "1px solid rgba(255, 255, 255, 0.15)" : isContrast ? "1px solid rgba(255, 255, 255, 0.7)" : "1px solid rgba(0, 0, 0, 0.12)";
    const textColor = isDark || isContrast ? "#ffffff" : "#242424";
    const textSecondary = isDark || isContrast ? "rgba(255, 255, 255, 0.75)" : "rgba(0, 0, 0, 0.65)";
    const textTertiary = isDark || isContrast ? "rgba(255, 255, 255, 0.55)" : "rgba(0, 0, 0, 0.50)";
    const inputTextColor = isDark || isContrast ? "#ffffff" : "#242424";
    const inputPlaceholderColor = isDark || isContrast ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)";
    const pickerBg = isDark ? "rgba(42, 42, 42, 1)" : isContrast ? "#000000" : "#ffffff";
    const pickerBorder = isDark ? "1px solid rgba(255, 255, 255, 0.2)" : isContrast ? "1px solid rgba(255, 255, 255, 0.8)" : "1px solid rgba(0, 0, 0, 0.15)";
    const pickerText = textColor;
    const pickerHover = isDark ? "rgba(255, 255, 255, 0.12)" : isContrast ? "rgba(255, 255, 255, 0.18)" : "rgba(0, 0, 0, 0.06)";
    const chipBg = isDark ? "rgba(255, 255, 255, 0.10)" : isContrast ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.06)";
    const chipBorder = isDark ? "1px solid rgba(255, 255, 255, 0.18)" : isContrast ? "1px solid rgba(255, 255, 255, 0.85)" : "1px solid rgba(0, 0, 0, 0.12)";
    const chipText = textColor;
    const userMessageBg = isDark ? "rgba(0, 120, 212, 0.30)" : isContrast ? "rgba(0, 120, 212, 0.45)" : "rgba(0, 120, 212, 0.15)";
    const assistantMessageBg = isDark ? "rgba(255, 255, 255, 0.08)" : isContrast ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.06)";
    const insightsBg = isDark ? "rgba(156, 39, 176, 0.15)" : isContrast ? "rgba(156, 39, 176, 0.25)" : "rgba(156, 39, 176, 0.08)";
    const iconColor = isDark ? "rgba(255, 255, 255, 0.85)" : isContrast ? "rgba(255, 255, 255, 0.95)" : "rgba(0, 0, 0, 0.70)";
    const iconHoverBg = isDark ? "rgba(255, 255, 255, 0.12)" : isContrast ? "rgba(255, 255, 255, 0.20)" : "rgba(0, 0, 0, 0.08)";
    const primaryIconColor = isDark ? "#64b5f6" : isContrast ? "#90caf9" : "#0078d4";

    // ── Profile: derive initials and full display name from userName ──
    const userInitials = useMemo(() => {
        if (!userName) return "SA";
        if (userName.includes("@")) {
            const local = userName.split("@")[0];      // e.g. "saisreenath.s"
            const parts = local.split(".");
            if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
            return local.substring(0, 2).toUpperCase();
        }
        const parts = userName.split(" ");
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return userName.substring(0, 2).toUpperCase();
    }, [userName]);

    const profileDisplayName = useMemo(() => {
        if (!userName) return "User";
        if (userName.includes("@")) {
            const local = userName.split("@")[0];
            return local.split(".").map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
        }
        return userName;
    }, [userName]);

    // Icons
    const DownloadIcon = ({ size = 18 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ color: primaryIconColor }}>
            <path d="M12 3v13m0 0 4-4m-4 4-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M20 16v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const CopyIcon = ({ size = 18 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ color: iconColor }}>
            <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const ShareIcon = ({ size = 18 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ color: iconColor }}>
            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="16 6 12 2 8 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <line x1="12" y1="2" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );

    const SparkStoreIcon = ({ size = 18, color = "currentColor" }) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke={color}
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            style={{ flexShrink: 0, display: "block" }}
        >
            {/* Rounded square outer body */}
            <rect x="2" y="3" width="20" height="18" rx="4" />
            {/* Wavy awning — two bumps spanning full width */}
            <path d="M2 10 C2 10, 3.5 7, 6 10 C8.5 13, 10 10, 12 10 C14 10, 15.5 13, 18 10 C20.5 7, 22 10, 22 10" />
            {/* Smile shelf at bottom */}
            <path d="M8 16 Q12 19 16 16" />
        </svg>
    );

    const IconButton = ({ title, onClick, disabled, children, primary = false, style = {} }) => (
        <button
            type="button"
            title={title}
            onClick={(e) => {
                e.stopPropagation();
                if (!disabled) onClick?.(e);
            }}
            disabled={disabled}
            style={{
                border: "none",
                background: "transparent",
                cursor: disabled ? "not-allowed" : "pointer",
                padding: primary ? "9px 11px" : "7px 9px",
                borderRadius: primary ? 10 : 8,
                opacity: disabled ? 0.35 : 1,
                fontSize: primary ? 18 : 16,
                fontWeight: primary ? 700 : 600,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.15s ease",
                color: iconColor,
                ...style,
            }}
            onMouseEnter={(e) => {
                if (!disabled) {
                    e.currentTarget.style.background = iconHoverBg;
                    e.currentTarget.style.transform = "scale(1.08)";
                }
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.transform = "scale(1)";
            }}
        >
            {children}
        </button>
    );

    const MenuItem = ({ label, onClick }) => (
        <div
            onClick={(e) => {
                e.stopPropagation();
                setShareMenuForMessageId(null);
                onClick?.();
            }}
            style={{
                padding: "9px 12px",
                borderRadius: 8,
                cursor: "pointer",
                fontSize: 13,
                color: textColor,
                transition: "background 0.12s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = pickerHover)}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
            {label}
        </div>
    );

    const setFeedback = (messageId, value) => {
        setFeedbackByMessageId((prev) => {
            const current = prev[messageId];
            const isToggleOff = current === value;

            // Track feedback event
            if (!isToggleOff) {
                // User is giving feedback (thumbs up or down)
                UsageLogService.trackEvent('MessageFeedbackProvided', {
                    feedbackType: value, // 'up' or 'down'
                    messageId: messageId,
                }, 'Feature');
            } else {
                // User is removing their feedback
                UsageLogService.trackEvent('MessageFeedbackRemoved', {
                    feedbackType: value, // 'up' or 'down'
                    messageId: messageId,
                }, 'Feature');
            }

            if (isToggleOff) {
                const copy = { ...prev };
                delete copy[messageId];
                return copy;
            }
            return { ...prev, [messageId]: value };
        });
    };

    const escapeHtml = (s) =>
        String(s)
            .replaceAll("&", "&amp;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;")
            .replaceAll('"', "&quot;")
            .replaceAll("'", "&#039;");

    const linkifyLine = (line) => {
        let s = line.replace(/<\/?sup>/gi, "");
        s = s.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (m, text, url) => {
            const safeText = escapeHtml(text);
            const safeUrl = escapeHtml(url);
            return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="color: ${primaryIconColor}; text-decoration: underline;">${safeText}</a>`;
        });
        s = s.replace(/<\s*(https?:\/\/[^>]+)\s*>/g, (m, url) => {
            const safeUrl = escapeHtml(url.trim());
            return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="color: ${primaryIconColor}; text-decoration: underline;">${safeUrl}</a>`;
        });
        s = s.replace(
            /(^|[\s(])((https?:\/\/)[^\s<>()]+)(?=$|[\s).,;!?:])/g,
            (m, lead, url) => {
                const safeUrl = escapeHtml(url);
                return `${lead}<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" style="color: ${primaryIconColor}; text-decoration: underline;">${safeUrl}</a>`;
            }
        );
        s = s.replace(
            /(^|[\s(])(www\.[^\s<>()]+)(?=$|[\s).,;!?:])/g,
            (m, lead, url) => {
                const full = `https://${url}`;
                const safeFull = escapeHtml(full);
                const safeText = escapeHtml(url);
                return `${lead}<a href="${safeFull}" target="_blank" rel="noopener noreferrer" style="color: ${primaryIconColor}; text-decoration: underline;">${safeText}</a>`;
            }
        );
        s = s.replace(/\*\*([^*]+)\*\*/g, (m, boldText) => `<b>${escapeHtml(boldText)}</b>`);
        return s;
    };

    const renderRichText = (text) => {
        const raw = text ?? "";

        // ── Inline markdown processor ─────────────────────────────────────────
        // Handles the structured markdown produced by the AI summarizer:
        //   ## Heading, ### Heading, **bold**, *italic*, [text](url), [N] citations,
        //   - / • bullet, "  - " sub-bullet, 1. ordered item, ---, blank lines.
        const inlineStyles = (s) => {
            // [text](url) links are extracted BEFORE HTML-escaping so that full Outlook/Teams
            // URLs (which contain & = ( ) characters) are preserved intact in the href
            // and never truncated by stop-at-paren regex patterns.
            //
            // Step 1: split into link tokens and plain-text tokens
            const LINK_RE = /\[([^\]]+)\]\((https?:\/\/(?:[^)(]|\((?:[^)(]|\([^)(]*\))*\))*)\)/g;
            const parts2 = [];
            let lastIdx = 0;
            let lm;
            LINK_RE.lastIndex = 0;
            while ((lm = LINK_RE.exec(s)) !== null) {
                if (lm.index > lastIdx) parts2.push({ type: 'text', val: s.slice(lastIdx, lm.index) });
                parts2.push({ type: 'link', text: lm[1], url: lm[2] });
                lastIdx = lm.index + lm[0].length;
            }
            if (lastIdx < s.length) parts2.push({ type: 'text', val: s.slice(lastIdx) });

            // Step 2: apply remaining markdown to plain-text segments only
            const processText = (raw) => {
                let out = escapeHtml(raw);
                // bare <url> links
                out = out.replace(/&lt;(https?:\/\/[^&]+)&gt;/g, (_, u) => {
                    const safeU = escapeHtml(u);
                    return `<a href="${safeU}" target="_blank" rel="noopener noreferrer" style="color:${primaryIconColor};text-decoration:underline;">${safeU}</a>`;
                });
                // bare https:// urls not already wrapped in a link
                out = out.replace(/(^|[\s(])(https?:\/\/[^\s<>)]+)(?=$|[\s).,;!?:])/g,
                    (_, lead, url) => `${lead}<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer" style="color:${primaryIconColor};text-decoration:underline;">${escapeHtml(url)}</a>`
                );
                // citation superscripts [1] [2] etc.
                out = out.replace(/\[(\d+)\]/g, (_, n) =>
                    `<sup style="font-size:10px;color:${primaryIconColor};font-weight:600;">[${n}]</sup>`
                );
                // **bold**
                out = out.replace(/\*\*([^*]+)\*\*/g, (_, b) => `<strong>${b}</strong>`);
                // *italic*
                out = out.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, (_, ix) => `<em>${ix}</em>`);
                return out;
            };

            // Step 3: reassemble — link tokens become <a> tags, text tokens are processed normally
            return parts2.map(p => {
                if (p.type === 'link') {
                    // Encode bare & that isn't already an HTML entity; leave %xx sequences intact
                    const safeHref = p.url.replace(/&(?!amp;|lt;|gt;|quot;|#\d+;)/g, '&amp;');
                    const safeText = escapeHtml(p.text);
                    return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" style="color:${primaryIconColor};text-decoration:underline;font-weight:500;">${safeText}</a>`;
                }
                return processText(p.val);
            }).join('');
        };

        const lines = raw.split("\n");
        const parts = [];
        let i = 0;

        while (i < lines.length) {
            const ln = lines[i];
            const trimmed = ln.trim();

            // ── Blank line ────────────────────────────────────────────────────
            if (trimmed === "") {
                parts.push(`<div style="height:6px;"></div>`);
                i++;
                continue;
            }

            // ── Horizontal rule ───────────────────────────────────────────────
            if (/^---+$/.test(trimmed)) {
                parts.push(`<hr style="border:none;border-top:1px solid ${
                    isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)"
                };margin:8px 0;" />`);
                i++;
                continue;
            }

            // ── Blockquote  (> text) ─────────────────────────────────────────
            if (/^>\s/.test(trimmed) || trimmed === '>') {
                const content = trimmed.replace(/^>\s*/, "");
                if (content) {
                    parts.push(`<div style="margin:3px 0 3px 4px;padding:4px 10px;border-left:3px solid ${
                        isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.18)"
                    };color:${isDark ? "#cbd5e1" : "#334155"};font-size:13px;line-height:1.6;">${inlineStyles(content)}</div>`);
                }
                i++;
                continue;
            }

            // ── H2  (## text) — Copilot-style section header with icon + divider ─
            if (/^##\s/.test(trimmed)) {
                const headText = trimmed.replace(/^##\s+/, "");
                // Map well-known section titles to icons
                const iconMap = {
                    "summary":      { icon: "📋", color: isDark ? "#7dd3fc" : "#0369a1" },
                    "key points":   { icon: "💡", color: isDark ? "#fde68a" : "#b45309" },
                    "action items": { icon: "✅", color: isDark ? "#86efac" : "#15803d" },
                    "sources":      { icon: "🔗", color: isDark ? "#c4b5fd" : "#6d28d9" },
                    "next steps":   { icon: "➡️", color: isDark ? "#fdba74" : "#c2410c" },
                };
                const key = headText.toLowerCase().trim();
                const meta = iconMap[key] || { icon: "▸", color: isDark ? "#94a3b8" : "#475569" };
                // Add a top divider for every section except the very first
                const divider = parts.length > 0
                    ? `<div style="height:1px;background:${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)"};margin:14px 0 10px;"></div>`
                    : "";
                parts.push(`${divider}<div style="display:flex;align-items:center;gap:7px;margin:0 0 6px;">` +
                    `<span style="font-size:14px;line-height:1;">${meta.icon}</span>` +
                    `<span style="font-size:13px;font-weight:700;color:${meta.color};letter-spacing:0.15px;">${inlineStyles(headText)}</span>` +
                    `</div>`);
                i++;
                continue;
            }

            // ── H3  (### text) ────────────────────────────────────────────────
            if (/^###\s/.test(trimmed)) {
                const headText = trimmed.replace(/^###\s+/, "");
                parts.push(`<div style="font-size:12px;font-weight:700;color:${
                    isDark ? "#cbd5e1" : "#334155"
                };margin:8px 0 3px;">${inlineStyles(headText)}</div>`);
                i++;
                continue;
            }

            // ── Ordered list item  (1. / 2. …) ───────────────────────────────
            const orderedMatch = trimmed.match(/^(\d+)\.\s+(.*)/);
            if (orderedMatch) {
                const [, num, content] = orderedMatch;
                parts.push(`<div style="display:flex;gap:6px;margin:3px 0;align-items:baseline;">
                    <span style="min-width:18px;font-weight:600;color:${primaryIconColor};font-size:12px;flex-shrink:0;">${escapeHtml(num)}.</span>
                    <span style="color:${isDark ? "#e2e8f0" : "#1e293b"};font-size:13px;line-height:1.5;">${inlineStyles(content)}</span>
                </div>`);
                i++;
                continue;
            }

            // ── Sub-bullet  ("  - " or "    • ") ─────────────────────────────
            if (/^\s{2,}[-•]\s/.test(ln)) {
                const content = ln.replace(/^\s+[-•]\s+/, "");
                parts.push(`<div style="display:flex;gap:6px;margin:2px 0 2px 18px;align-items:baseline;">
                    <span style="flex-shrink:0;color:${isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.30)"};font-size:11px;">◦</span>
                    <span style="color:${isDark ? "#cbd5e1" : "#334155"};font-size:12px;line-height:1.5;">${inlineStyles(content)}</span>
                </div>`);
                i++;
                continue;
            }

            // ── Top-level bullet  ("- " or "• ") ─────────────────────────────
            if (/^[-•]\s/.test(trimmed)) {
                const content = trimmed.replace(/^[-•]\s+/, "");
                parts.push(`<div style="display:flex;gap:7px;margin:3px 0;align-items:baseline;">
                    <span style="flex-shrink:0;color:${primaryIconColor};font-size:11px;margin-top:1px;">•</span>
                    <span style="color:${isDark ? "#e2e8f0" : "#1e293b"};font-size:13px;line-height:1.5;">${inlineStyles(content)}</span>
                </div>`);
                i++;
                continue;
            }

            // ── Plain paragraph line ──────────────────────────────────────────
            parts.push(`<div style="margin:3px 0;color:${
                isDark ? "#e2e8f0" : "#1e293b"
            };font-size:13px;line-height:1.6;">${inlineStyles(trimmed)}</div>`);
            i++;
        }

        return <div dangerouslySetInnerHTML={{ __html: parts.join("") }} />;
    };

    const integrationLabelByKey = useMemo(() => {
        const map = {};
        INTEGRATIONS.forEach((x) => (map[x.key] = x.label));
        return map;
    }, [INTEGRATIONS]);

    const agentLabelByKey = useMemo(() => {
        const map = {};
        AGENTS.forEach((x) => (map[x.key] = x.label));
        return map;
    }, [AGENTS]);

    const workplaceAgentLabelByKey = useMemo(() => {
        const map = {};
        WORKPLACE_AGENTS.forEach((x) => (map[x.key] = x.label));
        return map;
    }, [WORKPLACE_AGENTS]);

    const formatLikes = (n) => {
        const num = Number(n || 0);
        if (Number.isNaN(num)) return String(n || "0");
        if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
        if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
        return `${num}`;
    };

    const openSparkStore = () => {
        try { localStorage.setItem("spark_store_seen", "1"); } catch { /* ignore */ }
        setSparkStoreNudge(false);
        setSparkStoreDisplayLimit(50);
        setIsSparkStoreOpen(true);

        //Track Spark Store opened
        UsageLogService.trackFeatureUsage(
            'SparkStore',
            'opened',
            {
                totalSparks: allSparkStoreItems.length,
                dynamicSparksLoaded: dynamicSparksLoaded,
                lastSyncedAt: lastSyncedAt?.toISOString()
            }
        );
    };

    // ✨ Download Spark as Standalone Desktop App
    const downloadSparkApp = async (sparkId, sparkTitle) => {
        try {
            console.log(`[SparkInstall] Downloading Spark ${sparkId}: ${sparkTitle}`);

            const response = await fetch(
                `/api/sparkstore/install/${sparkId}?title=${encodeURIComponent(sparkTitle || 'Spark')}`,
                {
                    headers: {
                        "Authorization": `Bearer ${await AuthTokenProvider.getAccessToken()}`
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`Failed to generate app: ${response.status}`);
            }

            const htmlContent = await response.text();

            // Create download
            const blob = new Blob([htmlContent], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Spark-${sparkTitle.replace(/[^a-zA-Z0-9]/g, '-')}.html`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            console.log(`[SparkInstall] ✅ Downloaded: ${a.download}`);
            alert(`✅ Spark app downloaded!\n\nFile: ${a.download}\n\nOpen the HTML file from your Downloads folder to launch.`);
        } catch (error) {
            console.error('[SparkInstall] Failed:', error);
            alert(`❌ Download failed: ${error.message}`);
        }
    };

    return (
        <div
            className={appTheme}
            style={{
                height: "100vh",
                width: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "row",
                backgroundColor: isDark ? "#1e1e1e" : isContrast ? "#000000" : "#ffffff",
                color: textColor,
                position: "relative",
            }}
        >
            {/* ── CHAT HISTORY SIDEBAR ────────────────────────────────── */}
            {/* 🆕 ChatHistory feature flag check */}
            {!meetingId && flags.chatHistory && (
                <div style={{
                    width: isSidebarOpen ? (isMobile ? "100vw" : 240) : 0,
                    minWidth: isSidebarOpen ? (isMobile ? "100vw" : 240) : 0,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: isDark ? "#161616" : isContrast ? "#050505" : "#f4f4f4",
                    borderRight: isSidebarOpen
                        ? (isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)")
                        : "none",
                    overflow: "hidden",
                    transition: "width 0.22s ease, min-width 0.22s ease",
                    flexShrink: 0,
                    position: isMobile && isSidebarOpen ? "fixed" : "relative",
                    zIndex: isMobile && isSidebarOpen ? 9990 : "auto",
                    top: 0, left: 0,
                }}>
                    {isSidebarOpen && (
                        <>
                            {/* Sidebar header */}
                            <div style={{
                                padding: "12px 14px 8px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                flexShrink: 0,
                                borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)",
                                gap: 8,
                            }}>
                                {/* Collapse button — always visible, especially needed on mobile */}
                                <button
                                    type="button"
                                    title="Collapse sidebar"
                                    onClick={() => {
                                        setIsSidebarOpen(false);
                                        try { localStorage.setItem("spark_sidebar_open", "false"); } catch { /* ignore */ }
                                    }}
                                    style={{
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        width: 28, height: 28, borderRadius: 7, flexShrink: 0,
                                        border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                                        cursor: "pointer", color: textSecondary,
                                        transition: "background 0.15s",
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)"}
                                    onMouseLeave={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                                        <rect x="3" y="3" width="18" height="18" rx="2" />
                                        <line x1="9" y1="3" x2="9" y2="21" />
                                    </svg>
                                </button>

                                <span style={{ fontSize: 12, fontWeight: 700, color: textSecondary, letterSpacing: "0.3px", textTransform: "uppercase", flex: 1 }}>
                                    Chat History
                                </span>

                                {/* Clear all removed — use per-item delete instead */}
                            </div>

                            {/* ── SLIDING CONTENT AREA ──────────────────────────────── */}
                            <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>

                                {/* ── MAIN PANEL: slides left when settings open ── */}
                                <div style={{
                                    position: "absolute", inset: 0,
                                    display: "flex", flexDirection: "column",
                                    transform: isProfileSettingsOpen ? "translateX(-100%)" : "translateX(0%)",
                                    transition: "transform 0.30s cubic-bezier(0.4,0,0.2,1)",
                                    overflowY: "auto", overflowX: "hidden",
                                    background: isDark ? "#161616" : isContrast ? "#050505" : "#f4f4f4",
                                }}>

                                    {/* ── Doc Agents section (Content Generation) ── */}
                                    {flags.contentGeneration && (
                                        <div
                                            data-tour="sidebar-create"
                                            style={{ padding: "10px 8px 4px", flexShrink: 0 }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: textTertiary, textTransform: "uppercase", letterSpacing: "0.3px", padding: "0 6px 6px" }}>
                                                Create
                                            </div>
                                            {DOC_AGENTS.map(agent => {
                                                const isActive = docAgentType === agent.key;
                                                return (
                                                    <div
                                                        key={agent.key}
                                                        onClick={() => {
                                                            const previousAgent = docAgentType;
                                                            setDocAgentType(isActive ? null : agent.key);
                                                            if (!isActive) {
                                                                UsageLogService.trackEvent('DocumentAgentActivated', { agentType: agent.key, agentLabel: agent.label, sparkId: agent.sparkId, sparkName: agent.sparkName, previousAgent: previousAgent }, 'Feature');
                                                                setDocMessages([{ id: makeId(), role: "assistant", text: `Hi! I'm your ${agent.label} assistant. Describe the document you want me to create and I'll generate it for you.` }]);
                                                                setDocContent(""); setDocTitle(""); setDocSaveSuccess(""); setDocSaveError(""); setPptActiveSlide(0);
                                                            } else {
                                                                UsageLogService.trackEvent('DocumentAgentDeactivated', { agentType: agent.key, agentLabel: agent.label, hadContent: !!docContent, messageCount: docMessages.length }, 'Feature');
                                                            }
                                                        }}
                                                        style={{
                                                            display: "flex", alignItems: "center", gap: 10,
                                                            padding: "8px 10px", borderRadius: 8,
                                                            cursor: "pointer", marginBottom: 2,
                                                            background: isActive ? `${agent.color}18` : "transparent",
                                                            border: isActive ? `1px solid ${agent.color}44` : "1px solid transparent",
                                                            transition: "all 0.15s ease",
                                                        }}
                                                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"; }}
                                                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                                                    >
                                                        {agent.icon}
                                                        <span style={{ fontSize: 13, fontWeight: isActive ? 700 : 500, color: isActive ? agent.color : textSecondary }}>
                                                            {agent.label}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {/* Create Spark button */}
                                    {flags.createSpark && (
                                        <div
                                            data-tour="create-spark-btn"
                                            onClick={() => { UsageLogService.trackFeatureUsage('CreateSparkPanel', 'opened', {}); setIsSparkCreateOpen(true); }}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 10,
                                                padding: "8px 10px", borderRadius: 8,
                                                cursor: "pointer", marginBottom: 2, marginTop: 2,
                                                background: isSparkCreateOpen ? "rgba(8,145,178,0.15)" : "transparent",
                                                border: isSparkCreateOpen ? "1px solid rgba(8,145,178,0.40)" : "1px solid transparent",
                                                transition: "all 0.15s ease",
                                            }}
                                            onMouseEnter={e => { if (!isSparkCreateOpen) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"; }}
                                            onMouseLeave={e => { if (!isSparkCreateOpen) e.currentTarget.style.background = "transparent"; }}
                                        >
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                                <circle cx="12" cy="12" r="9" fill="#0891b2" opacity="0.15" stroke="#0891b2" strokeWidth="1.5" />
                                                <path d="M12 7v10M7 12h10" stroke="#0891b2" strokeWidth="2" strokeLinecap="round" />
                                            </svg>
                                            <span style={{ fontSize: 13, fontWeight: isSparkCreateOpen ? 700 : 500, color: isSparkCreateOpen ? "#0891b2" : textSecondary }}>
                                                Create Spark
                                            </span>
                                        </div>
                                    )}

                                    {/* ── SEARCH + HISTORY LIST ── */}
                                    <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>

                                        {/* Search box */}
                                        <div style={{ padding: "6px 8px 4px", flexShrink: 0 }}>
                                            <div style={{
                                                display: "flex", alignItems: "center", gap: 6,
                                                padding: "0 10px",
                                                height: 30, borderRadius: 8,
                                                background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                                                border: historySearch
                                                    ? "1px solid rgba(8,145,178,0.45)"
                                                    : isDark ? "1px solid rgba(255,255,255,0.09)" : "1px solid rgba(0,0,0,0.09)",
                                                transition: "border-color 0.15s ease",
                                            }}>
                                                {/* Search icon */}
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={historySearch ? "#0891b2" : textTertiary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                    <circle cx="11" cy="11" r="8"/>
                                                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                                </svg>
                                                {/* Input */}
                                                <input
                                                    type="text"
                                                    value={historySearch}
                                                    onChange={e => setHistorySearch(e.target.value)}
                                                    placeholder="Search history…"
                                                    style={{
                                                        flex: 1, border: "none", outline: "none",
                                                        background: "transparent",
                                                        fontSize: 11, color: textColor,
                                                        caretColor: "#0891b2",
                                                        "::placeholder": { color: textTertiary },
                                                    }}
                                                />
                                                {/* Clear button */}
                                                {historySearch && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setHistorySearch("")}
                                                        style={{
                                                            border: "none", background: "transparent",
                                                            cursor: "pointer", padding: 0, lineHeight: 1,
                                                            color: textTertiary, fontSize: 13, flexShrink: 0,
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            width: 14, height: 14, borderRadius: "50%",
                                                            transition: "color 0.12s",
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.color = textSecondary}
                                                        onMouseLeave={e => e.currentTarget.style.color = textTertiary}
                                                    >×</button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Result count pill — only when searching */}
                                        {historySearch.trim() && (() => {
                                            const q = historySearch.trim().toLowerCase();
                                            const count = chatHistories.filter(e =>
                                                e.title.toLowerCase().includes(q) ||
                                                (e.sparkName && e.sparkName.toLowerCase().includes(q))
                                            ).length;
                                            return (
                                                <div style={{ padding: "0 12px 4px", flexShrink: 0 }}>
                                                    <span style={{ fontSize: 10, color: count > 0 ? "#0891b2" : textTertiary, fontWeight: 600 }}>
                                                        {count > 0 ? `${count} result${count !== 1 ? "s" : ""}` : "No matches"}
                                                    </span>
                                                </div>
                                            );
                                        })()}

                                        {/* History list */}
                                        <div style={{ flex: 1, overflowY: "auto", padding: "4px 8px 8px" }}>
                                            {/* Loading state */}
                                            {isLoadingChatHistory && chatHistories.length === 0 ? (
                                                <div style={{ padding: "32px 12px", textAlign: "center", color: textTertiary, fontSize: 12, lineHeight: 1.6 }}>
                                                    <div style={{ fontSize: 24, marginBottom: 8 }}>⏳</div>
                                                    <div>Loading chat history...</div>
                                                </div>
                                            ) : chatHistories.length === 0 ? (
                                                <div style={{ padding: "32px 12px", textAlign: "center", color: textTertiary, fontSize: 12, lineHeight: 1.6 }}>
                                                    <div style={{ fontSize: 24, marginBottom: 8 }}>💬</div>
                                                    <div>No chat history yet.</div>
                                                    <div style={{ marginTop: 4, opacity: 0.7 }}>Previous chats will appear here.</div>
                                                </div>
                                            ) : (() => {
                                                const q = historySearch.trim().toLowerCase();
                                                const filtered = q
                                                    ? chatHistories.filter(e =>
                                                        e.title.toLowerCase().includes(q) ||
                                                        (e.sparkName && e.sparkName.toLowerCase().includes(q))
                                                    )
                                                    : chatHistories;

                                                if (filtered.length === 0) {
                                                    return (
                                                        <div style={{ padding: "28px 12px", textAlign: "center", color: textTertiary, fontSize: 12, lineHeight: 1.6 }}>
                                                            <div style={{ fontSize: 22, marginBottom: 8 }}>🔍</div>
                                                            <div>No results for</div>
                                                            <div style={{ fontWeight: 700, color: textSecondary, marginTop: 2 }}>"{historySearch.trim()}"</div>
                                                        </div>
                                                    );
                                                }

                                                // Highlight helper: wraps matched span in teal
                                                const highlight = (text, query) => {
                                                    if (!query) return text;
                                                    const idx = text.toLowerCase().indexOf(query.toLowerCase());
                                                    if (idx < 0) return text;
                                                    return (
                                                        <>
                                                            {text.slice(0, idx)}
                                                            <span style={{ background: "rgba(8,145,178,0.22)", color: "#0891b2", borderRadius: 2, padding: "0 1px" }}>
                                                                {text.slice(idx, idx + query.length)}
                                                            </span>
                                                            {text.slice(idx + query.length)}
                                                        </>
                                                    );
                                                };

                                                return (
                                                    <>
                                                        {filtered.map((entry) => {
                                                            const isActive = entry.id === currentChatId;
                                                            const dateLabel = (() => {
                                                                try {
                                                                    const d = new Date(entry.lastUpdatedAt || entry.createdAt);
                                                                    const now = new Date();
                                                                    const diffDays = Math.floor((now - d) / 86400000);
                                                                    if (diffDays === 0) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
                                                                    if (diffDays === 1) return "Yesterday";
                                                                    if (diffDays < 7) return d.toLocaleDateString([], { weekday: "short" });
                                                                    return d.toLocaleDateString([], { month: "short", day: "numeric" });
                                                                } catch { return ""; }
                                                            })();

                                                            return (
                                                                <div
                                                                    key={entry.id}
                                                                    onClick={() => loadChatFromHistory(entry.id)}
                                                                    style={{
                                                                        padding: "9px 10px", borderRadius: 8, cursor: "pointer", marginBottom: 2,
                                                                        background: isActive ? (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)") : "transparent",
                                                                        display: "flex", alignItems: "flex-start", gap: 6,
                                                                        transition: "background 0.12s ease", position: "relative",
                                                                    }}
                                                                    onMouseEnter={e => {
                                                                        if (!isActive) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)";
                                                                        const actionBtns = e.currentTarget.querySelectorAll(".hist-action");
                                                                        actionBtns.forEach(btn => { btn.style.opacity = "1"; });
                                                                    }}
                                                                    onMouseLeave={e => {
                                                                        if (!isActive) e.currentTarget.style.background = "transparent";
                                                                        const actionBtns = e.currentTarget.querySelectorAll(".hist-action");
                                                                        actionBtns.forEach(btn => { btn.style.opacity = "0"; });
                                                                    }}
                                                                >
                                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                                        {editingChatId === entry.id ? (
                                                                            <input
                                                                                autoFocus
                                                                                value={editingChatTitle}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                onChange={(e) => setEditingChatTitle(e.target.value)}
                                                                                onBlur={() => saveChatTitle(entry.id)}
                                                                                onKeyDown={(e) => {
                                                                                    if (e.key === "Enter") {
                                                                                        e.preventDefault();
                                                                                        saveChatTitle(entry.id);
                                                                                    }
                                                                                    if (e.key === "Escape") {
                                                                                        e.preventDefault();
                                                                                        cancelEditChatTitle();
                                                                                    }
                                                                                }}
                                                                                style={{
                                                                                    width: "100%",
                                                                                    fontSize: 12,
                                                                                    fontWeight: 600,
                                                                                    color: textColor,
                                                                                    background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.04)",
                                                                                    border: isDark ? "1px solid rgba(255,255,255,0.20)" : "1px solid rgba(0,0,0,0.16)",
                                                                                    borderRadius: 6,
                                                                                    padding: "4px 6px",
                                                                                    outline: "none"
                                                                                }}
                                                                            />
                                                                        ) : (
                                                                            <div
                                                                                onDoubleClick={(e) => startEditChatTitle(entry, e)}
                                                                                style={{
                                                                                    fontSize: 12,
                                                                                    fontWeight: 600,
                                                                                    color: isActive ? textColor : textSecondary,
                                                                                    overflow: "hidden",
                                                                                    textOverflow: "ellipsis",
                                                                                    whiteSpace: "nowrap",
                                                                                    lineHeight: 1.4
                                                                                }}
                                                                                title="Double-click to rename"
                                                                            >
                                                                                {highlight(entry.title || "Untitled Chat", historySearch.trim())}
                                                                            </div>
                                                                        )}
                                                                        <div style={{ fontSize: 10, color: textTertiary, marginTop: 2 }}>
                                                                            {dateLabel} · {entry.messageCount || 0} msg{entry.messageCount !== 1 ? "s" : ""}
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        className="hist-action hist-rename"
                                                                        type="button"
                                                                        title="Rename"
                                                                        onClick={(e) => startEditChatTitle(entry, e)}
                                                                        style={{
                                                                            border: "none",
                                                                            background: "transparent",
                                                                            cursor: "pointer",
                                                                            padding: "2px 4px",
                                                                            borderRadius: 4,
                                                                            color: textTertiary,
                                                                            fontSize: 12,
                                                                            lineHeight: 1,
                                                                            opacity: 0,
                                                                            transition: "opacity 0.12s, color 0.12s",
                                                                            flexShrink: 0
                                                                        }}
                                                                        onMouseEnter={e => e.currentTarget.style.color = "#0891b2"}
                                                                        onMouseLeave={e => e.currentTarget.style.color = textTertiary}
                                                                    >
                                                                        ✎
                                                                    </button>
                                                                    <button
                                                                        className="hist-action hist-delete"
                                                                        type="button"
                                                                        title="Delete"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            deleteChatHistory(entry.id);
                                                                        }}
                                                                        style={{
                                                                            border: "none",
                                                                            background: "transparent",
                                                                            cursor: "pointer",
                                                                            padding: "2px 4px",
                                                                            borderRadius: 4,
                                                                            color: textTertiary,
                                                                            fontSize: 14,
                                                                            lineHeight: 1,
                                                                            opacity: 0,
                                                                            transition: "opacity 0.12s, color 0.12s",
                                                                            flexShrink: 0
                                                                        }}
                                                                        onMouseEnter={e => e.currentTarget.style.color = "#ef4444"}
                                                                        onMouseLeave={e => e.currentTarget.style.color = textTertiary}
                                                                    >×</button>
                                                                </div>
                                                            );
                                                        })}

                                                        {/* Load More button */}
                                                        {hasMoreChats && (
                                                            <button
                                                                onClick={() => loadChatHistoryList(true)}
                                                                disabled={isLoadingChatHistory}
                                                                style={{
                                                                    width: "100%",
                                                                    padding: "8px",
                                                                    marginTop: 8,
                                                                    borderRadius: 7,
                                                                    border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                                                    background: "transparent",
                                                                    color: textSecondary,
                                                                    fontSize: 11,
                                                                    fontWeight: 600,
                                                                    cursor: isLoadingChatHistory ? "not-allowed" : "pointer",
                                                                    opacity: isLoadingChatHistory ? 0.5 : 1,
                                                                    transition: "background 0.12s",
                                                                }}
                                                                onMouseEnter={e => {
                                                                    if (!isLoadingChatHistory) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
                                                                }}
                                                                onMouseLeave={e => {
                                                                    e.currentTarget.style.background = "transparent";
                                                                }}
                                                            >
                                                                {isLoadingChatHistory ? "⏳ Loading..." : "📥 Load More"}
                                                            </button>
                                                        )}
                                                    </>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>

                                {/* ── SETTINGS PANEL: slides in from right ── */}
                                <div style={{
                                    position: "absolute", inset: 0,
                                    display: "flex", flexDirection: "column",
                                    transform: isProfileSettingsOpen ? "translateX(0%)" : "translateX(100%)",
                                    transition: "transform 0.30s cubic-bezier(0.4,0,0.2,1)",
                                    background: isDark ? "#161616" : isContrast ? "#050505" : "#f4f4f4",
                                    overflowX: "hidden",
                                }}>
                                    {/* Settings panel header */}
                                    <div style={{
                                        display: "flex", alignItems: "center", gap: 8,
                                        padding: "12px 12px 10px",
                                        borderBottom: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
                                        flexShrink: 0,
                                    }}>
                                        <button
                                            type="button"
                                            onClick={() => setIsProfileSettingsOpen(false)}
                                            style={{
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                                                border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                                background: "transparent", cursor: "pointer", color: textSecondary,
                                                transition: "background 0.15s",
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)"}
                                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                        >
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                                <polyline points="15 18 9 12 15 6"/>
                                            </svg>
                                        </button>
                                        <span style={{ fontSize: 13, fontWeight: 700, color: textColor, flex: 1 }}>Account & Settings</span>
                                    </div>

                                    {/* Settings scrollable body */}
                                    <div style={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "14px 12px 16px" }}>

                                        {/* User info card */}
                                        <div style={{
                                            borderRadius: 12, padding: "14px", marginBottom: 18,
                                            background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)",
                                            border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
                                        }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                {/* Avatar */}
                                                <div style={{
                                                    width: 42, height: 42, borderRadius: "50%", flexShrink: 0,
                                                    background: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.06)",
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    color: textSecondary, fontSize: 15, fontWeight: 700, letterSpacing: "0.5px",
                                                    userSelect: "none",
                                                }}>
                                                    {userInitials}
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {profileDisplayName}
                                                    </div>
                                                    <div style={{ fontSize: 11, color: textSecondary, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {userName || ""}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Resources section */}
                                        <div style={{ marginBottom: 8 }}>
                                            <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.7px", color: textTertiary, padding: "0 2px", marginBottom: 6 }}>
                                                Resources
                                            </div>

                                            {/* ── shared link-row helper ── */}
                                            {[
                                                {
                                                    href: "https://confluence-dts.atlassian.net/wiki/spaces/DIKB/pages/686751777/Spark+Anywhere+User+Guide",
                                                    label: "Help & Learning",
                                                    sub: "User Guide & Documentation",
                                                    icon: (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                                                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                                                        </svg>
                                                    ),
                                                },
                                                {
                                                    href: "https://engage.cloud.microsoft/main/groups/eyJfdHlwZSI6Ikdyb3VwIiwiaWQiOiIxNzc1MzA0NDU4MjQifQ/all",
                                                    label: "Join KSA Community",
                                                    sub: "Viva Engage · Spark Assist Group",
                                                    icon: (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                                            <circle cx="9" cy="7" r="4"/>
                                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                                        </svg>
                                                    ),
                                                },
                                                {
                                                    href: "https://spgi.service-now.com/esc?id=sc_cat_item&sys_id=d4c138654709d61092a502b2846d43d2&referrer=recent_items",
                                                    label: "Raise a Request",
                                                    sub: "Service Now · Support Portal",
                                                    icon: (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                                                            <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
                                                        </svg>
                                                    ),
                                                },
                                                {
                                                    href: "https://www.spglobal.com/en/privacy/privacy-policy-english",
                                                    label: "Terms of Use",
                                                    sub: "Privacy Policy",
                                                    icon: (
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                                        </svg>
                                                    ),
                                                },
                                            ].map(item => (
                                                <a
                                                    key={item.href}
                                                    href={item.href}
                                                    target="_blank" rel="noopener noreferrer"
                                                    style={{ textDecoration: "none", display: "block", marginBottom: 5 }}
                                                >
                                                    <div
                                                        style={{
                                                            display: "flex", alignItems: "center", gap: 10,
                                                            padding: "9px 10px", borderRadius: 9, cursor: "pointer",
                                                            background: "transparent",
                                                            border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
                                                            transition: "background 0.13s ease",
                                                        }}
                                                        onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"}
                                                        onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                                    >
                                                        <div style={{
                                                            width: 30, height: 30, borderRadius: 8, flexShrink: 0,
                                                            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.35)",
                                                        }}>
                                                            {item.icon}
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontSize: 12, fontWeight: 600, color: textColor }}>{item.label}</div>
                                                            <div style={{ fontSize: 10, color: textTertiary, marginTop: 1 }}>{item.sub}</div>
                                                        </div>
                                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={textTertiary} strokeWidth="2.5" strokeLinecap="round">
                                                            <polyline points="9 18 15 12 9 6"/>
                                                        </svg>
                                                    </div>
                                                </a>
                                            ))}
                                        </div>

                                        {/* App info footer */}
                                        <div style={{ marginTop: 16, paddingTop: 12, borderTop: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)" }}>
                                            <div style={{ fontSize: 10, color: textTertiary, opacity: 0.65, textAlign: "center" }}>Spark Anywhere &nbsp;&middot;&nbsp; Internal Use Only</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* ── PROFILE FOOTER ───────────────────────────────── */}
                            <div style={{
                                flexShrink: 0,
                                borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                                padding: "8px 10px",
                                background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)",
                            }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "6px 6px" }}>
                                    {/* Avatar circle — subtle, no gradient */}
                                    <div style={{
                                        width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                                        background: isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)",
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        color: textSecondary, fontSize: 11, fontWeight: 700, letterSpacing: "0.3px",
                                        userSelect: "none",
                                    }}>
                                        {userInitials}
                                    </div>

                                    {/* Name only */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 12, fontWeight: 600, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.3 }}>
                                            {profileDisplayName}
                                        </div>
                                    </div>

                                    {/* Settings gear button — grey, no teal tint */}
                                    <button
                                        type="button"
                                        title="Account & Settings"
                                        onClick={() => setIsProfileSettingsOpen(prev => !prev)}
                                        style={{
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                            border: isDark ? "1px solid rgba(255,255,255,0.11)" : "1px solid rgba(0,0,0,0.11)",
                                            background: isProfileSettingsOpen
                                                ? (isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)")
                                                : "transparent",
                                            cursor: "pointer",
                                            color: textSecondary,
                                            transition: "all 0.15s ease",
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)";
                                            e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.20)" : "rgba(0,0,0,0.18)";
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.background = isProfileSettingsOpen ? (isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)") : "transparent";
                                            e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.11)" : "rgba(0,0,0,0.11)";
                                        }}
                                    >
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="3"/>
                                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ── MAIN CONTENT COLUMN ─────────────────────────────────── */}
            <div style={{
                flex: 1,
                minWidth: 0,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
            }}>

                {/* ══════════════════════════════════════════════════
                DOC AGENT UI — shown when Word/PPT agent active
            ══════════════════════════════════════════════════ */}
                {docAgentType && (() => {
                    const agent = DOC_AGENTS.find(a => a.key === docAgentType);
                    if (!agent) return null;

                    return (
                        <div style={{
                            position: "fixed", inset: 0,
                            display: "flex", flexDirection: "row",
                            zIndex: 500,
                            backgroundColor: isDark ? "#1e1e1e" : "#ffffff",
                        }}>
                            {/* LEFT — Chat panel */}
                            <div style={{
                                width: isMobile ? "100%" : 380,
                                flexShrink: 0,
                                display: "flex", flexDirection: "column",
                                borderRight: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                backgroundColor: isDark ? "#252525" : "#fafafa",
                                height: "100%",
                            }}>
                                {/* Header */}
                                <div style={{
                                    display: "flex", alignItems: "center", gap: 10,
                                    padding: "12px 16px",
                                    borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                                    flexShrink: 0,
                                }}>
                                    {agent.icon}
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: textColor }}>{agent.label}</div>
                                        <div style={{ fontSize: 11, color: textSecondary }}>
                                            {docTitle || "Describe what you want to create"}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => { setDocAgentType(null); setDocContent(""); }}
                                        style={{ border: "none", background: "transparent", cursor: "pointer", color: textSecondary, fontSize: 18, padding: "4px 8px", borderRadius: 6 }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = iconHoverBg}
                                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                    >✕</button>
                                </div>

                                {/* Messages */}
                                <div style={{ flex: 1, overflowY: "auto", padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 }}>
                                    {docMessages.map(m => {
                                        const isUser = m.role === "user";
                                        return (
                                            <div key={m.id} style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", alignItems: "flex-start", gap: 8 }}>
                                                {!isUser && (
                                                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: agent.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
                                                        {agent.icon}
                                                    </div>
                                                )}
                                                <div style={{
                                                    maxWidth: "82%", padding: "9px 12px", borderRadius: 10, fontSize: 13,
                                                    background: isUser ? userMessageBg : assistantMessageBg,
                                                    color: textColor, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word",
                                                    border: isDark ? "1px solid rgba(255,255,255,0.07)" : "none",
                                                }}>
                                                    {renderRichText(m.text)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {isGeneratingDoc && (
                                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: agent.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                {agent.icon}
                                            </div>
                                            <div style={{ padding: "9px 12px", borderRadius: 10, background: assistantMessageBg, display: "flex", gap: 5, alignItems: "center" }}>
                                                {[0, 1, 2].map(i => (
                                                    <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: agent.color, animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Error/success banners */}
                                {(docSaveSuccess || docSaveError) && (
                                    <div style={{
                                        margin: "0 12px 8px", padding: "8px 12px", borderRadius: 8, fontSize: 12,
                                        background: docSaveSuccess ? "rgba(22,163,74,0.12)" : "rgba(220,38,38,0.12)",
                                        border: docSaveSuccess ? "1px solid rgba(22,163,74,0.30)" : "1px solid rgba(220,38,38,0.30)",
                                        color: docSaveSuccess ? (isDark ? "#86efac" : "#15803d") : (isDark ? "#fca5a5" : "#b91c1c"),
                                    }}>
                                        {docSaveSuccess || docSaveError}
                                    </div>
                                )}

                                {/* Input area */}
                                <div style={{
                                    padding: "10px 12px 12px",
                                    borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                                    flexShrink: 0,
                                    display: "flex", flexDirection: "column", gap: 8,
                                }}>
                                    {/* Save button — only when content exists */}
                                    {docContent && (
                                        <button
                                            type="button"
                                            onClick={saveDocToOneDrive}
                                            disabled={isSavingDoc}
                                            style={{
                                                width: "100%", padding: "8px", borderRadius: 8, border: "none",
                                                background: isSavingDoc ? (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)") : "linear-gradient(135deg, #0891b2, #0e7490)",
                                                color: isSavingDoc ? textSecondary : "#fff",
                                                fontSize: 13, fontWeight: 700, cursor: isSavingDoc ? "not-allowed" : "pointer",
                                                display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                                                boxShadow: isSavingDoc ? "none" : "0 2px 10px rgba(8,145,178,0.30)",
                                                transition: "all 0.15s ease",
                                            }}
                                        >
                                            {isSavingDoc ? (
                                                <>
                                                    <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", animation: "spin 0.7s linear infinite" }} />
                                                    Saving to OneDrive…
                                                </>
                                            ) : (
                                                <>☁️ Save to OneDrive</>
                                            )}
                                        </button>
                                    )}

                                    {/* Text input row */}
                                    <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                                        <textarea
                                            value={docDraft}
                                            onChange={(e) => setDocDraft(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" && !e.shiftKey) {
                                                    e.preventDefault();
                                                    sendDocMessage();
                                                }
                                            }}
                                            placeholder={`Describe the ${agent.label} document you want…`}
                                            rows={2}
                                            style={{
                                                flex: 1, padding: "9px 12px", borderRadius: 10, fontSize: 13,
                                                border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                                background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                                                color: textColor, outline: "none", resize: "none",
                                                fontFamily: "inherit", lineHeight: 1.5,
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={sendDocMessage}
                                            disabled={!docDraft.trim() || isGeneratingDoc}
                                            style={{
                                                width: 38, height: 38, borderRadius: 10, border: "none", flexShrink: 0,
                                                background: docDraft.trim() && !isGeneratingDoc ? `linear-gradient(135deg, ${agent.color}, ${agent.color}cc)` : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                                                color: docDraft.trim() && !isGeneratingDoc ? "#fff" : textTertiary,
                                                cursor: docDraft.trim() && !isGeneratingDoc ? "pointer" : "not-allowed",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                fontSize: 16, transition: "all 0.15s ease",
                                            }}
                                        >➤</button>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT — Document preview panel */}
                            {!isMobile && (
                                <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
                                    {/* Preview header */}
                                    <div style={{
                                        display: "flex", alignItems: "center", gap: 12,
                                        padding: "12px 20px",
                                        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                                        flexShrink: 0,
                                        backgroundColor: isDark ? "#1e1e1e" : "#ffffff",
                                    }}>
                                        {agent.icon}
                                        <span style={{ fontSize: 14, fontWeight: 600, color: textColor, flex: 1 }}>
                                            {docTitle || "Document Preview"}
                                        </span>
                                        {docContent && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // Download locally
                                                    const ts = new Date().toISOString().replace(/[:.]/g, "-");
                                                    const fileName = `${(docTitle || "document").replace(/[^a-zA-Z0-9 ]/g, "").trim()}-${ts}.html`;
                                                    const fullHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${docTitle}</title></head><body>${docContent}</body></html>`;
                                                    const blob = new Blob([fullHtml], { type: "text/html;charset=utf-8" });
                                                    const url = URL.createObjectURL(blob);
                                                    const a = document.createElement("a");
                                                    a.href = url; a.download = fileName;
                                                    document.body.appendChild(a); a.click(); a.remove();
                                                    URL.revokeObjectURL(url);
                                                }}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 6,
                                                    padding: "5px 12px", borderRadius: 7, border: "none",
                                                    background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                                    color: textSecondary, fontSize: 12, fontWeight: 600, cursor: "pointer",
                                                    transition: "background 0.15s",
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.13)" : "rgba(0,0,0,0.10)"}
                                                onMouseLeave={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
                                            >
                                                ⬇ Download
                                            </button>
                                        )}
                                    </div>

                                    {/* Preview content */}
                                    <div style={{ flex: 1, overflowY: "auto", backgroundColor: isDark ? "#2a2a2a" : "#f0f0f0", padding: 24, display: "flex", justifyContent: "center" }}>
                                        {!docContent ? (
                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", color: textTertiary, textAlign: "center", gap: 12 }}>
                                                <div style={{ fontSize: 48 }}>{docAgentType === "ppt" ? "📊" : "📄"}</div>
                                                <div style={{ fontSize: 16, fontWeight: 600, color: textSecondary }}>Your document will appear here</div>
                                                <div style={{ fontSize: 13, maxWidth: 280, lineHeight: 1.6 }}>
                                                    Describe what you want to create in the chat panel on the left
                                                </div>
                                            </div>
                                        ) : docAgentType === "ppt" ? (
                                            /* ── PPT MULTI-SLIDE PREVIEW ── */
                                            (() => {
                                                    const slides = parseDocSlides(docContent, docTitle);
                                                const safeIdx = Math.min(pptActiveSlide, slides.length - 1);
                                                const current = slides[safeIdx] || slides[0];
                                                return (
                                                    <div style={{ display: "flex", width: "100%", height: "100%", overflow: "hidden" }}>
                                                        {/* Slide thumbnail strip */}
                                                        <div style={{
                                                            width: 180, flexShrink: 0, overflowY: "auto",
                                                            background: "#1e1e2e",
                                                            borderRight: "1px solid rgba(255,255,255,0.08)",
                                                            padding: "10px 8px",
                                                            display: "flex", flexDirection: "column", gap: 6,
                                                        }}>
                                                            <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", letterSpacing: "0.5px", padding: "4px 6px 6px" }}>
                                                                {slides.length} Slides
                                                            </div>
                                                            {slides.map((sl, i) => (
                                                                <div
                                                                    key={i}
                                                                    onClick={() => setPptActiveSlide(i)}
                                                                    style={{
                                                                        borderRadius: 6, cursor: "pointer", overflow: "hidden",
                                                                        border: i === safeIdx
                                                                            ? `2px solid ${agent.color}`
                                                                            : "2px solid transparent",
                                                                        transition: "border-color 0.15s",
                                                                        background: "#ffffff",
                                                                    }}
                                                                >
                                                                    {/* Mini slide thumbnail */}
                                                                    <div style={{
                                                                        aspectRatio: "16/9", padding: "6px 8px",
                                                                        display: "flex", flexDirection: "column", gap: 3,
                                                                        overflow: "hidden",
                                                                    }}>
                                                                        <div style={{
                                                                            fontSize: 7, fontWeight: 700, color: agent.color,
                                                                            borderBottom: `1px solid ${agent.color}44`,
                                                                            paddingBottom: 2, marginBottom: 2,
                                                                            overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                                                                        }}>
                                                                            {sl.title}
                                                                        </div>
                                                                        <div style={{ fontSize: 5.5, color: "#555", lineHeight: 1.4, overflow: "hidden", maxHeight: 36 }}
                                                                            dangerouslySetInnerHTML={{ __html: sl.body.replace(/<[^>]+>/g, " ").trim().slice(0, 120) }}
                                                                        />
                                                                    </div>
                                                                    <div style={{
                                                                        fontSize: 9, fontWeight: 600,
                                                                        color: i === safeIdx ? agent.color : "rgba(255,255,255,0.5)",
                                                                        background: i === safeIdx ? `${agent.color}18` : "rgba(255,255,255,0.06)",
                                                                        padding: "3px 6px", textAlign: "center",
                                                                    }}>
                                                                        {i + 1}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>

                                                        {/* Active slide main view */}
                                                        <div style={{
                                                            flex: 1, display: "flex", flexDirection: "column",
                                                            alignItems: "center", justifyContent: "center",
                                                            padding: "32px 40px",
                                                            background: "#2a2a3e",
                                                            overflow: "auto",
                                                            gap: 16,
                                                        }}>
                                                            {/* Slide counter + nav */}
                                                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                                                <button type="button"
                                                                    disabled={safeIdx === 0}
                                                                    onClick={() => setPptActiveSlide(safeIdx - 1)}
                                                                    style={{
                                                                        width: 30, height: 30, borderRadius: "50%", border: "none",
                                                                        background: safeIdx === 0 ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.18)",
                                                                        color: "#fff", cursor: safeIdx === 0 ? "not-allowed" : "pointer",
                                                                        fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
                                                                    }}>‹</button>
                                                                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", fontWeight: 600 }}>
                                                                    {pptActiveSlide + 1} / {slides.length}
                                                                </div>
                                                                <button type="button"
                                                                    disabled={safeIdx === slides.length - 1}
                                                                    onClick={() => setPptActiveSlide(safeIdx + 1)}
                                                                    style={{
                                                                        width: 30, height: 30, borderRadius: "50%", border: "none",
                                                                        background: safeIdx === slides.length - 1 ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.18)",
                                                                        color: "#fff", cursor: safeIdx === slides.length - 1 ? "not-allowed" : "pointer",
                                                                        fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center",
                                                                    }}>›</button>
                                                            </div>

                                                            {/* The slide itself — 16:9 */}
                                                            <div style={{
                                                                width: "100%", maxWidth: 720,
                                                                aspectRatio: "16/9",
                                                                background: "#ffffff",
                                                                borderRadius: 10,
                                                                boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
                                                                display: "flex", flexDirection: "column",
                                                                overflow: "hidden",
                                                                position: "relative",
                                                            }}>
                                                                {/* Slide header bar */}
                                                                <div style={{
                                                                    background: `linear-gradient(135deg, ${agent.color}, ${agent.color}cc)`,
                                                                    padding: "18px 32px 14px",
                                                                    flexShrink: 0,
                                                                }}>
                                                                    <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", lineHeight: 1.2 }}>
                                                                        {current.title}
                                                                    </div>
                                                                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.7)", marginTop: 4, fontWeight: 600 }}>
                                                                        {docTitle || "Presentation"} · Slide {safeIdx + 1}
                                                                    </div>
                                                                </div>

                                                                {/* Slide body */}
                                                                <div style={{
                                                                    flex: 1, padding: "20px 32px", overflow: "hidden",
                                                                    fontFamily: "'Segoe UI', Arial, sans-serif",
                                                                    fontSize: 13, lineHeight: 1.6, color: "#242424",
                                                                }}>
                                                                    <style>{`
                                                                .ppt-slide-body p { margin: 6px 0; }
                                                                .ppt-slide-body ul { padding-left: 18px; margin: 6px 0; }
                                                                .ppt-slide-body li { margin: 4px 0; font-size: 13px; }
                                                                .ppt-slide-body strong { color: ${agent.color}; }
                                                                .ppt-slide-body h3 { font-size: 14px; color: ${agent.color}; margin: 8px 0 4px; }
                                                                .ppt-slide-body table { width: 100%; border-collapse: collapse; font-size: 11px; }
                                                                .ppt-slide-body th { background: ${agent.color}; color: white; padding: 5px 8px; text-align: left; }
                                                                .ppt-slide-body td { padding: 4px 8px; border: 1px solid #eee; }
                                                            `}</style>
                                                                    <div className="ppt-slide-body"
                                                                        dangerouslySetInnerHTML={{ __html: mdToHtml(current.body) }}
                                                                    />
                                                                </div>

                                                                {/* Slide number badge */}
                                                                <div style={{
                                                                    position: "absolute", bottom: 10, right: 14,
                                                                    fontSize: 10, color: "rgba(0,0,0,0.3)", fontWeight: 600,
                                                                }}>
                                                                    {safeIdx + 1}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })()
                                        ) : (
                                            /* ── WORD DOCUMENT PREVIEW — A4 page ── */
                                            <div style={{
                                                width: "100%", height: "100%", overflow: "auto",
                                                background: "#e8e8e8",
                                                padding: "32px 24px",
                                                display: "flex", justifyContent: "center",
                                            }}>
                                                <div style={{
                                                    width: "100%", maxWidth: 794, /* A4 width in px at 96dpi */
                                                    minHeight: 1123, /* A4 height */
                                                    background: "#ffffff",
                                                    boxShadow: "0 4px 32px rgba(0,0,0,0.18)",
                                                    padding: "72px 80px",
                                                    fontFamily: "'Georgia', 'Times New Roman', serif",
                                                    fontSize: 14, lineHeight: 1.8, color: "#1a1a1a",
                                                }}>
                                                    <style>{`
                                                .word-preview h1 { font-size: 28px; color: ${agent.color}; border-bottom: 3px solid ${agent.color}; padding-bottom: 10px; margin: 0 0 24px; font-family: 'Segoe UI', Arial, sans-serif; font-weight: 800; }
                                                .word-preview h2 { font-size: 20px; color: ${agent.color}; margin: 32px 0 12px; font-family: 'Segoe UI', Arial, sans-serif; font-weight: 700; }
                                                .word-preview h3 { font-size: 16px; color: #333; margin: 20px 0 8px; font-family: 'Segoe UI', Arial, sans-serif; font-weight: 600; }
                                                .word-preview h4 { font-size: 14px; color: #555; margin: 16px 0 6px; font-family: 'Segoe UI', Arial, sans-serif; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
                                                .word-preview p { margin: 0 0 12px; }
                                                .word-preview ul, .word-preview ol { padding-left: 28px; margin: 8px 0 16px; }
                                                .word-preview li { margin: 5px 0; }
                                                .word-preview strong { color: #111; font-weight: 700; }
                                                .word-preview em { font-style: italic; color: #444; }
                                                .word-preview table { width: 100%; border-collapse: collapse; margin: 20px 0; font-family: 'Segoe UI', Arial, sans-serif; font-size: 13px; }
                                                .word-preview th { background: ${agent.color}; color: white; padding: 10px 14px; text-align: left; font-weight: 700; }
                                                .word-preview td { padding: 9px 14px; border: 1px solid #ddd; vertical-align: top; }
                                                .word-preview tr:nth-child(even) td { background: #f7f9ff; }
                                                .word-preview hr { border: none; border-top: 2px solid ${agent.color}33; margin: 28px 0; }
                                                .word-preview blockquote { border-left: 4px solid ${agent.color}; margin: 16px 0; padding: 10px 18px; background: ${agent.color}08; color: #333; font-style: italic; }
                                            `}</style>
                                                    <div className="word-preview"
                                                        dangerouslySetInnerHTML={{ __html: mdToHtml(docContent) }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}

            {/* ═══════════════════════════════════════════
            SPARK ANYWHERE LOADING SCREEN
            ═══════════════════════════════════════════ */}
            {isAppLoading && (
                <div style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: 99999,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    background: "linear-gradient(135deg, #0a0f1e 0%, #0d1b3e 35%, #0a1628 65%, #060d1a 100%)",
                    backgroundSize: "400% 400%",
                    animation: "gradientShift 8s ease infinite",
                }}>
                    {/* Particle field */}
                    {[...Array(22)].map((_, i) => {
                        const size = 3 + (i % 5) * 2;
                        const left = 5 + (i * 37) % 90;
                        const top = 5 + (i * 53) % 85;
                        const delay = (i * 0.31) % 4;
                        const dur = 4 + (i % 5);
                        const colors = ["#3b82f6", "#60a5fa", "#93c5fd", "#fbbf24", "#a78bfa", "#34d399"];
                        const color = colors[i % colors.length];
                        return (
                            <div key={i} style={{
                                position: "absolute",
                                left: `${left}%`,
                                top: `${top}%`,
                                width: size,
                                height: size,
                                borderRadius: "50%",
                                background: color,
                                opacity: 0.55,
                                boxShadow: `0 0 ${size * 3}px ${color}`,
                                animation: `sparkFloat ${dur}s ease-in-out ${delay}s infinite`,
                            }} />
                        );
                    })}

                    {/* Radial glow */}
                    <div style={{
                        position: "absolute",
                        width: 320, height: 320,
                        borderRadius: "50%",
                        background: "radial-gradient(circle, rgba(59,130,246,0.18) 0%, rgba(59,130,246,0.06) 50%, transparent 70%)",
                        filter: "blur(20px)",
                    }} />

                    {/* Outer ring */}
                    <div style={{
                        position: "absolute",
                        width: 168, height: 168,
                        borderRadius: "50%",
                        border: "1.5px solid rgba(99,179,237,0.25)",
                        animation: "ringPulse 2.4s ease-in-out infinite",
                    }} />

                    {/* Inner ring */}
                    <div style={{
                        position: "absolute",
                        width: 128, height: 128,
                        borderRadius: "50%",
                        border: "1px solid rgba(147,197,253,0.20)",
                        animation: "ringPulse 2.4s ease-in-out 0.4s infinite",
                    }} />

                    {/* Logo circle */}
                    <div style={{
                        position: "relative",
                        width: 88, height: 88,
                        borderRadius: "50%",
                        background: "linear-gradient(145deg, #1e40af 0%, #1d4ed8 40%, #2563eb 70%, #3b82f6 100%)",
                        boxShadow: "0 0 0 1px rgba(147,197,253,0.35), 0 8px 32px rgba(37,99,235,0.55), 0 0 60px rgba(59,130,246,0.25)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        animation: "floatUp 0.7s cubic-bezier(0.16,1,0.3,1) both",
                        overflow: "hidden",
                    }}>
                        <img
                            src="/assets/spark-widget.png"
                            alt="Spark"
                            style={{
                                width: 72,
                                height: 72,
                                objectFit: "contain",
                                mixBlendMode: "screen",
                            }}
                        />
                        <div style={{
                            position: "absolute", inset: 0,
                            borderRadius: "50%",
                            background: "linear-gradient(145deg, rgba(255,255,255,0.18) 0%, transparent 60%)",
                            pointerEvents: "none",
                        }} />
                    </div>

                    {/* Text block */}
                    <div style={{
                        marginTop: 32,
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 10,
                        padding: "0 24px",
                    }}>
                        {/* App name shimmer */}
                        <div style={{
                            fontSize: 22,
                            fontWeight: 800,
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                            background: "linear-gradient(90deg, #60a5fa 0%, #93c5fd 20%, #ffffff 40%, #fbbf24 60%, #93c5fd 80%, #60a5fa 100%)",
                            backgroundSize: "200% auto",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            backgroundClip: "text",
                            animation: "shimmerSlide 2.8s linear infinite, floatUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.15s both",
                            fontFamily: "'Segoe UI', system-ui, sans-serif",
                        }}>
                            Spark Anywhere
                        </div>

                        {/* Divider */}
                        <div style={{
                            width: 48, height: 1.5,
                            borderRadius: 999,
                            background: "linear-gradient(90deg, transparent, rgba(99,179,237,0.6), transparent)",
                            animation: "floatUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.25s both",
                        }} />

                        {/* Tagline */}
                        <div style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "rgba(147,197,253,0.85)",
                            letterSpacing: "0.04em",
                            lineHeight: 1.6,
                            maxWidth: 260,
                            animation: "floatUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.35s both",
                            fontFamily: "'Segoe UI', system-ui, sans-serif",
                        }}>
                            Your One Stop Workplace Assistant
                        </div>

                        {/* Powered by badge */}
                        <div style={{
                            marginTop: 4,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "5px 13px",
                            borderRadius: 999,
                            background: "rgba(255,255,255,0.05)",
                            border: "1px solid rgba(99,179,237,0.20)",
                            animation: "floatUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.45s both",
                        }}>
                            <div style={{
                                width: 6, height: 6,
                                borderRadius: "50%",
                                background: "#34d399",
                                boxShadow: "0 0 8px #34d399",
                                animation: "ringPulse 1.8s ease-in-out infinite",
                            }} />
                            <span style={{
                                fontSize: 10,
                                fontWeight: 600,
                                color: "rgba(147,197,253,0.75)",
                                letterSpacing: "0.08em",
                                textTransform: "uppercase",
                                fontFamily: "'Segoe UI', system-ui, sans-serif",
                            }}>
                                Powered by Spark Assist
                            </span>
                        </div>
                    </div>

                    {/* Loading dots */}
                    <div style={{
                        marginTop: 36,
                        display: "flex",
                        gap: 8,
                        alignItems: "center",
                        animation: "floatUp 0.9s cubic-bezier(0.16,1,0.3,1) 0.55s both",
                    }}>
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} style={{
                                width: i === 1 || i === 2 ? 8 : 6,
                                height: i === 1 || i === 2 ? 8 : 6,
                                borderRadius: "50%",
                                background: i % 2 === 0 ? "#3b82f6" : "#60a5fa",
                                boxShadow: `0 0 10px ${i % 2 === 0 ? "#3b82f6" : "#60a5fa"}`,
                                animation: `dotBlink 1.4s ease-in-out ${i * 0.18}s infinite`,
                            }} />
                        ))}
                    </div>

                    {/* Footer */}
                    <div style={{
                        position: "absolute",
                        bottom: 24,
                        fontSize: 10,
                        color: "rgba(147,197,253,0.35)",
                        letterSpacing: "0.10em",
                        textTransform: "uppercase",
                        fontFamily: "'Segoe UI', system-ui, sans-serif",
                        animation: "floatUp 1s ease 0.8s both",
                    }}>
                        S&P Global · Enterprise AI Platform
                    </div>
                </div>
            )}

            <style>{`
                @keyframes sparkNudge {
                    0%   { transform: translateY(0) scale(1); }
                    18%  { transform: translateY(-2px) scale(1.06); }
                    36%  { transform: translateY(0) scale(1); }
                    54%  { transform: translateY(-2px) scale(1.06); }
                    72%  { transform: translateY(0) scale(1); }
                    100% { transform: translateY(0) scale(1); }                   
                }

                @keyframes softGlow {
                    0%   { box-shadow: 0 0 0 rgba(124,58,237,0); }
                    50%  { box-shadow: 0 10px 28px rgba(124,58,237,0.25); }
                    100% { box-shadow: 0 0 0 rgba(124,58,237,0); }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                @keyframes bounce {
                    0%, 80%, 100% { transform: translateY(0); opacity: 0.5; }
                    40%           { transform: translateY(-5px); opacity: 1; }
                }

                @keyframes spin {
                    to { transform: rotate(360deg); }
                }



                @keyframes sparkFloat {
    0%   { transform: translateY(0px) scale(1);   opacity: 0.6; }
    50%  { transform: translateY(-18px) scale(1.1); opacity: 1; }
    100% { transform: translateY(0px) scale(1);   opacity: 0.6; }
}
@keyframes ringPulse {
    0%   { transform: scale(0.92); opacity: 0.8; }
    50%  { transform: scale(1.08); opacity: 0.3; }
    100% { transform: scale(0.92); opacity: 0.8; }
}
@keyframes shimmerSlide {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
}
@keyframes floatUp {
    0%   { opacity: 0; transform: translateY(28px) scale(0.94); }
    100% { opacity: 1; transform: translateY(0)    scale(1);    }
}
@keyframes particleDrift {
    0%   { transform: translate(0, 0)      opacity: 0;   }
    10%  { opacity: 1; }
    90%  { opacity: 1; }
    100% { transform: translate(var(--tx), var(--ty)); opacity: 0; }
}
@keyframes dotBlink {
    0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
    40%           { opacity: 1;   transform: scale(1.2); }
}
@keyframes gradientShift {
    0%   { background-position: 0% 50%;   }
    50%  { background-position: 100% 50%; }
    100% { background-position: 0% 50%;   }
}


            `}</style>

        

            {/* Spark Store icon – fixed top-right */}
            {/* ── Unified App Header: Work|Web · Spark Store · New Chat ───── */}
                {!meetingId && (
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: isMobile ? "6px 10px 0" : "7px 14px",
                        flexWrap: "wrap",
                        position: "relative",
                        backgroundColor: isDark ? "#1e1e1e" : isContrast ? "#000" : "#ffffff",
                        borderBottom: showWelcome
                        ? (isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)")
                        : surfaceBorder,
                    flexShrink: 0,
                    gap: 8,
                    zIndex: 10,
                }}>

                        {/* Sidebar toggle button */}
                        {/* 🆕 ChatHistory feature flag check */}
                        {!meetingId && flags.chatHistory && (
                            <button
                                data-tour="sidebar-toggle"
                                type="button"
                                title={isSidebarOpen ? "Collapse history" : "Expand history"}
                                onClick={() => {
                                const next = !isSidebarOpen;
                                setIsSidebarOpen(next);
                                try { localStorage.setItem("spark_sidebar_open", String(next)); } catch { /* ignore */ }
                            }}
                            style={{
                                display: "flex", alignItems: "center", justifyContent: "center",
                                width: 30, height: 30,
                                borderRadius: 7,
                                border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                                cursor: "pointer",
                                color: textSecondary,
                                flexShrink: 0,
                                transition: "background 0.15s",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)"}
                            onMouseLeave={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)"}
                        >
                            {isSidebarOpen ? (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="15 18 9 12 15 6" />
                                </svg>
                            ) : (
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="9 18 15 12 9 6" />
                                </svg>
                            )}
                        </button>
                    )}

                        {/* CENTER — Work | Web | Sphere | People pill toggle */}
                        {/* On desktop: absolutely centered. On mobile: hidden here, rendered below as full-width row */}
                        {!isMobile && (
                            <div
                                data-tour="mode-toggle"
                                style={{
                                    position: "absolute",
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    display: "inline-flex",
                                    borderRadius: 7,
                                    border: isDark
                                        ? "1px solid rgba(255,255,255,0.11)"
                                        : "1px solid rgba(0,0,0,0.11)",
                                    overflow: "hidden",
                                    background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                                    zIndex: 1,
                                }}>
                                {[
                                    { key: "work",   label: "Work",     color: null,        title: "Work — Chat with your Selected Spark and Workplace integrations" },
                                    { key: "web",    label: "Web",      color: null,        title: "Web — search and answer questions using live public web results" },
                                    { key: "sphere", label: "Intranet", color: "#2563EB",   title: "Intranet — search S&P Global's internal enterprise content and documents" },
                                ].filter(({ key }) => key !== "people").map(({ key, label, color, title }) => {
                                    const isActive = searchMode === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            title={title}
                                            onClick={() => {
                                                        const previousMode = searchMode;
                                                        setSearchMode(key);

                                                        // Reset integrations/agents when leaving Work mode
                                                        if (previousMode === "work" && key !== "work") {
                                                            setSelectedIntegrations([]);
                                                            setSelectedAgents([]);
                                                        }

                                                        //Track search mode change
                                                        if (previousMode !== key) {
                                                    UsageLogService.trackEvent('SearchModeChanged', {
                                                        fromMode: previousMode,
                                                        toMode: key,
                                                        previousSparkId: selectedSpark?.sparkId,
                                                        previousSparkName: selectedSpark?.sparkName,
                                                        newSparkId: key === "web" ? 56534
                                                            : key === "sphere" ? 20685
                                                                : key === "people" ? 14651
                                                                    : selectedSpark?.sparkId,
                                                        newSparkName: key === "web" ? "Generic"
                                                            : key === "sphere" ? "Ask Sphere"
                                                                : key === "people" ? "Ask People - Solutions"
                                                                    : selectedSpark?.sparkName,
                                                    }, 'Navigation');

                                                    //Update spark context for locked modes
                                                    if (key === "web") {
                                                        UsageLogService.updateSparkContext(56534, "Generic");
                                                    } else if (key === "sphere") {
                                                        UsageLogService.updateSparkContext(20685, "Ask Sphere");
                                                    } else if (key === "people") {
                                                        UsageLogService.updateSparkContext(14651, "Ask People - Solutions");
                                                    }
                                                    // For "work" mode, keep current selected spark (already set)
                                                }
                                            }}
                                            style={{
                                                padding: "5px 18px",
                                                border: "none",
                                                cursor: "pointer",
                                                fontSize: 13,
                                                fontWeight: isActive ? 700 : 500,
                                                background: isActive
                                                    ? (color
                                                        ? `${color}22`
                                                        : (isDark ? "rgba(255,255,255,0.11)" : "rgba(0,0,0,0.08)"))
                                                    : "transparent",
                                                color: isActive ? (color || textColor) : textSecondary,
                                                transition: "all 0.15s ease",
                                            }}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Spacer pushes right group to the end */}
                        <div style={{ flex: 1 }} />

                    {/* RIGHT GROUP — Spark Store + New Chat */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

                            {/* Admin button — only for admin role */}
                            {isAdmin && (
                            <button
                                type="button"
                                title="Feature Flags Admin"
                                onClick={() => setShowAdminPanel(true)}
                                style={{
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    width: 28, height: 28, borderRadius: 7,
                                    border: isDark ? "1px solid rgba(20,184,166,0.30)" : "1px solid rgba(20,184,166,0.25)",
                                    background: "transparent",
                                    cursor: "pointer",
                                    color: "#14b8a6",
                                    flexShrink: 0,
                                    transition: "all 0.2s",
                                }}
                                onMouseEnter={e => {
                                    e.currentTarget.style.background = "rgba(20,184,166,0.12)";
                                    e.currentTarget.style.borderColor = "rgba(20,184,166,0.50)";
                                }}
                                onMouseLeave={e => {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.borderColor = isDark ? "rgba(20,184,166,0.30)" : "rgba(20,184,166,0.25)";
                                }}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="3" />
                                    <path d="M12 1v6m0 6v6M6 12H1m11 0h6" />
                                </svg>
                            </button>
                        )}

                        {/* Tour help button */}
                        <button
                            type="button"
                            title="Take a guided tour"
                            onClick={() => setShowTour(true)}
                            style={{
                                display: "flex", alignItems: "center", justifyContent: "center",
                                width: 28, height: 28, borderRadius: 7,
                                border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                background: "transparent",
                                cursor: "pointer",
                                color: textSecondary, flexShrink: 0,
                                transition: "background 0.15s",
                            }}
                            onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.07)"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                        >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                {/* Compass — intuitive "guided tour" metaphor */}
                                <circle cx="12" cy="12" r="10"/>
                                <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
                            </svg>
                        </button>

                        {/* 🆕 EWI Analytics Dashboard icon — feature flag: analyticsDashboard */}
                        {flags.analyticsDashboard && isAdmin && (
                            <button
                                type="button"
                                title="EWI Platform Analytics Dashboard"
                                onClick={() => setShowAnalyticsDashboard(true)}
                                style={{
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    width: 28, height: 28, borderRadius: 7,
                                    border: isDark ? "1px solid rgba(0,212,255,0.25)" : "1px solid rgba(0,150,180,0.20)",
                                    background: "transparent",
                                    cursor: "pointer",
                                    color: isDark ? "#00d4ff" : "#0891b2", flexShrink: 0,
                                    transition: "background 0.15s",
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = isDark ? "rgba(0,212,255,0.10)" : "rgba(0,150,180,0.08)"}
                                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <rect x="3" y="3" width="7" height="7" rx="1"/>
                                    <rect x="14" y="3" width="7" height="7" rx="1"/>
                                    <rect x="3" y="14" width="7" height="7" rx="1"/>
                                    <rect x="14" y="14" width="7" height="7" rx="1"/>
                                </svg>
                            </button>
                        )}

                        {/* Spark Store ghost button */}
                        {/* 🆕 SparkStore feature flag check */}
                        {flags.sparkStore.enabled && (
                            <button
                                data-tour="spark-store-btn"
                                type="button"
                                onClick={openSparkStore}
                                title="Spark Store"
                            style={{
                                display: "flex", alignItems: "center", gap: 7,
                                padding: "5px 11px",
                                borderRadius: 8,
                                border: isDark
                                    ? "1px solid rgba(255,255,255,0.10)"
                                    : "1px solid rgba(0,0,0,0.10)",
                                background: isDark
                                    ? "rgba(255,255,255,0.04)"
                                    : "rgba(0,0,0,0.03)",
                                color: textSecondary,
                                fontSize: 12, fontWeight: 600,
                                cursor: "pointer",
                                transition: "background 0.15s",
                                animation: sparkStoreNudge
                                    ? "sparkNudge 1.1s ease-in-out 0s 3"
                                    : "none",
                                whiteSpace: "nowrap",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = isDark
                                    ? "rgba(255,255,255,0.09)"
                                    : "rgba(0,0,0,0.07)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = isDark
                                    ? "rgba(255,255,255,0.04)"
                                    : "rgba(0,0,0,0.03)";
                            }}
                        >
                            <SparkStoreIcon size={16} />
                            {!isMobile && (
                                <span style={{ color: textSecondary }}>Spark Store</span>
                            )}
                        </button>
                        )}

                    {/* New Chat teal button */}
                        <button
                            data-tour="new-chat-btn"
                            type="button"
                            onClick={handleNewChat}
                            disabled={isSavingChat}
                            style={{
                                display: "flex", alignItems: "center", gap: 6,
                                padding: isMobile ? "5px 10px" : "6px 15px",
                                borderRadius: 8,
                                border: "none",
                                background: isSavingChat
                                    ? (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)")
                                    : "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                                color: isSavingChat ? textSecondary : "#ffffff",
                                fontSize: 12, fontWeight: 700,
                                cursor: isSavingChat ? "not-allowed" : "pointer",
                                boxShadow: isSavingChat ? "none" : "0 2px 10px rgba(8,145,178,0.30)",
                                transition: "all 0.15s ease",
                                whiteSpace: "nowrap",
                                opacity: isSavingChat ? 0.6 : 1,
                            }}
                            onMouseEnter={(e) => {
                                if (!isSavingChat) {
                                    e.currentTarget.style.background = "linear-gradient(135deg, #0e7490, #155e75)";
                                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(8,145,178,0.40)";
                                }
                            }}
                            onMouseLeave={(e) => {
                                if (!isSavingChat) {
                                    e.currentTarget.style.background = "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)";
                                    e.currentTarget.style.boxShadow = "0 2px 10px rgba(8,145,178,0.30)";
                                }
                            }}
                        >
                            {isSavingChat ? (
                                <>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: "spin 0.8s linear infinite" }}>
                                        <circle cx="12" cy="12" r="10" opacity="0.25" />
                                        <path d="M12 2 A10 10 0 0 1 22 12" opacity="1" />
                                    </svg>
                                    {!isMobile && "Saving..."}
                                </>
                            ) : (
                                <>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" strokeWidth="2.8" strokeLinecap="round">
                                        <line x1="12" y1="5" x2="12" y2="19" />
                                        <line x1="5" y1="12" x2="19" y2="12" />
                                    </svg>
                                    {!isMobile && "New Chat"}
                                </>
                            )}
                        </button>
                    </div>

                        {/* Mobile-only: full-width underline tab row (second row inside header) */}
                        {isMobile && (
                            <div style={{
                                width: "100%",
                                display: "flex",
                                marginTop: 4,
                                borderTop: isDark
                                    ? "1px solid rgba(255,255,255,0.06)"
                                    : "1px solid rgba(0,0,0,0.06)",
                            }}>
                                {[
                                    { key: "work", label: "Work", color: null },
                                    { key: "web", label: "Web", color: null },
                                    { key: "sphere", label: "Sphere", color: "#2563EB" },
                                    { key: "people", label: "People", color: "#06B6D4" },
                                ].map(({ key, label, color }) => {
                                    const isActive = searchMode === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={() => {
                                                    setSearchMode(key);
                                                    if (searchMode === "work" && key !== "work") {
                                                        setSelectedIntegrations([]);
                                                        setSelectedAgents([]);
                                                    }
                                                }}
                                                style={{
                                                    flex: 1,
                                                    padding: "7px 4px",
                                                    border: "none",
                                                    borderBottom: isActive
                                                    ? `2px solid ${color || (isDark ? "#fff" : "#242424")}`
                                                    : "2px solid transparent",
                                                cursor: "pointer",
                                                fontSize: 13,
                                                fontWeight: isActive ? 700 : 500,
                                                background: "transparent",
                                                color: isActive ? (color || textColor) : textSecondary,
                                                transition: "all 0.15s ease",
                                            }}
                                        >
                                            {label}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Meeting Header */}


            {!!meetingId && (() => {
                // ── Live recording timer (inline — no extra useState needed if you already
                //    have one; if not, add these two lines near your other state declarations:
                //      const [recElapsed, setRecElapsed] = React.useState("0:00");
                //      const [dotVisible, setDotVisible] = React.useState(true);
                //    and these two useEffects — OR just replace with the component version
                //    in MeetingSttToolbar.jsx which handles it for you)

                const hasTranscript = !!transcript;
                const hasSTTResult = !!meetingSttResult;

                const accentColor = "#5b5ef4";
                const dangerColor = "#e53e3e";
                const successColor = "#22c55e";
                const amberColor = "#f59e0b";

                const cardBg = isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.025)";
                const cardBorder = isDark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.08)";
                const divider = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
                const btnBase = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)";
                const btnHover = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)";

                const statusColor = isMeetingRecording ? dangerColor
                    : isMeetingTranscribing ? accentColor
                        : hasSTTResult ? successColor
                            : meetingSttError ? amberColor
                                : textSecondary;

                const statusLabel = isMeetingRecording ? "Recording in progress"
                    : isMeetingTranscribing ? "Processing audio…"
                        : hasSTTResult ? `Transcript ready · ${meetingSttResult?.utterances?.length ?? 0} segments`
                            : meetingSttError ? "Recording error"
                                : hasTranscript ? `Teams transcript loaded · ${transcript.length.toLocaleString()} chars`
                                    : "Ready";

                // Shared ghost icon-button style
                const iconBtn = (disabled, hoverColor) => ({
                    display: "flex", alignItems: "center", justifyContent: "center",
                    width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                    border: `1px solid ${cardBorder}`,
                    background: "transparent",
                    color: disabled ? textSecondary : textColor,
                    cursor: disabled ? "not-allowed" : "pointer",
                    opacity: disabled ? 0.38 : 1,
                    fontSize: 15,
                    transition: "background 0.15s",
                });

                return (
                    <div style={{
                        borderBottom: surfaceBorder,
                        backgroundColor: isDark ? "#1e1e1e" : isContrast ? "#0a0a0a" : "#f8f8fc",
                    }}>
                        {/* ── Title bar ─────────────────────────────────────────────────── */}
                        <div style={{
                            display: "flex", alignItems: "center",
                            padding: "10px 14px 8px",
                            gap: 8,
                        }}>
                            {/* Spark logo mark */}
                            <div style={{
                                width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                                background: `linear-gradient(135deg, ${accentColor} 0%, #4f46e5 100%)`,
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 14,
                            }}>
                                ✦
                            </div>
                            <span style={{ fontWeight: 700, fontSize: 14, color: textColor, flex: 1 }}>
                                Spark Meet
                            </span>

                            {/* ── EWI Analytics Dashboard icon ─────────────────────────── */}
                            {flags.analyticsDashboard && isAdmin && (
                            <MeetIconBtn
                                title="EWI Platform Analytics Dashboard"
                                onClick={() => setShowAnalyticsDashboard(true)}
                                isDark={isDark} cardBorder={cardBorder} textSecondary={textSecondary} textColor={textColor}
                                color={accentColor}
                            >
                                📊
                            </MeetIconBtn>
                            )}
                        </div>
                        <div style={{
                            display: "flex", alignItems: "center",
                            margin: "0 14px",
                            padding: "6px 10px",
                            borderRadius: 8,
                            background: cardBg,
                            border: `1px solid ${cardBorder}`,
                            gap: 7,
                            marginBottom: 10,
                        }}>
                            {/* Status dot */}
                            <span style={{
                                width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
                                background: statusColor,
                                boxShadow: isMeetingRecording
                                    ? `0 0 0 3px ${dangerColor}33`
                                    : "none",
                                animation: isMeetingRecording ? "sparkPulse 1.2s ease-in-out infinite" : "none",
                            }} />
                            <span style={{
                                flex: 1, fontSize: 11.5, fontWeight: 500, color: statusColor,
                                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                            }}>
                                {statusLabel}
                            </span>

                            {/* Live timer — only while recording */}
                            {isMeetingRecording && meetingRecordingStartedAt && (
                                <RecordingTimer
                                    startedAt={meetingRecordingStartedAt}
                                    color={dangerColor}
                                />
                            )}
                        </div>

                        {/* ── Primary record button ─────────────────────────────────────── */}
                        <div style={{ padding: "0 14px", marginBottom: 8 }}>
                            <button
                                type="button"
                                onClick={isMeetingRecording ? stopMeetingSTT : startMeetingSTT}
                                disabled={isMeetingTranscribing}
                                style={{
                                    width: "100%", height: 38, borderRadius: 10, border: "none",
                                    cursor: isMeetingTranscribing ? "not-allowed" : "pointer",
                                    fontWeight: 700, fontSize: 13, letterSpacing: "0.1px",
                                    display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
                                    opacity: isMeetingTranscribing ? 0.55 : 1,
                                    transition: "opacity 0.15s, box-shadow 0.15s",
                                    background: isMeetingRecording
                                        ? `linear-gradient(135deg, ${dangerColor}, #c53030)`
                                        : `linear-gradient(135deg, ${accentColor}, #4f46e5)`,
                                    color: "#fff",
                                    boxShadow: isMeetingRecording
                                        ? `0 2px 14px rgba(229,62,62,0.4)`
                                        : `0 2px 14px rgba(91,94,244,0.35)`,
                                }}
                            >
                                {isMeetingTranscribing ? (
                                    <>⏳ Processing audio…</>
                                ) : isMeetingRecording ? (
                                    <><SvgStop /> Stop Recording</>
                                ) : (
                                    <><SvgMic /> Start Recording</>
                                )}
                            </button>
                        </div>

                        {/* ── Secondary icon row ───────────────────────────────────────── */}
                        <div style={{
                            display: "flex", alignItems: "center",
                            padding: "0 14px", marginBottom: 10,
                            gap: 5,
                        }}>
                            {/* Load Teams transcript */}
                            <MeetIconBtn
                                title={isLoadingTranscript ? "Loading…" : "Load Teams transcript"}
                                onClick={loadTranscript}
                                disabled={isLoadingTranscript || isMeetingRecording}
                                isDark={isDark} cardBorder={cardBorder} textSecondary={textSecondary} textColor={textColor}
                            >
                                {isLoadingTranscript ? "⏳" : "📥"}
                            </MeetIconBtn>

                            {/* View raw Teams transcript */}
                            <MeetIconBtn
                                title="View raw Teams transcript"
                                onClick={() => setShowTranscriptViewer(true)}
                                disabled={!hasTranscript}
                                isDark={isDark} cardBorder={cardBorder} textSecondary={textSecondary} textColor={textColor}
                            >
                                👁️
                            </MeetIconBtn>

                            {/* Generate insights from Teams transcript */}
                            <MeetIconBtn
                                title="Generate AI insights from Teams transcript"
                                onClick={generateInsights}
                                disabled={!hasTranscript || isGeneratingInsights || isMeetingTranscribing}
                                color={accentColor}
                                isDark={isDark} cardBorder={cardBorder} textSecondary={textSecondary} textColor={textColor}
                            >
                                {isGeneratingInsights ? "⏳" : "💡"}
                            </MeetIconBtn>

                            {/* View STT transcript + insights */}
                            <MeetIconBtn
                                title="View STT transcript & insights"
                                onClick={() => {
                                    setShowSttViewer(true);

                                    // Track STT viewer opened
                                    UsageLogService.trackFeatureUsage(
                                        'SttViewer',
                                        'opened',
                                        {
                                            utteranceCount: meetingSttResult?.utterances?.length || 0,
                                            speakerCount: new Set((meetingSttResult?.utterances || []).map(u => u.speakerTag)).size,
                                            transcriptLength: meetingSttResult?.fullTranscript?.length || 0,
                                            provider: sttProvider
                                        }
                                    );
                                }}
                                disabled={!hasSTTResult}
                                color={accentColor}
                                isDark={isDark} cardBorder={cardBorder} textSecondary={textSecondary} textColor={textColor}
                            >
                                🗣️
                            </MeetIconBtn>

                            {/* STT Provider toggle — G / K pill, lives here not in title bar */}
                            {/* 🆕 KenshoGoogleSwitch feature flag check */}
                            {flags.kenshoGoogleSwitch && (
                                <div style={{
                                    display: "flex", borderRadius: 99,
                                    border: `1px solid ${cardBorder}`,
                                    overflow: "hidden", flexShrink: 0,
                                }}>
                                    {[
                                        { key: "google", label: "Google STT", color: "#4285f4", title: "Google Cloud STT" },
                                        { key: "kensho", label: "Kensho Scribe", color: successColor, title: "Kensho Scribe STT" },
                                    ].map(p => (
                                        <button
                                            key={p.key}
                                            type="button"
                                            title={p.title}
                                            onClick={() => {
                                                const previousProvider = sttProvider;
                                                setSttProvider(p.key);

                                                // Track STT provider change
                                                if (previousProvider !== p.key) {
                                                    UsageLogService.trackEvent('SttProviderChanged', {
                                                        fromProvider: previousProvider,
                                                        toProvider: p.key,
                                                        location: meetingId ? 'MeetingSTT' : 'MessageSTT'
                                                    }, 'Feature');
                                                }
                                            }}
                                            disabled={isMeetingRecording || isMeetingTranscribing}
                                            style={{
                                                padding: "4px 9px", border: "none", cursor: "pointer",
                                                fontSize: 11, fontWeight: 700, lineHeight: "18px",
                                                background: sttProvider === p.key
                                                    ? (isDark ? "rgba(255,255,255,0.11)" : "rgba(0,0,0,0.07)")
                                                    : "transparent",
                                                color: sttProvider === p.key ? p.color : textSecondary,
                                                transition: "background 0.15s, color 0.15s",
                                                opacity: (isMeetingRecording || isMeetingTranscribing) ? 0.4 : 1,
                                            }}
                                        >
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Flex spacer */}
                            <div style={{ flex: 1 }} />

                            {/* Start Over */}
                            {/* Refresh / reset STT session — clears transcript + sessionStorage */}
                            <MeetIconBtn
                                title="Refresh — clear STT transcript and reset session"
                                onClick={clearMeetingState}
                                disabled={isMeetingRecording || isMeetingTranscribing}
                                danger dangerColor={dangerColor}
                                isDark={isDark} cardBorder={cardBorder} textSecondary={textSecondary} textColor={textColor}
                            >
                                🔄
                            </MeetIconBtn>

                            {/* Start Over — clear chat messages */}
                            {messages.length > 1 && (
                                <MeetIconBtn
                                    title="Start over (clear chat messages)"
                                    onClick={handleStartOver}
                                    disabled={isMeetingRecording}
                                    danger dangerColor={dangerColor}
                                    isDark={isDark} cardBorder={cardBorder} textSecondary={textSecondary} textColor={textColor}
                                >
                                    ↺
                                </MeetIconBtn>
                            )}
                        </div>

                        {/* ── Error banner ─────────────────────────────────────────────── */}
                        {meetingSttError && (
                            <div style={{
                                margin: "0 14px 10px",
                                padding: "7px 10px",
                                borderRadius: 8,
                                background: "rgba(245,158,11,0.1)",
                                border: "1px solid rgba(245,158,11,0.2)",
                                fontSize: 11, color: amberColor, lineHeight: 1.5,
                            }}>
                                ⚠️ {meetingSttError}
                            </div>
                        )}

                        {/* Animations */}
                        <style>{`
                @keyframes sparkPulse {
                    0%, 100% { box-shadow: 0 0 0 2px ${dangerColor}44; }
                    50%       { box-shadow: 0 0 0 5px ${dangerColor}11; }
                }
            `}</style>
                    </div>
                );
            })()}

            {/* Messages Area with Ultra-Compact Welcome Screen */}
            {/* Messages Area — Copilot-style */}
            <div style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                overflowY: showWelcome ? "hidden" : "auto",
                overflowX: "hidden",
                padding: showWelcome ? 0 : (isMobile ? 8 : 12),
            }}>

                {/* ── COPILOT-STYLE WELCOME SCREEN ──────────────────────── */}
                {showWelcome && (() => {
                    return (
                    <div style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: isMobile ? "16px 14px 32px" : "0 24px 48px",
                        animation: "fadeIn 0.35s ease-out",
                        minHeight: 0,
                    }}>

                        {/* Heading — Spark icon + personalised time greeting */}
                        <div style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 14,
                            marginBottom: 26,
                        }}>
                            {/* Spark brand image from assets — transparent outline version */}
                            <img
                                src="/assets/spark-icon-welcome.png"
                                alt="Spark Assist"
                                style={{
                                    width: isMobile ? 72 : 96,
                                    height: isMobile ? 72 : 96,
                                    objectFit: "contain",
                                    // In dark mode: invert(1) flips white→black then hue-rotate(180deg)
                                    // restores the original hues, eliminating the baked-in white square.
                                    // brightness(0.95) softens the result to match the dark UI tone.
                                    filter: isDark
                                        ? "invert(1) hue-rotate(180deg) brightness(0.95)"
                                        : "none",
                                }}
                                onError={(e) => {
                                    // Fallback to inline SVG if image not found
                                    const el = document.createElement("div");
                                    el.innerHTML = `<svg width="${isMobile ? 72 : 96}" height="${isMobile ? 72 : 96}" viewBox="0 0 100 100" fill="none"><path d="M 93 50 C 93 55, 85.7 56.3, 84.2 61.1 C 82.7 65.9, 87.7 71.3, 84.8 75.3 C 81.9 79.3, 75.2 76.2, 71.2 79.1 C 67.2 82, 68.1 89.4, 63.3 90.9 C 58.5 92.4, 55 86, 50 86 C 45 86, 41.5 92.4, 36.7 90.9 C 31.9 89.4, 32.8 82, 28.8 79.1 C 24.8 76.2, 18.1 79.3, 15.2 75.3 C 12.3 71.3, 17.3 65.9, 15.8 61.1 C 14.3 56.3, 7 55, 7 50 C 7 45, 14.3 43.7, 15.8 38.9 C 17.3 34.1, 12.3 28.7, 15.2 24.7 C 18.1 20.7, 24.8 23.8, 28.8 20.9 C 32.8 18, 31.9 10.6, 36.7 9.1 C 41.5 7.6, 45 14, 50 14 C 55 14, 58.5 7.6, 63.3 9.1 C 68.1 10.6, 67.2 18, 71.2 20.9 C 75.2 23.8, 81.9 20.7, 84.8 24.7 C 87.7 28.7, 82.7 34.1, 84.2 38.9 C 85.7 43.7, 93 45, 93 50 Z" stroke="#0891b2" stroke-width="3.5" fill="none"/><path d="M 34 70 L 50 27 L 66 70 M 41 52 L 59 52" stroke="#0891b2" stroke-width="5" fill="none" stroke-linecap="round" stroke-linejoin="round"/><path d="M 33 67 L 64 30" stroke="#0891b2" stroke-width="4" fill="none" stroke-linecap="round"/></svg>`;
                                    e.target.replaceWith(el.firstChild);
                                }}
                            />
                            {/* Time-based personalised greeting */}
                            <div style={{
                                fontSize: isMobile ? 22 : 30,
                                fontWeight: 700,
                                color: textColor,
                                textAlign: "center",
                                letterSpacing: "-0.3px",
                                lineHeight: 1.2,
                            }}>
                                {timeGreeting}{firstName ? `, ${firstName}` : ""}
                            </div>
                        </div>

                        {/* ── Centered input card ─────────────────────────── */}
                        {/* ── Centered input card ─────────────────────────── */}
                            <div
                                data-tour="composer"
                                style={{
                                    width: "100%",
                                    maxWidth: 860,
                                    borderRadius: 26,
                                    border: isDark
                                        ? "1.5px solid rgba(255,255,255,0.11)"
                                        : "1.5px solid rgba(0,0,0,0.10)",
                                    background: isDark ? "rgba(34,34,34,0.99)" : "#ffffff",
                                    boxShadow: isDark
                                        ? "0 10px 50px rgba(0,0,0,0.45)"
                                        : "0 10px 44px rgba(0,0,0,0.08)",
                                    padding: "22px 26px 16px",
                                    position: "relative",
                                }}>

                            {/* ── Picker dropdown — renders inside welcome card ── */}
                            {pickerMode && searchMode === "work" && (
                                <div style={{
                                    marginBottom: 10,
                                    border: pickerBorder,
                                    borderRadius: 12,
                                    background: pickerBg,
                                    color: pickerText,
                                    maxHeight: 280,
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                    boxShadow: isDark ? "0 12px 28px rgba(0,0,0,0.5)" : "0 12px 28px rgba(0,0,0,0.18)",
                                }}>
                                    {/* ── Pinned search box for Spark mode ── */}
                                    {pickerMode === "spark" ? (
                                        <div style={{ padding: "8px 8px 4px", flexShrink: 0 }}>
                                            <div style={{
                                                display: "flex", alignItems: "center", gap: 6,
                                                padding: "0 10px", height: 32, borderRadius: 8,
                                                background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
                                                border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.11)",
                                            }}>
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={textTertiary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                                </svg>
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    value={sparkChipSearch}
                                                    onChange={e => { setSparkChipSearch(e.target.value); setPickerQuery(e.target.value); }}
                                                    onKeyDown={e => { if (e.key === "Escape") { setPickerMode(null); setPickerQuery(""); setSparkChipSearch(""); } }}
                                                    placeholder="Search sparks…"
                                                    style={{
                                                        flex: 1, border: "none", outline: "none",
                                                        background: "transparent",
                                                        fontSize: 12, color: textColor,
                                                        caretColor: "#0891b2",
                                                    }}
                                                />
                                                {sparkChipSearch && (
                                                    <button type="button" onClick={() => { setSparkChipSearch(""); setPickerQuery(""); }}
                                                        style={{ border: "none", background: "transparent", cursor: "pointer", color: textTertiary, fontSize: 14, lineHeight: 1, padding: 0, display: "flex", alignItems: "center" }}>×</button>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: 11, color: textTertiary, padding: "8px 8px 4px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0 }}>
                                            {pickerMode === "agent"
                                                ? `Select Agents${pickerQuery ? ` (filter: "${pickerQuery}")` : ""}`
                                                : pickerMode === "workplace-agent"
                                                    ? `Select Workplace Agents${pickerQuery ? ` (filter: "${pickerQuery}")` : ""}`
                                                    : `Select Integrations${pickerQuery ? ` (filter: "${pickerQuery.replace("/", "")}")` : ""}`}
                                        </div>
                                    )}
                                    <div style={{ overflowY: "auto", flex: 1, padding: "0 8px 8px" }}>

                                    {pickerMode === "spark" ? (
                                        <>
                                            {filteredSparks.length === 0 ? (
                                                <div style={{ padding: "9px 12px", fontSize: 13, color: textSecondary }}>No matches</div>
                                            ) : (
                                                filteredSparks.map((s) => (
                                                    <div key={s.sparkId} onClick={() => selectSpark(s)}
                                                        style={{ padding: "8px 10px", borderRadius: 10, cursor: "pointer", display: "flex", gap: 10, alignItems: "flex-start", transition: "background 0.12s ease" }}
                                                        onMouseEnter={(e) => (e.currentTarget.style.background = pickerHover)}
                                                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                                                        {/* Accent dot */}
                                                        <div style={{
                                                            width: 32, height: 32, borderRadius: 9, flexShrink: 0, marginTop: 1,
                                                            background: s.accent ? `${s.accent}22` : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                                                            border: `1px solid ${s.accent ? s.accent + "44" : (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)")}`,
                                                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                            fontSize: 13, color: s.accent || primaryIconColor,
                                                        }}>
                                                            ✦
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            {/* Row 1: name + certified + likes */}
                                                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                                <div style={{ fontWeight: 700, fontSize: 13, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                                                                    {s.sparkName}
                                                                </div>
                                                                {s.certified && (
                                                                    <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 999, background: isDark ? "rgba(16,185,129,0.18)" : "rgba(16,185,129,0.12)", color: "#10b981", fontWeight: 700, flexShrink: 0 }}>
                                                                        ✓ Certified
                                                                    </span>
                                                                )}
                                                                {s.likes > 0 && (
                                                                    <span style={{ fontSize: 11, color: textTertiary, flexShrink: 0 }}>★ {s.likes}</span>
                                                                )}
                                                            </div>
                                                            {/* Row 2: creator */}
                                                            {s.creatorName && (
                                                                <div style={{ fontSize: 11, color: textTertiary, marginTop: 1 }}>by {s.creatorName}</div>
                                                            )}
                                                            {/* Row 3: description preview */}
                                                            {s.description && (
                                                                <div style={{ fontSize: 11, color: textSecondary, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    {s.description.length > 70 ? s.description.slice(0, 70) + "…" : s.description}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))
                                            )}
                                            {/* Footer hint when showing default (unfiltered) list */}
                                            {!pickerQuery && allSparks.length > SPARK_PICKER_DEFAULT_LIMIT && (
                                                <div style={{ padding: "6px 10px 4px", fontSize: 11, color: textTertiary, borderTop: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)", marginTop: 4, textAlign: "center" }}>
                                                    Showing top {SPARK_PICKER_DEFAULT_LIMIT} of {allSparks.length} sparks — type to search all
                                                </div>
                                            )}
                                        </>
                                    ) : pickerMode === "agent" ? (
                                        filteredAgents.map((x) => {
                                            const checked = selectedAgents.includes(x.key);
                                            return (
                                                <div key={x.key} onClick={() => toggleAgent(x.key)}
                                                    style={{ padding: "9px 12px", borderRadius: 10, cursor: "pointer", fontSize: 13, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", background: checked ? (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)") : "transparent", transition: "background 0.12s ease" }}
                                                    onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = pickerHover; }}
                                                    onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = "transparent"; }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                        <span style={{ width: 18, height: 18, borderRadius: 4, border: isDark ? "1.5px solid rgba(255,255,255,0.4)" : "1.5px solid rgba(0,0,0,0.3)", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, color: checked ? primaryIconColor : textTertiary }}>
                                                            {checked ? "✓" : ""}
                                                        </span>
                                                        <span style={{ color: textColor }}>{x.label}</span>
                                                    </div>
                                                    <div style={{ fontSize: 12, color: textTertiary }}>{checked ? "Selected" : "Select"}</div>
                                                </div>
                                            );
                                        })
                                    ) : pickerMode === "workplace-agent" ? (
                                        <>
                                            {workplaceAgentFlags?.allowSelectAll !== false && (
                                                <div
                                                    onClick={selectAllWorkplaceAgents}
                                                    style={{ padding: "9px 12px", borderRadius: 10, cursor: "pointer", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center", background: selectedWorkplaceAgents.length === WORKPLACE_AGENTS.length && WORKPLACE_AGENTS.length > 0 ? (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)") : "transparent" }}>
                                                    <span style={{ color: textColor, fontWeight: 700 }}>Select All</span>
                                                    <span style={{ fontSize: 12, color: textTertiary }}>{selectedWorkplaceAgents.length === WORKPLACE_AGENTS.length && WORKPLACE_AGENTS.length > 0 ? "Selected" : "Select"}</span>
                                                </div>
                                            )}
                                            {filteredWorkplaceAgents.map((x) => {
                                                const checked = selectedWorkplaceAgents.includes(x.key);
                                                return (
                                                    <div key={x.key}
                                                        onClick={() => toggleWorkplaceAgent(x.key)}
                                                        style={{ padding: "9px 12px", borderRadius: 10, cursor: "pointer", fontSize: 13, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", background: checked ? (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)") : "transparent", transition: "background 0.12s ease" }}
                                                        onMouseEnter={(e) => { if (!checked) e.currentTarget.style.background = pickerHover; }}
                                                        onMouseLeave={(e) => { if (!checked) e.currentTarget.style.background = "transparent"; }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                            <div style={{ width: 32, height: 32, borderRadius: 8, background: checked ? `${x.color || "#0078D4"}22` : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"), border: checked ? `1.5px solid ${x.color || "#0078D4"}66` : (isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)"), display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                                {x.icon}
                                                            </div>
                                                            <span style={{ color: textColor, fontWeight: checked ? 700 : 500 }}>{x.label}</span>
                                                        </div>
                                                        <div style={{ fontSize: 12, color: textTertiary }}>{checked ? "Selected" : "Select"}</div>
                                                    </div>
                                                );
                                            })}
                                        </>
                                    ) : (
                                        filteredIntegrations.map((x) => {
                                            const checked = selectedIntegrations.includes(x.key);
                                            const isDisabledByWorkplaceAgents = selectedWorkplaceAgents.length > 0;
                                            return (
                                                <div key={x.key}
                                                    onClick={() => {
                                                        if (isDisabledByWorkplaceAgents) return; // Prevent click if disabled
                                                        if (x.key === "ado") { setPickerMode(null); setPickerQuery(""); openAdoBoardSearch(); return; }
                                                        toggleIntegration(x.key);
                                                    }}
                                                    style={{ padding: "9px 12px", borderRadius: 10, cursor: isDisabledByWorkplaceAgents ? "not-allowed" : "pointer", fontSize: 13, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", background: checked ? (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)") : "transparent", transition: "background 0.12s ease", opacity: isDisabledByWorkplaceAgents ? 0.4 : 1, pointerEvents: isDisabledByWorkplaceAgents ? "none" : "auto" }}
                                                    onMouseEnter={(e) => { if (!checked && !isDisabledByWorkplaceAgents) e.currentTarget.style.background = pickerHover; }}
                                                    onMouseLeave={(e) => { if (!checked && !isDisabledByWorkplaceAgents) e.currentTarget.style.background = "transparent"; }}
                                                    title={isDisabledByWorkplaceAgents ? "Integrations disabled when Workplace Agents are selected" : ""}
                                                >
                                                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                        <div style={{ width: 32, height: 32, borderRadius: 8, background: checked ? `${x.color}22` : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"), border: checked ? `1.5px solid ${x.color}66` : (isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)"), display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                                            {x.icon}
                                                        </div>
                                                        <span style={{ color: textColor, fontWeight: checked ? 700 : 500 }}>{x.label}</span>
                                                    </div>
                                                    <div style={{ fontSize: 12, color: textTertiary }}>{checked ? "Selected" : "Select"}</div>
                                                </div>
                                            );
                                        })
                                    )}
                                    </div>
                                </div>
                            )}

                            {/* ── Active integration + agent pills ── */}
                            {searchMode === "work" && (selectedIntegrations.length > 0 || selectedAgents.length > 0 || selectedWorkplaceAgents.length > 0) && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                                    {selectedIntegrations.map((k) => (
                                        <div key={k} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px", borderRadius: 999, background: chipBg, border: chipBorder, color: chipText, fontSize: 12 }}>
                                            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                                <span style={{ display: "inline-flex", alignItems: "center" }}>{INTEGRATIONS.find(i => i.key === k)?.icon}</span>
                                                <span style={{ fontWeight: 700 }}>{integrationLabelByKey[k]}</span>
                                            </span>
                                            <button type="button" onClick={() => removeIntegrationPill(k)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, color: chipText, fontSize: 16, lineHeight: "14px", fontWeight: 700 }}>×</button>
                                        </div>
                                    ))}
                                    {selectedAgents.map((k) => (
                                        <div key={k} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px", borderRadius: 999, background: chipBg, border: chipBorder, color: chipText, fontSize: 12 }}>
                                            <span style={{ fontWeight: 700 }}>{agentLabelByKey[k]}</span>
                                            <button type="button" onClick={() => removeAgentPill(k)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, color: chipText, fontSize: 16, lineHeight: "14px", fontWeight: 700 }}>×</button>
                                        </div>
                                    ))}
                                    {selectedWorkplaceAgents.map((k) => {
                                        const waMeta = WORKPLACE_AGENT_ICONS[k];
                                        return (
                                            <div key={`wa-${k}`} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px", borderRadius: 999, background: chipBg, border: chipBorder, color: chipText, fontSize: 12 }}>
                                                <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                                    {waMeta?.icon && <span style={{ display: "inline-flex", alignItems: "center" }}>{waMeta.icon}</span>}
                                                    <span style={{ fontWeight: 700 }}>{workplaceAgentLabelByKey[k] || k}</span>
                                                </span>
                                                <button type="button" onClick={() => removeWorkplaceAgentPill(k)} style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, color: chipText, fontSize: 16, lineHeight: "14px", fontWeight: 700 }}>×</button>
                                            </div>
                                        );
                                    })}
                                    {/* Debug toggle — persistent pill, visible whenever WA agents are selected */}
                                    {isWorkplaceAgentFrameworkEnabled && selectedWorkplaceAgents.length > 0 && (
                                        <button
                                            type="button"
                                            title={showWaDebugLogs ? "Hide debug logs" : "Show debug logs"}
                                            onClick={() => {
                                                const next = !showWaDebugLogs;
                                                setShowWaDebugLogs(next);
                                                try { localStorage.setItem("spark_wa_debug_logs", next ? "1" : "0"); } catch { /* ignore */ }
                                            }}
                                            style={{
                                                display: "inline-flex", alignItems: "center", gap: 5,
                                                background: showWaDebugLogs
                                                    ? (isDark ? "rgba(8,145,178,0.20)" : "rgba(8,145,178,0.12)")
                                                    : chipBg,
                                                border: showWaDebugLogs
                                                    ? "1px solid rgba(8,145,178,0.45)"
                                                    : chipBorder,
                                                borderRadius: 999, padding: "4px 10px",
                                                cursor: "pointer", transition: "all 0.15s",
                                                fontSize: 12, fontWeight: 700,
                                                color: showWaDebugLogs ? (isDark ? "#67e8f9" : "#0e7490") : chipText,
                                            }}
                                        >
                                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                                <polyline points="2,4 5,6 2,8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                <line x1="6" y1="8" x2="10" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                            </svg>
                                            {showWaDebugLogs ? "Debug On" : "Debug"}
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Text input */}
                                <Input
                                    style={{
                                        width: "100%",
                                        fontSize: 16,
                                        background: "transparent",
                                        color: isDark || isContrast ? "#e8e8e8" : "#242424",
                                        border: "none",
                                        minHeight: 56,
                                        padding: "6px 0",
                                        caretColor: isDark || isContrast ? "#e8e8e8" : "#242424",
                                        WebkitTextFillColor: isDark || isContrast ? "#e8e8e8" : "#242424",
                                    }}
                                    value={draft}
                                    placeholder={
                                        searchMode === "work"
                                            ? "Ask your question (type \"@\" to switch sparks, \"/\" for integrations)"
                                            : searchMode === "web"
                                                ? "Ask anything — answer from live web results"
                                                : searchMode === "sphere"
                                                    ? "Search Sphere — find content on the S&P Global intranet"
                                                    : "Ask People — HR, wellness and people queries"
                                    }
                                onChange={(e) => handleDraftChange(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        if (pickerMode === "spark" && filteredSparks.length > 0) {
                                            selectSpark(filteredSparks[0]);
                                            return;
                                        }
                                        send();
                                    }
                                    if (e.key === "Escape") {
                                        setPickerMode(null);
                                        setPickerQuery("");
                                        setShareMenuForMessageId(null);
                                    }
                                }}
                            />

                            {/* Thin divider */}
                            <div style={{ height: 1, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)", margin: "10px 0 8px" }} />

                            {/* ── Bottom row: chips left · mic+send right ── */}
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, flexWrap: "wrap" }}>

                                {/* LEFT — Spark · Integrations · Agents chips (Work only) */}
                                <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>

                                        {/* ── Quick Spark Preset button (+) ── */}
                                        {searchMode === "work" && (
                                            <div style={{ position: "relative" }}>
                                                <button
                                                    type="button"
                                                    title="Quick-select Classic Chat or Default Spark"
                                                    onClick={() => setQuickSparkOpen(o => !o)}
                                                    style={{
                                                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                        width: 32, height: 32, borderRadius: "50%",
                                                        border: quickSparkOpen
                                                            ? "1.5px solid rgba(8,145,178,0.55)"
                                                            : (isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(0,0,0,0.16)"),
                                                        background: quickSparkOpen
                                                            ? "rgba(8,145,178,0.15)"
                                                            : (isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"),
                                                        color: quickSparkOpen ? "#0891b2" : (isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.55)"),
                                                        fontSize: 18, lineHeight: 1, cursor: "pointer",
                                                        transition: "all 0.15s ease",
                                                        flexShrink: 0,
                                                    }}
                                                >+</button>
                                                {quickSparkOpen && (
                                                    <>
                                                        <div style={{ position: "fixed", inset: 0, zIndex: 890 }} onClick={() => setQuickSparkOpen(false)} />
                                                        <div style={{
                                                            position: "absolute", bottom: "calc(100% + 8px)", left: 0,
                                                            zIndex: 891, minWidth: 280,
                                                            background: isDark ? "#1e1e2e" : "#ffffff",
                                                            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                                            borderRadius: 12, boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.55)" : "0 8px 32px rgba(0,0,0,0.14)",
                                                            padding: "6px",
                                                            animation: "cspIn 0.15s cubic-bezier(0.16,1,0.3,1)",
                                                        }}>
                                                            <div style={{ fontSize: 10, fontWeight: 700, color: isDark ? "rgba(255,255,255,0.35)" : "#9ca3af", textTransform: "uppercase", letterSpacing: "0.6px", padding: "4px 10px 6px" }}>Quick Select</div>
                                                            {PRESET_SPARKS.map(p => {
                                                                const isActive = selectedSpark?.sparkId === p.sparkId;
                                                                return (
                                                                    <div key={p.sparkId} onClick={() => selectPresetSpark(p)}
                                                                        style={{
                                                                            display: "flex", alignItems: "flex-start", gap: 10,
                                                                            padding: "9px 10px", borderRadius: 8, cursor: "pointer",
                                                                            background: isActive ? "rgba(8,145,178,0.12)" : "transparent",
                                                                            border: isActive ? "1px solid rgba(8,145,178,0.25)" : "1px solid transparent",
                                                                            transition: "background 0.12s",
                                                                        }}
                                                                        onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
                                                                        onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                                                                    >
                                                                        <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{p.icon}</span>
                                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                                            <div style={{ fontSize: 13, fontWeight: 700, color: isActive ? "#0891b2" : (isDark ? "#fff" : "#111827") }}>
                                                                                {p.sparkName}
                                                                                {isActive && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#0891b2", background: "rgba(8,145,178,0.12)", padding: "1px 6px", borderRadius: 4 }}>active</span>}
                                                                            </div>
                                                                            <div style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,0.45)" : "#6b7280", marginTop: 2, lineHeight: 1.4 }}>{p.desc}</div>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {searchMode === "work" && isWorkplaceAgentFrameworkEnabled && (
                                            <div
                                                onClick={openWorkplaceAgentPickerFromChip}
                                                style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px", borderRadius: 999, background: chipBg, border: chipBorder, color: chipText, cursor: (selectedIntegrations.length > 0 || selectedAgents.length > 0) ? "default" : "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", opacity: (selectedIntegrations.length > 0 || selectedAgents.length > 0) ? 0.4 : 1, pointerEvents: (selectedIntegrations.length > 0 || selectedAgents.length > 0) ? "none" : "auto", transition: "all 0.12s ease" }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                                    <rect x="2" y="2" width="9" height="9" rx="2" fill="#f25022" />
                                                    <rect x="13" y="2" width="9" height="9" rx="2" fill="#7fba00" />
                                                    <rect x="2" y="13" width="9" height="9" rx="2" fill="#00a4ef" />
                                                    <rect x="13" y="13" width="9" height="9" rx="2" fill="#ffb900" />
                                                </svg>
                                                {selectedWorkplaceAgents.length > 0 && (
                                                    <span style={{ fontWeight: 700, color: primaryIconColor }}>{selectedWorkplaceAgents.length}</span>
                                                )}
                                                <span style={{ opacity: 0.7 }}>▾</span>
                                            </div>
                                        )}

                                        {/* Spark chip — only show in Work mode */}
                                        {searchMode === "work" && (
                                            <div
                                                onClick={openSparkPickerFromChip}
                                                style={{
                                                    display: "inline-flex", alignItems: "center", gap: 4,
                                                    padding: "5px 9px", borderRadius: 999,
                                                    background: chipBg, border: chipBorder, color: chipText,
                                                    cursor: "pointer",
                                                    fontSize: 11, fontWeight: 600,
                                                    whiteSpace: "nowrap", maxWidth: 140,
                                                    overflow: "hidden",
                                                    pointerEvents: (selectedIntegrations.length > 0 || selectedWorkplaceAgents.length > 0) ? "none" : "auto",
                                                    opacity: (selectedIntegrations.length > 0 || selectedWorkplaceAgents.length > 0) ? 0.4 : 1,
                                                    transition: "all 0.12s ease",
                                                }}
                                            >
                                                <span style={{ fontWeight: 800 }}>⚡</span>
                                                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 80, opacity: 0.9 }}>
                                                    {selectedSpark?.sparkName || "Content Generator"}
                                                </span>
                                                <span style={{ opacity: 0.7 }}>▾</span>
                                            </div>
                                        )}

                                        {/* Integrations chip — Work mode only */}
                                        {searchMode === "work" && false && (
                                            <div
                                                data-tour="integrations-chip"
                                                onClick={openIntegrationPickerFromChip}
                                                style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px", borderRadius: 999, background: chipBg, border: chipBorder, color: chipText, cursor: selectedWorkplaceAgents.length > 0 ? "default" : "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", opacity: selectedWorkplaceAgents.length > 0 ? 0.4 : 1, pointerEvents: selectedWorkplaceAgents.length > 0 ? "none" : "auto", transition: "all 0.12s ease" }}
                                            >
                                                <svg width="13" height="13" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                                                    <rect x="1" y="1" width="7" height="7" rx="1.5" fill="#0078d4" />
                                                    <rect x="10" y="1" width="7" height="7" rx="1.5" fill="#0078d4" opacity="0.7" />
                                                    <rect x="1" y="10" width="7" height="7" rx="1.5" fill="#0078d4" opacity="0.7" />
                                                    <rect x="10" y="10" width="7" height="7" rx="1.5" fill="#0078d4" opacity="0.5" />
                                                </svg>
                                                {selectedIntegrations.length > 0 && (
                                                    <span style={{ fontWeight: 700, color: primaryIconColor }}>{selectedIntegrations.length}</span>
                                                )}
                                                <span style={{ opacity: 0.7 }}>▾</span>
                                            </div>
                                        )}

                                    {/* Agents chip — Work mode only + feature flag check */}
                                    {searchMode === "work" && flags.agents && (
                                        <div
                                            onClick={openAgentPickerFromChip}
                                            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 9px", borderRadius: 999, background: chipBg, border: chipBorder, color: chipText, cursor: selectedWorkplaceAgents.length > 0 ? "default" : "pointer", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap", opacity: selectedWorkplaceAgents.length > 0 ? 0.4 : 1, pointerEvents: selectedWorkplaceAgents.length > 0 ? "none" : "auto", transition: "all 0.12s ease" }}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="7" r="4" />
                                                <path d="M9 21v-2a4 4 0 0 1 4-4h0" />
                                                <path d="M9 15l1.5 2L12 16l1.5 1L15 15" strokeWidth="1.5" />
                                                <path d="M12 16v5M11 17.5l1 1.5 1-1.5" strokeWidth="1.5" />
                                            </svg>
                                            {selectedAgents.length > 0 && (
                                                <span style={{ fontWeight: 700, color: primaryIconColor }}>{selectedAgents.length}</span>
                                            )}
                                            <span style={{ opacity: 0.7 }}>▾</span>
                                        </div>
                                    )}
                                </div>

                                {/* RIGHT — AI Settings + Mic + Send */}
                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    {/* AI SETTINGS SLIDERS BUTTON — only shown for Classic Chat */}
                                    {selectedSpark?.sparkId === 1 && (() => {
                                        const hasCustom = modelSettings.modelId !== "GPT-4.1" || modelSettings.styleKey !== "Balanced" || modelSettings.responseLength !== "Medium";
                                        return (
                                            <div style={{ position: "relative" }}>
                                                <button
                                                    ref={modelSettingsBtnRef}
                                                    type="button"
                                                    title="AI Settings — model, style & length"
                                                    onClick={(e) => toggleModelSettings(e)}
                                                    data-model-popup
                                                    style={{
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        width: 36, height: 36, borderRadius: 10, cursor: "pointer",
                                                        border: isModelSettingsOpen ? "1.5px solid rgba(8,145,178,0.55)" : hasCustom ? "1.5px solid rgba(8,145,178,0.38)" : (isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)"),
                                                        background: isModelSettingsOpen ? "rgba(8,145,178,0.15)" : hasCustom ? "rgba(8,145,178,0.08)" : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                                                        color: isModelSettingsOpen || hasCustom ? "#0891b2" : (isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.55)"),
                                                        transition: "all 0.15s ease", position: "relative",
                                                    }}
                                                >
                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                                                        <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
                                                        <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
                                                        <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
                                                        <line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/>
                                                    </svg>
                                                    {hasCustom && !isModelSettingsOpen && (
                                                        <div style={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: "50%", background: "#0891b2", boxShadow: "0 0 4px rgba(8,145,178,0.8)" }} />
                                                    )}
                                                </button>
                                            </div>
                                        );
                                    })()}

                                    {/* 🆕 Voice feature flag check */}
                                    {flags.voice && (
                                        <IconButton
                                            title={isTranscribing ? "Transcribing…" : isListening ? "Stop recording" : "Speak your message"}
                                            onClick={isListening ? stopSpeechCapture : startSpeechCapture}
                                            disabled={isTranscribing}
                                            primary
                                            style={{
                                                background: isListening ? "rgba(220,38,38,0.12)" : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                                                border: isListening ? "1px solid rgba(220,38,38,0.40)" : isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
                                                animation: isListening ? "ringPulse 1.2s ease-in-out infinite" : "none",
                                            }}
                                        >
                                            {isTranscribing ? (
                                                <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px solid rgba(234,179,8,0.3)", borderTopColor: "#eab308", animation: "spin 0.7s linear infinite" }} />
                                            ) : (
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ color: isListening ? "#ef4444" : primaryIconColor }}>
                                                    <rect x="9" y="2" width="6" height="12" rx="3" stroke="currentColor" strokeWidth="2" />
                                                    <path d="M5 10a7 7 0 0 0 14 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                    <line x1="12" y1="17" x2="12" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                    <line x1="9" y1="21" x2="15" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                </svg>
                                            )}
                                        </IconButton>
                                    )}

                                    <IconButton
                                        title={canSend ? "Send" : "Type a message to send"}
                                        onClick={send}
                                        disabled={!canSend}
                                        primary
                                        style={{
                                            background: canSend ? (isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)") : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                                            border: canSend ? "1px solid rgba(0,120,212,0.35)" : isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
                                            transition: "all 0.15s ease",
                                        }}
                                    >
                                        <span style={{ fontSize: 18, color: canSend ? primaryIconColor : iconColor }}>➤</span>
                                    </IconButton>
                                </div>
                            </div>
                        </div>

                        {/* ── Prompt suggestion pills ──────────────────────── */}
                        <div style={{
                            display: "flex",
                            gap: 8,
                            marginTop: 12,
                            flexWrap: "wrap",
                            justifyContent: "center",
                            width: "100%",
                            maxWidth: 860,
                        }}>
                            {WELCOME_PROMPTS.slice(0, isMobile ? 2 : 3).map((prompt) => (
                                <div
                                    key={prompt.id}
                                    onClick={() => handleWelcomePromptClick(prompt)}
                                    style={{
                                        flex: "1 1 0",
                                        minWidth: isMobile ? "44%" : 150,
                                        maxWidth: 240,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "8px 14px 8px 10px",
                                        borderRadius: 999,
                                        background: isDark
                                            ? "rgba(255,255,255,0.05)"
                                            : "rgba(0,0,0,0.03)",
                                        border: isDark
                                            ? `1.5px solid rgba(255,255,255,0.10)`
                                            : `1.5px solid rgba(0,0,0,0.09)`,
                                        cursor: "pointer",
                                        transition: "all 0.18s ease",
                                        overflow: "hidden",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.background = isDark ? `${prompt.accent}18` : `${prompt.accent}10`;
                                        e.currentTarget.style.borderColor = prompt.accent;
                                        e.currentTarget.style.transform = "translateY(-2px)";
                                        e.currentTarget.style.boxShadow = `0 4px 14px ${prompt.accent}28`;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)";
                                        e.currentTarget.style.borderColor = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.09)";
                                        e.currentTarget.style.transform = "translateY(0)";
                                        e.currentTarget.style.boxShadow = "none";
                                    }}
                                >
                                    {/* Accent icon dot */}
                                    <div style={{
                                        width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
                                        background: `${prompt.accent}18`,
                                        border: `1px solid ${prompt.accent}35`,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        fontSize: 13,
                                    }}>
                                        {prompt.icon}
                                    </div>

                                    {/* Title */}
                                    <span style={{
                                        flex: 1, minWidth: 0,
                                        fontSize: 12, fontWeight: 600, color: textColor,
                                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                    }}>
                                        {prompt.title}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Browse sparks link */}
                        {/* 🆕 SparkStore feature flag check */}
                        {flags.sparkStore.enabled && (
                            <button
                                type="button"
                                onClick={openSparkStore}
                                style={{
                                    marginTop: 18,
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    fontSize: 12,
                                    color: primaryIconColor,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    opacity: 0.75,
                                    transition: "opacity 0.15s",
                                    padding: "4px 8px",
                                    borderRadius: 6,
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                                onMouseLeave={(e) => e.currentTarget.style.opacity = "0.75"}
                            >
                                <SparkStoreIcon size={13} />
                                Browse all Sparks
                            </button>
                        )}
                    </div>
                    );
                })()}

                {/* Regular Messages - Only show if NOT showing welcome */}
                {!showWelcome && messages.map((m) => {
                    const isUser = m.role === "user";
                    const isAssistant = !isUser;
                    const feedback = isAssistant ? feedbackByMessageId[m.id] : null;

                    return (
                        <div
                            key={m.id}
                            style={{
                                display: "flex",
                                justifyContent: isUser ? "flex-end" : "flex-start",
                                alignItems: "flex-start",
                                gap: isAssistant ? 9 : 0,
                                marginBottom: 10,
                            }}
                        >
                            {/* ── Spark avatar — left of every assistant bubble ── */}
                            {/* ── Spark avatar — left of every assistant bubble ── */}
                            {isAssistant && (
                                <div style={{
                                    width: 34,
                                    height: 34,
                                    borderRadius: "50%",
                                    overflow: "hidden",
                                    flexShrink: 0,
                                    marginTop: 4,
                                    background: "#0e7490",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}>
                                    <img
                                        src="/assets/spark-icon.png"
                                        alt="Spark"
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                        onError={(e) => { e.target.style.display = "none"; }}
                                    />
                                </div>
                            )}
                            <div
                                style={{
                                    maxWidth: m.isEmailDraftMessage ? "min(640px, 96vw)" : "85%",
                                    width: m.isEmailDraftMessage ? "min(640px, 96vw)" : undefined,
                                    padding: "11px 13px",
                                    borderRadius: 12,
                                    background: isUser ? userMessageBg : assistantMessageBg,
                                    whiteSpace: m.isEmailDraftMessage ? "normal" : "pre-wrap",
                                    wordBreak: "break-word",
                                    border: isDark || isContrast ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: 12,
                                        color: textTertiary,
                                        marginBottom: 7,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                    }}
                                >
                                    <div style={{ flex: 1, fontWeight: 600 }}>{isUser ? "You" : "Spark Assist"}</div>

                                    {isAssistant && (
                                        <div data-tour="message-actions" style={{ display: "inline-flex", gap: 6, alignItems: "center", position: "relative" }}>
                                            <IconButton title="Download as .txt" onClick={() => downloadTextAsFile(m.text, "spark-response")} disabled={!m.text} primary>
                                                <DownloadIcon />
                                            </IconButton>

                                            <IconButton title="Copy" onClick={() => copyToClipboard(m.text)} disabled={!m.text}>
                                                <CopyIcon />
                                            </IconButton>

                                            {/* Inline share action icons — always visible, no dropdown needed */}
                                            <div style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 2,
                                                padding: "2px 4px",
                                                borderRadius: 8,
                                                border: isDark
                                                    ? "1px solid rgba(255,255,255,0.07)"
                                                    : "1px solid rgba(0,0,0,0.07)",
                                                background: isDark
                                                    ? "rgba(255,255,255,0.04)"
                                                    : "rgba(0,0,0,0.03)",
                                            }}>
                                                {/* Email */}
                                                <button type="button" title="Send Email"
                                                    onClick={() => openEmailModalForText(m.text)}
                                                    disabled={!m.text}
                                                    style={{
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        width: 28, height: 28, borderRadius: 6,
                                                        border: "none", background: "transparent",
                                                        cursor: m.text ? "pointer" : "not-allowed",
                                                        opacity: m.text ? 1 : 0.35,
                                                        color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)",
                                                        transition: "background 0.13s, color 0.13s",
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)";
                                                        e.currentTarget.style.color = "#0078d4";
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.background = "transparent";
                                                        e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)";
                                                    }}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <rect x="2" y="4" width="20" height="16" rx="2" />
                                                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                                                    </svg>
                                                </button>

                                                {/* Teams */}
                                                <button type="button" title="Post to Teams"
                                                    onClick={() => postToTeams(m.text)}
                                                    disabled={!m.text}
                                                    style={{
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        width: 28, height: 28, borderRadius: 6,
                                                        border: "none", background: "transparent",
                                                        cursor: m.text ? "pointer" : "not-allowed",
                                                        opacity: m.text ? 1 : 0.35,
                                                        color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)",
                                                        transition: "background 0.13s, color 0.13s",
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)";
                                                        e.currentTarget.style.color = "#5059C9";
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.background = "transparent";
                                                        e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)";
                                                    }}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                        <path d="M14.5 6.5a2 2 0 1 0-4 0 2 2 0 0 0 4 0Z" fill="currentColor" />
                                                        <path d="M18 8h-1.5a1.5 1.5 0 1 1 0-3H18a1.5 1.5 0 0 1 0 3Z" fill="currentColor" opacity="0.7" />
                                                        <path d="M12.5 9H6a2 2 0 0 0-2 2v5a4 4 0 0 0 4 4h3a4 4 0 0 0 4-4v-5a2 2 0 0 0-2-2H12.5Z" fill="currentColor" />
                                                    </svg>
                                                </button>

                                                {/* OneDrive */}
                                                <button type="button" title="Upload to OneDrive"
                                                    onClick={() => uploadToOneDrive(m.text)}
                                                    disabled={!m.text}
                                                    style={{
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        width: 28, height: 28, borderRadius: 6,
                                                        border: "none", background: "transparent",
                                                        cursor: m.text ? "pointer" : "not-allowed",
                                                        opacity: m.text ? 1 : 0.35,
                                                        color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)",
                                                        transition: "background 0.13s, color 0.13s",
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)";
                                                        e.currentTarget.style.color = "#0078D4";
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.background = "transparent";
                                                        e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)";
                                                    }}
                                                >
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                        <path d="M9.5 13.5 5 11.2A5.5 5.5 0 0 1 14.8 7a4 4 0 0 1 5.2 3.8v.2l-10.5 2.5Z" fill="currentColor" />
                                                        <path d="M9.5 13.5 20 11l.5 1A4 4 0 0 1 16.5 18H7.5a3.5 3.5 0 0 1-2.5-6l4.5 1.5Z" fill="currentColor" opacity="0.75" />
                                                    </svg>
                                                </button>

                                                {/* ADO */}
                                                {/* 🆕 Sharing.ado feature flag check */}
                                                {flags.sharing.ado && (
                                                    <button type="button" title="Add to ADO"
                                                        onClick={() => addToAdo(m.text)}
                                                        disabled={!m.text}
                                                        style={{
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            width: 28, height: 28, borderRadius: 6,
                                                            border: "none", background: "transparent",
                                                            cursor: m.text ? "pointer" : "not-allowed",
                                                            opacity: m.text ? 1 : 0.35,
                                                            color: isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)",
                                                            transition: "background 0.13s, color 0.13s",
                                                        }}
                                                        onMouseEnter={e => {
                                                            e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)";
                                                            e.currentTarget.style.color = "#0078D4";
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.currentTarget.style.background = "transparent";
                                                            e.currentTarget.style.color = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.45)";
                                                        }}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                            <path d="M3 6l5-3 8 3.5V21l-8-3.5L3 20V6Z" fill="currentColor" />
                                                            <path d="M16 6.5L21 4v14l-5 2.5V6.5Z" fill="currentColor" opacity="0.5" />
                                                            <path d="M8 3l8 3.5v14L8 17V3Z" fill="currentColor" opacity="0.8" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {renderRichText(m.text)}

                                {/* ── Premium Outlook-style email compose card (Message 2) ── */}
                                {m.isEmailDraftMessage && m.emailDraft && !dismissedActionIds[`draft-${m.id}`] && (() => {
                                    const draft = m.emailDraft;
                                    const ed = draftEdits[m.id] || {};
                                    const to      = ed.to      !== undefined ? ed.to      : (draft.to      || draft.To      || "");
                                    const subject = ed.subject !== undefined ? ed.subject : (draft.subject || draft.Subject || "");
                                    // Body: use AI draft body; if the signature placeholder exists, replace it with the real user name
                                    const rawBody = draft.body || draft.Body || "";
                                    const sigName = userName || "Spark Assist User";
                                    const bodyWithSig = rawBody.includes("{{user_name}}")
                                        ? rawBody.replace(/\{\{user_name\}\}/g, sigName)
                                        : rawBody;
                                    const body = ed.body !== undefined ? ed.body : bodyWithSig;
                                    const isSending = !!ed.sending;
                                    const isSent    = !!ed.sent;
                                    const setField = (field, val) =>
                                        setDraftEdits(prev => ({ ...prev, [m.id]: { ...(prev[m.id] || {}), [field]: val } }));

                                    const sendDraft = async () => {
                                        if (!to || !subject) return;
                                        setField("sending", true);
                                        try {
                                            await ApiClient.post("/api/workplace-agents/execute", {
                                                stepId: `draft-send-${m.id}`,
                                                agentName: "outlook",
                                                action: "send",
                                                isApproved: true,
                                                workflowId: `draft-${m.id}`,
                                                validatedParameters: { to, subject, body }
                                            });
                                            setField("sent", true);
                                            setField("sending", false);
                                        } catch (e) {
                                            setField("sending", false);
                                            alert(`Failed to send email: ${e.message}`);
                                        }
                                    };

                                    // ── Design tokens ──────────────────────────────────────────────
                                    const cardBorder   = isDark ? "rgba(14,165,233,0.30)" : "rgba(14,116,185,0.22)";
                                    const cardBg       = isDark ? "rgba(14,165,233,0.05)" : "#ffffff";
                                    const cardShadow   = isDark ? "0 4px 24px rgba(0,0,0,0.45)" : "0 4px 20px rgba(14,116,185,0.10)";
                                    const headerBg     = isDark ? "rgba(14,165,233,0.13)" : "rgba(14,116,185,0.07)";
                                    const headerBorder = isDark ? "rgba(14,165,233,0.22)" : "rgba(14,116,185,0.14)";
                                    const fieldBorder  = isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.09)";
                                    const fieldDivider = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
                                    const inputBg      = isDark ? "rgba(255,255,255,0.04)" : "rgba(248,250,252,1)";
                                    const inputFocusBg = isDark ? "rgba(255,255,255,0.07)" : "#ffffff";
                                    const labelColor   = isDark ? "rgba(148,163,184,1)" : "rgba(100,116,139,1)";
                                    const accentBlue   = isDark ? "#38bdf8" : "#0369a1";
                                    const accentGreen  = isDark ? "#4ade80" : "#15803d";
                                    const btnPrimary   = isDark ? "rgba(14,165,233,0.25)" : "rgba(14,116,185,0.12)";
                                    const btnPrimaryHov= isDark ? "rgba(14,165,233,0.38)" : "rgba(14,116,185,0.22)";
                                    const footerBg     = isDark ? "rgba(255,255,255,0.03)" : "rgba(248,250,252,1)";

                                    if (isSent) {
                                        return (
                                            <div style={{ marginTop: 16, borderRadius: 14, border: `1px solid ${cardBorder}`, background: cardBg, boxShadow: cardShadow, overflow: "hidden" }}>
                                                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 18px", background: headerBg, borderBottom: `1px solid ${headerBorder}` }}>
                                                    <span style={{ fontSize: 17 }}>✅</span>
                                                    <span style={{ fontSize: 13, fontWeight: 700, color: accentGreen }}>Email sent successfully</span>
                                                </div>
                                                <div style={{ padding: "12px 18px", fontSize: 12.5, color: labelColor, lineHeight: 1.6 }}>
                                                    Your email was delivered to <strong style={{ color: textColor }}>{to}</strong>.
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div style={{ marginTop: 16, borderRadius: 14, border: `1px solid ${cardBorder}`, background: cardBg, boxShadow: cardShadow, overflow: "hidden" }}>

                                            {/* ── Titlebar ────────────────────────────────── */}
                                            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 16px 11px 18px", background: headerBg, borderBottom: `1px solid ${headerBorder}` }}>
                                                {/* Outlook-style envelope icon */}
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, color: accentBlue }}>
                                                    <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" fill="none"/>
                                                    <path d="M2 7.5l10 6.5 10-6.5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="none"/>
                                                </svg>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: 12.5, fontWeight: 700, color: accentBlue, letterSpacing: 0.1 }}>✉️ New Message</div>
                                                    <div style={{ fontSize: 10.5, color: labelColor, marginTop: 1 }}>AI-drafted · review before sending</div>
                                                </div>
                                                <button type="button" title="Discard draft"
                                                    onClick={() => setDismissedActionIds(prev => ({ ...prev, [`draft-${m.id}`]: true }))}
                                                    style={{ border: "none", background: "transparent", cursor: "pointer", color: labelColor, fontSize: 18, lineHeight: 1, padding: "2px 4px", borderRadius: 5, transition: "color 0.15s" }}
                                                    onMouseEnter={e => e.currentTarget.style.color = textColor}
                                                    onMouseLeave={e => e.currentTarget.style.color = labelColor}
                                                >×</button>
                                            </div>

                                            {/* ── Address fields ──────────────────────────── */}
                                            <div style={{ borderBottom: `1px solid ${fieldDivider}` }}>
                                                {/* To */}
                                                <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${fieldDivider}` }}>
                                                    <span style={{ fontSize: 11.5, fontWeight: 600, color: labelColor, minWidth: 66, padding: "10px 0 10px 18px", borderRight: `1px solid ${fieldDivider}` }}>To</span>
                                                    <input
                                                        value={to}
                                                        onChange={e => setField("to", e.target.value)}
                                                        placeholder="recipient@company.com"
                                                        style={{ flex: 1, fontSize: 12.5, padding: "9px 14px", border: "none", background: "transparent", color: textColor, outline: "none", minWidth: 0 }}
                                                    />
                                                </div>
                                                {/* Subject */}
                                                <div style={{ display: "flex", alignItems: "center" }}>
                                                    <span style={{ fontSize: 11.5, fontWeight: 600, color: labelColor, minWidth: 66, padding: "10px 0 10px 18px", borderRight: `1px solid ${fieldDivider}` }}>Subject</span>
                                                    <input
                                                        value={subject}
                                                        onChange={e => setField("subject", e.target.value)}
                                                        style={{ flex: 1, fontSize: 12.5, fontWeight: 500, padding: "9px 14px", border: "none", background: "transparent", color: textColor, outline: "none", minWidth: 0 }}
                                                    />
                                                </div>
                                            </div>

                                            {/* ── Body ────────────────────────────────────── */}
                                            <div style={{ padding: "14px 18px 10px", background: inputBg }}>
                                                <textarea
                                                    value={body}
                                                    onChange={e => {
                                                        setField("body", e.target.value);
                                                        e.target.style.height = "auto";
                                                        e.target.style.height = e.target.scrollHeight + "px";
                                                    }}
                                                    ref={el => {
                                                        if (el && el.style.height === "") {
                                                            el.style.height = "auto";
                                                            el.style.height = Math.max(el.scrollHeight, 220) + "px";
                                                        }
                                                    }}
                                                    onFocus={e => e.currentTarget.parentElement.style.background = inputFocusBg}
                                                    onBlur={e => e.currentTarget.parentElement.style.background = inputBg}
                                                    style={{
                                                        width: "100%",
                                                        fontSize: 13,
                                                        lineHeight: 1.75,
                                                        fontFamily: "'Segoe UI', system-ui, sans-serif",
                                                        padding: "0",
                                                        border: "none",
                                                        background: "transparent",
                                                        color: textColor,
                                                        outline: "none",
                                                        resize: "none",
                                                        whiteSpace: "pre-wrap",
                                                        minHeight: 220,
                                                        overflowY: "hidden",
                                                        boxSizing: "border-box",
                                                    }}
                                                />
                                            </div>

                                            {/* ── Footer / toolbar ────────────────────────── */}
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 18px 12px", background: footerBg, borderTop: `1px solid ${fieldDivider}` }}>
                                                {/* Sender hint */}
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" style={{ color: labelColor, flexShrink: 0 }}>
                                                        <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.8" fill="none"/>
                                                        <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" fill="none"/>
                                                    </svg>
                                                    <span style={{ fontSize: 11, color: labelColor }}>{sigName}</span>
                                                </div>
                                                {/* Action buttons */}
                                                <div style={{ display: "flex", gap: 8 }}>
                                                    <button type="button"
                                                        onClick={() => setDismissedActionIds(prev => ({ ...prev, [`draft-${m.id}`]: true }))}
                                                        style={{ padding: "6px 16px", borderRadius: 8, cursor: "pointer", border: `1px solid ${fieldBorder}`, background: "transparent", color: labelColor, fontSize: 12, fontWeight: 600, transition: "all 0.15s" }}
                                                        onMouseEnter={e => { e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; e.currentTarget.style.color = textColor; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = labelColor; }}
                                                    >🗑 Discard</button>
                                                    <button type="button" onClick={sendDraft} disabled={isSending || !to || !subject}
                                                        style={{
                                                            display: "flex", alignItems: "center", gap: 6,
                                                            padding: "6px 18px", borderRadius: 8,
                                                            cursor: isSending || !to || !subject ? "not-allowed" : "pointer",
                                                            border: `1px solid ${accentBlue}55`,
                                                            background: btnPrimary,
                                                            color: accentBlue, fontSize: 12, fontWeight: 700,
                                                            opacity: isSending || !to || !subject ? 0.55 : 1,
                                                            transition: "all 0.15s"
                                                        }}
                                                        onMouseEnter={e => { if (!isSending && to && subject) e.currentTarget.style.background = btnPrimaryHov; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = btnPrimary; }}
                                                    >
                                                        {isSending ? (
                                                            <>
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ animation: "spin 1s linear infinite" }}>
                                                                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" strokeDasharray="28 56" fill="none"/>
                                                                </svg>
                                                                Sending…
                                                            </>
                                                        ) : (
                                                            <>
                                                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                                                    <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                    <path d="M22 2L15 22l-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                                </svg>
                                                                Send
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* ── Pending Write Action button (e.g. Send Email from Workplace Agent) ── */}
                                {m.pendingWriteAction && !dismissedActionIds[`pwa-${m.id}`] && (() => {
                                    const pwa = m.pendingWriteAction;
                                    const isEmail = /outlook|email|mail/i.test(pwa.agent || pwa.agentName || "");
                                    const isTeams = /teams/i.test(pwa.agent || pwa.agentName || "");
                                    const accentColor = isTeams ? (isDark ? "#a5b4fc" : "#5b5ea6") : (isDark ? "#67e8f9" : "#0e7490");
                                    const accentBg   = isTeams ? (isDark ? "rgba(98,100,167,0.12)" : "rgba(98,100,167,0.07)") : (isDark ? "rgba(8,145,178,0.10)" : "rgba(8,145,178,0.06)");
                                    const accentBorder = isTeams ? (isDark ? "rgba(98,100,167,0.35)" : "rgba(98,100,167,0.22)") : (isDark ? "rgba(8,145,178,0.30)" : "rgba(8,145,178,0.20)");
                                    const btnBg      = isTeams ? (isDark ? "rgba(98,100,167,0.20)" : "rgba(98,100,167,0.10)") : (isDark ? "rgba(8,145,178,0.18)" : "rgba(8,145,178,0.10)");
                                    const label = isEmail ? "Send Email" : isTeams ? "Post to Teams" : `${pwa.action || "Send"}`;
                                    return (
                                        <div style={{
                                            marginTop: 10,
                                            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                                            padding: "8px 10px", borderRadius: 9,
                                            background: accentBg,
                                            border: `1px solid ${accentBorder}`,
                                        }}>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, color: accentColor }}>
                                                <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" fill="none" />
                                                <path d="M2 8l10 7 10-7" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="none" />
                                            </svg>
                                            <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: isDark ? "rgba(255,255,255,0.80)" : "rgba(0,0,0,0.70)" }}>
                                                {isEmail ? `Ready to send this summary as an email${pwa.recipientHint ? ` to ${pwa.recipientHint}` : ""}?` : `Ready to ${label}?`}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setWriteActionDialog(pwa)}
                                                style={{
                                                    padding: "4px 14px", borderRadius: 7, cursor: "pointer",
                                                    border: `1px solid ${accentBorder}`,
                                                    background: btnBg,
                                                    color: accentColor,
                                                    fontSize: 12, fontWeight: 700,
                                                    transition: "opacity 0.13s",
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.opacity = "0.80"}
                                                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                            >
                                                {label}
                                            </button>
                                            <button
                                                type="button"
                                                title="Dismiss"
                                                onClick={() => setDismissedActionIds(prev => ({ ...prev, [`pwa-${m.id}`]: true }))}
                                                style={{
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                                                    border: "none", background: "transparent", cursor: "pointer",
                                                    color: textSecondary, fontSize: 14, lineHeight: 1,
                                                    transition: "opacity 0.13s",
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.opacity = "0.60"}
                                                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                            >✕</button>
                                        </div>
                                    );
                                })()}

                                {/* ── Suggested Action Banner ── */}
                                {m.suggestedAction?.type === "send_email" && !dismissedActionIds[m.id] && (() => {
                                    const hint = m.suggestedAction.recipientHint;
                                    const label = hint
                                        ? `Send this as an email to ${hint}?`
                                        : "Send this as an email?";
                                    return (
                                        <div style={{
                                            marginTop: 10,
                                            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                                            padding: "8px 10px", borderRadius: 9,
                                            background: isDark ? "rgba(8,145,178,0.10)" : "rgba(8,145,178,0.06)",
                                            border: `1px solid ${isDark ? "rgba(8,145,178,0.30)" : "rgba(8,145,178,0.20)"}`,
                                        }}>
                                            {/* Envelope icon */}
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, color: isDark ? "#67e8f9" : "#0e7490" }}>
                                                <rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="1.8" fill="none" />
                                                <path d="M2 8l10 7 10-7" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="none" />
                                            </svg>
                                            <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: isDark ? "rgba(255,255,255,0.80)" : "rgba(0,0,0,0.70)" }}>
                                                {label}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    // Pre-fill email modal: body = full response text,
                                                    // To = recipient hint (left blank if just a name — user completes it),
                                                    // Subject = first line of the response or a sensible default
                                                    const firstLine = m.text.split("\n").find(l => l.trim().length > 5) ?? "";
                                                    const subject = firstLine.replace(/^#+\s*/, "").slice(0, 80) ||
                                                        `Spark Summary – ${selectedSpark?.sparkName ?? "Spark"}`;
                                                    setEmailTo("");
                                                    setEmailSubject(subject);
                                                    setEmailBody(m.text);
                                                    setShowEmailModal(true);
                                                    // Auto-focus the To field after modal opens
                                                    setTimeout(() => {
                                                        const toInput = document.querySelector("[data-email-to-input]");
                                                        if (toInput) toInput.focus();
                                                    }, 120);
                                                }}
                                                style={{
                                                    padding: "4px 12px", borderRadius: 7, cursor: "pointer",
                                                    border: `1px solid ${isDark ? "rgba(8,145,178,0.50)" : "rgba(8,145,178,0.35)"}`,
                                                    background: isDark ? "rgba(8,145,178,0.18)" : "rgba(8,145,178,0.10)",
                                                    color: isDark ? "#67e8f9" : "#0e7490",
                                                    fontSize: 12, fontWeight: 700,
                                                    transition: "opacity 0.13s",
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.opacity = "0.80"}
                                                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                            >
                                                Open Email
                                            </button>
                                            {/* Dismiss */}
                                            <button
                                                type="button"
                                                title="Dismiss"
                                                onClick={() => setDismissedActionIds(prev => ({ ...prev, [m.id]: true }))}
                                                style={{
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                                                    border: "none", background: "transparent", cursor: "pointer",
                                                    color: textSecondary, fontSize: 14, lineHeight: 1,
                                                    transition: "opacity 0.13s",
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.opacity = "0.60"}
                                                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                            >
                                                                 ✕
                                                            </button>
                                                        </div>
                                                    );
                                                })()}

                                                {/* ── Post to Teams suggested action banner ── */}
                                                {m.suggestedAction?.type === "post_to_teams" && !dismissedActionIds[m.id] && (
                                                    <div style={{
                                                        marginTop: 10,
                                                        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                                                        padding: "8px 10px", borderRadius: 9,
                                                        background: isDark ? "rgba(98,100,167,0.12)" : "rgba(98,100,167,0.07)",
                                                        border: `1px solid ${isDark ? "rgba(98,100,167,0.35)" : "rgba(98,100,167,0.22)"}`,
                                                    }}>
                                                        {/* Teams icon */}
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, color: isDark ? "#a5b4fc" : "#5b5ea6" }}>
                                                            <path d="M14 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm4 8h-2a4 4 0 0 1 4 4v4h-2v-4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v4H8v-4a4 4 0 0 1 4-4h-2a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" fill="currentColor"/>
                                                        </svg>
                                                        <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: isDark ? "rgba(255,255,255,0.80)" : "rgba(0,0,0,0.70)" }}>
                                                            Share this to a Teams chat or channel?
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => postToTeams(m.text)}
                                                            style={{
                                                                padding: "4px 12px", borderRadius: 7, cursor: "pointer",
                                                                border: `1px solid ${isDark ? "rgba(98,100,167,0.55)" : "rgba(98,100,167,0.38)"}`,
                                                                background: isDark ? "rgba(98,100,167,0.20)" : "rgba(98,100,167,0.10)",
                                                                color: isDark ? "#a5b4fc" : "#5b5ea6",
                                                                fontSize: 12, fontWeight: 700,
                                                                transition: "opacity 0.13s",
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.opacity = "0.80"}
                                                            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                                        >
                                                            Post to Teams
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Dismiss"
                                                            onClick={() => setDismissedActionIds(prev => ({ ...prev, [m.id]: true }))}
                                                            style={{
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                                                                border: "none", background: "transparent", cursor: "pointer",
                                                                color: textSecondary, fontSize: 14, lineHeight: 1,
                                                                transition: "opacity 0.13s",
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.opacity = "0.60"}
                                                            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                                        >✕</button>
                                                    </div>
                                                )}

                                                {/* ── Create Calendar Invite suggested action banner ── */}
                                                {m.suggestedAction?.type === "create_calendar" && !dismissedActionIds[m.id] && (
                                                    <div style={{
                                                        marginTop: 10,
                                                        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                                                        padding: "8px 10px", borderRadius: 9,
                                                        background: isDark ? "rgba(22,163,74,0.10)" : "rgba(22,163,74,0.06)",
                                                        border: `1px solid ${isDark ? "rgba(22,163,74,0.32)" : "rgba(22,163,74,0.22)"}`,
                                                    }}>
                                                        {/* Calendar icon */}
                                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0, color: isDark ? "#86efac" : "#15803d" }}>
                                                            <rect x="3" y="4" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.8" fill="none"/>
                                                            <path d="M3 9h18" stroke="currentColor" strokeWidth="1.8"/>
                                                            <path d="M8 2v4M16 2v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                                                            <circle cx="8" cy="14" r="1.2" fill="currentColor"/>
                                                            <circle cx="12" cy="14" r="1.2" fill="currentColor"/>
                                                            <circle cx="16" cy="14" r="1.2" fill="currentColor"/>
                                                        </svg>
                                                        <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: isDark ? "rgba(255,255,255,0.80)" : "rgba(0,0,0,0.70)" }}>
                                                            Create a calendar invite for this?
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => openCalendarModal(m.text, m.suggestedAction.titleHint)}
                                                            style={{
                                                                padding: "4px 12px", borderRadius: 7, cursor: "pointer",
                                                                border: `1px solid ${isDark ? "rgba(22,163,74,0.50)" : "rgba(22,163,74,0.38)"}`,
                                                                background: isDark ? "rgba(22,163,74,0.18)" : "rgba(22,163,74,0.10)",
                                                                color: isDark ? "#86efac" : "#15803d",
                                                                fontSize: 12, fontWeight: 700,
                                                                transition: "opacity 0.13s",
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.opacity = "0.80"}
                                                            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                                        >
                                                            Create Invite
                                                        </button>
                                                        <button
                                                            type="button"
                                                            title="Dismiss"
                                                            onClick={() => setDismissedActionIds(prev => ({ ...prev, [m.id]: true }))}
                                                            style={{
                                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                                width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
                                                                border: "none", background: "transparent", cursor: "pointer",
                                                                color: textSecondary, fontSize: 14, lineHeight: 1,
                                                                transition: "opacity 0.13s",
                                                            }}
                                                            onMouseEnter={e => e.currentTarget.style.opacity = "0.60"}
                                                            onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                                        >✕</button>
                                                    </div>
                                                )}

                                                {/* ── Workplace Trace — collapsible "How I answered this" panel ── */}
                                {m.workplaceTrace && Array.isArray(m.workplaceTrace.steps) && m.workplaceTrace.steps.length > 0 && (() => {
                                    const traceOpen = !!expandedTraceIds[m.id];
                                    const trace = m.workplaceTrace;
                                    return (
                                        <div style={{ marginTop: 10, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.07)", paddingTop: 8 }}>
                                            {/* Toggle button */}
                                            <button
                                                type="button"
                                                onClick={() => setExpandedTraceIds(prev => ({ ...prev, [m.id]: !prev[m.id] }))}
                                                style={{
                                                    display: "inline-flex", alignItems: "center", gap: 5,
                                                    background: "none", border: "none", cursor: "pointer",
                                                    padding: "2px 4px", borderRadius: 6,
                                                    fontSize: 11, fontWeight: 600,
                                                    color: isDark ? "rgba(103,232,249,0.85)" : "#0e7490",
                                                    letterSpacing: "0.2px",
                                                    transition: "opacity 0.13s",
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
                                                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                                title={traceOpen ? "Hide processing steps" : "Show how this answer was built"}
                                            >
                                                {/* Small chevron icon */}
                                                <svg
                                                    width="11" height="11" viewBox="0 0 12 12" fill="none"
                                                    style={{ transition: "transform 0.2s", transform: traceOpen ? "rotate(90deg)" : "rotate(0deg)" }}
                                                >
                                                    <path d="M4 2.5l4 3.5-4 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {traceOpen ? "Hide" : "How I answered this"}
                                                <span style={{ opacity: 0.6, fontWeight: 400 }}>
                                                    · {(m.workplaceTrace?.steps?.length || 0)} step{(m.workplaceTrace?.steps?.length || 0) !== 1 ? "s" : ""}
                                                </span>
                                            </button>

                                            {/* Expandable trace body */}
                                            {traceOpen && (
                                                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 0 }}>
                                                    {/* KQL pill */}
                                                    {trace.kql && (
                                                        <div style={{
                                                            marginBottom: 8, padding: "5px 9px", borderRadius: 7,
                                                            background: isDark ? "rgba(8,145,178,0.12)" : "rgba(8,145,178,0.07)",
                                                            border: "1px solid rgba(8,145,178,0.22)",
                                                            fontFamily: "monospace", fontSize: 11,
                                                            color: isDark ? "#67e8f9" : "#0e7490",
                                                            lineHeight: 1.5,
                                                        }}>
                                                            <span style={{ opacity: 0.6, marginRight: 5 }}>KQL →</span>
                                                            <span style={{ fontWeight: 700 }}>{trace.kql.kql}</span>
                                                            {trace.kql.sources?.length > 0 && (
                                                                <span style={{ marginLeft: 8, opacity: 0.65 }}>[{trace.kql.sources.join(", ")}]</span>
                                                            )}
                                                        </div>
                                                    )}
                                                    {/* Step timeline */}
                                                    {trace.steps.map((step, i) => (
                                                        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 9, opacity: 0.82 }}>
                                                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, paddingTop: 2 }}>
                                                                <div style={{
                                                                    width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                                                                    background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                                                                    border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)",
                                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                                    fontSize: 8,
                                                                }}>
                                                                    <span style={{ opacity: 0.6 }}>✓</span>
                                                                </div>
                                                                {i < trace.steps.length - 1 && (
                                                                    <div style={{ width: 1, flex: 1, minHeight: 8, background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)", marginTop: 2 }} />
                                                                )}
                                                            </div>
                                                            <div style={{ flex: 1, paddingBottom: i < trace.steps.length - 1 ? 4 : 0 }}>
                                                                <div style={{ fontSize: 12, fontWeight: 500, color: textColor, lineHeight: 1.3 }}>
                                                                    {step.emoji && <span style={{ marginRight: 5 }}>{step.emoji}</span>}
                                                                    {step.title}
                                                                </div>
                                                                {step.detail && (
                                                                    <div style={{ fontSize: 11, color: textSecondary, marginTop: 1, lineHeight: 1.4 }}>{step.detail}</div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {/* Hit counts */}
                                                    {trace.hits && (
                                                        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 5 }}>
                                                            {Object.entries(trace.hits.counts).map(([src, n]) => (
                                                                <span key={src} style={{
                                                                    fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 999,
                                                                    background: isDark ? "rgba(8,145,178,0.15)" : "rgba(8,145,178,0.08)",
                                                                    border: "1px solid rgba(8,145,178,0.22)",
                                                                    color: isDark ? "#67e8f9" : "#0e7490",
                                                                }}>
                                                                    {src} · {n}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* ── Workplace Agent Trace — "How I answered this" accordion ── */}
                                {m.workplaceAgentTrace && m.workplaceAgentTrace.steps?.length > 0 && (() => {
                                    const traceKey = `wat-${m.id}`;
                                    const traceOpen = !!expandedTraceIds[traceKey];
                                    const trace = m.workplaceAgentTrace;
                                    const stageKeys = ["discover", "plan", "validate", "execute"];
                                    const stageLabels = { discover: "Discover", plan: "Plan", validate: "Validate", execute: "Execute" };
                                    const stageDone = {
                                        discover: trace.steps.some(s => /preflight|availab|discover/i.test(s.title || "")),
                                        plan: trace.steps.some(s => /plan|orchestrat/i.test(s.title || "")),
                                        validate: true,
                                        execute: trace.steps.some(s => /execut|completed/i.test(s.title || "")),
                                    };
                                    return (
                                        <div style={{ marginTop: 10, borderTop: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.07)", paddingTop: 8 }}>
                                            {/* Toggle button */}
                                            <button
                                                type="button"
                                                onClick={() => setExpandedTraceIds(prev => ({ ...prev, [traceKey]: !prev[traceKey] }))}
                                                style={{
                                                    display: "inline-flex", alignItems: "center", gap: 5,
                                                    background: "none", border: "none", cursor: "pointer",
                                                    padding: "2px 4px", borderRadius: 6,
                                                    fontSize: 11, fontWeight: 600,
                                                    color: isDark ? "rgba(103,232,249,0.85)" : "#0e7490",
                                                    letterSpacing: "0.2px", transition: "opacity 0.13s",
                                                }}
                                                onMouseEnter={e => e.currentTarget.style.opacity = "0.75"}
                                                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
                                                title={traceOpen ? "Hide processing steps" : "Show how this answer was built"}
                                            >
                                                <svg width="11" height="11" viewBox="0 0 12 12" fill="none"
                                                    style={{ transition: "transform 0.2s", transform: traceOpen ? "rotate(90deg)" : "rotate(0deg)" }}>
                                                    <path d="M4 2.5l4 3.5-4 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                                {traceOpen ? "Hide" : "How I answered this"}
                                                <span style={{ opacity: 0.6, fontWeight: 400 }}>· {trace.steps.length} step{trace.steps.length !== 1 ? "s" : ""}</span>
                                            </button>

                                            {traceOpen && (
                                                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                                                    {/* Stage pills: Discover › Plan › Validate › Execute */}
                                                    <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                                        {stageKeys.map((key, i) => (
                                                            <React.Fragment key={key}>
                                                                <span style={{
                                                                    fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 999,
                                                                    color: stageDone[key] ? (isDark ? "#67e8f9" : "#0e7490") : textTertiary,
                                                                    background: stageDone[key] ? (isDark ? "rgba(8,145,178,0.18)" : "rgba(8,145,178,0.10)") : (isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"),
                                                                    border: stageDone[key] ? "1px solid rgba(8,145,178,0.35)" : (isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)"),
                                                                }}>{stageDone[key] ? "✓ " : "… "}{stageLabels[key]}</span>
                                                                {i < stageKeys.length - 1 && (
                                                                    <span style={{ color: textTertiary, fontSize: 10, alignSelf: "center" }}>›</span>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </div>

                                                    {/* Intent pill */}
                                                    {trace.intent && (
                                                        <div style={{
                                                            padding: "4px 9px", borderRadius: 7,
                                                            background: isDark ? "rgba(139,92,246,0.12)" : "rgba(139,92,246,0.07)",
                                                            border: "1px solid rgba(139,92,246,0.25)",
                                                            fontSize: 11, color: isDark ? "#c4b5fd" : "#6d28d9",
                                                        }}>
                                                            <span style={{ opacity: 0.6, marginRight: 5 }}>Intent →</span>
                                                            <span style={{ fontWeight: 600 }}>{trace.intent}</span>
                                                        </div>
                                                    )}

                                                    {/* KQL pill */}
                                                    {trace.kql?.kql && (
                                                        <div style={{
                                                            padding: "5px 9px", borderRadius: 7,
                                                            background: isDark ? "rgba(8,145,178,0.12)" : "rgba(8,145,178,0.07)",
                                                            border: "1px solid rgba(8,145,178,0.22)",
                                                            fontFamily: "monospace", fontSize: 11,
                                                            color: isDark ? "#67e8f9" : "#0e7490", lineHeight: 1.5,
                                                        }}>
                                                            <span style={{ opacity: 0.6, marginRight: 5 }}>Search →</span>
                                                            <span style={{ fontWeight: 700 }}>{trace.kql.kql}</span>
                                                            {trace.kql.sources?.length > 0 && (
                                                                <span style={{ marginLeft: 8, opacity: 0.65 }}>[{trace.kql.sources.join(", ")}]</span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Hit counts */}
                                                    {trace.hits && Object.keys(trace.hits.counts || {}).length > 0 && (
                                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                                                            {Object.entries(trace.hits.counts).map(([src, n]) => (
                                                                <span key={src} style={{
                                                                    fontSize: 11, fontWeight: 700, padding: "2px 7px", borderRadius: 999,
                                                                    background: isDark ? "rgba(8,145,178,0.15)" : "rgba(8,145,178,0.08)",
                                                                    border: "1px solid rgba(8,145,178,0.22)",
                                                                    color: isDark ? "#67e8f9" : "#0e7490",
                                                                }}>{src} · {n}</span>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Step timeline */}
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
                                                        {trace.steps.map((step, i) => (
                                                            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 9, opacity: 0.85 }}>
                                                                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, paddingTop: 2 }}>
                                                                    <div style={{
                                                                        width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                                                                        background: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
                                                                        border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)",
                                                                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8,
                                                                    }}>
                                                                        <span style={{ opacity: 0.6 }}>✓</span>
                                                                    </div>
                                                                    {i < trace.steps.length - 1 && (
                                                                        <div style={{ width: 1, flex: 1, minHeight: 8, background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)", marginTop: 2 }} />
                                                                    )}
                                                                </div>
                                                                <div style={{ flex: 1, paddingBottom: i < trace.steps.length - 1 ? 4 : 0 }}>
                                                                    <div style={{ fontSize: 12, fontWeight: 500, color: textColor, lineHeight: 1.3 }}>
                                                                        {step.emoji && <span style={{ marginRight: 5 }}>{step.emoji}</span>}
                                                                        {step.title}
                                                                    </div>
                                                                    {step.detail && (
                                                                        <div style={{ fontSize: 11, color: textSecondary, marginTop: 1, lineHeight: 1.4 }}>{step.detail}</div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                {/* ── STT Recovery Buttons (Change 8) ───────────────────── */}
                                {m.type === "stt_recovery" && (
                                    <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                const backup = sessionStorage.getItem("stt_backup_audio");
                                                const startedAt = sessionStorage.getItem("stt_backup_startedAt");
                                                if (!backup) return;

                                                // Convert base64 data URL back to Blob
                                                const res = await fetch(backup);
                                                const blob = await res.blob();

                                                // Clear backup keys
                                                sessionStorage.removeItem("stt_backup_audio");
                                                sessionStorage.removeItem("stt_backup_time");
                                                sessionStorage.removeItem("stt_backup_startedAt");

                                                // Remove recovery message from chat
                                                setMessages(prev => prev.filter(m => m.type !== "stt_recovery"));

                                                // Restore startedAt and process
                                                setMeetingRecordingStartedAt(startedAt || "");
                                                await transcribeMeetingAudio(blob, "audio/webm");
                                            }}
                                            style={{
                                                padding: "7px 16px",
                                                borderRadius: 8,
                                                border: "none",
                                                background: primaryIconColor,
                                                color: "#fff",
                                                cursor: "pointer",
                                                fontSize: 12,
                                                fontWeight: 700,
                                            }}
                                        >
                                            💾 Recover Recording
                                        </button>

                                        <button
                                            type="button"
                                            onClick={() => {
                                                sessionStorage.removeItem("stt_backup_audio");
                                                sessionStorage.removeItem("stt_backup_time");
                                                sessionStorage.removeItem("stt_backup_startedAt");
                                                setMessages(prev => prev.filter(m => m.type !== "stt_recovery"));
                                            }}
                                            style={{
                                                padding: "7px 16px",
                                                borderRadius: 8,
                                                border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                                background: "transparent",
                                                color: textSecondary,
                                                cursor: "pointer",
                                                fontSize: 12,
                                            }}
                                        >
                                            Discard
                                        </button>
                                    </div>
                                )}
                                {/* ── END STT Recovery Buttons ───────────────────────────── */}

                                {isAssistant && (
                                    <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                        <IconButton title="Thumbs up" onClick={async () => {
                                            const newFeedback = feedback === "up" ? null : "up";
                                            setFeedbackByMessageId(prev => ({ ...prev, [m.id]: newFeedback }));

                                            // Save to Cosmos DB
                                            if (currentChatId) {
                                                try {
                                                    await ChatHistoryService.updateMessageFeedback(currentChatId, m.id, newFeedback);
                                                    console.log(`✅ Saved feedback for message ${m.id}: ${newFeedback || "cleared"}`);
                                                } catch (error) {
                                                    console.warn("Failed to save feedback:", error);
                                                    // Revert on error
                                                    setFeedbackByMessageId(prev => ({ ...prev, [m.id]: feedback }));
                                                }
                                            }
                                        }}>
                                            <span style={{ fontWeight: feedback === "up" ? 900 : 600, opacity: feedback === "up" ? 1 : 0.7, fontSize: 16 }}>👍</span>
                                        </IconButton>

                                        <IconButton title="Thumbs down" onClick={async () => {
                                            const newFeedback = feedback === "down" ? null : "down";
                                            setFeedbackByMessageId(prev => ({ ...prev, [m.id]: newFeedback }));

                                            // Save to Cosmos DB
                                            if (currentChatId) {
                                                try {
                                                    await ChatHistoryService.updateMessageFeedback(currentChatId, m.id, newFeedback);
                                                    console.log(`✅ Saved feedback for message ${m.id}: ${newFeedback || "cleared"}`);
                                                } catch (error) {
                                                    console.warn("Failed to save feedback:", error);
                                                    // Revert on error
                                                    setFeedbackByMessageId(prev => ({ ...prev, [m.id]: feedback }));
                                                }
                                            }
                                        }}>
                                            <span style={{ fontWeight: feedback === "down" ? 900 : 600, opacity: feedback === "down" ? 1 : 0.7, fontSize: 16 }}>👎</span>
                                        </IconButton>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                {insights && (
                    <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 12, color: textSecondary, marginBottom: 7, display: "flex", alignItems: "center", fontWeight: 600 }}>
                            <b style={{ flex: 1 }}>Meeting Insights</b>

                            <div style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
                                <IconButton title="Download insights as .txt" onClick={() => downloadTextAsFile(insights, "meeting-insights")} primary>
                                    <DownloadIcon />
                                </IconButton>

                                <IconButton title="Copy insights" onClick={() => copyToClipboard(insights)}>
                                    <CopyIcon />
                                </IconButton>

                                <div style={{ position: "relative" }}>
                                    <IconButton title="Share insights" onClick={() => setShareMenuForMessageId((prev) => (prev === "INSIGHTS" ? null : "INSIGHTS"))}>
                                        <ShareIcon />
                                    </IconButton>

                                    {shareMenuForMessageId === "INSIGHTS" && (
                                        <div
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                position: "absolute",
                                                top: 36,
                                                right: 0,
                                                width: 200,
                                                borderRadius: 12,
                                                border: pickerBorder,
                                                background: pickerBg,
                                                color: pickerText,
                                                padding: 8,
                                                boxShadow: isDark ? "0 12px 28px rgba(0,0,0,0.5)" : "0 12px 28px rgba(0,0,0,0.18)",
                                                zIndex: 9999,
                                            }}
                                        >
                                            <div style={{ fontSize: 11, color: textTertiary, padding: "6px 8px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" }}>Share via</div>
                                            <MenuItem label="Send Email" onClick={() => openEmailModalForText(insights)} />
                                            <MenuItem label="Post to Teams" onClick={() => postToTeams(insights)} />
                                            {/* 🆕 Sharing.ado feature flag check */}
                                            {flags.sharing.ado && (
                                                <MenuItem label="Add to ADO" onClick={() => addToAdo(insights)} />
                                            )}
                                            <MenuItem label="Upload to OneDrive" onClick={() => uploadToOneDrive(insights)} />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div
                            style={{
                                padding: "12px",
                                borderRadius: 12,
                                background: insightsBg,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                                border: isDark || isContrast ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
                            }}
                        >
                            {renderRichText(insights)}
                        </div>
                    </div>
                )}

                {/* ── WORKPLACE AGENT PROGRESS PANEL — shown immediately when integrations are active ── */}
                {isSending && selectedIntegrations.length > 0 && (
                    <div style={{
                        display: "flex", justifyContent: "flex-start", alignItems: "flex-start",
                        gap: 9, marginBottom: 10,
                    }}>
                        {/* Spark avatar */}
                        <div style={{
                            width: 34, height: 34, borderRadius: "50%", overflow: "hidden",
                            flexShrink: 0, marginTop: 4, background: "#0e7490",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <img src="/assets/spark-icon.png" alt="Spark"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                onError={e => { e.target.style.display = "none"; }} />
                        </div>

                        {/* Steps card */}
                        <div style={{
                            maxWidth: "85%", padding: "12px 14px", borderRadius: 12,
                            background: assistantMessageBg,
                            border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.06)",
                            display: "flex", flexDirection: "column", gap: 0,
                        }}>
                            {/* Header */}
                            <div style={{
                                fontSize: 11, fontWeight: 700, color: primaryIconColor,
                                marginBottom: 10, letterSpacing: "0.3px", textTransform: "uppercase",
                                display: "flex", alignItems: "center", gap: 6,
                            }}>
                                <div style={{
                                    width: 7, height: 7, borderRadius: "50%",
                                    background: primaryIconColor,
                                    animation: "ringPulse 1.2s ease-in-out infinite",
                                }} />
                                Workplace Search
                            </div>

                            {/* KQL pill */}
                            {workplaceKql && (
                                <div style={{
                                    marginBottom: 10, padding: "6px 10px", borderRadius: 8,
                                    background: isDark ? "rgba(8,145,178,0.12)" : "rgba(8,145,178,0.07)",
                                    border: "1px solid rgba(8,145,178,0.25)",
                                    fontFamily: "monospace", fontSize: 11,
                                    color: isDark ? "#67e8f9" : "#0e7490",
                                    lineHeight: 1.5,
                                }}>
                                    <span style={{ opacity: 0.6, marginRight: 5 }}>KQL →</span>
                                    <span style={{ fontWeight: 700 }}>{workplaceKql.kql}</span>
                                    {workplaceKql.sources?.length > 0 && (
                                        <span style={{ marginLeft: 8, opacity: 0.65 }}>
                                            [{workplaceKql.sources.join(", ")}]
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Step list — or initialising placeholder while first SSE event is in flight */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {workplaceSteps.length === 0 && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                        <div style={{
                                            width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
                                            background: isDark ? "rgba(8,145,178,0.20)" : "rgba(8,145,178,0.10)",
                                            border: "1.5px solid rgba(8,145,178,0.50)",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0891b2", animation: "ringPulse 1.2s ease-in-out infinite" }} />
                                        </div>
                                        <span style={{ fontSize: 13, color: textSecondary }}>
                                            Querying workplace sources…
                                        </span>
                                    </div>
                                )}
                                {workplaceSteps.map((step, i) => {
                                    const isLast = i === workplaceSteps.length - 1;
                                    return (
                                        <div key={i} style={{
                                            display: "flex", alignItems: "flex-start", gap: 9,
                                            opacity: isLast ? 1 : 0.72,
                                        }}>
                                            {/* Timeline dot + line */}
                                            <div style={{
                                                display: "flex", flexDirection: "column",
                                                alignItems: "center", flexShrink: 0, paddingTop: 2,
                                            }}>
                                                <div style={{
                                                    width: 18, height: 18, borderRadius: "50%",
                                                    background: isLast
                                                        ? (isDark ? "rgba(8,145,178,0.30)" : "rgba(8,145,178,0.15)")
                                                        : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)"),
                                                    border: isLast
                                                        ? "1.5px solid rgba(8,145,178,0.60)"
                                                        : (isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)"),
                                                    display: "flex", alignItems: "center", justifyContent: "center",
                                                    fontSize: 9,
                                                }}>
                                                    {isLast
                                                        ? <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0891b2", animation: "ringPulse 1.2s ease-in-out infinite" }} />
                                                        : <span style={{ fontSize: 8, opacity: 0.7 }}>✓</span>
                                                    }
                                                </div>
                                                {i < workplaceSteps.length - 1 && (
                                                    <div style={{ width: 1, flex: 1, minHeight: 8, background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)", marginTop: 2 }} />
                                                )}
                                            </div>

                                            {/* Text */}
                                            <div style={{ flex: 1, paddingBottom: i < workplaceSteps.length - 1 ? 4 : 0 }}>
                                                <div style={{ fontSize: 13, fontWeight: isLast ? 700 : 500, color: textColor, lineHeight: 1.3 }}>
                                                    {step.emoji && <span style={{ marginRight: 5 }}>{step.emoji}</span>}
                                                    {step.title}
                                                </div>
                                                {step.detail && (
                                                    <div style={{ fontSize: 11, color: textSecondary, marginTop: 2, lineHeight: 1.4 }}>
                                                        {step.detail}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Animated "working" row after last step */}
                                <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 2 }}>
                                    <div style={{ width: 18, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                                        <div style={{ display: "flex", gap: 3 }}>
                                            {[0, 1, 2].map(i => (
                                                <div key={i} style={{
                                                    width: 4, height: 4, borderRadius: "50%",
                                                    background: primaryIconColor,
                                                    animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                                                }} />
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                {/* Hit counts row */}
                                {workplaceHits && (
                                    <div style={{
                                        marginTop: 6, display: "flex", flexWrap: "wrap", gap: 6,
                                    }}>
                                        {Object.entries(workplaceHits.counts).map(([src, n]) => (
                                            <span key={src} style={{
                                                fontSize: 11, fontWeight: 700, padding: "2px 8px",
                                                borderRadius: 999,
                                                background: isDark ? "rgba(8,145,178,0.15)" : "rgba(8,145,178,0.08)",
                                                border: "1px solid rgba(8,145,178,0.25)",
                                                color: isDark ? "#67e8f9" : "#0e7490",
                                            }}>
                                                {src} · {n}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── WORKPLACE AGENT ORCHESTRATION PANEL — live progress trace ── */}
                {((isSending && selectedWorkplaceAgents.length > 0) || (showWaDebugLogs && workplaceAgentSteps.length > 0) || !!workplaceAgentError) && (
                    <div style={{
                        display: "flex", justifyContent: "flex-start", alignItems: "flex-start",
                        gap: 9, marginBottom: 10,
                    }}>
                        <div style={{
                            width: 34, height: 34, borderRadius: "50%", overflow: "hidden",
                            flexShrink: 0, marginTop: 4, background: "#0e7490",
                            display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                            <img src="/assets/spark-icon.png" alt="Spark"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                onError={e => { e.target.style.display = "none"; }} />
                        </div>

                        <div style={{
                            maxWidth: "85%", padding: "12px 14px", borderRadius: 12,
                            background: assistantMessageBg,
                            border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.06)",
                            display: "flex", flexDirection: "column", gap: 8,
                        }}>
                            {/* ── Header ── */}
                            <div style={{
                                display: "flex", alignItems: "center", justifyContent: "space-between",
                            }}>
                                <div style={{
                                    fontSize: 11, fontWeight: 700, color: primaryIconColor,
                                    letterSpacing: "0.3px", textTransform: "uppercase",
                                    display: "flex", alignItems: "center", gap: 6,
                                }}>
                                    <div style={{
                                        width: 7, height: 7, borderRadius: "50%",
                                        background: primaryIconColor,
                                        animation: isSending ? "ringPulse 1.2s ease-in-out infinite" : "none",
                                    }} />
                                    O365 Agent Orchestration
                                </div>
                            </div>

                            {/* ── Stage pills + progress bar ── */}
                            {(() => {
                                const hasPlan     = workplaceAgentSteps.some(s => /plan|orchestrat/i.test(s.title || ""));
                                const hasExecute  = workplaceAgentSteps.some(s => /execut|completed/i.test(s.title || ""));
                                const stageStates = [
                                    { key: "discover", label: "Discover", done: selectedWorkplaceAgents.length > 0 },
                                    { key: "plan",     label: "Plan",     done: hasPlan },
                                    { key: "validate", label: "Validate", done: hasPlan },   // validation is now automatic
                                    { key: "execute",  label: "Execute",  done: hasExecute },
                                ];
                                const doneCount  = stageStates.filter(s => s.done).length;
                                const progressPct = Math.max(8, Math.round((doneCount / stageStates.length) * 100));

                                return (
                                    <>
                                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                                            {stageStates.map((s, i) => (
                                                <React.Fragment key={s.key}>
                                                    <span style={{
                                                        fontSize: 10, fontWeight: 700, padding: "3px 7px", borderRadius: 999,
                                                        color: s.done ? (isDark ? "#67e8f9" : "#0e7490") : textTertiary,
                                                        background: s.done ? (isDark ? "rgba(8,145,178,0.18)" : "rgba(8,145,178,0.10)") : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)"),
                                                        border: s.done ? "1px solid rgba(8,145,178,0.35)" : (isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.08)"),
                                                    }}>
                                                        {s.done ? "✓ " : "… "}{s.label}
                                                    </span>
                                                    {i < stageStates.length - 1 && (
                                                        <span style={{ color: textTertiary, fontSize: 10, alignSelf: "center" }}>›</span>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </div>

                                        <div style={{
                                            width: "100%", height: 4, borderRadius: 999,
                                            background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)",
                                            overflow: "hidden",
                                        }}>
                                            <div style={{
                                                width: `${progressPct}%`, height: "100%",
                                                background: "linear-gradient(90deg, #0ea5e9, #14b8a6)",
                                                transition: "width 0.35s ease",
                                            }} />
                                        </div>
                                    </>
                                );
                            })()}

                            {/* ── Step timeline ── */}
                            {workplaceAgentSteps.length === 0 ? (
                                <div style={{ fontSize: 12, color: textSecondary }}>Initializing…</div>
                            ) : (
                                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                    {workplaceAgentSteps.map((step, i) => (
                                        <div key={`wa-step-${i}`} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                                            <div style={{ width: 18, textAlign: "center", fontSize: 11, paddingTop: 1 }}>{step.emoji || "•"}</div>
                                            <div style={{ flex: 1 }}>
                                                <div style={{ fontSize: 12, color: textColor, fontWeight: 600, lineHeight: 1.3 }}>{step.title}</div>
                                                {step.detail ? <div style={{ fontSize: 11, color: textSecondary, marginTop: 1 }}>{step.detail}</div> : null}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* ── Error ── */}
                            {workplaceAgentError ? (
                                <div style={{
                                    padding: "6px 8px", borderRadius: 8,
                                    background: isDark ? "rgba(220,38,38,0.20)" : "rgba(220,38,38,0.08)",
                                    border: "1px solid rgba(220,38,38,0.35)",
                                    fontSize: 11, color: isDark ? "#fca5a5" : "#b91c1c",
                                    whiteSpace: "pre-wrap",
                                }}>
                                    {workplaceAgentError}
                                </div>
                            ) : null}

                            {/* ── Debug logs — only when toggle is ON ── */}
                            {showWaDebugLogs && workplaceAgentLiveLogs.length > 0 && (
                                <div style={{
                                    padding: "8px 10px", borderRadius: 8,
                                    background: isDark ? "rgba(15,23,42,0.55)" : "rgba(15,23,42,0.05)",
                                    border: isDark ? "1px solid rgba(148,163,184,0.35)" : "1px solid rgba(100,116,139,0.30)",
                                    fontSize: 10, color: isDark ? "#cbd5e1" : "#334155",
                                }}>
                                    <div style={{ fontWeight: 700, marginBottom: 5, fontSize: 10 }}>Debug Logs</div>
                                    <div style={{
                                        maxHeight: 200, overflowY: "auto", whiteSpace: "pre-wrap",
                                        fontFamily: "Consolas, Menlo, Monaco, monospace", lineHeight: 1.35,
                                    }}>
                                        {workplaceAgentLiveLogs.slice(-100).join("\n")}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Processing animation — shown for regular (non-workplace) chat */}
                {isSending && selectedIntegrations.length === 0 && selectedWorkplaceAgents.length === 0 && (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            gap: 9,
                            marginBottom: 10,
                        }}
                    >
                        {/* Spark avatar on typing indicator */}               
                        <div style={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            overflow: "hidden",
                            flexShrink: 0,
                            marginTop: 4,
                            background: "#0e7490",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}>
                            <img
                                src="/assets/spark-icon.png"
                                alt="Spark"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                onError={(e) => { e.target.style.display = "none"; }}
                            />
                        </div>
                        <div
                            style={{
                                maxWidth: "85%",
                                padding: "11px 14px",
                                borderRadius: 12,
                                background: assistantMessageBg,
                                border: isDark || isContrast ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.06)",
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                            }}
                        >
                            {/* Spark label */}
                            <div style={{ fontSize: 12, color: textTertiary, fontWeight: 600 }}>
                                Spark Assist
                            </div>

                            {/* Cycling status text */}
                            <div
                                style={{
                                    fontSize: 13,
                                    color: textSecondary,
                                    minWidth: 220,
                                }}
                            >
                                {processingMessage}
                            </div>

                            {/* Animated dots bar */}
                            <div style={{ display: "flex", gap: 5, alignItems: "center", marginTop: 2 }}>
                                {[0, 1, 2].map((i) => (
                                    <div
                                        key={i}
                                        style={{
                                            width: 7,
                                            height: 7,
                                            borderRadius: "50%",
                                            background: primaryIconColor,
                                            opacity: 0.85,
                                            animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                <div ref={endRef} />            </div>


            {/* Composer (continues with all existing code unchanged - too long to include in response limit) */}
            {/* Include complete composer, pickers, modals code from your original file here */}

            {/* Composer + Pickers */}
            <div style={{ padding: isMobile ? 8 : 12, borderTop: surfaceBorder, backgroundColor: isDark ? "#252525" : isContrast ? "#0a0a0a" : "#fafafa", display: showWelcome ? "none" : "block" }}>
                {pickerMode && searchMode === "work" && (
                    <div
                        style={{
                            marginBottom: 10,
                            border: pickerBorder,
                            borderRadius: 12,
                            background: pickerBg,
                            color: pickerText,
                            maxHeight: 300,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                            boxShadow: isDark ? "0 12px 28px rgba(0,0,0,0.5)" : "0 12px 28px rgba(0,0,0,0.18)",
                        }}
                    >
                        {/* ── Pinned search box for Spark mode ── */}
                        {pickerMode === "spark" ? (
                            <div style={{ padding: "8px 8px 4px", flexShrink: 0 }}>
                                <div style={{
                                    display: "flex", alignItems: "center", gap: 6,
                                    padding: "0 10px", height: 34, borderRadius: 8,
                                    background: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
                                    border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.11)",
                                }}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={textTertiary} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                    </svg>
                                    <input
                                        autoFocus
                                        type="text"
                                        value={sparkChipSearch}
                                        onChange={e => { setSparkChipSearch(e.target.value); setPickerQuery(e.target.value); }}
                                        onKeyDown={e => { if (e.key === "Escape") { setPickerMode(null); setPickerQuery(""); setSparkChipSearch(""); } }}
                                        placeholder="Search sparks by name, tag or creator…"
                                        style={{
                                            flex: 1, border: "none", outline: "none",
                                            background: "transparent",
                                            fontSize: 13, color: textColor,
                                            caretColor: "#0891b2",
                                        }}
                                    />
                                    {sparkChipSearch && (
                                        <button type="button" onClick={() => { setSparkChipSearch(""); setPickerQuery(""); }}
                                            style={{ border: "none", background: "transparent", cursor: "pointer", color: textTertiary, fontSize: 16, lineHeight: 1, padding: 0, display: "flex", alignItems: "center" }}>×</button>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div style={{ fontSize: 11, color: textTertiary, padding: "8px 8px 4px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", flexShrink: 0 }}>
                                {pickerMode === "agent"
                                    ? `Select Agents (multi-select)${pickerQuery ? ` (filter: "${pickerQuery}")` : ""}`
                                    : pickerMode === "workplace-agent"
                                        ? `Select Workplace Agents (multi-select)${pickerQuery ? ` (filter: "${pickerQuery}")` : ""}`
                                        : `Select Integrations (multi-select)${pickerQuery ? ` (filter: "${pickerQuery.replace("/", "")}")` : ""}`}
                            </div>
                        )}
                        <div style={{ overflowY: "auto", flex: 1, padding: "0 8px 8px" }}>

                        {pickerMode === "spark" ? (
                            <>
                                {filteredSparks.length === 0 ? (
                                    <div style={{ padding: "9px 12px", fontSize: 13, color: textSecondary }}>No matches</div>
                                ) : (
                                    filteredSparks.map((s) => (
                                        <div
                                            key={s.sparkId}
                                            onClick={() => selectSpark(s)}
                                            style={{ padding: "8px 10px", borderRadius: 10, cursor: "pointer", display: "flex", gap: 10, alignItems: "flex-start", transition: "background 0.12s ease" }}
                                            onMouseEnter={(e) => (e.currentTarget.style.background = pickerHover)}
                                            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                                        >
                                            {/* Accent dot */}
                                            <div style={{
                                                width: 34, height: 34, borderRadius: 10, flexShrink: 0, marginTop: 1,
                                                background: s.accent ? `${s.accent}22` : (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
                                                border: `1px solid ${s.accent ? s.accent + "44" : (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.10)")}`,
                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                fontSize: 14, color: s.accent || primaryIconColor,
                                            }}>
                                                ✦
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                {/* Row 1: name + certified + likes */}
                                                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                    <div style={{ fontWeight: 700, fontSize: 13, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                                                        {s.sparkName}
                                                    </div>
                                                    {s.certified && (
                                                        <span style={{ fontSize: 10, padding: "1px 6px", borderRadius: 999, background: isDark ? "rgba(16,185,129,0.18)" : "rgba(16,185,129,0.12)", color: "#10b981", fontWeight: 700, flexShrink: 0 }}>
                                                            ✓ Certified
                                                        </span>
                                                    )}
                                                    {s.likes > 0 && (
                                                        <span style={{ fontSize: 11, color: textTertiary, flexShrink: 0 }}>★ {s.likes}</span>
                                                    )}
                                                </div>
                                                {/* Row 2: creator */}
                                                {s.creatorName && (
                                                    <div style={{ fontSize: 11, color: textTertiary, marginTop: 1 }}>by {s.creatorName}</div>
                                                )}
                                                {/* Row 3: description preview */}
                                                {s.description && (
                                                    <div style={{ fontSize: 11, color: textSecondary, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {s.description.length > 80 ? s.description.slice(0, 80) + "…" : s.description}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                                {/* Footer hint when showing default (unfiltered) list */}
                                {!pickerQuery && allSparks.length > SPARK_PICKER_DEFAULT_LIMIT && (
                                    <div style={{ padding: "6px 10px 4px", fontSize: 11, color: textTertiary, borderTop: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)", marginTop: 4, textAlign: "center" }}>
                                        Showing top {SPARK_PICKER_DEFAULT_LIMIT} of {allSparks.length} sparks — type to search all
                                    </div>
                                )}
                            </>
                        ) : pickerMode === "agent" ? (
                            filteredAgents.length === 0 ? (
                                <div style={{ padding: "9px 12px", fontSize: 13, color: textSecondary }}>No matches</div>
                            ) : (
                                filteredAgents.map((x) => {
                                    const checked = selectedAgents.includes(x.key);
                                    return (
                                        <div
                                            key={x.key}
                                            onClick={() => toggleAgent(x.key)}
                                            style={{
                                                padding: "9px 12px",
                                                borderRadius: 10,
                                                cursor: "pointer",
                                                fontSize: 13,
                                                display: "flex",
                                                justifyContent: "space-between",
                                                gap: 10,
                                                alignItems: "center",
                                                background: checked ? (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)") : "transparent",
                                                transition: "background 0.12s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (!checked) e.currentTarget.style.background = pickerHover;
                                            }}
                                            onMouseLeave={(e) => {
                                                if (!checked) e.currentTarget.style.background = "transparent";
                                            }}
                                        >
                                            <div style={{ overflowWrap: "anywhere", display: "flex", alignItems: "center", gap: 10 }}>
                                                <span
                                                    style={{
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: 4,
                                                        border: isDark || isContrast ? "1.5px solid rgba(255,255,255,0.4)" : "1.5px solid rgba(0,0,0,0.3)",
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontSize: 12,
                                                        fontWeight: 700,
                                                        color: checked ? (isDark ? "#64b5f6" : "#0078d4") : textTertiary,
                                                    }}
                                                >
                                                    {checked ? "✓" : ""}
                                                </span>
                                                <span style={{ color: textColor }}>{x.label}</span>
                                            </div>
                                            <div style={{ fontSize: 12, color: textTertiary }}>{checked ? "Selected" : "Select"}</div>
                                        </div>
                                    );
                                })
                            )
                        ) : pickerMode === "workplace-agent" ? (
                            filteredWorkplaceAgents.length === 0 ? (
                                <div style={{ padding: "9px 12px", fontSize: 13, color: textSecondary }}>No matches</div>
                            ) : (
                                <>
                                    {workplaceAgentFlags?.allowSelectAll !== false && (
                                        <div
                                            onClick={selectAllWorkplaceAgents}
                                            style={{
                                                padding: "9px 12px",
                                                borderRadius: 10,
                                                cursor: "pointer",
                                                fontSize: 13,
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                background: selectedWorkplaceAgents.length === WORKPLACE_AGENTS.length && WORKPLACE_AGENTS.length > 0
                                                    ? (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)")
                                                    : "transparent",
                                            }}
                                        >
                                            <span style={{ color: textColor, fontWeight: 700 }}>Select All</span>
                                            <span style={{ fontSize: 12, color: textTertiary }}>
                                                {selectedWorkplaceAgents.length === WORKPLACE_AGENTS.length && WORKPLACE_AGENTS.length > 0 ? "Selected" : "Select"}
                                            </span>
                                        </div>
                                    )}
                                    {filteredWorkplaceAgents.map((x) => {
                                        const checked = selectedWorkplaceAgents.includes(x.key);
                                        return (
                                            <div
                                                key={x.key}
                                                onClick={() => toggleWorkplaceAgent(x.key)}
                                                style={{
                                                    padding: "9px 12px",
                                                    borderRadius: 10,
                                                    cursor: "pointer",
                                                    fontSize: 13,
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    gap: 10,
                                                    alignItems: "center",
                                                    background: checked ? (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)") : "transparent",
                                                    transition: "background 0.12s ease",
                                                }}
                                            >
                                                <div style={{ overflowWrap: "anywhere", display: "flex", alignItems: "center", gap: 10 }}>
                                                    <span
                                                        style={{
                                                            width: 18,
                                                            height: 18,
                                                            borderRadius: 4,
                                                            border: isDark || isContrast ? "1.5px solid rgba(255,255,255,0.4)" : "1.5px solid rgba(0,0,0,0.3)",
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: 12,
                                                            fontWeight: 700,
                                                            color: checked ? (isDark ? "#64b5f6" : "#0078d4") : textTertiary,
                                                        }}
                                                    >
                                                        {checked ? "✓" : ""}
                                                    </span>
                                                    <span style={{ color: textColor }}>{x.label}</span>
                                                </div>
                                                <div style={{ fontSize: 12, color: textTertiary }}>{checked ? "Selected" : "Select"}</div>
                                            </div>
                                        );
                                    })}
                                </>
                            )
                        ) : filteredIntegrations.length === 0 ? (
                            <div style={{ padding: "9px 12px", fontSize: 13, color: textSecondary }}>No matches</div>
                        ) : (
                            filteredIntegrations.map((x) => {
                                const checked = selectedIntegrations.includes(x.key);
                                return (
                                    <div
                                        key={x.key}
                                        onClick={() => {
                                            if (x.key === "ado") {
                                                setPickerMode(null);
                                                setPickerQuery("");
                                                openAdoBoardSearch();
                                                return;
                                            }
                                            toggleIntegration(x.key);
                                        }}
                                        style={{
                                            padding: "9px 12px",
                                            borderRadius: 10,
                                            cursor: "pointer",
                                            fontSize: 13,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            gap: 10,
                                            alignItems: "center",
                                            background: checked ? (isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)") : "transparent",
                                            transition: "background 0.12s ease",
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!checked) e.currentTarget.style.background = pickerHover;
                                        }}
                                        onMouseLeave={(e) => {
                                            if (!checked) e.currentTarget.style.background = "transparent";
                                        }}
                                    >
                                        <div style={{ overflowWrap: "anywhere", display: "flex", alignItems: "center", gap: 10 }}>
                                            <div style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: 8,
                                                background: checked ? `${x.color}22` : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                                                border: checked ? `1.5px solid ${x.color}66` : (isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)"),
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                                transition: "all 0.15s ease",
                                            }}>
                                                {x.icon}
                                            </div>
                                            <span style={{ color: textColor, fontWeight: checked ? 700 : 500 }}>{x.label}</span>
                                        </div>
                                                                <div style={{ fontSize: 12, color: textTertiary }}>{checked ? "Selected" : "Select"}</div>
                                                            </div>
                                                        );
                                                    })
                                                )}
                                               </div>
                                            </div>
                                        )}

                                           <div
                                               data-tour="composer"
                        style={{
                            border: surfaceBorder,
                            background: surfaceBg,
                            borderRadius: 16,
                            padding: 11,
                        }}
                    >
                    {(selectedIntegrations.length > 0 || selectedAgents.length > 0 || selectedWorkplaceAgents.length > 0) && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 9 }}>
                            {selectedIntegrations.map((k) => (
                                <div
                                    key={k}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "5px 11px",
                                        borderRadius: 999,
                                        background: chipBg,
                                        border: chipBorder,
                                        color: chipText,
                                        fontSize: 12,
                                    }}
                                >
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                                        <span style={{ display: "inline-flex", alignItems: "center" }}>
                                            {INTEGRATIONS.find(i => i.key === k)?.icon}
                                        </span>
                                        <span style={{ fontWeight: 700 }}>{integrationLabelByKey[k]}</span>
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeIntegrationPill(k)}
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            padding: 0,
                                            color: chipText,
                                            opacity: 0.85,
                                            fontSize: 16,
                                            lineHeight: "14px",
                                            fontWeight: 700,
                                        }}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {selectedAgents.map((k) => (
                                <div
                                    key={k}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "5px 11px",
                                        borderRadius: 999,
                                        background: chipBg,
                                        border: chipBorder,
                                        color: chipText,
                                        fontSize: 12,
                                    }}
                                >
                                    <span style={{ fontWeight: 700 }}>{agentLabelByKey[k]}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeAgentPill(k)}
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            padding: 0,
                                            color: chipText,
                                            opacity: 0.85,
                                            fontSize: 16,
                                            lineHeight: "14px",
                                            fontWeight: 700,
                                        }}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                            {selectedWorkplaceAgents.map((k) => (
                                <div
                                    key={`wa-${k}`}
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "5px 11px",
                                        borderRadius: 999,
                                        background: chipBg,
                                        border: chipBorder,
                                        color: chipText,
                                        fontSize: 12,
                                    }}
                                >
                                    <span style={{ fontWeight: 700 }}>{workplaceAgentLabelByKey[k] || k}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeWorkplaceAgentPill(k)}
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            padding: 0,
                                            color: chipText,
                                            opacity: 0.85,
                                            fontSize: 16,
                                            lineHeight: "14px",
                                            fontWeight: 700,
                                        }}
                                        title="Remove"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}

                        </div>
                    )}

                    <div style={{ position: "relative" }}>
                            <Input
                                style={{
                                    width: "100%",
                                    minWidth: 0,
                                    height: isCompact ? 38 : 48,
                                    fontSize: 14,
                                    background: "transparent",
                                    color: isDark ? "#e8e8e8" : "#242424",
                                    caretColor: isDark ? "#e8e8e8" : "#242424",
                                    border: "none",
                                    WebkitTextFillColor: isDark ? "#e8e8e8" : "#242424",
                                }}
                            value={draft}
                                placeholder={
                                    isSending ? "Sending…"
                                        : searchMode === "work"
                                            ? isCompact
                                                ? 'Message… ("@" Spark, "/" integrations)'
                                                : 'Type a message ("@" for Spark, "/" for integrations)'
                                            : searchMode === "sphere"
                                                ? 'Ask Sphere — search the S&P Global intranet…'
                                                : searchMode === "people"
                                                    ? 'Ask People — HR, wellness, benefits…'
                                                    : 'Ask anything'
                                }
                            onChange={(e) => handleDraftChange(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    if (pickerMode === "spark" && filteredSparks.length > 0) {
                                        selectSpark(filteredSparks[0]);
                                        return;
                                    }
                                    send();
                                }

                                if (e.key === "Escape") {
                                    setPickerMode(null);
                                    setPickerQuery("");
                                    setShareMenuForMessageId(null);
                                    setIsSparkStoreOpen(false);
                                }
                            }}
                        />
                        <style>{`
                            .${appTheme} input::placeholder {
                                color: ${inputPlaceholderColor};
                                opacity: 1;
                            }
                        `}</style>
                    </div>

                    <div style={{ marginTop: 9, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", gap: isCompact ? 4 : 8, alignItems: "center", flexWrap: "wrap" }}>

                                {/* ── Quick Spark Preset button (+) ── */}
                                {searchMode === "work" && (
                                    <div style={{ position: "relative" }}>
                                        <button
                                            type="button"
                                            title="Quick-select Classic Chat or Default Spark"
                                            onClick={() => setQuickSparkOpen(o => !o)}
                                            style={{
                                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                width: isCompact ? 26 : 30, height: isCompact ? 26 : 30, borderRadius: "50%",
                                                border: quickSparkOpen
                                                    ? "1.5px solid rgba(8,145,178,0.55)"
                                                    : (isDark ? "1px solid rgba(255,255,255,0.18)" : "1px solid rgba(0,0,0,0.16)"),
                                                background: quickSparkOpen
                                                    ? "rgba(8,145,178,0.15)"
                                                    : (isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)"),
                                                color: quickSparkOpen ? "#0891b2" : (isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.55)"),
                                                fontSize: 20, lineHeight: 1, cursor: "pointer",
                                                transition: "all 0.15s ease", flexShrink: 0,
                                            }}
                                        >+</button>
                                        {quickSparkOpen && (
                                            <>
                                                <div style={{ position: "fixed", inset: 0, zIndex: 890 }} onClick={() => setQuickSparkOpen(false)} />
                                                <div style={{
                                                    position: "absolute", bottom: "calc(100% + 8px)", left: 0,
                                                    zIndex: 891, minWidth: 300,
                                                    background: isDark ? "#1e1e2e" : "#ffffff",
                                                    border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                                    borderRadius: 12, boxShadow: isDark ? "0 8px 32px rgba(0,0,0,0.55)" : "0 8px 32px rgba(0,0,0,0.14)",
                                                    padding: "6px",
                                                    animation: "cspIn 0.15s cubic-bezier(0.16,1,0.3,1)",
                                                }}>
                                                    <div style={{ fontSize: 10, fontWeight: 700, color: isDark ? "rgba(255,255,255,0.35)" : "#9ca3af", textTransform: "uppercase", letterSpacing: "0.6px", padding: "4px 10px 6px" }}>Quick Select</div>
                                                    {PRESET_SPARKS.map(p => {
                                                        const isActive = selectedSpark?.sparkId === p.sparkId;
                                                        return (
                                                            <div key={p.sparkId} onClick={() => selectPresetSpark(p)}
                                                                style={{
                                                                    display: "flex", alignItems: "flex-start", gap: 10,
                                                                    padding: "9px 10px", borderRadius: 8, cursor: "pointer",
                                                                    background: isActive ? "rgba(8,145,178,0.12)" : "transparent",
                                                                    border: isActive ? "1px solid rgba(8,145,178,0.25)" : "1px solid transparent",
                                                                    transition: "background 0.12s",
                                                                }}
                                                                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
                                                                onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                                                            >
                                                                <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{p.icon}</span>
                                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                                    <div style={{ fontSize: 13, fontWeight: 700, color: isActive ? "#0891b2" : (isDark ? "#fff" : "#111827") }}>
                                                                        {p.sparkName}
                                                                        {isActive && <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, color: "#0891b2", background: "rgba(8,145,178,0.12)", padding: "1px 6px", borderRadius: 4 }}>active</span>}
                                                                    </div>
                                                                    <div style={{ fontSize: 11, color: isDark ? "rgba(255,255,255,0.45)" : "#6b7280", marginTop: 2, lineHeight: 1.4 }}>{p.desc}</div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Spark chip — only show in Work mode */}
                                {searchMode === "work" && (
                                    <div
                                        onClick={openSparkPickerFromChip}
                                        style={{
                                            display: "inline-flex", alignItems: "center",
                                            gap: isCompact ? 4 : 8,
                                            padding: isCompact ? "5px 9px" : "7px 12px",
                                            borderRadius: 999,
                                            background: chipBg, border: chipBorder, color: chipText,
                                            cursor: (selectedIntegrations.length > 0 || selectedWorkplaceAgents.length > 0) ? "default" : "pointer",
                                            fontSize: isCompact ? 11 : 12,
                                            whiteSpace: "nowrap", transition: "all 0.12s ease",
                                            opacity: (selectedIntegrations.length > 0 || selectedWorkplaceAgents.length > 0) ? 0.4 : 1,
                                            pointerEvents: (selectedIntegrations.length > 0 || selectedWorkplaceAgents.length > 0) ? "none" : "auto",
                                        }}
                                    >
                                        <span style={{ fontWeight: 800 }}>⚡</span>
                                        {isCompact ? (
                                            <span style={{ opacity: 0.9, maxWidth: 48, overflow: "hidden", textOverflow: "ellipsis" }}>
                                                {selectedSpark?.sparkName || "Classic"}
                                            </span>
                                        ) : (
                                            <>
                                                <span style={{ fontWeight: 800 }}>Spark</span>
                                                <span style={{ opacity: 0.9, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {selectedSpark?.sparkName || "Content Generator"}
                                                </span>
                                            </>
                                        )}
                                        <span style={{ opacity: 0.75 }}>▾</span>
                                    </div>
                                )}

                                {/* Integrations chip — hidden in web mode */}
                                {searchMode === "work" && (
                                    <div
                                        data-tour="integrations-chip"
                                        onClick={openIntegrationPickerFromChip}
                                        style={{
                                            display: "inline-flex", alignItems: "center",
                                            gap: isCompact ? 4 : 8,
                                            padding: isCompact ? "5px 9px" : "7px 12px",
                                            borderRadius: 999,
                                            background: chipBg, border: chipBorder, color: chipText,
                                            cursor: "pointer",
                                        fontSize: isCompact ? 11 : 12,
                                        whiteSpace: "nowrap", transition: "all 0.12s ease",
                                    }}
                                >
                                    {isCompact ? (
                                        <>
                                            <svg width="13" height="13" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                                                <rect x="1" y="1" width="7" height="7" rx="1.5" fill="#0078d4" />
                                                <rect x="10" y="1" width="7" height="7" rx="1.5" fill="#0078d4" opacity="0.7" />
                                                <rect x="1" y="10" width="7" height="7" rx="1.5" fill="#0078d4" opacity="0.7" />
                                                <rect x="10" y="10" width="7" height="7" rx="1.5" fill="#0078d4" opacity="0.5" />
                                            </svg>
                                            {selectedIntegrations.length > 0 && (
                                                <span style={{ fontWeight: 700 }}>{selectedIntegrations.length}</span>
                                            )}
                                            <span style={{ opacity: 0.75 }}>▾</span>
                                        </>
                                    ) : (
                                        <>
                                            <span style={{ fontWeight: 800 }}>Workplace Integrations</span>
                                            <span style={{ opacity: 0.85, fontWeight: 700 }}>{selectedIntegrations.length ? `(${selectedIntegrations.length})` : ""}</span>
                                            <span style={{ opacity: 0.75 }}>▾</span>
                                        </>
                                    )}
                                </div>
                            )}

                            {/* Agents chip — hidden in web mode */}
                            {/* 🆕 Agents feature flag check */}
                            {searchMode === "work" && flags.agents && (
                                <div
                                    onClick={openAgentPickerFromChip}
                                    style={{
                                        display: "inline-flex", alignItems: "center",
                                        gap: isCompact ? 4 : 8,
                                        padding: isCompact ? "5px 9px" : "7px 12px",
                                        borderRadius: 999,
                                        background: chipBg, border: chipBorder, color: chipText,
                                        cursor: "pointer",
                                        fontSize: isCompact ? 11 : 12,
                                        whiteSpace: "nowrap", transition: "all 0.12s ease",
                                    }}
                                >
                                    {isCompact ? (
                                        <>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="7" r="4" />
                                                <path d="M9 21v-2a4 4 0 0 1 4-4h0" />
                                                <path d="M9 15l1.5 2L12 16l1.5 1L15 15" strokeWidth="1.5" />
                                                <path d="M12 16v5M11 17.5l1 1.5 1-1.5" strokeWidth="1.5" />
                                            </svg>
                                            {selectedAgents.length > 0 && (
                                                <span style={{ fontWeight: 700 }}>{selectedAgents.length}</span>
                                            )}
                                            <span style={{ opacity: 0.75 }}>▾</span>
                                        </>
                                    ) : (
                                        <>
                                            <span style={{ fontWeight: 800 }}>Agents</span>
                                            <span style={{ opacity: 0.85, fontWeight: 700 }}>{selectedAgents.length ? `(${selectedAgents.length})` : ""}</span>
                                            <span style={{ opacity: 0.75 }}>▾</span>
                                        </>
                                    )}
                                </div>
                            )}

                            {searchMode === "work" && isWorkplaceAgentFrameworkEnabled && (
                                <div
                                    onClick={openWorkplaceAgentPickerFromChip}
                                    style={{
                                        display: "inline-flex", alignItems: "center",
                                        gap: isCompact ? 4 : 8,
                                        padding: isCompact ? "5px 9px" : "7px 12px",
                                        borderRadius: 999,
                                        background: chipBg, border: chipBorder, color: chipText,
                                        cursor: "pointer",
                                        fontSize: isCompact ? 11 : 12,
                                        whiteSpace: "nowrap", transition: "all 0.12s ease",
                                    }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                        <rect x="2" y="2" width="9" height="9" rx="2" fill="#f25022"/>
                                        <rect x="13" y="2" width="9" height="9" rx="2" fill="#7fba00"/>
                                        <rect x="2" y="13" width="9" height="9" rx="2" fill="#00a4ef"/>
                                        <rect x="13" y="13" width="9" height="9" rx="2" fill="#ffb900"/>
                                    </svg>
                                    <span style={{ fontWeight: 800 }}>Workplace Agents</span>
                                    <span style={{ opacity: 0.85, fontWeight: 700 }}>{selectedWorkplaceAgents.length ? `(${selectedWorkplaceAgents.length})` : ""}</span>
                                    <span style={{ opacity: 0.75 }}>▾</span>
                                </div>
                            )}
                        </div>

                        {/* ── RIGHT SIDE: Mic + Send ──────────────────── */}
                        {/* ── RIGHT SIDE: STT Toggle + Mic + Send ──────── */}
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>

                            {/* STT PROVIDER TOGGLE */}
                            {/* 🆕 KenshoGoogleSwitch feature flag check */}
                            {!meetingId && !isMobile && flags.kenshoGoogleSwitch && <div style={{
                                display: "flex",
                                alignItems: "center",
                                borderRadius: 999,
                                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
                                border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                                overflow: "hidden",
                            }}>
                                <button type="button" onClick={() => setSttProvider("google")} title="Use Google STT"
                                    style={{
                                        padding: "5px 10px", border: "none", cursor: "pointer", fontSize: 11,
                                        fontWeight: sttProvider === "google" ? 700 : 500,
                                        background: sttProvider === "google"
                                            ? (isDark ? "rgba(66,133,244,0.30)" : "rgba(66,133,244,0.15)")
                                            : "transparent",
                                        color: sttProvider === "google"
                                            ? (isDark ? "#7eb3ff" : "#1a73e8")
                                            : textSecondary,
                                        transition: "all 0.15s ease",
                                    }}>
                                    🔵 Google
                                </button>
                                <button type="button" onClick={() => setSttProvider("kensho")} title="Use Kensho Scribe"
                                    style={{
                                        padding: "5px 10px", border: "none", cursor: "pointer", fontSize: 11,
                                        fontWeight: sttProvider === "kensho" ? 700 : 500,
                                        background: sttProvider === "kensho"
                                            ? (isDark ? "rgba(0,180,120,0.25)" : "rgba(0,160,100,0.12)")
                                            : "transparent",
                                        color: sttProvider === "kensho"
                                            ? (isDark ? "#4ade80" : "#15803d")
                                            : textSecondary,
                                        transition: "all 0.15s ease",
                                    }}>
                                    🟢 Kensho
                                </button>
                            </div>}

                            {/* AI SETTINGS SLIDERS BUTTON — only shown for Classic Chat */}
                            {selectedSpark?.sparkId === 1 && (() => {
                                const hasCustom = modelSettings.modelId !== "GPT-4.1" || modelSettings.styleKey !== "Balanced" || modelSettings.responseLength !== "Medium";
                                return (
                                    <div style={{ position: "relative" }}>
                                        <button
                                            ref={modelSettingsBtnRef}
                                            type="button"
                                            title="AI Settings — model, style & length"
                                            onClick={(e) => toggleModelSettings(e)}
                                            data-model-popup
                                            style={{
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                width: 36, height: 36, borderRadius: 10, cursor: "pointer",
                                                border: isModelSettingsOpen ? "1.5px solid rgba(8,145,178,0.55)" : hasCustom ? "1.5px solid rgba(8,145,178,0.38)" : (isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)"),
                                                background: isModelSettingsOpen ? "rgba(8,145,178,0.15)" : hasCustom ? "rgba(8,145,178,0.08)" : (isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"),
                                                color: isModelSettingsOpen || hasCustom ? "#0891b2" : (isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.55)"),
                                                transition: "all 0.15s ease", position: "relative",
                                            }}
                                        >
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
                                                <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
                                                <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
                                                <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
                                                <line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/>
                                            </svg>
                                            {hasCustom && !isModelSettingsOpen && (
                                                <div style={{ position: "absolute", top: 4, right: 4, width: 6, height: 6, borderRadius: "50%", background: "#0891b2", boxShadow: "0 0 4px rgba(8,145,178,0.8)" }} />
                                            )}
                                        </button>
                                    </div>
                                );
                            })()}

                            {/* MIC BUTTON — Flow A single utterance */}
                            {/* 🆕 Voice feature flag check */}
                            {flags.voice && (
                                <IconButton
                                    title={
                                        isTranscribing ? "Transcribing…" :
                                            isListening ? "Stop recording" :
                                                "Speak your message"
                                    }
                                    onClick={isListening ? stopSpeechCapture : startSpeechCapture}
                                    disabled={isTranscribing}
                                    primary
                                    style={{
                                        background: isListening
                                            ? "rgba(220, 38, 38, 0.15)"
                                            : isTranscribing
                                                ? "rgba(234, 179, 8, 0.15)"
                                                : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)",
                                        border: isListening
                                            ? "1px solid rgba(220, 38, 38, 0.50)"
                                            : isTranscribing
                                                ? "1px solid rgba(234, 179, 8, 0.40)"
                                                : isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
                                        // Reuses existing ringPulse keyframe
                                        animation: isListening ? "ringPulse 1.2s ease-in-out infinite" : "none",
                                    }}
                                >
                                    {isTranscribing ? (
                                        // Spinner — reuses existing "spin" keyframe
                                        <div style={{
                                            width: 16, height: 16, borderRadius: "50%",
                                            border: "2px solid rgba(234,179,8,0.3)",
                                            borderTopColor: "#eab308",
                                            animation: "spin 0.7s linear infinite",
                                        }} />
                                    ) : (
                                        // Mic SVG — inline, no library
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                                            aria-hidden="true"
                                            style={{ color: isListening ? "#ef4444" : primaryIconColor }}>
                                            <rect x="9" y="2" width="6" height="12" rx="3"
                                                stroke="currentColor" strokeWidth="2" />
                                            <path d="M5 10a7 7 0 0 0 14 0"
                                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            <line x1="12" y1="17" x2="12" y2="21"
                                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                            <line x1="9" y1="21" x2="15" y2="21"
                                                stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                        </svg>
                                    )}
                                </IconButton>
                            )}

                            {/* SEND BUTTON — completely unchanged from original */}
                            <IconButton
                                title={canSend ? "Send" : "Type a message to send"}
                                onClick={send}
                                disabled={!canSend}
                                primary
                            >
                                <span style={{ fontSize: 18, color: canSend ? primaryIconColor : iconColor }}>➤</span>
                            </IconButton>

                        </div>
                    </div>
                </div>

                {/* Flow A speech status — recording/transcribing/error */}
                {/* 🆕 Voice feature flag check */}
                {flags.voice && (isListening || isTranscribing || speechError) && (
                    <div style={{
                        marginTop: 5, fontSize: 11,
                        display: "flex", alignItems: "center", gap: 6,
                        color: speechError
                            ? (isDark ? "#fca5a5" : "#b91c1c")
                            : isTranscribing ? "#eab308" : "#ef4444",
                    }}>
                        {(isListening || isTranscribing) && (
                            <div style={{
                                width: 7, height: 7, borderRadius: "50%",
                                background: isTranscribing ? "#eab308" : "#ef4444",
                                flexShrink: 0,
                                animation: "ringPulse 1s ease-in-out infinite",
                            }} />
                        )}
                        {isListening ? "🎙️ Recording… tap mic to stop" :
                            isTranscribing ? `⏳ Transcribing with ${sttProvider === "kensho" ? "Kensho Scribe" : "Google STT"}…` :
                                speechError}
                    </div>
                )}

                {/* Original tip line — keep exactly as-is */}
                    {!meetingId && !isMobile && (
                        <div style={{ marginTop: 9, fontSize: 12, color: textTertiary }}>
                            {searchMode === "work" ? (
                                <>Tip: Type <b>@</b> to switch Sparks, type <b>/</b> for integrations.</>
                            ) : searchMode === "sphere" ? (
                                <>Searching <b>Sphere</b> — S&P Global's intranet. Switch to <b>Work</b> tab to use integrations.</>
                            ) : searchMode === "people" ? (
                                <>Asking <b>Ask People</b> — HR, wellness and people queries. Switch to <b>Work</b> tab for full features.</>
                            ) : (
                                <>Searching with <b>Web</b>. Switch to <b>Work</b> tab to use integrations and custom Sparks.</>
                            )}
                        </div>
                    )}

            </div>



            {/* 🆕 2024-12-20: Enhanced Spark Store with Search, Tabs, and Sort */}
            {/* ═══════════════════════════════════════════
            MODEL SETTINGS POPUP
            ═══════════════════════════════════════════ */}
            {isModelSettingsOpen && (
                <div data-model-popup>
                    {/* Backdrop */}
                    <div
                        style={{ position: "fixed", inset: 0, zIndex: 9990 }}
                        onClick={() => { setIsModelSettingsOpen(false); setIsModelDropdownOpen(false); }}
                    />
                    {/* Panel */}
                    <div
                        data-model-popup
                        style={{
                            position: "fixed",
                            zIndex: 9991,
                            ...(isMobile ? {
                                bottom: 0, left: 0, right: 0,
                                borderRadius: "20px 20px 0 0",
                                maxHeight: "88dvh",
                            } : {
                                ...modelPopupStyle,
                                borderRadius: 18,
                            }),
                            background: isDark ? "#13131c" : "#ffffff",
                            border: isDark ? "1px solid rgba(255,255,255,0.11)" : "1px solid rgba(0,0,0,0.09)",
                            boxShadow: isDark
                                ? "0 28px 80px rgba(0,0,0,0.80), 0 0 0 1px rgba(255,255,255,0.04)"
                                : "0 24px 64px rgba(0,0,0,0.17), 0 4px 16px rgba(0,0,0,0.08)",
                            overflowY: "auto", overflowX: "hidden",
                        }}
                    >
                        {/* Mobile drag handle */}
                        {isMobile && (
                            <div style={{ display: "flex", justifyContent: "center", padding: "12px 0 4px" }}>
                                <div style={{ width: 36, height: 4, borderRadius: 2, background: isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.14)" }} />
                            </div>
                        )}

                        {/* ── HEADER ── */}
                        <div style={{
                            padding: "18px 20px 14px",
                            borderBottom: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
                            background: isDark
                                ? "rgba(8,145,178,0.08)"
                                : "rgba(8,145,178,0.04)",
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                                <div style={{
                                    width: 38, height: 38, borderRadius: 11, flexShrink: 0,
                                    background: "#0891b2",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    boxShadow: "0 4px 14px rgba(8,145,178,0.35)",
                                }}>
                                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round">
                                        <line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/>
                                        <line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/>
                                        <line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/>
                                        <line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/>
                                    </svg>
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 15, fontWeight: 800, color: textColor, letterSpacing: "-0.2px" }}>AI Settings</div>
                                    <div style={{ fontSize: 11, color: textSecondary, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {(() => {
                                            const prov = aiModels.find(g => g.models.some(m => m.id === modelSettings.modelId));
                                            return `${prov?.provider ?? ""} · ${modelSettings.modelId} · ${modelSettings.styleKey} · ${modelSettings.responseLength}`;
                                        })()}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => { setIsModelSettingsOpen(false); setIsModelDropdownOpen(false); }}
                                    style={{ border: "none", background: "transparent", cursor: "pointer", color: textSecondary, fontSize: 18, padding: "4px 6px", borderRadius: 7, lineHeight: 1, flexShrink: 0 }}
                                    onMouseEnter={e => e.currentTarget.style.background = iconHoverBg}
                                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                                >✕</button>
                            </div>
                        </div>

                        {/* ── BODY ── */}
                            <div style={{ padding: "18px 20px 22px", display: "flex", flexDirection: "column", gap: 22 }}>

                                {isLoadingAiSettings && (
                                    <div style={{
                                        padding: "10px 14px", borderRadius: 8, fontSize: 12,
                                        background: isDark ? "rgba(8,145,178,0.12)" : "rgba(8,145,178,0.08)",
                                        border: "1px solid rgba(8,145,178,0.25)",
                                        color: textSecondary,
                                        display: "flex", alignItems: "center", gap: 8, marginBottom: 16
                                    }}>
                                        <div style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(8,145,178,0.33)", borderTopColor: "#0891b2", animation: "spin 0.7s linear infinite" }} />
                                        Loading latest model configurations...
                                    </div>
                                )}
                                {aiSettingsError && (
                                    <div style={{
                                        padding: "10px 14px", borderRadius: 8, fontSize: 12,
                                        background: "rgba(245,158,11,0.10)", border: "1px solid rgba(245,158,11,0.24)",
                                        color: isDark ? "#fbbf24" : "#d97706", marginBottom: 16
                                    }}>
                                        ⚠ {aiSettingsError}
                                    </div>
                                )}

                            {/* ── SECTION: MODEL ── */}
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.8px", color: textTertiary, marginBottom: 10 }}>
                                    Choose Model
                                </div>

                                {/* Dropdown trigger */}
                                <div
                                    onClick={() => setIsModelDropdownOpen(prev => !prev)}
                                    style={{
                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                        padding: "10px 13px", borderRadius: 10, cursor: "pointer", userSelect: "none",
                                        border: isModelDropdownOpen
                                            ? "1.5px solid rgba(8,145,178,0.55)"
                                            : isDark ? "1.5px solid rgba(255,255,255,0.12)" : "1.5px solid rgba(0,0,0,0.11)",
                                        background: isModelDropdownOpen
                                            ? (isDark ? "rgba(8,145,178,0.11)" : "rgba(8,145,178,0.05)")
                                            : (isDark ? "rgba(255,255,255,0.05)" : "#fafafa"),
                                        transition: "all 0.15s ease",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                                        {(() => {
                                            const prov = aiModels.find(g => g.models.some(m => m.id === modelSettings.modelId));
                                            const allModels = aiModels.flatMap(g => g.models);
                                            const mObj = allModels.find(x => x.id === modelSettings.modelId);
                                            return (
                                                <>
                                                    <div style={{ width: 9, height: 9, borderRadius: "50%", flexShrink: 0, background: prov?.color ?? "#888", boxShadow: `0 0 6px ${prov?.color ?? "#888"}66` }} />
                                                    <span style={{ fontSize: 11, color: textSecondary, fontWeight: 600, flexShrink: 0 }}>{prov?.provider ?? "Model"}</span>
                                                    <div style={{ width: 1, height: 11, background: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)", flexShrink: 0 }} />
                                                    <span style={{ fontSize: 13, fontWeight: 800, color: textColor, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                        {modelSettings.modelId}
                                                    </span>
                                                    {mObj?.badge && (
                                                        <span style={{
                                                            fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 4, flexShrink: 0,
                                                            background: `${prov?.color ?? "#888"}20`, color: prov?.color ?? "#888",
                                                            border: `1px solid ${prov?.color ?? "#888"}35`,
                                                        }}>{mObj.badge}</span>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={textSecondary} strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink: 0, transform: isModelDropdownOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                                        <polyline points="6 9 12 15 18 9"/>
                                    </svg>
                                </div>

                                {/* Dropdown list */}
                                {isModelDropdownOpen && (
                                    <div style={{
                                        marginTop: 6, borderRadius: 10, overflow: "hidden",
                                        border: isDark ? "1px solid rgba(255,255,255,0.11)" : "1px solid rgba(0,0,0,0.10)",
                                        background: isDark ? "#1a1a26" : "#ffffff",
                                        boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.55)" : "0 12px 32px rgba(0,0,0,0.14)",
                                        maxHeight: 228, overflowY: "auto",
                                    }}>
                                        {aiModels.map((group, gi) => (
                                            <div key={group.provider}>
                                                <div style={{
                                                    display: "flex", alignItems: "center", gap: 7,
                                                    padding: "8px 13px 5px",
                                                    borderTop: gi > 0 ? (isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.06)") : "none",
                                                }}>
                                                    <div style={{ width: 7, height: 7, borderRadius: "50%", background: group.color }} />
                                                    <span style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.6px", color: group.color }}>
                                                        {group.provider}
                                                    </span>
                                                </div>
                                                {group.models.map(m => {
                                                    const sel = modelSettings.modelId === m.id;
                                                    return (
                                                        <div
                                                            key={m.id}
                                                            onClick={() => { updateModelSettings({ modelId: m.id }); setIsModelDropdownOpen(false); }}
                                                            style={{
                                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                                padding: "9px 13px", cursor: "pointer",
                                                                background: sel ? `${group.color}12` : "transparent",
                                                                transition: "background 0.12s ease",
                                                            }}
                                                            onMouseEnter={e => { if (!sel) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
                                                            onMouseLeave={e => { if (!sel) e.currentTarget.style.background = "transparent"; }}
                                                        >
                                                            <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                                                                <div style={{
                                                                    width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
                                                                    background: sel ? group.color : (isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"),
                                                                    boxShadow: sel ? `0 0 5px ${group.color}` : "none",
                                                                    transition: "all 0.15s",
                                                                }} />
                                                                <span style={{ fontSize: 13, fontWeight: sel ? 700 : 500, color: sel ? textColor : textSecondary }}>
                                                                    {m.label}
                                                                </span>
                                                            </div>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                                                                {m.badge && (
                                                                    <span style={{
                                                                        fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4,
                                                                        background: `${group.color}18`, color: group.color,
                                                                        border: `1px solid ${group.color}30`,
                                                                    }}>{m.badge}</span>
                                                                )}
                                                                {sel && (
                                                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={group.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="20 6 9 17 4 12" />
                                                                    </svg>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* ── SECTION: CONVERSATIONAL STYLE ── */}
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.8px", color: textTertiary, marginBottom: 10 }}>
                                    Conversational Style
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                                    {conversationStyles.map(style => {
                                        const active = modelSettings.styleKey === style.key;
                                        return (
                                            <div
                                                key={style.key}
                                                onClick={() => updateModelSettings({ styleKey: style.key, temperature: style.temp })}
                                                style={{
                                                    padding: "13px 8px 11px", borderRadius: 12, cursor: "pointer",
                                                    textAlign: "center", position: "relative", overflow: "hidden",
                                                    border: active ? `1.5px solid ${style.color}60` : isDark ? "1.5px solid rgba(255,255,255,0.09)" : "1.5px solid rgba(0,0,0,0.09)",
                                                    background: active
                                                        ? `linear-gradient(145deg, ${style.color}16, ${style.color}08)`
                                                        : isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                                                    transition: "all 0.15s ease",
                                                }}
                                                onMouseEnter={e => { if (!active) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)"; }}
                                                onMouseLeave={e => { if (!active) e.currentTarget.style.background = active ? `linear-gradient(145deg, ${style.color}16, ${style.color}08)` : isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"; }}
                                            >
                                                {active && (
                                                    <div style={{ position: "absolute", top: 7, right: 7, width: 16, height: 16, borderRadius: "50%", background: style.color, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                    </div>
                                                )}
                                                <div style={{ fontSize: 24, marginBottom: 7, lineHeight: 1 }}>{style.icon}</div>
                                                <div style={{ fontSize: 12, fontWeight: 800, color: active ? style.color : textColor, letterSpacing: "-0.1px" }}>{style.key}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* ── SECTION: RESPONSE LENGTH ── */}
                            <div>
                                <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.8px", color: textTertiary, marginBottom: 10 }}>
                                    Response Length
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 }}>
                                    {responseLengths.map(len => {
                                        const active = modelSettings.responseLength === len.key;
                                        const barColor = "#0891b2";
                                        const barWidths = [10, 18, 26];
                                        return (
                                            <div
                                                key={len.key}
                                                onClick={() => updateModelSettings({ responseLength: len.key })}
                                                style={{
                                                    padding: "13px 8px 11px", borderRadius: 12, cursor: "pointer",
                                                    textAlign: "center", position: "relative",
                                                    border: active ? `1.5px solid ${barColor}55` : isDark ? "1.5px solid rgba(255,255,255,0.09)" : "1.5px solid rgba(0,0,0,0.09)",
                                                    background: active
                                                        ? `linear-gradient(145deg, ${barColor}13, ${barColor}07)`
                                                        : isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
                                                    transition: "all 0.15s ease",
                                                }}
                                                onMouseEnter={e => { if (!active) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.04)"; }}
                                                onMouseLeave={e => { if (!active) e.currentTarget.style.background = active ? `linear-gradient(145deg, ${barColor}13, ${barColor}07)` : isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)"; }}
                                            >
                                                {active && (
                                                    <div style={{ position: "absolute", top: 7, right: 7, width: 16, height: 16, borderRadius: "50%", background: barColor, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                                        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                                    </div>
                                                )}
                                                <div style={{ display: "flex", gap: 3, justifyContent: "center", alignItems: "flex-end", marginBottom: 9, height: 14 }}>
                                                    {barWidths.map((w, i) => (
                                                        <div key={i} style={{
                                                            width: w, height: 4 + i * 2, borderRadius: 3,
                                                            background: i < len.barCount
                                                                ? (active ? barColor : (isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.25)"))
                                                                : (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)"),
                                                            transition: "background 0.15s",
                                                        }} />
                                                    ))}
                                                </div>
                                                <div style={{ fontSize: 12, fontWeight: 800, color: active ? barColor : textColor }}>{len.key}</div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Footer */}
                            <div style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center", paddingTop: 2 }}>
                                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#10b981", boxShadow: "0 0 6px #10b98155" }} />
                                <span style={{ fontSize: 11, color: textTertiary, fontWeight: 600 }}>Changes apply live to all messages</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {
                isSparkStoreOpen && (
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.55)",
                            display: "flex",
                            alignItems: isMobile ? "flex-end" : "center",
                            justifyContent: "center",
                            padding: isMobile ? 0 : 16,
                            zIndex: 9999,
                        }}
                        onClick={() => {
                            setIsSparkStoreOpen(false);
                            setSparkStoreSearch("");
                            setSparkStoreCategory("all");
                            setSparkStoreSortBy("most_liked");
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: isMobile ? "100vw" : "min(1000px, 96vw)",
                                maxHeight: isMobile ? "88dvh" : "90vh",
                                overflow: "hidden",
                                borderRadius: isMobile ? "16px 16px 0 0" : 14,
                                background: isDark ? "#16161f" : isContrast ? "#000000" : "#ffffff",
                                color: textColor,
                                boxShadow: isDark ? "0 16px 44px rgba(0,0,0,0.65)" : "0 16px 44px rgba(0,0,0,0.25)",
                                border: isDark || isContrast ? "1px solid rgba(255, 255, 255, 0.14)" : "1px solid rgba(0,0,0,0.10)",
                                display: "flex",
                                flexDirection: "column",
                            }}
                        >
                            {/* Header */}
                            <div
                                style={{
                                    padding: "14px 16px",
                                    borderBottom: isDark || isContrast ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.08)",
                                    background: isDark ? "rgba(8,145,178,0.12)" : "rgba(8,145,178,0.07)",
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                    <div
                                        style={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 10,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: isDark ? "rgba(8,145,178,0.22)" : "rgba(8,145,178,0.12)",
                                            border: isDark ? "1px solid rgba(8,145,178,0.30)" : "1px solid rgba(8,145,178,0.22)",
                                        }}
                                    >
                                        <SparkStoreIcon size={20} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <Text weight="semibold" size={500} style={{ color: textColor }}>
                                            Spark Store
                                        </Text>
                                        <div style={{ marginTop: 2, fontSize: 11, color: textSecondary, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                                            <span>
                                                Showing {filteredSparkStoreItems.length} of {allSparkStoreItems.length} Sparks
                                            </span>
                                            {isDynamicSparksLoading && sparkSyncTotal > 0 && (
                                                <span style={{ color: primaryIconColor, fontWeight: 600 }}>
                                                    · syncing {sparkSyncTotal.toLocaleString()}…
                                                </span>
                                            )}
                                            {!isDynamicSparksLoading && lastSyncedAt && (
                                                <span style={{ opacity: 0.55 }}>
                                                    · synced {lastSyncedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                                </span>
                                            )}
                                        </div>

                                        {/* 🆕 Helpful hint in separate line */}
                                        {!isDynamicSparksLoading && (
                                            <div style={{
                                                fontSize: 10,
                                                color: textSecondary,
                                                fontStyle: "italic",
                                            }}>
                                                Hint : Looking for a newly created Spark? Click the refresh icon to see the latest updates
                                            </div>
                                        )}

                                        {/* Progress bar — visible during sync */}
                                        {isDynamicSparksLoading && sparkSyncProgress > 0 && (
                                            <div style={{
                                                marginTop: 6,
                                                height: 3,
                                                borderRadius: 999,
                                                background: isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)",
                                                overflow: "hidden",
                                                width: "100%",
                                            }}>
                                                <div style={{
                                                    height: "100%",
                                                    borderRadius: 999,
                                                    width: `${sparkSyncProgress}%`,
                                                    background: `linear-gradient(90deg, #0891b2, #0e7490)`,
                                                    transition: "width 0.4s ease",
                                                }} />
                                            </div>
                                        )}

                                        {/* Completion flash */}
                                        {!isDynamicSparksLoading && sparkSyncProgress === 100 && (
                                            <div style={{
                                                marginTop: 4, fontSize: 10, fontWeight: 700,
                                                color: "#22c55e",
                                                animation: "fadeIn 0.3s ease",
                                            }}>
                                                ✅ Sync complete — {sparkSyncTotal.toLocaleString()} sparks loaded
                                            </div>
                                        )}
                                    </div>

                                    {/* Sync button */}
                                    <button
                                        type="button"
                                        title={isDynamicSparksLoading
                                            ? "Syncing…"
                                            : `Sync all Sparks from catalog\nLast synced: ${lastSyncedAt ? lastSyncedAt.toLocaleString() : "never"}`}
                                        onClick={forceFullSync}
                                        disabled={isDynamicSparksLoading}
                                        style={{
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
                                            border: isDark
                                                ? "1px solid rgba(255,255,255,0.12)"
                                                : "1px solid rgba(0,0,0,0.12)",
                                            background: "transparent",
                                            cursor: isDynamicSparksLoading ? "not-allowed" : "pointer",
                                            opacity: isDynamicSparksLoading ? 0.45 : 1,
                                            color: primaryIconColor,
                                            transition: "background 0.15s, transform 0.15s",
                                        }}
                                        onMouseEnter={(e) => {
                                            if (!isDynamicSparksLoading)
                                                e.currentTarget.style.background = iconHoverBg;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = "transparent";
                                        }}
                                    >
                                        {isDynamicSparksLoading ? (
                                            <div style={{
                                                width: 14, height: 14, borderRadius: "50%",
                                                border: `2px solid ${primaryIconColor}33`,
                                                borderTopColor: primaryIconColor,
                                                animation: "spin 0.7s linear infinite",
                                            }} />
                                        ) : (
                                            <svg width="16" height="16" viewBox="0 0 24 24"
                                                fill="none" stroke="currentColor"
                                                strokeWidth="2.2" strokeLinecap="round"
                                                strokeLinejoin="round">
                                                <path d="M23 4v6h-6" />
                                                <path d="M1 20v-6h6" />
                                                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
                                                <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
                                            </svg>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsSparkStoreOpen(false);
                                            setSparkStoreSearch("");
                                            setSparkStoreCategory("all");
                                            setSparkStoreSortBy("most_liked");
                                        }}
                                        style={{
                                            border: "none",
                                            background: "transparent",
                                            cursor: "pointer",
                                            color: textSecondary,
                                            fontSize: 20,
                                            padding: "6px 10px",
                                            borderRadius: 10,
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.background = iconHoverBg)}
                                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                                        aria-label="Close"
                                    >
                                        ✕
                                    </button>
                                </div>

                                {/* 🆕 Search Bar and Sort Dropdown Row */}
                                <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
                                    {/* Search Input */}
                                    <div style={{ flex: 1 }}>
                                        <Input
                                            value={sparkStoreSearch}
                                                onChange={(e) => {
                                                    const searchTerm = e.target.value;
                                                    setSparkStoreSearch(searchTerm);
                                                    setSparkStoreDisplayLimit(50);

                                                    // Track search after 500ms delay (debounced)
                                                    if (searchTerm.trim().length >= 3) {
                                                        clearTimeout(window._sparkSearchTimeout);
                                                        window._sparkSearchTimeout = setTimeout(() => {
                                                            UsageLogService.trackSearch(
                                                                searchTerm,
                                                                filteredSparkStoreItems.length,
                                                                {
                                                                    source: 'SparkStore',
                                                                    category: sparkStoreCategory,
                                                                    sortBy: sparkStoreSortBy
                                                                }
                                                            );
                                                        }, 500);
                                                    }
                                                }}
                                            placeholder="🔍 Search Spark name, tag, creator name or email"
                                            style={{
                                                width: "100%",
                                                fontSize: 14,
                                                background: isDark ? "rgba(255,255,255,0.045)" : "#ffffff",
                                                border: isDark ? "1.5px solid rgba(255,255,255,0.13)" : "1.5px solid rgba(0,0,0,0.13)",
                                                color: textColor,
                                                WebkitTextFillColor: isDark || isContrast ? "#fff" : "#111827",
                                            }}
                                        />
                                    </div>

                                    {/* 🆕 Sort filter icon + custom dropdown */}
                                    <div style={{ position: "relative" }}>
                                        <button
                                            type="button"
                                            title="Sort options"
                                            onClick={() => setSparkStoreSortMenuOpen(o => !o)}
                                            style={{
                                                display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                                                height: 38, padding: "0 12px", borderRadius: 9, cursor: "pointer",
                                                border: sparkStoreSortMenuOpen || sparkStoreSortBy !== "most_liked"
                                                    ? "1.5px solid rgba(8,145,178,0.55)"
                                                    : (isDark ? "1.5px solid rgba(255,255,255,0.13)" : "1.5px solid rgba(0,0,0,0.13)"),
                                                background: sparkStoreSortMenuOpen || sparkStoreSortBy !== "most_liked"
                                                    ? "rgba(8,145,178,0.12)"
                                                    : (isDark ? "rgba(255,255,255,0.045)" : "#ffffff"),
                                                color: sparkStoreSortMenuOpen || sparkStoreSortBy !== "most_liked" ? "#0891b2" : textSecondary,
                                                transition: "all 0.15s ease", flexShrink: 0,
                                            }}
                                        >
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/>
                                                <line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                                            </svg>
                                            <span style={{ fontSize: 12, fontWeight: 700 }}>
                                                {sparkStoreSortBy === "most_liked" ? "Sort" :
                                                 sparkStoreSortBy === "creation_date" ? "Date" :
                                                 sparkStoreSortBy === "spark_name" ? "Name" :
                                                 sparkStoreSortBy === "creator_name" ? "Creator" : "Updated"}
                                            </span>
                                            {sparkStoreSortBy !== "most_liked" && (
                                                <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#0891b2", marginLeft: 2, boxShadow: "0 0 4px rgba(8,145,178,0.8)" }} />
                                            )}
                                        </button>
                                        {sparkStoreSortMenuOpen && (
                                            <>
                                                <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => setSparkStoreSortMenuOpen(false)} />
                                                <div style={{
                                                    position: "absolute", top: "calc(100% + 6px)", right: 0,
                                                    zIndex: 9999, minWidth: 200,
                                                    background: isDark ? "#16161f" : "#ffffff",
                                                    border: isDark ? "1.5px solid rgba(255,255,255,0.10)" : "1.5px solid rgba(0,0,0,0.10)",
                                                    borderRadius: 10, boxShadow: isDark ? "0 8px 28px rgba(0,0,0,0.5)" : "0 8px 28px rgba(0,0,0,0.12)",
                                                    padding: "4px",
                                                    animation: "cspIn 0.15s cubic-bezier(0.16,1,0.3,1)",
                                                }}>
                                                    <div style={{ fontSize: 10, fontWeight: 700, color: isDark ? "rgba(255,255,255,0.35)" : "#9ca3af", textTransform: "uppercase", letterSpacing: "0.6px", padding: "5px 10px 6px" }}>Sort by</div>
                                                    {[
                                                        { value: "most_liked",    label: "Most Liked",    icon: "🔥" },
                                                        { value: "creation_date", label: "Creation Date", icon: "📅" },
                                                        { value: "spark_name",    label: "Spark Name",    icon: "🔤" },
                                                        { value: "creator_name",  label: "Creator Name",  icon: "👤" },
                                                        { value: "updated_date",  label: "Updated Date",  icon: "🔄" },
                                                    ].map(opt => {
                                                        const isSel = sparkStoreSortBy === opt.value;
                                                        return (
                                                            <div key={opt.value}
                                                                onClick={() => { setSparkStoreSortBy(opt.value); setSparkStoreSortMenuOpen(false); }}
                                                                style={{
                                                                    display: "flex", alignItems: "center", gap: 8,
                                                                    padding: "8px 10px", borderRadius: 7, cursor: "pointer",
                                                                    background: isSel ? "rgba(8,145,178,0.12)" : "transparent",
                                                                    border: isSel ? "1px solid rgba(8,145,178,0.25)" : "1px solid transparent",
                                                                    color: isSel ? "#0891b2" : (isDark ? "rgba(255,255,255,0.85)" : "#111827"),
                                                                    fontSize: 13, fontWeight: isSel ? 700 : 500,
                                                                    transition: "background 0.1s",
                                                                }}
                                                                onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)"; }}
                                                                onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = "transparent"; }}
                                                            >
                                                                <span>{opt.icon}</span>
                                                                <span style={{ flex: 1 }}>{opt.label}</span>
                                                                {isSel && <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Category Tabs */}
                                <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
                                    {[
                                        { key: "all", label: "All Sparks" },
                                        { key: "certified", label: "Certified" },
                                        { key: "my_likes", label: "My Likes" },
                                        // { key: "shared", label: "Shared with Me" },
                                        { key: "recent", label: "Recently Added" },
                                    ].map((cat) => {
                                        const isActive = sparkStoreCategory === cat.key;
                                        return (
                                            <button
                                                key={cat.key}
                                                type="button"
                                                onClick={() => {
                                                    setSparkStoreCategory(cat.key);
                                                    setSparkStoreDisplayLimit(50);
                                                }}
                                                style={{
                                                    padding: "7px 14px",
                                                    borderRadius: 8,
                                                    border: isActive
                                                        ? "1.5px solid rgba(8,145,178,0.55)"
                                                        : isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                                    background: isActive
                                                        ? "rgba(8,145,178,0.14)"
                                                        : (isDark ? "rgba(255,255,255,0.03)" : "transparent"),
                                                    color: isActive ? "#0891b2" : textColor,
                                                    fontSize: 13,
                                                    fontWeight: isActive ? 700 : 500,
                                                    cursor: "pointer",
                                                    transition: "all 0.15s ease",
                                                }}
                                                onMouseEnter={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isActive) {
                                                        e.currentTarget.style.background = "transparent";
                                                    }
                                                }}
                                            >
                                                {cat.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Content */}
                            <div ref={sparkStoreScrollRef} style={{ padding: 14, overflowY: "auto", flex: 1 }}>
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                        gap: 12,
                                    }}
                                >
                                    {filteredSparkStoreItems.length === 0 ? (
                                        <div style={{
                                            gridColumn: "1 / -1",
                                            padding: 40,
                                            textAlign: "center",
                                            color: textSecondary,
                                        }}>
                                            <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
                                            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
                                                No sparks found
                                            </div>
                                            <div style={{ fontSize: 13 }}>
                                                Try a different search term or category
                                            </div>
                                        </div>
                                    ) : (
                                        filteredSparkStoreItems.slice(0, sparkStoreDisplayLimit).map((it) => {
                                            const isActive = selectedSpark?.sparkId === it.sparkId;

                                            return (
                                                <div
                                                    key={it.sparkId}
                                                    style={{
                                                        borderRadius: 14,
                                                        border: isActive
                                                            ? `2px solid ${it.accent}99`
                                                            : isDark || isContrast ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                                        background: isActive
                                                            ? `${it.accent}0D`
                                                            : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.03)",
                                                        padding: isActive ? 11 : 12,
                                                        display: "flex",
                                                        gap: 12,
                                                        alignItems: "flex-start",
                                                        transition: "transform 0.14s ease, box-shadow 0.14s ease",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.transform = "translateY(-2px)";
                                                        e.currentTarget.style.boxShadow = isDark
                                                            ? "0 14px 34px rgba(0,0,0,0.45)"
                                                            : "0 14px 34px rgba(0,0,0,0.14)";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.transform = "translateY(0)";
                                                        e.currentTarget.style.boxShadow = "none";
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: 44,
                                                            height: 44,
                                                            borderRadius: 14,
                                                            background: `${it.accent}22`,
                                                            border: `1px solid ${it.accent}40`,
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            fontSize: 20,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {it.icon}
                                                    </div>

                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                            <div style={{ fontWeight: 800, color: textColor, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {it.title}
                                                            </div>

                                                            <div style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 6, color: textSecondary, fontSize: 12 }}>
                                                                <span>★</span>
                                                                <span style={{ fontWeight: 700 }}>{formatLikes(it.likes)}</span>
                                                            </div>
                                                        </div>

                                                        {/* 🆕 Creator Name */}
                                                        <div style={{ marginTop: 4, fontSize: 11, color: textTertiary }}>
                                                            by {it.creatorName}
                                                        </div>

                                                        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                                            {it.tags.map((t) => (
                                                                <span
                                                                    key={t}
                                                                    style={{
                                                                        fontSize: 11,
                                                                        padding: "3px 8px",
                                                                        borderRadius: 999,
                                                                        border: isDark || isContrast ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)",
                                                                        background: isDark ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.65)",
                                                                        color: textSecondary,
                                                                        fontWeight: 700,
                                                                    }}
                                                                >
                                                                    {t}
                                                                </span>
                                                            ))}
                                                        </div>

                                                        <div style={{ marginTop: 8, fontSize: 12, color: textSecondary, lineHeight: 1.35 }}>
                                                            {it.description}
                                                        </div>

                                                        <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end", gap: 8, alignItems: "center" }}>
                                                            {/* Install button - Download standalone HTML app */}
                                                            {flags.sparkStore.embedIcon && (
                                                                <button
                                                                    type="button"
                                                                    title="Install as desktop app"
                                                                    onClick={(e) => { e.stopPropagation(); downloadSparkApp(it.sparkId, it.title); }}
                                                                    style={{
                                                                        width: 34, height: 34, borderRadius: 9, border: "none",
                                                                        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                                                        color: "#10b981", cursor: "pointer",
                                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                                        transition: "background 0.15s", flexShrink: 0,
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "rgba(16,185,129,0.20)" : "rgba(16,185,129,0.12)"}
                                                                    onMouseLeave={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
                                                                >
                                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                                                        <polyline points="7 10 12 15 17 10"/>
                                                                        <line x1="12" y1="15" x2="12" y2="3"/>
                                                                    </svg>
                                                                </button>
                                                            )}
                                                            {/* Embed button */}
                                                            {/* 🆕 SparkStore.embedIcon feature flag check */}
                                                            {flags.sparkStore.embedIcon && (
                                                                <button
                                                                    type="button"
                                                                    title="Get embed code"
                                                                    onClick={(e) => { e.stopPropagation(); setEmbedSpark({ sparkId: it.sparkId, title: it.title }); }}
                                                                    style={{
                                                                        width: 34, height: 34, borderRadius: 9, border: "none",
                                                                        background: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                                                                        color: textSecondary, cursor: "pointer",
                                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                                        transition: "background 0.15s", flexShrink: 0,
                                                                    }}
                                                                    onMouseEnter={(e) => e.currentTarget.style.background = isDark ? "rgba(13,126,143,0.28)" : "rgba(13,126,143,0.12)"}
                                                                    onMouseLeave={(e) => e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}
                                                                >
                                                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="16 18 22 12 16 6" />
                                                                        <polyline points="8 6 2 12 8 18" />
                                                                    </svg>
                                                                </button>
                                                            )}
                                                            {/* Activate button */}
                                                            <button
                                                                type="button"
                                                                onClick={() => activateSparkFromStore(it.sparkId)}
                                                                disabled={isActive}
                                                                style={{
                                                                    padding: "8px 14px",
                                                                    borderRadius: 10,
                                                                    border: isActive ? "none" : "1.5px solid rgba(8,145,178,0.50)",
                                                                    cursor: isActive ? "default" : "pointer",
                                                                    fontWeight: 700,
                                                                    fontSize: 12,
                                                                    background: isActive
                                                                        ? (isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)")
                                                                        : "rgba(8,145,178,0.12)",
                                                                    color: isActive ? textSecondary : "#0891b2",
                                                                    transition: "all 0.15s ease",
                                                                    opacity: isActive ? 0.7 : 1,
                                                                }}
                                                                onMouseEnter={(e) => {
                                                                    if (!isActive) {
                                                                        e.currentTarget.style.background = "#0891b2";
                                                                        e.currentTarget.style.color = "#fff";
                                                                    }
                                                                }}
                                                                onMouseLeave={(e) => {
                                                                    if (!isActive) {
                                                                        e.currentTarget.style.background = "rgba(8,145,178,0.12)";
                                                                        e.currentTarget.style.color = "#0891b2";
                                                                    }
                                                                }}
                                                                title={isActive ? "Already active" : "Activate"}
                                                            >
                                                                {isActive ? "✓ Active" : "Activate"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    )}

                                    {/* Load More row — spans full grid width */}
                                    {/* Scroll sentinel — shows while more items remain */}
                                    {filteredSparkStoreItems.length > sparkStoreDisplayLimit && (
                                        <div style={{
                                            gridColumn: "1 / -1",
                                            display: "flex",
                                            flexDirection: "column",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "20px 0 8px",
                                        }}>
                                            {/* Animated dots */}
                                            <div style={{ display: "flex", gap: 6 }}>
                                                {[0, 1, 2].map(i => (
                                                    <div key={i} style={{
                                                        width: 6, height: 6,
                                                        borderRadius: "50%",
                                                        background: primaryIconColor,
                                                        opacity: 0.7,
                                                        animation: `bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
                                                    }} />
                                                ))}
                                            </div>
                                            <div style={{ fontSize: 11, color: textTertiary }}>
                                                {sparkStoreDisplayLimit} of {filteredSparkStoreItems.length} — scroll for more
                                            </div>
                                        </div>
                                    )}

                                    {/* All loaded indicator */}
                                    {filteredSparkStoreItems.length > 0 &&
                                        filteredSparkStoreItems.length <= sparkStoreDisplayLimit && (
                                            <div style={{
                                                gridColumn: "1 / -1",
                                                textAlign: "center",
                                                padding: "16px 0 4px",
                                                fontSize: 11,
                                                color: textTertiary,
                                            }}>
                                                ✓ All {filteredSparkStoreItems.length} sparks loaded
                                            </div>
                                        )}
                               
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }



            {/* ═══════════════════════════════════════════
    ADO BOARD SEARCH MODAL
    ═══════════════════════════════════════════ */}
            {isAdoBoardModalOpen && (
                <div
                    style={{
                        position: "fixed", inset: 0,
                        background: "rgba(0,0,0,0.55)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16, zIndex: 9999,
                    }}
                    onClick={() => setIsAdoBoardModalOpen(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: isMobile ? "100vw" : "min(740px, 96vw)",
                            maxHeight: isMobile ? "92dvh" : "92vh",
                            overflowY: "auto",
                            borderRadius: isMobile ? "16px 16px 0 0" : 14,
                            padding: 20,
                            background: isDark ? "rgba(42,42,42,1)" : isContrast ? "#000" : "#fff",
                            color: textColor,
                            boxShadow: isDark ? "0 16px 40px rgba(0,0,0,0.6)" : "0 16px 40px rgba(0,0,0,0.25)",
                            border: isDark || isContrast ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.10)",
                            display: "flex", flexDirection: "column", gap: 16,
                        }}
                    >
                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, fontSize: 20,
                                background: isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)",
                                border: "1px solid rgba(0,120,212,0.30)",
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>🎯</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, fontSize: 16, color: textColor }}>ADO Board Search</div>
                                <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                                    Filter by project, team, board, classification and swimlane
                                </div>
                            </div>
                            {adoBoardStep === "results" && (
                                <button
                                    type="button"
                                    onClick={() => setAdoBoardStep("configure")}
                                    style={{
                                        padding: "6px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                                        border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                        background: "transparent", color: textSecondary, cursor: "pointer",
                                    }}
                                >
                                    ← Filters
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setIsAdoBoardModalOpen(false)}
                                style={{
                                    border: "none", background: "transparent", cursor: "pointer",
                                    color: textSecondary, fontSize: 20, padding: "4px 8px", borderRadius: 8,
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = iconHoverBg}
                                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >✕</button>
                        </div>

                        {/* Error */}
                        {adoBoardError && (
                            <div style={{
                                padding: "10px 14px", borderRadius: 8, fontSize: 13,
                                background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.30)",
                                color: isDark ? "#fca5a5" : "#b91c1c",
                            }}>
                                {adoBoardError}
                            </div>
                        )}

                        {/* ── CONFIGURE STEP ── */}
                        {adoBoardStep === "configure" && (
                            <>
                                {/* Row 1: Project + Team */}
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>

                                    {/* Project with search */}
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                            Project {isLoadingAdoBoardProjects && <span style={{ opacity: 0.5, fontWeight: 400 }}>— loading...</span>}
                                            <span style={{ color: "rgba(220,38,38,0.8)", marginLeft: 2 }}>*</span>
                                        </div>
                                        <input
                                            placeholder="Search projects..."
                                            onChange={(e) => setAdoBoardProjectSearch(e.target.value)}
                                            value={adoBoardProjectSearch || ""}
                                            style={{
                                                width: "100%", padding: "7px 10px", borderRadius: 7, fontSize: 12,
                                                border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                                background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                                                color: textColor, outline: "none", boxSizing: "border-box", marginBottom: 6,
                                            }}
                                        />
                                        <div style={{
                                            maxHeight: 150, overflowY: "auto", borderRadius: 8,
                                            border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                        }}>
                                            {adoBoardProjects
                                                .filter(p => !adoBoardProjectSearch || p.name.toLowerCase().includes(adoBoardProjectSearch.toLowerCase()))
                                                .map((p) => {
                                                    const sel = adoBoardSelectedProject?.name === p.name;
                                                    return (
                                                        <div
                                                            key={p.id}
                                                            onClick={async () => {
                                                                setAdoBoardSelectedProject(p);
                                                                setAdoBoardProjectSearch("");
                                                                await Promise.all([
                                                                    fetchAdoBoardTeams(p.name),
                                                                    fetchAdoAreaPaths(p.name),
                                                                ]);
                                                            }}
                                                            style={{
                                                                padding: "8px 12px", cursor: "pointer", fontSize: 13,
                                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                                background: sel ? (isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)") : "transparent",
                                                                borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                            }}
                                                            onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = pickerHover; }}
                                                            onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent"; }}
                                                        >
                                                            <span style={{ fontWeight: sel ? 700 : 500, color: textColor }}>{p.name}</span>
                                                            {sel && <span style={{ color: primaryIconColor }}>✓</span>}
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    </div>

                                    {/* Team */}
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                            Team {isLoadingAdoBoardTeams && <span style={{ opacity: 0.5, fontWeight: 400 }}>— loading...</span>}
                                            <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: 4 }}>(optional)</span>
                                        </div>
                                        <div style={{
                                            maxHeight: 180, overflowY: "auto", borderRadius: 8,
                                            border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                            opacity: adoBoardTeams.length === 0 ? 0.4 : 1,
                                        }}>
                                            {adoBoardTeams.length === 0 ? (
                                                <div style={{ padding: "12px", textAlign: "center", fontSize: 12, color: textTertiary }}>
                                                    Select a project first
                                                </div>
                                            ) : (
                                                <>
                                                    {/* Any team option */}
                                                    <div
                                                        onClick={() => { setAdoBoardSelectedTeam(null); setAdoBoards([]); setAdoSelectedBoard(null); setAdoSwimlanes([]); setAdoSelectedSwimlane(null); }}
                                                        style={{
                                                            padding: "8px 12px", cursor: "pointer", fontSize: 13,
                                                            display: "flex", alignItems: "center", justifyContent: "space-between",
                                                            background: !adoBoardSelectedTeam ? (isDark ? "rgba(0,120,212,0.15)" : "rgba(0,120,212,0.07)") : "transparent",
                                                            borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                        }}
                                                    >
                                                        <span style={{ color: textSecondary, fontStyle: "italic" }}>Any team</span>
                                                        {!adoBoardSelectedTeam && <span style={{ color: primaryIconColor }}>✓</span>}
                                                    </div>
                                                    {adoBoardTeams.map((t) => {
                                                        const sel = adoBoardSelectedTeam?.name === t.name;
                                                        return (
                                                            <div
                                                                key={t.id}
                                                                onClick={async () => {
                                                                    setAdoBoardSelectedTeam(t);
                                                                    await fetchAdoBoards(adoBoardSelectedProject.name, t.name);
                                                                }}
                                                                style={{
                                                                    padding: "8px 12px", cursor: "pointer", fontSize: 13,
                                                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                                                    background: sel ? (isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)") : "transparent",
                                                                    borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                                }}
                                                                onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = pickerHover; }}
                                                                onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent"; }}
                                                            >
                                                                <span style={{ fontWeight: sel ? 700 : 500, color: textColor }}>{t.name}</span>
                                                                {sel && <span style={{ color: primaryIconColor }}>✓</span>}
                                                            </div>
                                                        );
                                                    })}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Row 2: Board + Swimlane — both optional */}
                                {adoBoardSelectedTeam && (
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                        {/* Board */}
                                        <div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                                Board {isLoadingAdoBoards && <span style={{ opacity: 0.5, fontWeight: 400 }}>— loading...</span>}
                                                <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: 4 }}>(optional)</span>
                                            </div>
                                            <div style={{
                                                maxHeight: 140, overflowY: "auto", borderRadius: 8,
                                                border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                            }}>
                                                <div
                                                    onClick={() => { setAdoSelectedBoard(null); setAdoSwimlanes([]); setAdoSelectedSwimlane(null); }}
                                                    style={{
                                                        padding: "8px 12px", cursor: "pointer", fontSize: 13,
                                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                                        background: !adoSelectedBoard ? (isDark ? "rgba(0,120,212,0.15)" : "rgba(0,120,212,0.07)") : "transparent",
                                                        borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                    }}
                                                >
                                                    <span style={{ color: textSecondary, fontStyle: "italic" }}>Any board</span>
                                                    {!adoSelectedBoard && <span style={{ color: primaryIconColor }}>✓</span>}
                                                </div>
                                                {adoBoards.map((b) => {
                                                    const sel = adoSelectedBoard?.id === b.id;
                                                    return (
                                                        <div
                                                            key={b.id}
                                                            onClick={async () => {
                                                                setAdoSelectedBoard(b);
                                                                await fetchAdoSwimlanes(adoBoardSelectedProject.name, adoBoardSelectedTeam.name, b.id);
                                                            }}
                                                            style={{
                                                                padding: "8px 12px", cursor: "pointer", fontSize: 13,
                                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                                background: sel ? (isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)") : "transparent",
                                                                borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                            }}
                                                            onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = pickerHover; }}
                                                            onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent"; }}
                                                        >
                                                            <span style={{ fontWeight: sel ? 700 : 500, color: textColor }}>📋 {b.name}</span>
                                                            {sel && <span style={{ color: primaryIconColor }}>✓</span>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Swimlane — only shown when board selected */}
                                        {adoSelectedBoard && (
                                            <div>
                                                <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                                    Swimlane {isLoadingAdoSwimlanes && <span style={{ opacity: 0.5, fontWeight: 400 }}>— loading...</span>}
                                                    <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: 4 }}>(optional)</span>
                                                </div>
                                                <div style={{
                                                    maxHeight: 140, overflowY: "auto", borderRadius: 8,
                                                    border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                                }}>
                                                    <div
                                                        onClick={() => setAdoSelectedSwimlane(null)}
                                                        style={{
                                                            padding: "8px 12px", cursor: "pointer", fontSize: 13,
                                                            display: "flex", alignItems: "center", justifyContent: "space-between",
                                                            background: !adoSelectedSwimlane ? (isDark ? "rgba(0,120,212,0.15)" : "rgba(0,120,212,0.07)") : "transparent",
                                                            borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                        }}
                                                    >
                                                        <span style={{ color: textSecondary, fontStyle: "italic" }}>Any swimlane</span>
                                                        {!adoSelectedSwimlane && <span style={{ color: primaryIconColor }}>✓</span>}
                                                    </div>
                                                    {adoSwimlanes.map((s) => {
                                                        const sel = adoSelectedSwimlane?.id === s.id;
                                                        return (
                                                            <div
                                                                key={s.id || s.name}
                                                                onClick={() => setAdoSelectedSwimlane(sel ? null : s)}
                                                                style={{
                                                                    padding: "8px 12px", cursor: "pointer", fontSize: 13,
                                                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                                                    background: sel ? (isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)") : "transparent",
                                                                    borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                                }}
                                                                onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = pickerHover; }}
                                                                onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent"; }}
                                                            >
                                                                <span style={{ fontWeight: sel ? 700 : 500, color: textColor }}>🏊 {s.name}</span>
                                                                {sel && <span style={{ color: primaryIconColor }}>✓</span>}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Classification (Area Path) */}
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                        Classification (Area Path)
                                        {isLoadingAdoAreaPaths && <span style={{ opacity: 0.5, fontWeight: 400, marginLeft: 8 }}>— loading...</span>}
                                        <span style={{ fontWeight: 400, opacity: 0.6, marginLeft: 6 }}>(optional)</span>
                                    </div>
                                    <div style={{
                                        maxHeight: 130, overflowY: "auto", borderRadius: 8,
                                        border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                    }}>
                                        <div
                                            onClick={() => setAdoSelectedAreaPath(null)}
                                            style={{
                                                padding: "7px 12px", cursor: "pointer", fontSize: 12,
                                                display: "flex", alignItems: "center", justifyContent: "space-between",
                                                background: !adoSelectedAreaPath ? (isDark ? "rgba(0,120,212,0.15)" : "rgba(0,120,212,0.07)") : "transparent",
                                                borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                            }}
                                        >
                                            <span style={{ color: textSecondary, fontStyle: "italic" }}>Any area path</span>
                                            {!adoSelectedAreaPath && <span style={{ color: primaryIconColor }}>✓</span>}
                                        </div>
                                        {adoAreaPaths.map((ap) => {
                                            const sel = adoSelectedAreaPath?.path === ap.path;
                                            const depth = (ap.path.match(/\\/g) || []).length - 1;
                                            return (
                                                <div
                                                    key={ap.id + ap.path}
                                                    onClick={() => setAdoSelectedAreaPath(sel ? null : ap)}
                                                    style={{
                                                        padding: "7px 12px", cursor: "pointer", fontSize: 12,
                                                        paddingLeft: 12 + depth * 14,
                                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                                        background: sel ? (isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)") : "transparent",
                                                        borderBottom: isDark ? "1px solid rgba(255,255,255,0.03)" : "1px solid rgba(0,0,0,0.03)",
                                                    }}
                                                    onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = pickerHover; }}
                                                    onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "transparent"; }}
                                                >
                                                    <span style={{ fontWeight: sel ? 700 : 400, color: textColor }}>
                                                        {depth > 0 ? "└ " : ""}{ap.name}
                                                    </span>
                                                    {sel && <span style={{ color: primaryIconColor }}>✓</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Filters row: Work Item Type + State */}
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>Work Item Type</div>
                                        <select
                                            value={adoBoardWorkItemType}
                                            onChange={(e) => setAdoBoardWorkItemType(e.target.value)}
                                            style={{
                                                width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 13,
                                                border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                                background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                                                color: textColor, outline: "none",
                                            }}
                                        >
                                            {["Any", "User Story", "Feature", "Epic", "Task", "Bug", "Test Case", "Issue"].map(t => (
                                                <option key={t} value={t}>{t}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>State</div>
                                        <select
                                            value={adoBoardState}
                                            onChange={(e) => setAdoBoardState(e.target.value)}
                                            style={{
                                                width: "100%", padding: "8px 10px", borderRadius: 8, fontSize: 13,
                                                border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                                background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                                                color: textColor, outline: "none",
                                            }}
                                        >
                                            {["Any", "New", "Active", "Resolved", "Closed", "Removed"].map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Search text */}
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                        Search Keywords <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span>
                                    </div>
                                    <input
                                        value={adoBoardSearchText}
                                        onChange={(e) => setAdoBoardSearchText(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") runAdoBoardSearch(); }}
                                        placeholder="e.g. login bug, API performance..."
                                        style={{
                                            width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                                            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                            background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                                            color: textColor, outline: "none", boxSizing: "border-box",
                                        }}
                                    />
                                </div>

                                {/* Active filters summary + Clear All */}
                                {(adoBoardSelectedProject || adoBoardSelectedTeam || adoSelectedAreaPath || adoSelectedSwimlane || adoBoardWorkItemType !== "Any" || adoBoardState !== "Any" || adoBoardSearchText) && (
                                    <div style={{
                                        padding: "10px 12px", borderRadius: 8, fontSize: 12,
                                        background: isDark ? "rgba(0,120,212,0.08)" : "rgba(0,120,212,0.05)",
                                        border: isDark ? "1px solid rgba(0,120,212,0.20)" : "1px solid rgba(0,120,212,0.15)",
                                        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap",
                                    }}>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center", flex: 1 }}>
                                            <span style={{ color: textSecondary, fontWeight: 600, marginRight: 4 }}>Active filters:</span>
                                            {adoBoardSelectedProject && <span style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.15)", color: primaryIconColor, fontWeight: 600 }}>📁 {adoBoardSelectedProject.name}</span>}
                                            {adoBoardSelectedTeam && <span style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.15)", color: primaryIconColor, fontWeight: 600 }}>👥 {adoBoardSelectedTeam.name}</span>}
                                            {adoSelectedBoard && <span style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.15)", color: primaryIconColor, fontWeight: 600 }}>📋 {adoSelectedBoard.name}</span>}
                                            {adoSelectedAreaPath && <span style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.15)", color: primaryIconColor, fontWeight: 600 }}>🗂 {adoSelectedAreaPath.name}</span>}
                                            {adoSelectedSwimlane && <span style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.15)", color: primaryIconColor, fontWeight: 600 }}>🏊 {adoSelectedSwimlane.name}</span>}
                                            {adoBoardWorkItemType !== "Any" && <span style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.15)", color: primaryIconColor, fontWeight: 600 }}>🎯 {adoBoardWorkItemType}</span>}
                                            {adoBoardState !== "Any" && <span style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.15)", color: primaryIconColor, fontWeight: 600 }}>🔵 {adoBoardState}</span>}
                                            {adoBoardSearchText && <span style={{ padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.15)", color: primaryIconColor, fontWeight: 600 }}>🔍 "{adoBoardSearchText}"</span>}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setAdoBoardSelectedProject(null);
                                                setAdoBoardSelectedTeam(null);
                                                setAdoSelectedBoard(null);
                                                setAdoSelectedAreaPath(null);
                                                setAdoSelectedSwimlane(null);
                                                setAdoBoardWorkItemType("Any");
                                                setAdoBoardState("Any");
                                                setAdoBoardSearchText("");
                                                setAdoBoardTeams([]);
                                                setAdoBoards([]);
                                                setAdoSwimlanes([]);
                                                setAdoAreaPaths([]);
                                                setAdoBoardSearchResults([]);
                                                setAdoBoardProjectSearch("");
                                            }}
                                            style={{
                                                padding: "4px 12px", borderRadius: 6, fontSize: 11, fontWeight: 700,
                                                border: "1px solid rgba(220,38,38,0.30)",
                                                background: "rgba(220,38,38,0.08)",
                                                color: isDark ? "#fca5a5" : "#b91c1c",
                                                cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
                                            }}
                                        >
                                            ✕ Clear All
                                        </button>
                                    </div>
                                )}

                                {/* Footer */}
                                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 4 }}>
                                    <button
                                        type="button"
                                        onClick={() => setIsAdoBoardModalOpen(false)}
                                        style={{
                                            padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                                            border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                            background: "transparent", color: textColor, cursor: "pointer",
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={runAdoBoardSearch}
                                        disabled={!adoBoardSelectedProject || isSearchingAdoBoard}
                                        style={{
                                            padding: "9px 22px", borderRadius: 8, fontSize: 13, fontWeight: 800,
                                            border: "none",
                                            background: !adoBoardSelectedProject || isSearchingAdoBoard
                                                ? (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)")
                                                : "rgba(0,120,212,0.90)",
                                            color: !adoBoardSelectedProject || isSearchingAdoBoard ? textSecondary : "#fff",
                                            cursor: !adoBoardSelectedProject || isSearchingAdoBoard ? "not-allowed" : "pointer",
                                            display: "flex", alignItems: "center", gap: 8,
                                        }}
                                    >
                                        {isSearchingAdoBoard ? (
                                            <>
                                                <div style={{
                                                    width: 14, height: 14, borderRadius: "50%",
                                                    border: "2px solid rgba(255,255,255,0.3)",
                                                    borderTopColor: "#fff",
                                                    animation: "spin 0.7s linear infinite",
                                                }} />
                                                Searching...
                                            </>
                                        ) : "🔍 Search Work Items"}
                                    </button>
                                </div>
                            </>
                        )}

                        {/* ── RESULTS STEP ── */}
                        {adoBoardStep === "results" && (
                            <>
                                {/* Results summary bar */}
                                <div style={{
                                    display: "flex", alignItems: "center", justifyContent: "space-between",
                                    padding: "8px 12px", borderRadius: 8,
                                    background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
                                    border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                                }}>
                                    <div style={{ fontSize: 13, fontWeight: 700, color: textColor }}>
                                        {adoBoardSearchResults.length} results
                                        {adoBoardTotalResults > adoBoardSearchResults.length && (
                                            <span style={{ fontWeight: 400, color: textSecondary, marginLeft: 6 }}>
                                                (showing {adoBoardSearchResults.length} of {adoBoardTotalResults})
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                        {adoBoardSelectedProject && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.12)", color: primaryIconColor }}>📁 {adoBoardSelectedProject.name}</span>}
                                        {adoBoardSelectedTeam && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.12)", color: primaryIconColor }}>👥 {adoBoardSelectedTeam.name}</span>}
                                        {adoSelectedAreaPath && <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 999, background: "rgba(0,120,212,0.12)", color: primaryIconColor }}>🗂 {adoSelectedAreaPath.name}</span>}
                                    </div>
                                </div>

                                {/* Results list */}
                                {adoBoardSearchResults.length === 0 ? (
                                    <div style={{
                                        padding: "40px 20px", textAlign: "center",
                                        color: textSecondary, fontSize: 14,
                                    }}>
                                        <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
                                        No work items found matching your filters.
                                        <div style={{ marginTop: 8, fontSize: 12, color: textTertiary }}>
                                            Try broadening your filters or changing the search keywords.
                                        </div>
                                    </div>
                                ) : (
                                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 420, overflowY: "auto" }}>
                                        {adoBoardSearchResults.map((wi) => {
                                            const typeColors = {
                                                "User Story": "#0078d4",
                                                "Bug": "#cc293d",
                                                "Task": "#f2cb1d",
                                                "Feature": "#773b93",
                                                "Epic": "#ff7b00",
                                            };
                                            const typeColor = typeColors[wi.workItemType] || "#666";
                                            const stateColor = wi.state === "Active" ? "#16a34a"
                                                : wi.state === "Resolved" || wi.state === "Closed" ? "#6b7280"
                                                    : wi.state === "New" ? "#2563eb" : "#d97706";

                                            return (
                                                <div
                                                    key={wi.id}
                                                    onClick={() => toggleAdoWorkItemSelection(wi)}
                                                    style={{
                                                        padding: "11px 14px", borderRadius: 9,
                                                        border: adoSelectedWorkItems.find(x => x.id === wi.id)
                                                            ? "1.5px solid rgba(0,120,212,0.60)"
                                                            : isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                                                        background: adoSelectedWorkItems.find(x => x.id === wi.id)
                                                            ? (isDark ? "rgba(0,120,212,0.15)" : "rgba(0,120,212,0.07)")
                                                            : isDark ? "rgba(255,255,255,0.03)" : "#fafafa",
                                                        display: "flex", gap: 10, cursor: "pointer",
                                                        transition: "all 0.12s ease",
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        if (!adoSelectedWorkItems.find(x => x.id === wi.id))
                                                            e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.06)" : "#f0f4ff";
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (!adoSelectedWorkItems.find(x => x.id === wi.id))
                                                            e.currentTarget.style.background = isDark ? "rgba(255,255,255,0.03)" : "#fafafa";
                                                    }}
                                                >
                                                    {/* Checkbox */}
                                                    <div style={{
                                                        width: 20, height: 20, borderRadius: 5, flexShrink: 0, marginTop: 2,
                                                        border: adoSelectedWorkItems.find(x => x.id === wi.id)
                                                            ? "2px solid #0078d4"
                                                            : isDark ? "1.5px solid rgba(255,255,255,0.3)" : "1.5px solid rgba(0,0,0,0.25)",
                                                        background: adoSelectedWorkItems.find(x => x.id === wi.id) ? "#0078d4" : "transparent",
                                                        display: "flex", alignItems: "center", justifyContent: "center",
                                                        transition: "all 0.12s ease",
                                                    }}>
                                                        {adoSelectedWorkItems.find(x => x.id === wi.id) && (
                                                            <span style={{ color: "#fff", fontSize: 12, fontWeight: 700, lineHeight: 1 }}>✓</span>
                                                        )}
                                                    </div>

                                                    {/* Content */}
                                                    <div style={{ flex: 1, minWidth: 0 }}>
                                                        <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 5 }}>
                                                            <div style={{
                                                                padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700, flexShrink: 0,
                                                                background: `${typeColor}22`, color: typeColor, border: `1px solid ${typeColor}44`,
                                                            }}>
                                                                {wi.workItemType}
                                                            </div>
                                                            <span style={{ fontWeight: 700, fontSize: 13, color: primaryIconColor, lineHeight: 1.4 }}>
                                                                #{wi.id} — {wi.title}
                                                            </span>
                                                        </div>
                                                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 11 }}>
                                                            <span style={{
                                                                padding: "1px 7px", borderRadius: 999, fontWeight: 600,
                                                                background: `${stateColor}18`, color: stateColor, border: `1px solid ${stateColor}30`,
                                                            }}>
                                                                {wi.state}
                                                            </span>
                                                            {wi.assignedTo && wi.assignedTo !== "Unassigned" && (
                                                                <span style={{ color: textSecondary }}>👤 {wi.assignedTo.split(" <")[0]}</span>
                                                            )}
                                                            {wi.areaPath && (
                                                                <span style={{ color: textTertiary }}>🗂 {wi.areaPath.split("\\").pop()}</span>
                                                            )}
                                                            {wi.priority && <span style={{ color: textTertiary }}>P{wi.priority}</span>}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                            })}
                            </div>
                    )}

                                {/* Selection summary + footer */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 10, paddingTop: 4 }}>

                                    {/* Selected count banner */}
                                    {adoSelectedWorkItems.length > 0 && (
                                        <div style={{
                                            padding: "10px 14px", borderRadius: 8,
                                            background: isDark ? "rgba(0,120,212,0.15)" : "rgba(0,120,212,0.08)",
                                            border: "1px solid rgba(0,120,212,0.30)",
                                            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                                        }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: primaryIconColor }}>
                                                {adoSelectedWorkItems.length} work item{adoSelectedWorkItems.length !== 1 ? "s" : ""} selected
                                            </div>
                                            <div style={{ display: "flex", gap: 6 }}>
                                                <button
                                                    type="button"
                                                    onClick={() => setAdoSelectedWorkItems([])}
                                                    style={{
                                                        padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                                                        border: "1px solid rgba(220,38,38,0.30)",
                                                        background: "rgba(220,38,38,0.08)",
                                                        color: isDark ? "#fca5a5" : "#b91c1c", cursor: "pointer",
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        // Select all visible results
                                                        setAdoSelectedWorkItems(adoBoardSearchResults);
                                                    }}
                                                    style={{
                                                        padding: "4px 10px", borderRadius: 6, fontSize: 11, fontWeight: 600,
                                                        border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                                        background: "transparent", color: textSecondary, cursor: "pointer",
                                                    }}
                                                >
                                                    Select All
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                        <button
                                            type="button"
                                            onClick={() => setAdoBoardStep("configure")}
                                            style={{
                                                padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                                                border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                                background: "transparent", color: textColor, cursor: "pointer",
                                            }}
                                        >
                                            ← Modify Filters
                                        </button>
                                        <div style={{ display: "flex", gap: 8 }}>
                                            <button
                                                type="button"
                                                onClick={runAdoBoardSearch}
                                                disabled={isSearchingAdoBoard}
                                                style={{
                                                    padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                                                    border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                                    background: "transparent", color: textColor,
                                                    cursor: isSearchingAdoBoard ? "not-allowed" : "pointer",
                                                }}
                                            >
                                                {isSearchingAdoBoard ? "Searching..." : "🔄 Re-run"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={applyAdoWorkItemsToContext}
                                                disabled={adoSelectedWorkItems.length === 0}
                                                style={{
                                                    padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800,
                                                    border: "none",
                                                    background: adoSelectedWorkItems.length === 0
                                                        ? (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)")
                                                        : "rgba(0,120,212,0.90)",
                                                    color: adoSelectedWorkItems.length === 0 ? textSecondary : "#fff",
                                                    cursor: adoSelectedWorkItems.length === 0 ? "not-allowed" : "pointer",
                                                    display: "flex", alignItems: "center", gap: 6,
                                                }}
                                            >
                                                ✓ Use {adoSelectedWorkItems.length > 0 ? `${adoSelectedWorkItems.length} ` : ""}Selected
                                            </button>
                                        </div>
                                    </div>
                                </div>
                    </>
            )}
                </div>
    </div>
    )
}










            {/* ═══════════════════════════════════════════
    POST TO TEAMS MODAL
    ═══════════════════════════════════════════ */}
            {isTeamsModalOpen && (
                <div
                    style={{
                        position: "fixed", inset: 0,
                        background: "rgba(0,0,0,0.55)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16, zIndex: 9999,
                    }}
                    onClick={() => { if (!isPostingToTeams) setIsTeamsModalOpen(false); }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: isMobile ? "100vw" : "min(680px, 96vw)",
                            maxHeight: "92vh",
                            overflowY: "auto",
                            borderRadius: 14,
                            padding: 20,
                            background: isDark ? "rgba(42,42,42,1)" : isContrast ? "#000" : "#fff",
                            color: textColor,
                            boxShadow: isDark ? "0 16px 40px rgba(0,0,0,0.6)" : "0 16px 40px rgba(0,0,0,0.25)",
                            border: isDark || isContrast ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.10)",
                            display: "flex", flexDirection: "column", gap: 16,
                        }}
                    >
                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, fontSize: 20,
                                background: isDark ? "rgba(98,100,167,0.25)" : "rgba(98,100,167,0.12)",
                                border: "1px solid rgba(98,100,167,0.35)",
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>
                                💬
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, fontSize: 16, color: textColor }}>Post to Microsoft Teams</div>
                                <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                                    Send this response to a chat, group, or channel
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsTeamsModalOpen(false)}
                                disabled={isPostingToTeams}
                                style={{
                                    border: "none", background: "transparent", cursor: "pointer",
                                    color: textSecondary, fontSize: 20, padding: "4px 8px", borderRadius: 8,
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = iconHoverBg}
                                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >✕</button>
                        </div>

                        {/* Error / Success */}
                        {teamsPostError && (
                            <div style={{
                                padding: "10px 14px", borderRadius: 8, fontSize: 13,
                                background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.30)",
                                color: isDark ? "#fca5a5" : "#b91c1c",
                            }}>
                                {teamsPostError}
                            </div>
                        )}
                        {teamsPostSuccess && (
                            <div style={{
                                padding: "12px 14px", borderRadius: 8, fontSize: 13,
                                background: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.30)",
                                color: isDark ? "#86efac" : "#15803d",
                                display: "flex", flexDirection: "column", gap: 8,
                            }}>
                                <div>{teamsPostSuccess}</div>
                                {teamsPostedUrl && (
                                    <a href={teamsPostedUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: primaryIconColor, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                                        🔗 View in Teams
                                    </a>
                                )}
                            </div>
                        )}

                        {/* Destination Type Toggle */}
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 8 }}>
                                Post To
                            </div>
                            <div style={{
                                display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8,
                            }}>
                                {[
                                    { key: "chat", label: "Individual Chat", icon: "👤" },
                                    { key: "group", label: "Group Chat", icon: "👥" },
                                    { key: "channel", label: "Channel", icon: "📢" },
                                ].map(({ key, label, icon }) => {
                                    const selected = teamsDestType === key;
                                    return (
                                        <button
                                            key={key}
                                            type="button"
                                            onClick={async () => {
                                                setTeamsDestType(key);
                                                setTeamsSelectedChat(null);
                                                setTeamsSelectedTeam(null);
                                                setTeamsSelectedChannel(null);
                                                setTeamsPostError("");
                                                if (key === "chat" || key === "group") {
                                                    if (teamsChats.length === 0) await fetchTeamsChats();
                                                } else {
                                                    if (teamsJoinedTeams.length === 0) await fetchTeamsTeams();
                                                }
                                            }}
                                            style={{
                                                padding: "10px 8px",
                                                borderRadius: 9,
                                                border: selected
                                                    ? "1.5px solid rgba(98,100,167,0.7)"
                                                    : isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                                background: selected
                                                    ? (isDark ? "rgba(98,100,167,0.22)" : "rgba(98,100,167,0.10)")
                                                    : "transparent",
                                                cursor: "pointer",
                                                display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
                                                transition: "all 0.15s ease",
                                            }}
                                        >
                                            <span style={{ fontSize: 20 }}>{icon}</span>
                                            <span style={{
                                                fontSize: 11, fontWeight: selected ? 700 : 500,
                                                color: selected ? primaryIconColor : textSecondary,
                                            }}>
                                                {label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Chat / Group selector */}
                        {(teamsDestType === "chat" || teamsDestType === "group") && (
                            <div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 8 }}>
                                    Select {teamsDestType === "chat" ? "Person" : "Group Chat"}
                                    {isLoadingTeamsChats && <span style={{ opacity: 0.6, fontWeight: 400, marginLeft: 8 }}>— loading...</span>}
                                </div>

                                {/* Search */}
                                <input
                                    value={teamsChatSearch}
                                    onChange={(e) => setTeamsChatSearch(e.target.value)}
                                    placeholder="Search chats..."
                                    style={{
                                        width: "100%", padding: "8px 12px", borderRadius: 8, fontSize: 13,
                                        border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                        background: isDark ? "rgba(255,255,255,0.06)" : "#fafafa",
                                        color: textColor, outline: "none", boxSizing: "border-box", marginBottom: 8,
                                    }}
                                />

                                <div style={{
                                    maxHeight: 220, overflowY: "auto", borderRadius: 8,
                                    border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                }}>
                                    {isLoadingTeamsChats ? (
                                        <div style={{ padding: "16px", textAlign: "center", fontSize: 13, color: textSecondary }}>
                                            Loading chats...
                                        </div>
                                    ) : (
                                        teamsChats
                                            .filter((c) => {
                                                const isGroup = c.chatType === "group";
                                                const matchType = teamsDestType === "group" ? isGroup : !isGroup;
                                                const matchSearch = !teamsChatSearch ||
                                                    (c.displayName || "").toLowerCase().includes(teamsChatSearch.toLowerCase());
                                                return matchType && matchSearch;
                                            })
                                            .map((chat) => {
                                                const selected = teamsSelectedChat?.id === chat.id;
                                                return (
                                                    <div
                                                        key={chat.id}
                                                        onClick={() => setTeamsSelectedChat(chat)}
                                                        style={{
                                                            padding: "10px 12px", cursor: "pointer",
                                                            display: "flex", alignItems: "center", gap: 10,
                                                            background: selected
                                                                ? (isDark ? "rgba(98,100,167,0.22)" : "rgba(98,100,167,0.10)")
                                                                : "transparent",
                                                            borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                            transition: "background 0.12s ease",
                                                        }}
                                                        onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = pickerHover; }}
                                                        onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
                                                    >
                                                        {/* Avatar */}
                                                        <div style={{
                                                            width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                                                            background: `hsl(${(chat.displayName || "U").charCodeAt(0) * 7 % 360}, 55%, 45%)`,
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            fontSize: 13, fontWeight: 700, color: "#fff",
                                                        }}>
                                                            {(chat.displayName || "?").charAt(0).toUpperCase()}
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: 600, color: textColor, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                {chat.displayName || "(No name)"}
                                                            </div>
                                                            {chat.lastMessagePreview && (
                                                                <div style={{ fontSize: 10, color: textTertiary, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    {chat.lastMessagePreview}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {selected && <span style={{ color: primaryIconColor, fontWeight: 700, fontSize: 16 }}>✓</span>}
                                                    </div>
                                                );
                                            })
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Channel selector — Team first, then Channel */}
                        {teamsDestType === "channel" && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                                {/* Team picker */}
                                <div>
                                    <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 8 }}>
                                        Select Team
                                        {isLoadingTeamsTeams && <span style={{ opacity: 0.6, fontWeight: 400, marginLeft: 8 }}>— loading...</span>}
                                    </div>
                                    <div style={{
                                        maxHeight: 180, overflowY: "auto", borderRadius: 8,
                                        border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                    }}>
                                        {isLoadingTeamsTeams ? (
                                            <div style={{ padding: "16px", textAlign: "center", fontSize: 13, color: textSecondary }}>Loading teams...</div>
                                        ) : (
                                            teamsJoinedTeams.map((team) => {
                                                const selected = teamsSelectedTeam?.id === team.id;
                                                return (
                                                    <div
                                                        key={team.id}
                                                        onClick={async () => {
                                                            setTeamsSelectedTeam(team);
                                                            setTeamsSelectedChannel(null);
                                                            await fetchTeamsChannels(team.id);
                                                        }}
                                                        style={{
                                                            padding: "10px 12px", cursor: "pointer",
                                                            display: "flex", alignItems: "center", gap: 10,
                                                            background: selected
                                                                ? (isDark ? "rgba(98,100,167,0.22)" : "rgba(98,100,167,0.10)")
                                                                : "transparent",
                                                            borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                            transition: "background 0.12s ease",
                                                        }}
                                                        onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = pickerHover; }}
                                                        onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
                                                    >
                                                        <div style={{
                                                            width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                                                            background: `hsl(${(team.displayName || "T").charCodeAt(0) * 11 % 360}, 50%, 40%)`,
                                                            display: "flex", alignItems: "center", justifyContent: "center",
                                                            fontSize: 14, fontWeight: 700, color: "#fff",
                                                        }}>
                                                            {(team.displayName || "T").charAt(0).toUpperCase()}
                                                        </div>
                                                        <div style={{ flex: 1, fontWeight: 600, color: textColor, fontSize: 13 }}>
                                                            {team.displayName}
                                                        </div>
                                                        {selected && <span style={{ color: primaryIconColor, fontWeight: 700, fontSize: 16 }}>✓</span>}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>

                                {/* Channel picker */}
                                {teamsSelectedTeam && (
                                    <div>
                                        <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 8 }}>
                                            Select Channel
                                            {isLoadingTeamsChannels && <span style={{ opacity: 0.6, fontWeight: 400, marginLeft: 8 }}>— loading...</span>}
                                        </div>
                                        <div style={{
                                            maxHeight: 180, overflowY: "auto", borderRadius: 8,
                                            border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                        }}>
                                            {isLoadingTeamsChannels ? (
                                                <div style={{ padding: "16px", textAlign: "center", fontSize: 13, color: textSecondary }}>Loading channels...</div>
                                            ) : (
                                                teamsChannels.map((ch) => {
                                                    const selected = teamsSelectedChannel?.id === ch.id;
                                                    return (
                                                        <div
                                                            key={ch.id}
                                                            onClick={() => setTeamsSelectedChannel(ch)}
                                                            style={{
                                                                padding: "10px 12px", cursor: "pointer",
                                                                display: "flex", alignItems: "center", gap: 10,
                                                                background: selected
                                                                    ? (isDark ? "rgba(98,100,167,0.22)" : "rgba(98,100,167,0.10)")
                                                                    : "transparent",
                                                                borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                                transition: "background 0.12s ease",
                                                            }}
                                                            onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = pickerHover; }}
                                                            onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
                                                        >
                                                            <span style={{ color: textSecondary, fontSize: 15 }}>#</span>
                                                            <div style={{ flex: 1 }}>
                                                                <div style={{ fontWeight: 600, color: textColor, fontSize: 13 }}>{ch.displayName}</div>
                                                                {ch.description && (
                                                                    <div style={{ fontSize: 10, color: textTertiary, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                        {ch.description}
                                                                    </div>
                                                                )}
                                                            </div>
                                                            {selected && <span style={{ color: primaryIconColor, fontWeight: 700, fontSize: 16 }}>✓</span>}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Optional subject */}
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                Subject / Title <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span>
                            </div>
                            <input
                                value={teamsSubject}
                                onChange={(e) => setTeamsSubject(e.target.value)}
                                placeholder="e.g. Spark Assist Response"
                                disabled={isPostingToTeams}
                                style={{
                                    width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                                    border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                    background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                                    color: textColor, outline: "none", boxSizing: "border-box",
                                }}
                            />
                        </div>

                        {/* Message content */}
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                Message Content
                            </div>
                            <textarea
                                value={teamsMessageContent}
                                onChange={(e) => setTeamsMessageContent(e.target.value)}
                                disabled={isPostingToTeams}
                                rows={6}
                                style={{
                                    width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 12,
                                    border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                    background: isDark ? "rgba(0,0,0,0.25)" : "#fafafa",
                                    color: textColor, outline: "none", resize: "vertical",
                                    fontFamily: "ui-monospace, Consolas, monospace",
                                    lineHeight: 1.5, boxSizing: "border-box",
                                }}
                            />
                            <div style={{ fontSize: 11, color: textTertiary, marginTop: 4 }}>
                                {teamsMessageContent.length.toLocaleString()} characters
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 4 }}>
                            <button
                                type="button"
                                onClick={() => setIsTeamsModalOpen(false)}
                                disabled={isPostingToTeams}
                                style={{
                                    padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                                    border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                    background: "transparent", color: textColor, cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submitTeamsPost}
                                disabled={isPostingToTeams || !!teamsPostSuccess}
                                style={{
                                    padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800,
                                    border: "none",
                                    background: isPostingToTeams || !!teamsPostSuccess
                                        ? (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)")
                                        : "rgba(98,100,167,0.90)",
                                    color: isPostingToTeams || !!teamsPostSuccess ? textSecondary : "#fff",
                                    cursor: isPostingToTeams || !!teamsPostSuccess ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", gap: 8,
                                    transition: "transform 0.12s ease",
                                }}
                                onMouseEnter={(e) => {
                                    if (!isPostingToTeams && !teamsPostSuccess) e.currentTarget.style.transform = "scale(1.02)";
                                }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                            >
                                {isPostingToTeams ? (
                                    <>
                                        <div style={{
                                            width: 14, height: 14, borderRadius: "50%",
                                            border: "2px solid rgba(255,255,255,0.3)",
                                            borderTopColor: "#fff",
                                            animation: "spin 0.7s linear infinite",
                                        }} />
                                        Posting...
                                    </>
                                ) : "💬 Post to Teams"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

          



            {/* OneDrive Upload Modal */}
            {isOneDriveModalOpen && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.55)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: 16,
                        zIndex: 9999,
                    }}
                    onClick={() => { if (!isUploadingToOneDrive) setIsOneDriveModalOpen(false); }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: isMobile ? "100vw" : "min(660px, 96vw)",
                            maxHeight: "90vh",
                            overflowY: "auto",
                            borderRadius: 14,
                            padding: 20,
                            background: isDark ? "rgba(42,42,42,1)" : isContrast ? "#000" : "#fff",
                            color: textColor,
                            boxShadow: isDark ? "0 16px 40px rgba(0,0,0,0.6)" : "0 16px 40px rgba(0,0,0,0.25)",
                            border: isDark || isContrast ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.10)",
                            display: "flex",
                            flexDirection: "column",
                            gap: 16,
                        }}
                    >
                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, fontSize: 22,
                                background: isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)",
                                border: "1px solid rgba(0,120,212,0.30)",
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>
                                ☁️
                            </div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, fontSize: 16, color: textColor }}>
                                    Upload to OneDrive
                                </div>
                                <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                                    Save response as a .txt file to your OneDrive
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsOneDriveModalOpen(false)}
                                disabled={isUploadingToOneDrive}
                                style={{
                                    border: "none", background: "transparent", cursor: "pointer",
                                    color: textSecondary, fontSize: 20, padding: "4px 8px", borderRadius: 8,
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = iconHoverBg}
                                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Error banner */}
                        {oneDriveError && (
                            <div style={{
                                padding: "10px 14px", borderRadius: 8, fontSize: 13,
                                background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.30)",
                                color: isDark ? "#fca5a5" : "#b91c1c",
                            }}>
                                {oneDriveError}
                            </div>
                        )}

                        {/* Success banner */}
                        {oneDriveSuccess && (
                            <div style={{
                                padding: "12px 14px", borderRadius: 8, fontSize: 13,
                                background: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.30)",
                                color: isDark ? "#86efac" : "#15803d",
                                display: "flex", flexDirection: "column", gap: 8,
                            }}>
                                <div>{oneDriveSuccess}</div>
                                {oneDriveUploadedUrl && (
                                    <a href={oneDriveUploadedUrl} target="_blank" rel="noopener noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: primaryIconColor, fontSize: 13, fontWeight: 700, textDecoration: "none" }}>
                                        🔗 Open file in OneDrive
                                    </a>
                                )}
                            </div>
                        )}

                        {/* File Name */}
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                File Name <span style={{ color: "rgba(220,38,38,0.8)" }}>*</span>
                                <span style={{ fontWeight: 400, marginLeft: 6, opacity: 0.7 }}>(will be saved as .txt)</span>
                            </div>
                            <input
                                value={oneDriveFileName}
                                onChange={(e) => setOneDriveFileName(e.target.value)}
                                placeholder="spark-response.txt"
                                disabled={isUploadingToOneDrive}
                                style={{
                                    width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                                    border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                    background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                                    color: textColor, outline: "none", boxSizing: "border-box",
                                }}
                            />
                        </div>

                        {/* Folder Picker */}
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 8 }}>
                                Save to Folder
                                {isLoadingOneDriveFolders && (
                                    <span style={{ opacity: 0.6, fontWeight: 400, marginLeft: 8 }}>— loading...</span>
                                )}
                            </div>

                            {/* Breadcrumb */}
                            <div style={{
                                display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap",
                                marginBottom: 10, padding: "7px 10px", borderRadius: 8,
                                background: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                                border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                            }}>
                                {oneDriveBreadcrumb.map((crumb, index) => (
                                    <React.Fragment key={crumb.id}>
                                        <button
                                            type="button"
                                            onClick={() => navigateOneDriveBreadcrumb(crumb, index)}
                                            style={{
                                                border: "none", background: "transparent", cursor: "pointer",
                                                color: index === oneDriveBreadcrumb.length - 1 ? primaryIconColor : textSecondary,
                                                fontSize: 12, fontWeight: index === oneDriveBreadcrumb.length - 1 ? 700 : 500,
                                                padding: "2px 4px", borderRadius: 4,
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.background = iconHoverBg}
                                            onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                        >
                                            {index === 0 ? "🏠 " : ""}{crumb.name}
                                        </button>
                                        {index < oneDriveBreadcrumb.length - 1 && (
                                            <span style={{ color: textTertiary, fontSize: 11 }}>›</span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>

                            {/* Folder list */}
                            <div style={{
                                maxHeight: 200, overflowY: "auto", borderRadius: 8,
                                border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                            }}>
                                {/* Save here row */}
                                <div
                                    onClick={() => {
                                        setOneDriveSelectedFolder(oneDriveBreadcrumb[oneDriveBreadcrumb.length - 1].id);
                                        setOneDriveSelectedFolderName(oneDriveBreadcrumb[oneDriveBreadcrumb.length - 1].name);
                                    }}
                                    style={{
                                        padding: "9px 12px", cursor: "pointer", fontSize: 13,
                                        display: "flex", alignItems: "center", gap: 10,
                                        background: oneDriveSelectedFolder === oneDriveBreadcrumb[oneDriveBreadcrumb.length - 1].id
                                            ? (isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)")
                                            : "transparent",
                                        borderBottom: isDark ? "1px solid rgba(255,255,255,0.06)" : "1px solid rgba(0,0,0,0.05)",
                                        transition: "background 0.12s ease",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (oneDriveSelectedFolder !== oneDriveBreadcrumb[oneDriveBreadcrumb.length - 1].id)
                                            e.currentTarget.style.background = pickerHover;
                                    }}
                                    onMouseLeave={(e) => {
                                        if (oneDriveSelectedFolder !== oneDriveBreadcrumb[oneDriveBreadcrumb.length - 1].id)
                                            e.currentTarget.style.background = "transparent";
                                    }}
                                >
                                    <span style={{ fontSize: 16 }}>📂</span>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 700, color: textColor, fontSize: 13 }}>
                                            Save here — {oneDriveBreadcrumb[oneDriveBreadcrumb.length - 1].name}
                                        </div>
                                        <div style={{ fontSize: 10, color: textTertiary, marginTop: 1 }}>Current folder</div>
                                    </div>
                                    {oneDriveSelectedFolder === oneDriveBreadcrumb[oneDriveBreadcrumb.length - 1].id && (
                                        <span style={{ color: primaryIconColor, fontWeight: 700, fontSize: 16 }}>✓</span>
                                    )}
                                </div>

                                {/* Sub-folders */}
                                {isLoadingOneDriveFolders ? (
                                    <div style={{ padding: "16px 12px", textAlign: "center", fontSize: 13, color: textSecondary }}>
                                        Loading folders...
                                    </div>
                                ) : oneDriveFolders.length === 0 ? (
                                    <div style={{ padding: "12px", textAlign: "center", fontSize: 12, color: textTertiary }}>
                                        No sub-folders in this location
                                    </div>
                                ) : (
                                    oneDriveFolders.map((folder) => (
                                        <div
                                            key={folder.id}
                                            style={{
                                                padding: "9px 12px", cursor: "pointer", fontSize: 13,
                                                display: "flex", alignItems: "center", gap: 10,
                                                background: oneDriveSelectedFolder === folder.id
                                                    ? (isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)")
                                                    : "transparent",
                                                borderBottom: isDark ? "1px solid rgba(255,255,255,0.04)" : "1px solid rgba(0,0,0,0.04)",
                                                transition: "background 0.12s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (oneDriveSelectedFolder !== folder.id)
                                                    e.currentTarget.style.background = pickerHover;
                                            }}
                                            onMouseLeave={(e) => {
                                                if (oneDriveSelectedFolder !== folder.id)
                                                    e.currentTarget.style.background = "transparent";
                                            }}
                                        >
                                            <span
                                                style={{ fontSize: 16, cursor: "pointer" }}
                                                title="Open folder"
                                                onClick={(e) => { e.stopPropagation(); navigateOneDriveFolder(folder); }}
                                            >
                                                📁
                                            </span>
                                            <div
                                                style={{ flex: 1 }}
                                                onClick={() => {
                                                    setOneDriveSelectedFolder(folder.id);
                                                    setOneDriveSelectedFolderName(folder.name);
                                                }}
                                            >
                                                <div style={{ fontWeight: 600, color: textColor }}>{folder.name}</div>
                                                {folder.itemCount != null && (
                                                    <div style={{ fontSize: 10, color: textTertiary, marginTop: 1 }}>
                                                        {folder.itemCount} item{folder.itemCount !== 1 ? "s" : ""}
                                                    </div>
                                                )}
                                            </div>
                                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                {oneDriveSelectedFolder === folder.id && (
                                                    <span style={{ color: primaryIconColor, fontWeight: 700, fontSize: 16 }}>✓</span>
                                                )}
                                                <button
                                                    type="button"
                                                    title="Open folder"
                                                    onClick={(e) => { e.stopPropagation(); navigateOneDriveFolder(folder); }}
                                                    style={{
                                                        border: "none", background: "transparent", cursor: "pointer",
                                                        color: textTertiary, fontSize: 12, padding: "2px 6px",
                                                        borderRadius: 4, fontWeight: 600,
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.color = primaryIconColor}
                                                    onMouseLeave={(e) => e.currentTarget.style.color = textTertiary}
                                                >
                                                    Open ›
                                                </button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* File Content Preview */}
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                                File Content Preview
                            </div>
                            <textarea
                                value={oneDriveFileContent}
                                onChange={(e) => setOneDriveFileContent(e.target.value)}
                                disabled={isUploadingToOneDrive}
                                rows={6}
                                style={{
                                    width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 12,
                                    border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.12)",
                                    background: isDark ? "rgba(0,0,0,0.25)" : "#fafafa",
                                    color: textColor, outline: "none", resize: "vertical",
                                    fontFamily: "ui-monospace, Consolas, monospace",
                                    lineHeight: 1.5, boxSizing: "border-box",
                                }}
                            />
                            <div style={{ fontSize: 11, color: textTertiary, marginTop: 4 }}>
                                {oneDriveFileContent.length.toLocaleString()} characters
                                · ~{Math.ceil(new Blob([oneDriveFileContent]).size / 1024)} KB
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 4 }}>
                            <button
                                type="button"
                                onClick={() => setIsOneDriveModalOpen(false)}
                                disabled={isUploadingToOneDrive}
                                style={{
                                    padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                                    border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                    background: "transparent", color: textColor, cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={submitOneDriveUpload}
                                disabled={isUploadingToOneDrive || !oneDriveFileName.trim() || !!oneDriveSuccess}
                                style={{
                                    padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800,
                                    border: "none",
                                    background: isUploadingToOneDrive || !oneDriveFileName.trim() || !!oneDriveSuccess
                                        ? (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)")
                                        : "rgba(0,120,212,0.90)",
                                    color: isUploadingToOneDrive || !oneDriveFileName.trim() || !!oneDriveSuccess
                                        ? textSecondary : "#fff",
                                    cursor: isUploadingToOneDrive || !oneDriveFileName.trim() || !!oneDriveSuccess
                                        ? "not-allowed" : "pointer",
                                    display: "flex", alignItems: "center", gap: 8,
                                    transition: "transform 0.12s ease",
                                }}
                                onMouseEnter={(e) => {
                                    if (!isUploadingToOneDrive && oneDriveFileName.trim() && !oneDriveSuccess)
                                        e.currentTarget.style.transform = "scale(1.02)";
                                }}
                                onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                            >
                                {isUploadingToOneDrive ? (
                                    <>
                                        <div style={{
                                            width: 14, height: 14, borderRadius: "50%",
                                            border: "2px solid rgba(255,255,255,0.3)",
                                            borderTopColor: "#fff",
                                            animation: "spin 0.7s linear infinite",
                                        }} />
                                        Uploading...
                                    </>
                                ) : (
                                    "☁️ Upload to OneDrive"
                                )}
                            </button>
                        </div>

                    </div>
                </div>
            )}





{/* ADO Work Item Modal */ }
{
    isAdoModalOpen && (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                zIndex: 9999,
            }}
            onClick={() => { if (!isCreatingAdoItem) setIsAdoModalOpen(false); }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    width: isMobile ? "100vw" : "min(680px, 96vw)",
                    maxHeight: "90vh",
                    overflowY: "auto",
                    borderRadius: 14,
                    padding: 20,
                    background: isDark ? "rgba(42,42,42,1)" : isContrast ? "#000" : "#fff",
                    color: textColor,
                    boxShadow: isDark ? "0 16px 40px rgba(0,0,0,0.6)" : "0 16px 40px rgba(0,0,0,0.25)",
                    border: isDark || isContrast ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.10)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                }}
            >
                {/* Header */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            background: isDark ? "rgba(0,120,212,0.20)" : "rgba(0,120,212,0.10)",
                            border: "1px solid rgba(0,120,212,0.30)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 20,
                            flexShrink: 0,
                        }}
                    >
                        🎯
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 16, color: textColor }}>
                            Create ADO Work Item
                        </div>
                        <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                            spglobal · Azure DevOps
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={() => setIsAdoModalOpen(false)}
                        disabled={isCreatingAdoItem}
                        style={{
                            border: "none", background: "transparent", cursor: "pointer",
                            color: textSecondary, fontSize: 20, padding: "4px 8px", borderRadius: 8,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = iconHoverBg}
                        onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                    >
                        ✕
                    </button>
                </div>

                {/* Error / Success banners */}
                {adoError && (
                    <div style={{
                        padding: "10px 14px", borderRadius: 8, fontSize: 13,
                        background: "rgba(220,38,38,0.12)", border: "1px solid rgba(220,38,38,0.30)",
                        color: isDark ? "#fca5a5" : "#b91c1c",
                    }}>
                        {adoError}
                    </div>
                )}
                {adoSuccess && (
                    <div style={{
                        padding: "10px 14px", borderRadius: 8, fontSize: 13,
                        background: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.30)",
                        color: isDark ? "#86efac" : "#15803d",
                    }}>
                        {adoSuccess}
                    </div>
                )}

                {/* Row 1: Project + Work Item Type */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    {/* Project */}
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                            Project {isLoadingAdoProjects && <span style={{ opacity: 0.6 }}>— loading...</span>}
                        </div>
                        <select
                            value={adoSelectedProject}
                            onChange={(e) => {
                                setAdoSelectedProject(e.target.value);
                                fetchAdoIterations(e.target.value);
                            }}
                            disabled={isLoadingAdoProjects || isCreatingAdoItem}
                            style={{
                                width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                                border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                                color: textColor, outline: "none", cursor: "pointer",
                            }}
                        >
                            {adoProjects.length === 0 && (
                                <option value="Spark Assist">Spark Assist</option>
                            )}
                            {adoProjects.map((p) => (
                                <option key={p.id} value={p.name}>{p.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Work Item Type */}
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                            Work Item Type
                        </div>
                        <select
                            value={adoWorkItemType}
                            onChange={(e) => setAdoWorkItemType(e.target.value)}
                            disabled={isCreatingAdoItem}
                            style={{
                                width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                                border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                                background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                                color: textColor, outline: "none", cursor: "pointer",
                            }}
                        >
                            <option value="User Story">📖 User Story</option>
                            <option value="Feature">⭐ Feature</option>
                            <option value="Epic">🏔️ Epic</option>
                            <option value="Task">✅ Task</option>
                            <option value="Bug">🐛 Bug</option>
                        </select>
                    </div>
                </div>

                {/* Sprint / Iteration */}
                <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                        Sprint / Iteration {isLoadingAdoIterations && <span style={{ opacity: 0.6 }}>— loading...</span>}
                    </div>
                    <select
                        value={adoSelectedIteration}
                        onChange={(e) => setAdoSelectedIteration(e.target.value)}
                        disabled={isLoadingAdoIterations || isCreatingAdoItem}
                        style={{
                            width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                            border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                            background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                            color: textColor, outline: "none", cursor: "pointer",
                        }}
                    >
                        <option value="">— No sprint (backlog) —</option>
                        {adoIterations.map((it) => (
                            <option key={it.id} value={it.path}>{it.name}</option>
                        ))}
                    </select>
                </div>

                {/* Title */}
                <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                        Title <span style={{ color: "rgba(220,38,38,0.8)" }}>*</span>
                    </div>
                    <input
                        value={adoTitle}
                        onChange={(e) => setAdoTitle(e.target.value)}
                        placeholder={`Enter ${adoWorkItemType} title...`}
                        disabled={isCreatingAdoItem}
                        style={{
                            width: "100%", padding: "9px 12px", borderRadius: 8, fontSize: 13,
                            border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                            background: isDark ? "rgba(255,255,255,0.06)" : "#fff",
                            color: textColor, outline: "none", boxSizing: "border-box",
                        }}
                    />
                </div>

                {/* Description */}
                <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: textSecondary, marginBottom: 6 }}>
                        Description
                    </div>
                    <textarea
                        value={adoDescription}
                        onChange={(e) => setAdoDescription(e.target.value)}
                        disabled={isCreatingAdoItem}
                        rows={7}
                        style={{
                            width: "100%", padding: "10px 12px", borderRadius: 8, fontSize: 13,
                            border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                            background: isDark ? "rgba(0,0,0,0.25)" : "#fff",
                            color: textColor, outline: "none", resize: "vertical",
                            fontFamily: "inherit", lineHeight: 1.5, boxSizing: "border-box",
                        }}
                    />
                </div>

                {/* Footer */}
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, paddingTop: 4 }}>
                    <button
                        type="button"
                        onClick={() => setIsAdoModalOpen(false)}
                        disabled={isCreatingAdoItem}
                        style={{
                            padding: "9px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                            border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.15)",
                            background: "transparent", color: textColor, cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submitAdoWorkItem}
                        disabled={isCreatingAdoItem || !adoTitle.trim()}
                        style={{
                            padding: "9px 20px", borderRadius: 8, fontSize: 13, fontWeight: 800,
                            border: "none",
                            background: isCreatingAdoItem || !adoTitle.trim()
                                ? (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.08)")
                                : "rgba(0,120,212,0.90)",
                            color: isCreatingAdoItem || !adoTitle.trim() ? textSecondary : "#fff",
                            cursor: isCreatingAdoItem || !adoTitle.trim() ? "not-allowed" : "pointer",
                            transition: "transform 0.12s ease",
                            display: "flex", alignItems: "center", gap: 8,
                        }}
                        onMouseEnter={(e) => { if (!isCreatingAdoItem && adoTitle.trim()) e.currentTarget.style.transform = "scale(1.02)"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
                    >
                        {isCreatingAdoItem ? (
                            <>
                                <div style={{
                                    width: 14, height: 14, borderRadius: "50%",
                                    border: "2px solid rgba(255,255,255,0.3)",
                                    borderTopColor: "#fff",
                                    animation: "spin 0.7s linear infinite",
                                }} />
                                Creating...
                            </>
                        ) : (
                            `Create ${adoWorkItemType}`
                        )}
                    </button>
                </div>
            </div>
        </div>
    )
}




{/* ── EWI Analytics Dashboard Modal ───────────────────────────────────── */}
{showAnalyticsDashboard && (
    <div
        style={{
            position: "fixed", inset: 0,
            background: "rgba(0,0,0,0.72)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: isMobile ? 0 : 8,
            zIndex: 99998,
        }}
        onClick={() => setShowAnalyticsDashboard(false)}
    >
        <div
            onClick={(e) => e.stopPropagation()}
            style={{
                width: isMobile ? "100vw" : "98vw",
                height: isMobile ? "100vh" : "96vh",
                borderRadius: isMobile ? 0 : 14,
                background: "#0d1117",
                display: "flex", flexDirection: "column",
                overflow: "hidden",
                boxShadow: "0 32px 96px rgba(0,0,0,0.6)",
                border: "1px solid rgba(255,255,255,0.10)",
            }}
        >
            {/* Modal header */}
            <div style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "12px 16px",
                background: "linear-gradient(135deg,#0d7e8f 0%,#0a5f6e 100%)",
                flexShrink: 0,
            }}>
                <span style={{ fontSize: 18 }}>📊</span>
                <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#fff" }}>EWI Platform Analytics</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.70)", marginTop: 1 }}>Live usage insights</div>
                </div>
                <button
                    type="button"
                    onClick={() => setShowAnalyticsDashboard(false)}
                    style={{
                        background: "rgba(255,255,255,0.12)", border: "none",
                        borderRadius: 8, width: 30, height: 30,
                        color: "#fff", fontSize: 16, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        flexShrink: 0,
                    }}
                    title="Close"
                >
                    ✕
                </button>
            </div>
            {/* Dashboard iframe */}
            <iframe
                src="/spark_anywhere_analytics_platform.html"
                title="EWI Platform Analytics Dashboard"
                style={{ flex: 1, border: "none", width: "100%", display: "block" }}
                allow="clipboard-write"
            />
        </div>
    </div>
)}

{/* Transcript Viewer Modal - keeping complete existing code unchanged */ }
{
    showTranscriptViewer && (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                zIndex: 9999,
            }}
            onClick={() => setShowTranscriptViewer(false)}
        >
            <div
                style={{
                    width: "min(900px, 96vw)",
                    maxHeight: isMobile ? "88dvh" : "85vh",
                    overflow: "hidden",
                    borderRadius: 14,
                    background: isDark ? "rgba(42, 42, 42, 1)" : "#ffffff",
                    color: textColor,
                    boxShadow: isDark ? "0 16px 40px rgba(0,0,0,0.6)" : "0 16px 40px rgba(0,0,0,0.25)",
                    border: isDark ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.12)",
                    display: "flex",
                    flexDirection: "column",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        padding: "12px 16px",
                        borderBottom: surfaceBorder,
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                    }}
                >
                    <Text weight="semibold" size={400} style={{ flex: 1 }}>
                        Raw Meeting Transcript
                    </Text>
                    <div style={{ fontSize: 12, color: textSecondary }}>
                        {transcript.length.toLocaleString()} chars
                    </div>
                    <Button appearance="secondary" onClick={() => setShowTranscriptViewer(false)}>
                        Close
                    </Button>
                </div>

                <div
                    style={{
                        padding: 16,
                        overflowY: "auto",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: "ui-monospace, Consolas, monospace",
                        fontSize: 12,
                        lineHeight: 1.6,
                        background: isDark ? "rgba(0,0,0,0.25)" : "rgba(0,0,0,0.03)",
                    }}
                >
                    {transcript}
                </div>
            </div>
        </div>
    )
            }

            {/* ═══════════════════════════════════════════════════════
                STT TRANSCRIPT VIEWER MODAL
                Shows full meeting transcript with speaker labels,
                timestamps, and raw word-level details.
                Opened by tapping the 🗣️ button in the meeting header.
            ═══════════════════════════════════════════════════════ */}
            {showSttViewer && meetingSttResult && (
                <div
                    style={{
                        position: "fixed", inset: 0,
                        background: "rgba(0,0,0,0.6)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: isMobile ? 0 : 16, zIndex: 9999,
                    }}
                    onClick={() => setShowSttViewer(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: "min(860px, 96vw)",
                            maxHeight: "90vh",
                            overflow: "hidden",
                            borderRadius: 14,
                            background: isDark ? "rgba(36,36,36,1)" : "#ffffff",
                            color: textColor,
                            boxShadow: isDark ? "0 20px 48px rgba(0,0,0,0.65)" : "0 20px 48px rgba(0,0,0,0.22)",
                            border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)",
                            display: "flex", flexDirection: "column",
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{
                            padding: "14px 18px",
                            borderBottom: surfaceBorder,
                            background: isDark ? "rgba(0,120,212,0.12)" : "rgba(0,120,212,0.06)",
                            display: "flex", alignItems: "center", gap: 12,
                        }}>
                            <span style={{ fontSize: 22 }}>🗣️</span>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, fontSize: 15, color: textColor }}>
                                    STT Meeting Transcript
                                </div>
                                <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                                    {meetingSttResult.utterances?.length || 0} utterances
                                    · {new Set((meetingSttResult.utterances || []).map(u => u.speakerTag)).size} speaker(s)
                                    · {meetingSttResult.audioSizeBytes
                                        ? `${(meetingSttResult.audioSizeBytes / 1024).toFixed(0)} KB audio`
                                        : ""}
                                    · Google Cloud STT
                                </div>
                            </div>

                            {/* Copy full transcript button */}
                            <IconButton
                                title="Copy full transcript"
                                onClick={() => copyToClipboard(meetingSttResult.fullTranscript)}
                            >
                                <CopyIcon />
                            </IconButton>

                            {/* Download button */}
                            <IconButton
                                title="Download transcript as .txt"
                                onClick={() => {
                                    const lines = (meetingSttResult.utterances || [])
                                        .map(u => `[${u.startFormatted}] ${u.speakerLabel}: ${u.text}`)
                                        .join("\n");
                                    downloadTextAsFile(lines, "stt-transcript");
                                }}
                                primary
                            >
                                <DownloadIcon />
                            </IconButton>

                            <button
                                type="button"
                                onClick={() => setShowSttViewer(false)}
                                style={{
                                    border: "none", background: "transparent", cursor: "pointer",
                                    color: textSecondary, fontSize: 20, padding: "4px 8px", borderRadius: 8,
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = iconHoverBg}
                                onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                            >✕</button>
                        </div>

                        {/* Tabs: Utterances view | Raw word view */}
                        <SttTabViewer
                            meetingSttResult={meetingSttResult}
                            isDark={isDark}
                            textColor={textColor}
                            textSecondary={textSecondary}
                            textTertiary={textTertiary}
                            primaryIconColor={primaryIconColor}
                            meetingTitle={"Teams Meeting"}
                            participants={[]}  
                        />
                    </div>
                </div>
            )}

            <AgentValidation
                isOpen={isAgentValidationOpen}
                workflow={workflowValidation}
                theme={appTheme}
                isSubmitting={isExecutingAgentStep}
                onClose={() => {
                    if (!isExecutingAgentStep) {
                        setIsAgentValidationOpen(false);
                        setWorkflowValidation(null);
                    }
                }}
                onApprove={(step, payload) => executeValidatedWorkplaceStep(step, payload, true)}
                onReject={(step) => executeValidatedWorkplaceStep(step, {}, false)}
            />

            <O365WriteActionDialog
                isOpen={writeActionDialog !== null}
                action={writeActionDialog}
                theme={appTheme}
                isSubmitting={isWriteActionSubmitting}
                onSubmit={handleWriteActionSubmit}
                onCancel={() => setWriteActionDialog(null)}
            />

{/* Email Modal - keeping complete existing code unchanged */ }
{
    showEmailModal && (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.55)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
                zIndex: 9999,
            }}
            onClick={() => {
                if (!isSendingEmail) setShowEmailModal(false);
            }}
        >
            <div
                style={{
                    width: isMobile ? "100vw" : "min(640px, 96vw)",
                    maxHeight: "85vh",
                    overflowY: "auto",
                    borderRadius: 14,
                    padding: 16,
                    background: isDark ? "rgba(42, 42, 42, 1)" : isContrast ? "#000000" : "#ffffff",
                    color: textColor,
                    boxShadow: isDark ? "0 16px 40px rgba(0,0,0,0.6)" : "0 16px 40px rgba(0,0,0,0.25)",
                    border: isDark || isContrast ? "1px solid rgba(255, 255, 255, 0.15)" : "none",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ flex: 1 }}>
                        <Text weight="semibold" size={400} style={{ color: textColor }}>
                            Send Email
                        </Text>
                    </div>

                    <Button appearance="secondary" onClick={() => setShowEmailModal(false)} disabled={isSendingEmail}>
                        Close
                    </Button>
                </div>

                <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: textSecondary, marginBottom: 6, fontWeight: 600 }}>To</div>
                    <Input
                        data-email-to-input
                        value={emailTo}
                        placeholder="name@spglobal.com"
                        onChange={(e) => setEmailTo(e.target.value)}
                        disabled={isSendingEmail}
                        style={{ width: "100%" }}
                    />
                </div>

                <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: textSecondary, marginBottom: 6, fontWeight: 600 }}>Subject</div>
                    <Input
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        disabled={isSendingEmail}
                        style={{ width: "100%" }}
                    />
                </div>

                <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 12, color: textSecondary, marginBottom: 6, fontWeight: 600 }}>Body</div>
                    <textarea
                        value={emailBody}
                        onChange={(e) => setEmailBody(e.target.value)}
                        disabled={isSendingEmail}
                        style={{
                            width: "100%",
                            minHeight: 220,
                            padding: 11,
                            borderRadius: 10,
                            border: isDark || isContrast ? "1px solid rgba(255, 255, 255, 0.2)" : "1px solid rgba(0, 0, 0, 0.15)",
                            background: isDark ? "rgba(0, 0, 0, 0.3)" : isContrast ? "#0a0a0a" : "#ffffff",
                            color: textColor,
                            outline: "none",
                            resize: "vertical",
                            fontFamily: "inherit",
                            fontSize: 13,
                        }}
                    />
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                    <Button appearance="secondary" onClick={() => setShowEmailModal(false)} disabled={isSendingEmail}>
                        Cancel
                    </Button>
                                    <Button appearance="primary" onClick={sendEmail} disabled={isSendingEmail}>
                                        {isSendingEmail ? "Sending..." : "Send Email"}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    )
                }
            </div>

            {/* ── Calendar Invite Modal ── */}
            {isCalendarModalOpen && (
                <div
                    style={{
                        position: "fixed", inset: 0,
                        background: "rgba(0,0,0,0.55)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        padding: 16, zIndex: 9999,
                    }}
                    onClick={() => { if (!isCreatingCalendar) setIsCalendarModalOpen(false); }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            width: isMobile ? "100vw" : "min(560px, 96vw)",
                            maxHeight: "92vh", overflowY: "auto",
                            borderRadius: 14, padding: 20,
                            background: isDark ? "rgba(42,42,42,1)" : isContrast ? "#000" : "#fff",
                            color: textColor,
                            boxShadow: isDark ? "0 16px 40px rgba(0,0,0,0.6)" : "0 16px 40px rgba(0,0,0,0.25)",
                            border: isDark || isContrast ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.10)",
                            display: "flex", flexDirection: "column", gap: 14,
                        }}
                    >
                        {/* Header */}
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{
                                width: 38, height: 38, borderRadius: 10, fontSize: 20,
                                background: isDark ? "rgba(22,163,74,0.18)" : "rgba(22,163,74,0.10)",
                                border: "1px solid rgba(22,163,74,0.35)",
                                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                            }}>📅</div>
                            <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 800, fontSize: 16 }}>Create Calendar Invite</div>
                                <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                                    Creates an Outlook event and sends invites to attendees
                                </div>
                            </div>
                            <button type="button" onClick={() => setIsCalendarModalOpen(false)} disabled={isCreatingCalendar}
                                style={{ border: "none", background: "transparent", cursor: "pointer", color: textSecondary, fontSize: 20, padding: "4px 8px", borderRadius: 8 }}>✕</button>
                        </div>

                        {/* Title */}
                        <div>
                            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 5, fontWeight: 600 }}>Meeting Title *</div>
                            <Input value={calendarTitle} onChange={e => setCalendarTitle(e.target.value)}
                                placeholder="e.g. Smartsheet Integration Review" disabled={isCreatingCalendar} style={{ width: "100%" }} />
                        </div>

                        {/* Date + times row */}
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                            <div style={{ flex: "2 1 140px" }}>
                                <div style={{ fontSize: 12, color: textSecondary, marginBottom: 5, fontWeight: 600 }}>Date *</div>
                                <input type="date" value={calendarDate} onChange={e => setCalendarDate(e.target.value)}
                                    disabled={isCreatingCalendar}
                                    style={{
                                        width: "100%", padding: "6px 10px", borderRadius: 8,
                                        border: isDark || isContrast ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.15)",
                                        background: isDark ? "rgba(0,0,0,0.3)" : isContrast ? "#0a0a0a" : "#fff",
                                        color: textColor, fontSize: 13, fontFamily: "inherit",
                                    }} />
                            </div>
                            <div style={{ flex: "1 1 100px" }}>
                                <div style={{ fontSize: 12, color: textSecondary, marginBottom: 5, fontWeight: 600 }}>Start</div>
                                <input type="time" value={calendarStartTime} onChange={e => setCalendarStartTime(e.target.value)}
                                    disabled={isCreatingCalendar}
                                    style={{
                                        width: "100%", padding: "6px 10px", borderRadius: 8,
                                        border: isDark || isContrast ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.15)",
                                        background: isDark ? "rgba(0,0,0,0.3)" : isContrast ? "#0a0a0a" : "#fff",
                                        color: textColor, fontSize: 13, fontFamily: "inherit",
                                    }} />
                            </div>
                            <div style={{ flex: "1 1 100px" }}>
                                <div style={{ fontSize: 12, color: textSecondary, marginBottom: 5, fontWeight: 600 }}>End</div>
                                <input type="time" value={calendarEndTime} onChange={e => setCalendarEndTime(e.target.value)}
                                    disabled={isCreatingCalendar}
                                    style={{
                                        width: "100%", padding: "6px 10px", borderRadius: 8,
                                        border: isDark || isContrast ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.15)",
                                        background: isDark ? "rgba(0,0,0,0.3)" : isContrast ? "#0a0a0a" : "#fff",
                                        color: textColor, fontSize: 13, fontFamily: "inherit",
                                    }} />
                            </div>
                        </div>

                        {/* Attendees */}
                        <div>
                            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 5, fontWeight: 600 }}>Attendees (emails, comma-separated)</div>
                            <Input value={calendarAttendees} onChange={e => setCalendarAttendees(e.target.value)}
                                placeholder="alice@company.com, bob@company.com"
                                disabled={isCreatingCalendar} style={{ width: "100%" }} />
                        </div>

                        {/* Location */}
                        <div>
                            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 5, fontWeight: 600 }}>Location / Teams link (optional)</div>
                            <Input value={calendarLocation} onChange={e => setCalendarLocation(e.target.value)}
                                placeholder="Conference Room A or Teams meeting URL"
                                disabled={isCreatingCalendar} style={{ width: "100%" }} />
                        </div>

                        {/* Body */}
                        <div>
                            <div style={{ fontSize: 12, color: textSecondary, marginBottom: 5, fontWeight: 600 }}>Description / Agenda</div>
                            <textarea value={calendarBody} onChange={e => setCalendarBody(e.target.value)}
                                disabled={isCreatingCalendar}
                                style={{
                                    width: "100%", minHeight: 120, padding: 10, borderRadius: 9,
                                    border: isDark || isContrast ? "1px solid rgba(255,255,255,0.2)" : "1px solid rgba(0,0,0,0.15)",
                                    background: isDark ? "rgba(0,0,0,0.3)" : isContrast ? "#0a0a0a" : "#fff",
                                    color: textColor, fontSize: 13, fontFamily: "inherit",
                                    outline: "none", resize: "vertical",
                                }} />
                        </div>

                        {calendarError && (
                            <div style={{ fontSize: 12, color: "#ef4444", padding: "6px 10px", borderRadius: 8, background: "rgba(239,68,68,0.08)" }}>
                                {calendarError}
                            </div>
                        )}
                        {calendarSuccess && (
                            <div style={{ fontSize: 12, color: isDark ? "#86efac" : "#15803d", padding: "6px 10px", borderRadius: 8, background: "rgba(22,163,74,0.08)" }}>
                                {calendarSuccess}
                            </div>
                        )}

                        {/* Footer buttons */}
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                            <Button appearance="secondary" onClick={() => setIsCalendarModalOpen(false)} disabled={isCreatingCalendar}>
                                Cancel
                            </Button>
                            <Button appearance="primary" onClick={submitCalendarEvent} disabled={isCreatingCalendar}>
                                {isCreatingCalendar ? "Creating…" : "Create Invite"}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Create Spark Panel - only show if feature flag enabled */}
            {flags.createSpark && (
                <CreateSparkPanel
                    isOpen={isSparkCreateOpen}
                    onClose={() => setIsSparkCreateOpen(false)}
                    userName={userName}
                    isDark={isDark}
                    isContrast={isContrast}
                />
            )}

            {/* ── Spark Embed Modal ── */}
            {embedSpark && (() => {
                const EMBED_HOST = "https://j3zg21r5-44302.use2.devtunnels.ms";
                const embedCode =
                    `<!-- Spark Anywhere Widget – ${embedSpark.title} -->
<script
  src="${EMBED_HOST}/spark-embed.js"
  data-spark-id="${embedSpark.sparkId}"
  data-position="bottom-right"
  data-theme="light"
  data-title="${embedSpark.title}"
  defer>
</script>`;

                return (
                    <div
                        style={{
                            position: "fixed", inset: 0,
                            background: "rgba(0,0,0,0.60)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            padding: isMobile ? 0 : 20,
                            zIndex: 99999,
                        }}
                        onClick={() => { setEmbedSpark(null); setEmbedCopied(false); }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                width: isMobile ? "100vw" : "min(620px, 96vw)",
                                borderRadius: isMobile ? "16px 16px 0 0" : 14,
                                background: isDark ? "#1e1e1e" : "#ffffff",
                                color: textColor,
                                boxShadow: "0 24px 64px rgba(0,0,0,0.35)",
                                border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(0,0,0,0.10)",
                                display: "flex", flexDirection: "column",
                                overflow: "hidden",
                            }}
                        >
                            {/* Header */}
                            <div style={{
                                background: "linear-gradient(135deg, #0d7e8f 0%, #0a5f6e 100%)",
                                padding: "14px 18px",
                                display: "flex", alignItems: "center", gap: 12,
                            }}>
                                <div style={{
                                    width: 36, height: 36, borderRadius: 10,
                                    background: "rgba(255,255,255,0.15)",
                                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="16 18 22 12 16 6" />
                                        <polyline points="8 6 2 12 8 18" />
                                    </svg>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>Embed Spark Widget</div>
                                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 2 }}>
                                        {embedSpark.title} · Spark ID {embedSpark.sparkId}
                                    </div>
                                </div>
                                <button type="button"
                                    onClick={() => { setEmbedSpark(null); setEmbedCopied(false); }}
                                    style={{
                                        border: "none", background: "rgba(255,255,255,0.15)",
                                        borderRadius: 8, width: 30, height: 30,
                                        display: "flex", alignItems: "center", justifyContent: "center",
                                        cursor: "pointer", color: "#fff", fontSize: 16,
                                    }}
                                >✕</button>
                            </div>

                            {/* Steps */}
                            <div style={{ padding: "18px 20px 0", display: "flex", gap: 16, flexWrap: "wrap" }}>
                                {[
                                    { step: "1", label: "Copy the code below" },
                                    { step: "2", label: "Paste into any webpage HTML" },
                                    { step: "3", label: "Widget appears instantly" },
                                ].map(({ step, label }) => (
                                    <div key={step} style={{ display: "flex", alignItems: "center", gap: 8, flex: "1 1 0", minWidth: 120 }}>
                                        <div style={{
                                            width: 22, height: 22, borderRadius: "50%",
                                            background: "linear-gradient(135deg, #0d7e8f, #0a5f6e)",
                                            color: "#fff", fontSize: 11, fontWeight: 800,
                                            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                                        }}>{step}</div>
                                        <span style={{ fontSize: 12, color: textSecondary }}>{label}</span>
                                    </div>
                                ))}
                            </div>

                            {/* Code block */}
                            <div style={{ padding: "14px 20px 0" }}>
                                <div style={{
                                    borderRadius: 10,
                                    border: isDark ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(0,0,0,0.10)",
                                    overflow: "hidden",
                                }}>
                                    {/* Toolbar */}
                                    <div style={{
                                        display: "flex", alignItems: "center", justifyContent: "space-between",
                                        padding: "8px 12px",
                                        background: isDark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
                                        borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
                                    }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: textTertiary, letterSpacing: "0.04em" }}>HTML</span>
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                await navigator.clipboard.writeText(embedCode);
                                                setEmbedCopied(true);
                                                setTimeout(() => setEmbedCopied(false), 2500);
                                            }}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 6,
                                                padding: "5px 12px", borderRadius: 7, border: "none",
                                                background: embedCopied
                                                    ? "linear-gradient(135deg, #0d7e8f, #0a5f6e)"
                                                    : (isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.07)"),
                                                color: embedCopied ? "#fff" : textColor,
                                                fontSize: 12, fontWeight: 700, cursor: "pointer",
                                                transition: "all 0.2s ease",
                                            }}
                                        >
                                            {embedCopied ? "✓ Copied!" : "Copy Code"}
                                        </button>
                                    </div>
                                    {/* Code */}
                                    <pre style={{
                                        margin: 0, padding: "14px 16px",
                                        fontSize: 12, lineHeight: 1.7, overflowX: "auto",
                                        fontFamily: "ui-monospace, Consolas, monospace",
                                        background: isDark ? "#0d1117" : "#f6f8fa",
                                        color: isDark ? "#e6edf3" : "#24292f",
                                        whiteSpace: "pre",
                                    }}>
                                        {embedCode.split("\n").map((line, i) => {
                                            const isComment = line.trim().startsWith("<!--") || line.trim().startsWith("-->");
                                            const isAttr = /^\s+(src|data-|defer)/.test(line);
                                            const isTag = /^<script/.test(line.trim()) || line.trim() === "</script>";
                                            return (
                                                <div key={i} style={{
                                                    color: isComment ? (isDark ? "#8b949e" : "#6e7781")
                                                        : isAttr ? (isDark ? "#79c0ff" : "#0550ae")
                                                            : isTag ? (isDark ? "#ff7b72" : "#cf222e")
                                                                : "inherit",
                                                }}>
                                                    {line || "\u00a0"}
                                                </div>
                                            );
                                        })}
                                    </pre>
                                </div>
                            </div>

                            {/* Optional attrs hint */}
                            <div style={{ padding: "12px 20px 0" }}>
                                <div style={{
                                    padding: "10px 14px", borderRadius: 9,
                                    background: isDark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.03)",
                                    border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
                                }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: textSecondary, marginBottom: 5 }}>Optional attributes</div>
                                    <div style={{ fontSize: 11, color: textTertiary, lineHeight: 1.8, fontFamily: "ui-monospace, monospace" }}>
                                        <span style={{ color: primaryIconColor }}>data-theme</span>=&quot;light&quot; | &quot;dark&quot;&nbsp;&nbsp;
                                        <span style={{ color: primaryIconColor }}>data-position</span>=&quot;bottom-right&quot; | &quot;bottom-left&quot;&nbsp;&nbsp;
                                        <span style={{ color: primaryIconColor }}>data-title</span>=&quot;custom label&quot;
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div style={{
                                padding: "14px 20px 18px",
                                borderTop: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(0,0,0,0.07)",
                                marginTop: 14,
                                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                            }}>
                                <div style={{ fontSize: 11, color: textTertiary, lineHeight: 1.5, flex: 1 }}>
                                    Works on SharePoint, Confluence, custom portals, or any HTML page.
                                </div>
                                <button
                                    type="button"
                                    onClick={async () => {
                                        await navigator.clipboard.writeText(embedCode);
                                        setEmbedCopied(true);
                                        setTimeout(() => { setEmbedCopied(false); setEmbedSpark(null); }, 1800);
                                    }}
                                    style={{
                                        padding: "9px 20px", borderRadius: 9, border: "none",
                                        background: embedCopied
                                            ? "rgba(22,163,74,0.90)"
                                            : "linear-gradient(135deg, #0d7e8f 0%, #0a5f6e 100%)",
                                        color: "#fff", fontSize: 13, fontWeight: 800,
                                        cursor: "pointer", flexShrink: 0,
                                        transition: "all 0.2s ease",
                                        boxShadow: "0 3px 12px rgba(13,126,143,0.30)",
                                    }}
                                >
                                    {embedCopied ? "✓ Copied & Closing…" : "Copy & Close"}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* ── Guided Tour ── */}
            <GuidedTour
                isOpen={showTour}
                onClose={() => setShowTour(false)}
                isDark={isDark}
                isContrast={isContrast}
                flags={flags}
            />

            {/* Admin Flags Panel — only visible for admin users */}
            <AdminFlagsPanel
                isOpen={showAdminPanel}
                onClose={() => setShowAdminPanel(false)}
                userEmail={userName}
            />
        </div>
    );
};

export default ChatPanel;